/**
 * Application entry point: wires the sketch model, canvas, and table together.
 *
 * The flow is deliberately one-directional. User input hits a handler → the
 * handler mutates the model via an intent method → a render function reads the
 * model and repaints. Two render paths, and the distinction matters:
 *   renderAll()      rebuilds the table; only for structural changes (cleanup,
 *                    clear, undo). NEVER on focus or a keystroke.
 *   renderGeometry() updates the table in place, skipping the focused input;
 *                    for selection and length edits, so typing is never
 *                    interrupted by the input being torn down and rebuilt.
 *
 * Business rules are resolved once here (from the offline defaults, until the
 * sync engine exists) and threaded into the model. See
 * src/config/business-rules.js — the UI never reads a rule constant inline.
 */
import { COUNTERTOP_OTHER, DEFAULT_BUSINESS_RULES, EDITABLE_NUMBER_RULES, countertopChoiceForThickness, migrateStorageNeedKeys, resolveBusinessRules, versionedListSaveValue } from '../config/business-rules.js';
import { atticStairElevationForWall, ceilingOpeningElevationForWall, formatLength, parseLength, referenceLineRidesPlatform, roundToNearest, strokesToWalls } from '../geometry/index.js';
import { stackHasBaseTrim, stackHasCrownTrim } from '../geometry/trim.js';
import { createSketchModel } from './sketch-model.js';
import { createCanvas } from './canvas.js';
import { visualSnapClosedWalls } from './visual-snap-closure.js';
import { closeContextMenu, showContextMenu, showPopover } from './context-menu.js';
import { confirmAction } from './confirm-dialog.js';
import { createMeasurementSheet } from './measurement-sheet.js';
import { mountNumericPad } from './numeric-pad.js';
import { nonReentrantRender } from './non-reentrant-render.js';
import { createTable } from './table.js';
import { createElevation } from './elevation.js';
import {
  applyInteriorDraft,
  createInteriorDraft,
  draftInsertShelvesIntoOpening,
  draftOpenings,
  draftSetRequiredHolesOnly,
  draftSetShelfDoubleThick,
  doorDepthState,
  draftAdd,
  draftAddBank,
  draftAddDrawer,
  draftEnsureBandSpaces,
  draftFeedForUnit,
  draftHasHang,
  draftMove,
  draftRemove,
  draftRemoveDrawer,
  draftSetDoorHeight,
  draftSetDoorHandleType,
  draftSetDoorExtent,
  draftSetDoorStyle,
  draftSetDoorSplit,
  draftSetDoors,
  draftSetDrawerHeight,
  draftSetShelfFixed,
  draftSetShelfGroupCount,
  draftSetShelfGroupFixed,
  draftSetShelfPin,
  draftSetShelfLocked,
  spaceBoundaryShelves,
  draftPinShelvesAtSolved,
  draftSetShoeCount,
  draftSetShoeStyle,
  draftFillSpace,
  draftHealingNotes,
  draftSpaces,
  draftSplitSpace,
  draftSplitSpaceEvenly,
  draftMergeSpace,
  mergeSpaceTargets,
  mergeControlFor,
  evenSplitOpeningUnits,
  drawerHeightOptions,
  drawerStackState,
  elementLabel,
  previewElevation,
  spaceLabel,
  spaceRangeLabel,
  SPACE_TREATMENTS,
  STRUCTURAL_ROLE_LABELS,
} from './interior-editor.js';
import { doorBand } from './elevation-interior.js';
import { deriveDoorGeometry, doorStyleByKey } from '../geometry/doors.js';
// The routine interior edits, done ON the section row (ADR 0066). Every one of
// these delegates to the draft helpers imported above, so a row edit and the
// equivalent modal edit produce the same composition by construction.
import {
  applyInlineDoors,
  applyInlineDrawerCount,
  applyInlineDrawerFace,
  applyInlineShelfCount,
  doorDepthTitle,
  inlineInteriorTargets,
  DOOR_COVERAGE_CHOICES,
} from './inline-interior.js';
import { sectionCodeLabel, sectionRowIdentity } from './section-identity.js';
import { structuralShelfRoles } from '../geometry/interior.js';
import { planThumbSvg } from './plan-thumb.js';
import { identityTemplatePickerItems } from './identity-template-picker.js';
import { availableBuckets, bucketByKey, priceBasicSpace } from '../pricing/bucket-pricing.js';
// The handles-required take-off (ADR 0064) — the quantity purchasing buys.
import { handleTakeOff } from '../takeoff/handle-takeoff.js';
import {
  copySpaceDoc,
  duplicateSketchDoc,
  hydrateSketch,
  SKETCH_SCHEMA_VERSION,
  opportunityKey,
  readSketchMeta,
  serializeSketch,
  sketchLabel,
  spaceIdentityConflict,
} from '../store/sketch-store.js';
import {
  deleteSketch,
  getMeta,
  getSketch,
  listSketches,
  putSketch,
  requestPersistentStorage,
  setMeta,
  getMetaResult,
  getMeasurementSession,
  listMeasurementSessions,
  putMeasurementSession,
  listRemeasureSessions,
  putRemeasureSession,
} from '../store/local-db.js';
import { createReusableSectionLibrary } from '../store/reusable-sections.js';
import { retargetTemplateCopy, sketchContentSummary, templateUseDecision } from './template-apply.js';
import {
  applyMappingEdit,
  catalogClasses,
  catalogConfigsFor,
  mappingGrid,
  unreachableCatalogFamilies,
  validateMappingRow,
} from '../config/cv-mapping-editor.js';
import { applyRemeasure } from '../measurement/apply-remeasure.js';
import {
  completeRemeasureSession,
  createRemeasureSession,
  remeasureReportRows,
  setRemeasureValue,
} from '../measurement/remeasure-session.js';
import {
  createOpportunityInventoryRequest,
  decodeInventoryPayload,
  encodeInventoryPayload,
  validateResponse,
} from '../measurement/client-inventory.js';
import { inventoryShareUrl, publishInventoryShareLink } from '../sync/inventory-share-links.js';
import {
  BASE_ROOM_TEMPLATES,
  baseRoomTemplateDocument,
  instantiateBaseRoomTemplate,
  isBaseRoomTemplateId,
} from '../templates/base-room-templates.js';
import {
  addCeilingFieldFeature,
  addFieldFeature,
  applyCandidateReading,
  armMeasurementSlot,
  createMeasurementSession,
  createWallSlots,
  discardCandidateReading,
  resizeWallSlots,
  selectCandidateReading,
} from '../measurement/measurement-session.js';
import { applyReviewedMeasurements, reviewableSlots } from '../measurement/measurement-review.js';
import { bluetoothReadiness, describeBluetoothSelection, getPermittedBluetoothDevice, inspectGattServices, requestBluetoothDevice, subscribeLeicaDistanceEvidence } from '../measurement/bluetooth-readiness.js';
import {
  activateFieldCapture,
  applySelectedFieldCaptureReading,
  armFieldCaptureTarget,
  beginArmedFieldCaptureCorrection,
  captureFieldReading,
  createFieldCaptureMode,
  restoreFieldCaptureMode,
  serializeFieldCaptureMode,
  deleteFieldCaptureReading,
  deactivateFieldCapture,
  disarmFieldCaptureTarget,
  isArmedFieldCaptureTargetConfirmed,
  isArmedFieldCaptureTargetLocked,
  readingsForArmedTarget,
  selectFieldCaptureReading,
  selectedReadingForArmedTarget,
  validateFieldCaptureTarget,
} from '../measurement/field-capture-mode.js';
import {
  captureEntityId,
  roomSketchCaptureTargetValue,
  writeRoomSketchCaptureTarget,
} from '../measurement/room-sketch-capture-targets.js';

export function startApp(root = document, {
  sync = null,
  ruleRows = {},
  auth = null,
  supabase = null,
  onLocalSaveStatus = null,
  sectionLibrary = null,
} = {}) {
  // Live rules (ADR 0039): resolved from the last-synced rows, JS defaults
  // filling any gap. Captured once per boot — fresh pulls apply next load.
  const rules = resolveBusinessRules(ruleRows);
  const model = createSketchModel(rules);

  const svg = root.getElementById('canvas');
  const tbody = root.getElementById('wall-rows');
  const status = root.getElementById('status');

  const canvas = createCanvas(svg, {
    onSelectWall(id) {
      model.selectWall(id);
      renderGeometry();
      if (fieldCaptureMode.active && id) armCanvasWallTarget(id);
    },
    // Tapping an amber (auto) wall chip locks in the solver's length as measured.
    onConfirmWallLength(id) {
      if (model.confirmWallLength(id)) {
        renderAll(); // the wall flips auto→measured; table + plan both change
        scheduleSave();
      }
    },
    onStrokeEnd(points) {
      model.addStroke(points);
      // Leaving Draw mode will now offer to clean these up (owner, 2026-07-29).
      drewSinceDrawMode = true;
      canvas.renderRaw(model.state.strokes, model.state.current);
    },
    onAnnotate(points, style) {
      model.addAnnotation({ surface: 'plan', color: style.color, width: style.width, points });
      canvas.renderAnnotations(model.annotationsFor('plan'));
      scheduleSave();
    },
    onAddText(point, text, style) {
      model.addTextAnnotation({ surface: 'plan', text, color: style.color, point, fontSize: activeTextSize });
      canvas.renderAnnotations(model.annotationsFor('plan'));
      scheduleSave();
    },
    onMoveText(id, point) {
      if (model.moveTextAnnotation(id, point)) {
        canvas.renderAnnotations(model.annotationsFor('plan'));
        scheduleSave();
      }
    },
    onMoveLabel(id, patch) {
      if (model.moveWallLabel(id, patch)) { renderGeometry(); scheduleSave(); }
    },
    // Dropping a dragged section caption reorders it on its wall (ADR 0037).
    onSectionReorder(id, wallId, slot) {
      const s = model.state.sections.find((x) => x.id === id);
      if (s && s.wallId === wallId && model.reorderSection(id, slot)) repaintSections();
    },
    // The drag's VERTICAL component nudges the elevation caption (owner,
    // 2026-08-28) — accumulated onto whatever nudge the label already has.
    onSectionLabelNudge(id, deltaUnits) {
      const s = model.state.sections.find((x) => x.id === id);
      if (s && model.setSectionElevCodeOffset(id, (s.elevCodeDyUnits ?? 0) + deltaUnits)) {
        repaintSections();
        scheduleSave();
      }
    },
    onLabelMenu(id, x, y) { openLabelMenu(id, x, y); },
    onTextMenu(id, x, y) { openTextNoteMenu(id, x, y); },
    onEraseAnnotation(id) {
      if (model.removeAnnotation(id)) {
        canvas.renderAnnotations(model.annotationsFor('plan'));
        scheduleSave();
      }
    },
  });

  const table = createTable(tbody, {
    onEditLength(id, value) {
      // For a partition the LENGTH column is its DEPTH (how far it sticks into
      // the room); for a normal wall it is the wall length. (ADR 0032)
      const ok = model.isPartition(id) ? model.setPartitionDepth(id, value) : model.setWallLength(id, value);
      // On a rejected value nothing changed, so do not repaint at all — the
      // live input keeps focus and shows its own invalid state. On a valid
      // edit repaint IN PLACE (renderGeometry, not a table rebuild): rebuilding
      // would destroy the input the user is leaving and could steal focus from
      // the input they are moving to.
      if (ok) renderGeometry();
      return ok;
    },
    onEditAngle(id, cvAngle) {
      // Correcting a snapped-wrong direction re-solves the chain (downstream
      // walls shift), but the wall SET and order are unchanged, so repaint in
      // place like a length edit — no table rebuild.
      const ok = model.setWallAngle(id, cvAngle);
      if (ok) renderGeometry();
      return ok;
    },
    onEditThickness(id, value) {
      // A partition's thickness IS its footprint segment's length (re-solves);
      // for a normal wall it is a render-only override.
      const ok = model.isPartition(id) ? model.setWallLength(id, value) : model.setWallThickness(id, value);
      if (ok) renderGeometry();
      return ok;
    },
    onSelectRow(id) {
      model.selectWall(id);
      renderGeometry();
    },
    onArmLength(id) {
      if (fieldCaptureMode.active) armCanvasWallTarget(id);
    },
    onBindCaptureTarget(control, target) {
      makeCaptureTarget(control, target);
    },
    onToggleElevation(id, include) {
      // For a partition the checkbox includes/excludes BOTH its faces.
      if (model.isPartition(id)) model.setPartitionElevation(id, include);
      else model.toggleElevation(id, include);
      renderGeometry(); // updates the elevation if it's showing; also saves
    },
    onToggleSnapLock(id, locked) {
      const ok = model.setWallSnapLocked(id, locked);
      if (ok) renderGeometry();
      return ok;
    },
    onDeleteSegment(id) { deleteSegment(id); },
    isPartition: (id) => model.isPartition(id),
    partitionElevation: (id) => model.partitionElevationFor(id),
  });

  /**
   * Removes a row from the wall table, routing each kind of segment to the
   * operation that knows how to heal the chain: a partition and a door both
   * put their split host wall back together, a wall simply leaves and lets the
   * solver re-chain (owner, 2026-08-04 — a bad snap used to mean redrawing the
   * whole space).
   *
   * Confirmation is asked for only when the wall actually carries work.
   * A stub from a bad cleanup is always empty, and making the designer confirm
   * that is friction for nothing.
   */
  async function deleteSegment(id) {
    if (model.isPartition(id)) {
      if (model.removePartition(id)) renderAll();
      return;
    }
    const segment = model.state.walls.find((w) => w.id === id);
    if (!segment) return;
    if ((segment.segmentKind ?? 'wall') === 'opening') {
      if (model.removeDoor(id)) renderAll();
      return;
    }
    const impact = model.wallDeletionImpact(id);
    const carried = [
      [impact.sections, 'section'],
      [impact.obstructions, 'obstruction'],
      [impact.labels, 'label'],
      [impact.helperLines, 'depth line'],
    ].filter(([n]) => n > 0).map(([n, word]) => `${n} ${word}${n === 1 ? '' : 's'}`);
    const listed = carried.length > 1
      ? `${carried.slice(0, -1).join(', ')} and ${carried[carried.length - 1]}`
      : carried[0];
    if (carried.length) {
      const ok = await confirmAction({
        title: `Delete wall ${segment.wallNumber}?`,
        body: `It carries ${listed}, which will be deleted with it. The walls either side will meet.`,
        confirmLabel: 'Delete wall',
        danger: true,
      });
      if (!ok) return;
    }
    if (model.deleteWall(id)) renderAll();
  }

  /** The draft a wall-elevation shelf drag is writing through, or null. Held
   *  for the gesture so the invariants heal ONCE and element indices stay put
   *  — see onShelfDragStart. */
  let wallShelfDrag = null;

  const elevSvg = root.getElementById('elevation');
  const thumbSvg = root.getElementById('elev-thumbnail');
  const elevation =
    elevSvg && thumbSvg
      ? createElevation(elevSvg, thumbSvg, {
          onSelectWall(id) {
            model.selectWall(id);
            renderGeometry();
          },

          // ---- DRAG-TO-PLACE ON THE WALL ELEVATION --------------------------
          // The last surface of the feature. It differs from the section
          // editor's preview in one way that matters: there is NO OPEN DRAFT
          // here. The editor stages every edit and Apply commits; the wall
          // elevation has no Apply, so a drag has to go all the way to the
          // model — createInteriorDraft → pin → applyInteriorDraft → save.
          //
          // Two consequences, both handled rather than hoped about:
          //
          //  1. applyInteriorDraft re-runs the structural invariants. Owner
          //     ruling (2026-08-30): that is SILENT here. The helpers only ever
          //     INSERT a required support board — never remove, never reorder
          //     (interior.js) — so the worst case is a board APPEARING, which
          //     the drawing shows the instant it does, and the standing ruling
          //     is no confirmation on add or delete.
          //  2. Element indices must not move mid-drag. Healing runs ONCE, on
          //     the first move, and the same held draft is re-applied after
          //     that — so the index captured when the gesture began still
          //     addresses the same board when it ends.
          onShelfDragStart(index, sectionId) {
            // Prepared, NOT committed. A tap that never becomes a drag must
            // change nothing, and anchoring is a real edit (it pins shelves
            // that were solving in flow).
            const section = model.state.sections.find((sec) => sec.id === sectionId);
            const unit = inlineUnitFor(sectionId);
            if (!section || !unit) { wallShelfDrag = null; return; }
            const draft = createInteriorDraft(
              model.sectionInterior(sectionId), section.doors, section.requiredHolesOnly === true,
            );
            // The same anchoring the editor does, and for the same reason: an
            // unpinned shelf solves IN FLOW, so pinning the dragged one would
            // re-cut the segments and move boards the designer never touched.
            draftPinShelvesAtSolved(draft, unit, rules);
            wallShelfDrag = { sectionId, draft, committed: false };
          },

          onDragShelf(index, atUnits, sectionId) {
            const held = wallShelfDrag;
            // A drag whose start we could not prepare, or that somehow belongs
            // to another section, writes nothing rather than guessing.
            if (!held || held.sectionId !== sectionId) return;
            if (!draftSetShelfPin(held.draft, index, atUnits)) return;
            const res = applyInteriorDraft(model, sectionId, held.draft);
            if (!res.ok) return;
            held.committed = true;
            renderElevation();
            repaintSections();
            scheduleSave();
          },
          // A drag on the elevation is a coarse placement; the model clamps it
          // to the wall and the numeric fields below do the fine tuning.
          onMoveObstruction(id, placement) {
            if (!model.moveObstruction(id, placement)) return;
            model.selectWall(placement.wallId); // keep the edit box on the item
            renderGeometry(); // repaints the elevation + obstruction box, and saves
          },
          onAnnotate(points, style, wallId) {
            model.addAnnotation({ surface: 'elevation', color: style.color, width: style.width, points, wallId });
            renderElevation();
            scheduleSave();
          },
          onAddText(point, text, style, wallId) {
            model.addTextAnnotation({ surface: 'elevation', text, color: style.color, point, wallId, fontSize: activeTextSize });
            renderElevation();
            scheduleSave();
          },
          onMoveText(id, point, wallId) {
            if (model.moveTextAnnotation(id, point, wallId)) {
              renderElevation();
              scheduleSave();
            }
          },
          onMoveLabel(id, patch) {
            if (model.moveWallLabel(id, patch)) { renderElevation(); renderGeometry(); scheduleSave(); }
          },
          // Dropping a dragged section code reorders it on its wall (ADR 0037).
          onSectionReorder(id, wallId, slot) {
            const s = model.state.sections.find((x) => x.id === id);
            if (s && s.wallId === wallId && model.reorderSection(id, slot)) repaintSections();
          },
          // Vertical drag = caption nudge (owner, 2026-08-28), accumulated.
          onSectionLabelNudge(id, deltaUnits) {
            const s = model.state.sections.find((x) => x.id === id);
            if (s && model.setSectionElevCodeOffset(id, (s.elevCodeDyUnits ?? 0) + deltaUnits)) {
              repaintSections();
              scheduleSave();
            }
          },
          onLabelMenu(id, x, y) { openLabelMenu(id, x, y); },
          onTextMenu(id, x, y) { openTextNoteMenu(id, x, y); },
          onEraseAnnotation(id) {
            if (model.removeAnnotation(id)) {
              renderElevation();
              scheduleSave();
            }
          },
        })
      : null;

  let currentView = 'plan';
  const workspace = root.querySelector('.workspace');

  const fmt = (units) => formatLength(units, rules.measurementLabelFormat);
  /**
   * The DISPLAY formatter for every System-32-derived figure (ADR 0061, owner
   * 2026-08-09): imperial, rounded to `system_display_round_units` (¼"). The
   * same one formatter, with its rounding option — placement keeps the exact
   * value, only the label rounds, and a designer never sees a millimetre.
   */
  const fmtSystem = (units) => formatLength(units, rules.measurementLabelFormat, {
    roundToUnits: rules.systemDisplayRoundUnits,
  });
  // Placeholder shown in a wall's Thick input when it has no override — the
  // shop default, so a blank field visibly means "using the default".
  const thickPlaceholder = fmt(rules.defaultWallThicknessUnits);

  /**
   * Options the plan canvas needs to draw wall thickness and on-plan
   * obstructions to scale: a per-wall thickness (override, else the rule
   * default), the units→px scale, and the obstruction lookup. All render-only.
   */
  function wallRenderOpts() {
    return {
      thicknessFor: (w) => (Number.isFinite(w.thicknessUnits) ? w.thicknessUnits : rules.defaultWallThicknessUnits),
      obstructionsFor: (wallId) => model.obstructionsForWall(wallId),
      ceilingOpenings: model.state.ceilingOpenings,
      ladderCeilingInsetUnits: rules.atticLadderCeilingInsetUnits,
      ladderSideClearanceUnits: rules.atticLadderSideClearanceUnits,
      doorSwingLeafAllowanceUnits: rules.doorSwingLeafAllowanceUnits,
      bifoldFoldedProjectionUnits: rules.bifoldFoldedProjectionUnits,
      // Partition footprints (ADR 0032): the boundary stays solid through them
      // and each grows a spur into the room by its depth.
      partitionIds: new Set(model.state.partitions.map((p) => p.segmentId)),
      partitionDepthFor: (segmentId) => model.partitionDepthFor(segmentId),
      // Solver-derived length for an unmeasured wall, so the plan chip can show
      // it (amber = auto) and offer click-to-confirm instead of a bare dash.
      autoLengthFor: (w) => model.autoLength(w),
      // Standard section-type labels along the wall (ADR 0034).
      wallLabelsFor: (wallId) => model.wallLabelsForWall(wallId),
      // The two numbered faces of each partition, for on-plan number labels.
      partitionFaces: model.partitionFaceWalls().map((f) => ({ ...f, lengthLabel: fmt(f.lengthUnits) })),
      helperLinesFor: model.state.planHelperLinesVisible ? (wallId) => model.planHelperLinesFor(wallId) : () => [],
      // Snap closure only reconciles a perimeter already drawn as a loop. A one-
      // or two-wall design, or an intentional partial run, must remain open.
      armedMeasurementWallId: fieldCaptureMode.active && fieldCaptureMode.armedTarget?.kind === 'wall_length'
        ? fieldCaptureMode.armedTarget.entityId
        : null,
      // Wall sections (ADR 0037): solved layout + raw records per host.
      sectionLayoutFor: (wallId) => model.sectionLayoutForWall(wallId),
      sectionsFor: (wallId) => model.sectionsForWall(wallId),
      // The per-endpoint hanging-into-hanging override, which the plan marks
      // with a warning caption. Injected rather than read off a `model` the
      // canvas can reach: canvas.js owns no state, and reaching for one is
      // exactly the mistake this replaced.
      sectionIntoHangingAt: (section, end) => model.sectionIntoHangingAt(section, end),
      // Hang clearance (RS-4): whether a section holds a rod, by COMPOSITION
      // rather than type code (ADR 0057), and the admin-editable clear space
      // it needs in front of the carcass. The plan draws that as a second
      // outline; `depthUnits` stays the real footprint.
      sectionHangsFor: (section) => model.sectionHasHang(section),
      sectionHangClearanceUnits: rules.sectionHangClearanceUnits,
      // A wall's STATED length, resolved from the CANONICAL walls, so the plan
      // can scale on-wall content to the wall as DRAWN when snap closure has
      // moved its endpoints (RS-1). It must not come off the display wall: that
      // would just recover the drawn length and the factor would always be 1.
      statedLengthUnitsFor: (wallId) => {
        const wall = model.state.walls.find((w) => w.id === wallId);
        return wall ? model.wallLengthUnits(wall) : null;
      },
      // Units stacked on a run section, so the plan shows ALL units (ADR 0041).
      stacksFor: (wallId) => model.stacksForWall(wallId),
      connectingShelvesFor: (wallId) => model.connectingShelvesForWall(wallId),
      // L-shelf legs reaching along this wall from a neighbour's run (ADR 0042).
      lShelfLegsFor: (wallId) => model.lShelfSecondLegsForWall(wallId),
      lShelfInfoFor: (sec) => model.sectionLShelfInfo(sec),
      openSpanLabelMinUnits: rules.planOpenSpanLabelMinUnits,
      // Crown/base band on the plan (owner, 2026-08-28): drawn when either
      // trim is on; ¾" from the same rule the L-shelf backer uses.
      planTrimEnabled: model.state.crownEnabled === true || model.state.baseTrimEnabled === true,
      planTrimEligibleFor: (section, units) =>
        (model.state.crownEnabled === true && stackHasCrownTrim(units))
        || (model.state.baseTrimEnabled === true && stackHasBaseTrim(section, units)),
      planTrimUnits: rules.sectionPanelThicknessUnits,
      sectionDefaultDepthUnits: rules.sectionDefaultDepthUnits,
      formatLength: fmt,
      pxPerUnit: model.state.pxPerUnit,
      gridVisible: model.state.gridVisible,
      gridSpacingUnits: rules.gridSpacingUnits,
    };
  }

  /** Display geometry may reconcile closure; every model/calculation stays on actual walls. */
  function planDisplayWalls() {
    return visualSnapClosedWalls(model.state.walls, {
      enabled: model.state.snapClosed !== false && model.state.closedPerimeter === true,
      openChain: model.state.closedPerimeter !== true,
    });
  }

  /**
   * Repaint whichever drawing is showing — obstructions now appear on BOTH the
   * plan (thickness layer) and the elevation, so a change to one must refresh
   * the active view. Touches only the drawing, not the table or side panel.
   */
  function repaintView() {
    if (currentView === 'elevation') {
      renderElevation();
    } else {
      canvas.renderWalls(planDisplayWalls(), model.state.selectedWallId, (w) => model.displayLength(w), wallRenderOpts());
    }
  }

  function renderElevation() {
    if (!elevation) return;
    elevation.render(elevationWalls(), elevationRenderOpts());
    elevation.renderThumbnail(model.state.walls, model.state.selectedWallId, model.partitionFaceWalls());
    renderHelperToggles();
  }

  /**
   * The chain as the ELEVATION sees it: each partition's two faces spliced in
   * right after their footprint, so panels run in the same woven order as the
   * numbers (ADR 0032).
   */
  function elevationWalls() {
    const facesBySeg = new Map();
    for (const f of model.partitionFaceWalls()) {
      const list = facesBySeg.get(f.partitionSegmentId) ?? [];
      list.push(f);
      facesBySeg.set(f.partitionSegmentId, list);
    }
    const out = [];
    for (const seg of model.state.walls) {
      out.push(seg);
      if (facesBySeg.has(seg.id)) out.push(...facesBySeg.get(seg.id));
    }
    return out;
  }

  /**
   * Everything the elevation renderer needs, extracted so the measurement sheet
   * (ADR 0045) can draw with EXACTLY the same options against its own SVG —
   * a second options object would be a second thing to keep in step.
   */
  function elevationRenderOpts() {
    const visibleLines = (rules.elevationReferenceLines ?? []).filter((line) => model.isHelperLineVisible(line.label));
    // The designer's own lines (up to 3), when set and shown, join the
    // rule-driven ones.
    for (const line of model.customHelperLines()) {
      if (line.visible && Number.isFinite(line.units) && line.units > 0) {
        visibleLines.push({
          offset_from_floor_units: line.units,
          kind: 'custom',
          style: 'dashed',
          label: `Custom ${fmt(line.units)}`,
        });
      }
    }
    // Platform (ADR 0040): on a platform, the reference lines that mark section
    // features (panel tops, the bottom-shelf datum) ride UP with the sections;
    // true-height datums (countertop, stacked reach) stay put.
    const platformUnits = model.state.platformHeightUnits > 0 ? model.state.platformHeightUnits : 0;
    const referenceLines = platformUnits > 0
      ? visibleLines.map((line) => (referenceLineRidesPlatform(line.kind)
        ? { ...line, offset_from_floor_units: (line.offset_from_floor_units || 0) + platformUnits }
        : line))
      : visibleLines;
    // Section type anchors drive the platform math (default 'floor').
    const presetByCode = new Map((rules.sectionTypePresets ?? []).map((p) => [String(p.code).toUpperCase(), p]));
    return {
      // Shelf drag targets on the WALL elevation (owner rulings, 2026-08-30 —
      // the second surface). Every unit is draggable here: unlike the editor's
      // preview, no unit is "the open draft", so the section id stamped beside
      // each target is what says which composition a board belongs to.
      shelfDragTargetsFor: () => true,
      pxPerUnit: model.state.pxPerUnit ?? 3,
      ceilingHeightUnits: model.state.ceilingHeightUnits,
      referenceLines,
      // Sloped ceilings (ADR 0044): each wall's height profile, corner-derived.
      wallHeightProfileFor: (wallId) => model.wallHeightProfileFor(wallId),
      platformUnits,
      selectedId: model.state.selectedWallId,
      formatLength: fmt,
      obstructionsFor: (wallId) => model.obstructionsForWall(wallId),
      // The drawing carries the CODE only; the sheet's table carries the rest.
      obstructionCodeFor: (id) => model.obstructionCode(id),
      atticStairsFor: (wallId) => model.state.ceilingOpenings
        .map((opening) => atticStairElevationForWall(model.state.walls, opening, wallId, {
          pxPerUnit: model.state.pxPerUnit,
          ceilingHeightUnits: model.state.ceilingHeightUnits,
          ladderCeilingInsetUnits: rules.atticLadderCeilingInsetUnits,
          ladderSideClearanceUnits: rules.atticLadderSideClearanceUnits,
        }))
        .filter(Boolean),
      ceilingOpeningsFor: (wallId) => model.state.ceilingOpenings
        .map((opening) => ceilingOpeningElevationForWall(model.state.walls, opening, wallId, {
          pxPerUnit: model.state.pxPerUnit,
        }))
        .filter(Boolean),
      annotations: model.annotationsFor('elevation'),
      wallLabels: model.state.wallLabels,
      sectionLayoutFor: (wallId) => model.sectionLayoutForWall(wallId),
      connectingShelvesFor: (wallId) => model.connectingShelvesForWall(wallId),
      // Vertical placement of every run section AND anything stacked on it,
      // solved by the model (ADR 0040 platforms + ADR 0041 stacking).
      stacksFor: (wallId) => model.stacksForWall(wallId),
      // Composed interior per placed unit (ADR 0055, plan §5/4b): the solved
      // element spans + doors the elevation draws inside each carcass. Takes
      // the UNIT (not a section id) because the solve happens in the unit's
      // placed band — same reason stacksFor feeds placements, not sections.
      interiorFor: (unit) => model.interiorForUnit(unit),
      // An L-shelf's second leg, on the wall it is ATTACHED to (ADR 0042) —
      // its body lives in the neighbour's run, so only this feeds it here.
      lShelfLegsFor: (wallId) => model.lShelfSecondLegsForWall(wallId),
      lShelfInfoFor: (sec) => model.sectionLShelfInfo(sec),
      openSpanLabelMinUnits: rules.planOpenSpanLabelMinUnits,
      // The END PANEL of the run around the corner, which stands in front of
      // this wall — so a fixture hidden behind it reads as hidden, rather than
      // looking reachable on an elevation that cannot see it (owner,
      // 2026-08-05).
      abuttingPanelsFor: (wallId) => model.abuttingPanelsForWall(wallId),
      // Staged bucket cards ASSIGNED to this wall (ADR 0059) — a generic
      // glyph + count on the elevation, spatial context without placement.
      stagedForWall: (wallId) => (model.state.basic?.cards ?? [])
        .filter((c) => c.wallId === wallId && c.qty > 0)
        .map((c) => ({
          count: c.qty,
          label: (rules.basicBuckets ?? []).find((b) => b?.key === c.bucketKey)?.label ?? c.bucketKey,
        })),
      panelThicknessUnits: rules.sectionPanelThicknessUnits,
      // Heights RESOLVED (pin → space override → type → rule) so the drawing
      // always reflects the inheritance chain. anchor/workSurfaceUnits come from
      // the section-type preset (default floor) and drive the platform shift.
      sectionsFor: (wallId) => model.sectionsForWall(wallId).map((s) => {
        const preset = presetByCode.get(String(s.typeCode).toUpperCase());
        return {
          ...s,
          heightUnits: model.sectionHeightUnits(s),
          anchor: preset?.anchor ?? 'floor',
          workSurfaceUnits: preset?.workSurfaceUnits > 0 ? preset.workSurfaceUnits : null,
        };
      }),
      sectionDefaultHeightUnits: rules.sectionDefaultHeightUnits,
      gridVisible: model.state.gridVisible,
      gridSpacingUnits: rules.gridSpacingUnits,
      // The two independent dimension toggles (ADR 0061 item E): shelf spacing
      // and drawer face heights, drawn beside the marks they measure — and
      // labelled with the 1/4"-rounded System-32 display formatter.
      shelfSpacingDimsVisible: model.state.shelfSpacingDimsVisible === true,
      drawerFaceDimsVisible: model.state.drawerFaceDimsVisible === true,
      formatSystemLength: fmtSystem,
      // Doors as a left/right PAIR + the below-floor width caption (ADR 0060).
      doorPairCenterRevealUnits: rules.doorPairCenterRevealUnits,
      doorStyles: rules.doorStyles,
      hangRodDiameterUnits: rules.hangRodDiameterUnits,
      doorPairMinWidthUnits: rules.doorPairMinWidthUnits,
      elevationWidthLabelDropPx: rules.elevationWidthLabelDropPx,
      // HANDLE placement (ADR 0064): the face's ¼" side overlay, the handle
      // catalog and its default, and the two-pull threshold + its 25%
      // exception. Forwarded under their rule names so ONE resolver reads
      // them — see handleRules() in src/geometry/handles.js.
      drawerFaceSideOverlayUnits: rules.drawerFaceSideOverlayUnits,
      handleTypes: rules.handleTypes,
      defaultHandleType: rules.defaultHandleType,
      defaultHandleLengthMm: rules.defaultHandleLengthMm,
      handleTwoPullMinFaceWidthUnits: rules.handleTwoPullMinFaceWidthUnits,
      handleSinglePullMinWidthFraction: rules.handleSinglePullMinWidthFraction,
      handlePairPositions: rules.handlePairPositions,
      // Soffit FF labels: membership by the TYPE's capsSectionHeight property
      // (D110 — an admin-added capping type labels itself too).
      capsSectionHeightTypeKeys: new Set(
        (rules.obstructionTypes ?? []).filter((t) => t.capsSectionHeight === true).map((t) => t.key),
      ),
      crownEnabled: model.state.crownEnabled === true,
      baseTrimEnabled: model.state.baseTrimEnabled === true,
      // Resolved per space: the designer's override, else the admin rule
      // (owner, 2026-08-28: defaults "may vary/need to be overridden").
      crownMouldingHeightUnits: model.crownMouldingHeightUnits(),
      baseTrimHeightUnits: model.baseTrimHeightUnits(),
      baseTrimShelfOverlapRatio: rules.baseTrimShelfOverlapRatio,
      sectionShelfThicknessUnits: rules.sectionShelfThicknessUnits,
    };
  }

  const copyText = (text) => { try { navigator.clipboard?.writeText(String(text ?? '')); } catch { /* clipboard blocked */ } };

  /** Right-click menu for a standard wall label: copy its code, or delete it. */
  function openLabelMenu(id, x, y) {
    const label = model.state.wallLabels.find((l) => l.id === id);
    if (!label) return;
    showContextMenu(x, y, [
      { label: `Copy “${label.text}”`, onClick: () => copyText(label.text) },
      { label: 'Delete', danger: true, onClick: () => { if (model.removeWallLabel(id)) { renderGeometry(); scheduleSave(); } } },
    ]);
  }

  /** Right-click menu for a typed text note: copy, duplicate, or delete. */
  function openTextNoteMenu(id, x, y) {
    const note = model.state.annotations.find((a) => a.id === id && a.kind === 'text');
    if (!note) return;
    const repaint = () => {
      canvas.renderAnnotations(model.annotationsFor('plan'));
      if (currentView === 'elevation') renderElevation();
      scheduleSave();
    };
    // Sizes first: a note that came out too small should not have to be deleted
    // and retyped (owner, 2026-07-29).
    const sizeItems = TEXT_SIZES.map(({ label, size }) => ({
      label: `${label} text${(note.fontSize ?? 22) === size ? ' ✓' : ''}`,
      onClick: () => { if (model.setTextAnnotationSize(id, size)) repaint(); },
    }));
    showContextMenu(x, y, [
      ...sizeItems,
      { label: `Copy “${note.text}”`, onClick: () => copyText(note.text) },
      { label: 'Duplicate', onClick: () => {
        const p = note.points[0] ?? { x: 0, y: 0 };
        model.addTextAnnotation({ surface: note.surface, text: note.text, color: note.color, point: { x: p.x + 16, y: p.y - 16 }, wallId: note.wallId });
        repaint();
      } },
      { label: 'Delete', danger: true, onClick: () => { if (model.removeAnnotation(id)) repaint(); } },
    ]);
  }

  // ---- obstructions --------------------------------------------------------

  // Which face of a selected partition new obstructions/labels attach to.
  let selectedPartitionSide = 0;
  let obstructionScope = 'wall';

  /**
   * Room and wall fixtures share one conceptual Obstructions panel. Keeping
   * the selected scope in UI state avoids two tall, visually unrelated panels
   * while leaving the saved measurement model unchanged.
   */
  function setObstructionScope(scope) {
    obstructionScope = scope === 'room' ? 'room' : 'wall';
    for (const name of ['room', 'wall']) {
      const selected = obstructionScope === name;
      const tab = root.getElementById(`obstruction-scope-${name}`);
      const panel = root.getElementById(`obstruction-${name}-panel`);
      tab?.classList.toggle('obstruction-scope-tabs__tab--active', selected);
      tab?.setAttribute('aria-selected', String(selected));
      if (panel) panel.hidden = !selected;
    }
  }

  /**
   * The wall (or partition FACE) that obstructions/labels attach to right now,
   * or null. A real wall hosts directly; a selected partition hosts on one of
   * its two faces (the "side" the designer picks — ADR 0035).
   * @returns {{id:string, label:string, faces:object[]|null, faceIndex?:number}|null}
   */
  /**
   * Confirms an obstruction add while the DRAW tool is active (owner,
   * 2026-08-07: obstructions kept appearing mid-sketch, three times in one
   * session).
   *
   * No code path places one on its own — the canvas only feeds strokes — so
   * the adds were real taps: while sketching, a palm or sleeve brushing the
   * quick-add row fires it, and it lands on the SELECTED wall, which during
   * drawing is whatever cleanup or a delete left selected. That wall can be
   * across the room, which is why the adds looked unrelated to any button.
   * (The tab split is why this is new: Obstructions now sits high on the Space
   * tab, on-screen while sketching, where it used to be scrolled far down.)
   *
   * The confirm is scoped to draw mode on purpose. All three accidents
   * happened mid-sketch; a designer deliberately placing fixtures is in
   * select mode, and taxing every intentional add with a dialog would trade
   * one annoyance for a bigger one. The dialog NAMES the wall, so even a
   * confirmed brush shows where it is about to land.
   */
  async function confirmObstructionAdd(label, hostLabel) {
    if (canvas.getMode() !== 'draw' || currentView !== 'plan') return true;
    return confirmAction({
      title: `Add ${label} to ${hostLabel}?`,
      body: 'You are sketching — this button is easy to brush by accident. The fixture goes on the SELECTED wall, which may not be where you are drawing.',
      confirmLabel: 'Add it',
    });
  }

  function activeWallHost() {
    const sel = model.selectedSegment();
    if (!sel) return null;
    if ((sel.segmentKind ?? 'wall') === 'wall') return { id: sel.id, label: `W${sel.wallNumber}`, faces: null };
    if (model.isPartition(sel.id)) {
      const faces = model.partitionFaceWalls().filter((f) => f.partitionSegmentId === sel.id);
      if (faces.length < 2) return null;
      const faceIndex = Math.min(selectedPartitionSide, faces.length - 1);
      return { id: faces[faceIndex].id, label: `face W${faces[faceIndex].wallNumber}`, faces, faceIndex };
    }
    return null;
  }

  /** A "which side" toggle, shown when the host is a partition; else nothing. */
  function renderSideChooser(box, host) {
    if (!host?.faces) return;
    const row = document.createElement('div');
    row.className = 'side-chooser';
    const lbl = document.createElement('span');
    lbl.className = 'side-chooser__label';
    lbl.textContent = 'Side:';
    row.append(lbl);
    host.faces.forEach((f, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `side-chooser__btn${i === host.faceIndex ? ' side-chooser__btn--active' : ''}`;
      btn.textContent = `W${f.wallNumber}`;
      btn.addEventListener('click', () => { selectedPartitionSide = i; renderObstructions(); renderSectionsPanel(); });
      row.append(btn);
    });
    box.append(row);
  }

  /** Fill the type dropdown once from the rules catalog. */
  function populateObstructionTypes() {
    const select = root.getElementById('obstruction-type');
    if (!select || select.options.length) return;
    for (const type of rules.obstructionTypes ?? []) {
      if (type.host === 'ceiling') continue;
      const option = document.createElement('option');
      option.value = type.key;
      option.textContent = type.label;
      select.append(option);
    }
  }

  /**
   * Show the selected wall's obstructions with editable position and size.
   * Obstructions belong to a wall, so the panel follows the selection — and the
   * heading names the wall so it's obvious what you're editing.
   */
  function renderObstructions() {
    const list = root.getElementById('obstruction-list');
    const context = root.getElementById('wall-obstruction-context');
    const addBtn = root.getElementById('add-obstruction');
    if (!list) return;

    const host = activeWallHost();
    if (addBtn) addBtn.disabled = !host;
    if (context) context.textContent = host
      ? `Wall obstructions · ${host.label}`
      : 'Select a wall to add or edit wall obstructions.';

    renderObstructionQuickAdd(host);
    list.replaceChildren();
    if (!host) return;

    const items = model.obstructionsForWall(host.id);
    if (items.length === 0) {
      const hint = document.createElement('p');
      hint.className = 'obstruction-list__empty';
      hint.textContent = 'Nothing on this wall yet.';
      list.append(hint);
      return;
    }

    for (const item of items) {
      list.append(buildObstructionCard(item));
    }
  }

  /**
   * One-tap quick-add buttons for the common obstruction types (ADR 0034):
   * with a wall selected, tapping one places it at its standard mounting height
   * — the same flow as the label palette. The dropdown still holds the full set.
   */
  function renderObstructionQuickAdd(host) {
    const box = root.getElementById('obstruction-quick');
    if (!box) return;
    box.replaceChildren();
    if (!host) return;
    renderSideChooser(box, host); // partition: pick which face first
    const byKey = new Map((rules.obstructionTypes ?? []).map((t) => [t.key, t]));
    for (const key of rules.obstructionQuickKeys ?? []) {
      const type = byKey.get(key);
      if (!type) continue;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'label-preset';
      btn.textContent = shortTypeName(type.label);
      btn.title = `Add ${type.label} at its standard height`;
      btn.addEventListener('click', async () => {
        if (!(await confirmObstructionAdd(shortTypeName(type.label), host.label))) return;
        if (model.addObstruction(host.id, key)) {
          renderObstructions();
          repaintView();
          scheduleSave();
        }
      });
      box.append(btn);
    }
  }

  /** A short button label from a full obstruction label ("Outlet — duplex…" → "Outlet"). */
  function shortTypeName(label) {
    return String(label ?? '').split(/[—(]/)[0].trim() || String(label ?? '');
  }

  /**
   * Depth-line controls for the selected wall (ADR 0024): quick presets, a
   * custom distance, and the list of lines already on the wall. Mirrors the
   * obstruction panel — it follows the selection.
   */
  function renderDepthLines() {
    const title = root.getElementById('depth-lines-title');
    const presetsBox = root.getElementById('depth-lines-presets');
    const customInput = root.getElementById('depth-line-custom');
    const addBtn = root.getElementById('depth-line-add');
    const list = root.getElementById('depth-lines-list');
    if (!list || !presetsBox) return;

    const selected = model.selectedSegment();
    const isWall = selected && (selected.segmentKind ?? 'wall') === 'wall';
    if (title) title.textContent = isWall ? `Depth lines · W${selected.wallNumber}` : 'Depth lines';
    if (customInput) customInput.disabled = !isWall;
    if (addBtn) addBtn.disabled = !isWall;

    // Show/hide-all toggle, reflecting current visibility.
    const showToggle = root.getElementById('depth-lines-show');
    if (showToggle) showToggle.checked = model.state.planHelperLinesVisible !== false;

    const add = (offset) => {
      if (!isWall || !model.addPlanHelperLine(selected.id, offset)) return;
      renderDepthLines();
      repaintView();
      scheduleSave();
    };

    presetsBox.replaceChildren();
    for (const preset of rules.planDepthLinePresets ?? [12, 16, 24]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'depth-lines__preset';
      btn.textContent = `${preset}"`;
      btn.disabled = !isWall;
      btn.addEventListener('click', () => add(preset));
      presetsBox.append(btn);
    }

    list.replaceChildren();
    if (!isWall) return;
    for (const line of model.planHelperLinesFor(selected.id)) {
      const row = document.createElement('div');
      row.className = 'depth-lines__row';
      const label = document.createElement('span');
      label.textContent = fmt(line.offsetUnits);
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'obstruction__delete';
      del.textContent = '✕';
      del.setAttribute('aria-label', `Remove ${fmt(line.offsetUnits)} depth line`);
      del.addEventListener('click', () => {
        model.removePlanHelperLine(line.id);
        renderDepthLines();
        repaintView();
        scheduleSave();
      });
      row.append(label, del);
      list.append(row);
    }
  }

  function buildObstructionCard(item) {
    const card = document.createElement('details');
    card.className = 'obstruction';

    const head = document.createElement('summary');
    head.className = 'obstruction__head';
    const name = document.createElement('span');
    name.className = 'obstruction__name';
    name.textContent = item.label || item.type;

    // Lock toggle: a locked fixture cannot be dragged (accidentally nudged),
    // though its measurements are still typeable. The padlock reflects state.
    const lock = document.createElement('button');
    lock.type = 'button';
    lock.className = `obstruction__lock${item.locked ? ' obstruction__lock--on' : ''}`;
    lock.textContent = item.locked ? '🔒' : '🔓';
    lock.title = item.locked ? 'Locked — tap to unlock' : 'Unlocked — tap to lock in place';
    lock.setAttribute('aria-pressed', String(Boolean(item.locked)));
    lock.setAttribute('aria-label', `${item.locked ? 'Unlock' : 'Lock'} ${item.label || item.type}`);
    lock.addEventListener('click', (event) => {
      event.stopPropagation();
      model.setObstructionLocked(item.id);
      renderObstructions();
      repaintView(); // plan shows obstructions too, so repaint whichever is up
      scheduleSave();
    });

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'obstruction__delete';
    del.textContent = '✕';
    del.setAttribute('aria-label', `Remove ${item.label || item.type}`);
    del.addEventListener('click', (event) => {
      event.stopPropagation();
      model.removeObstruction(item.id);
      renderObstructions();
      repaintView(); // plan shows obstructions too, so repaint whichever is up
      scheduleSave();
    });
    head.append(name, lock, del);

    const fields = document.createElement('div');
    fields.className = 'obstruction__fields';
    const addOpeningLength = (labelText, key, value) => {
      const row = document.createElement('label'); row.className = 'obstruction__field';
      const text = document.createElement('span'); text.textContent = labelText;
      const control = document.createElement('input'); control.type = 'text'; control.className = 'obstruction__input'; control.value = value == null ? '' : fmt(value);
      control.addEventListener('change', () => {
        const raw = control.value.trim();
        const parsed = raw === '' ? null : parseLength(raw);
        const ok = (raw === '' || Number.isFinite(parsed))
          && model.setWallOpening(item.id, { [key]: parsed });
        control.classList.toggle('obstruction__input--invalid', !ok);
        if (ok) { repaintView(); scheduleSave(); }
      });
      makeCaptureTarget(control, {
        kind: 'opening_field', entityId: item.id, property: key,
        label: `${item.label || 'Opening'} · ${labelText}`,
      });
      row.append(text, control); fields.append(row);
    };
    if (item.type === 'wall_opening') {
      const opening = item.opening ?? { kind: 'swing' };
      const wrap = document.createElement('label'); wrap.className = 'obstruction__field';
      const label = document.createElement('span'); label.textContent = 'Opening type';
      const select = document.createElement('select'); select.className = 'obstruction__input';
      for (const [value, text] of [
        ['swing', 'Swing door'], ['pocket', 'Pocket door'], ['bifold_single', 'Bifold — single'],
        ['bifold_double', 'Bifold — double'], ['bypass', 'Bypass door'],
        ['cased_opening', 'Cased opening / no door'], ['uncased_opening', 'Uncased opening / no door'],
      ]) { const option = document.createElement('option'); option.value = value; option.textContent = text; select.append(option); }
      select.value = opening.kind ?? 'swing';
      select.addEventListener('change', () => {
        if (model.setWallOpening(item.id, { kind: select.value })) { renderObstructions(); repaintView(); scheduleSave(); }
      });
      wrap.append(label, select); fields.append(wrap);

      const addOpeningSelect = (labelText, key, choices, value) => {
        const row = document.createElement('label'); row.className = 'obstruction__field';
        const text = document.createElement('span'); text.textContent = labelText;
        const control = document.createElement('select'); control.className = 'obstruction__input';
        for (const [nextValue, nextText] of choices) { const option = document.createElement('option'); option.value = nextValue; option.textContent = nextText; control.append(option); }
        control.value = value;
        control.addEventListener('change', () => { if (model.setWallOpening(item.id, { [key]: control.value })) { repaintView(); scheduleSave(); } });
        row.append(text, control); fields.append(row);
      };
      if (opening.kind === 'swing') {
        addOpeningLength('Clear door opening (inside casing)', 'clearOpeningWidthUnits', opening.clearOpeningWidthUnits ?? item.widthUnits);
        addOpeningLength('Swing projection (clear + allowance by default)', 'swingProjectionUnits',
          opening.swingProjectionUnits ?? ((opening.clearOpeningWidthUnits ?? item.widthUnits) + rules.doorSwingLeafAllowanceUnits));
        addOpeningSelect('Swing direction', 'swingDirection', [['in', 'Into room'], ['out', 'Out of room']], opening.swingDirection ?? 'in');
        addOpeningSelect('Hinge — room view', 'hingeSide', [['left', 'Left'], ['right', 'Right']], opening.hingeSide ?? 'left');
      } else if (opening.kind === 'pocket') {
        addOpeningSelect('Slides', 'slideDirection', [['left', 'Left'], ['right', 'Right']], opening.slideDirection ?? 'left');
      } else if (opening.kind.startsWith('bifold')) {
        if (opening.kind === 'bifold_single') {
          addOpeningSelect('Folded side — room view', 'foldSide', [['left', 'Left'], ['right', 'Right']], opening.foldSide ?? 'left');
        }
        addOpeningLength('Folded projection outside room', 'bifoldStackProjectionUnits',
          opening.bifoldStackProjectionUnits ?? rules.bifoldFoldedProjectionUnits);
      } else if (opening.kind === 'bypass') {
        addOpeningLength('Clear opening — parked left', 'bypassClearLeftUnits', opening.bypassClearLeftUnits);
        addOpeningLength('Clear opening — parked right', 'bypassClearRightUnits', opening.bypassClearRightUnits);
      }
    }
    if (String(item.type).startsWith('window')) {
      addOpeningLength('Clear opening inside casing · width', 'clearOpeningWidthUnits', item.opening?.clearOpeningWidthUnits);
      addOpeningLength('Clear opening inside casing · height', 'clearOpeningHeightUnits', item.opening?.clearOpeningHeightUnits);
    }
    const addField = (labelText, field, value, tip) => {
      const wrap = document.createElement('label');
      wrap.className = 'obstruction__field';
      if (tip) wrap.title = tip;
      const span = document.createElement('span');
      span.textContent = labelText;
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'obstruction__input';
      // faceOffsetUnits is signed and flush-by-default: show 0 as empty with a
      // "flush" hint, negatives keep their sign (fmt handles that).
      if (field === 'faceOffsetUnits') {
        input.value = value ? fmt(value) : '';
        input.placeholder = 'flush';
      } else {
        input.value = value == null ? '' : fmt(value);
      }
      if (field === 'depthUnits') input.placeholder = 'flush';
      input.addEventListener('change', () => {
        const ok = field === 'referenceOffset'
          ? model.updateObstructionReferenceOffset(item.id, input.value)
          : model.updateObstruction(item.id, field, input.value);
        input.classList.toggle('obstruction__input--invalid', ok === false);
        if (ok) {
          repaintView();
          scheduleSave();
        }
      });
      makeCaptureTarget(input, {
        kind: item.type === 'wall_opening' ? 'opening_field' : 'feature_field',
        entityId: item.id,
        property: field,
        label: `${item.label || item.type} · ${labelText}`,
      });
      wrap.append(span, input);
      fields.append(wrap);
    };
    // The edge selector matches how the designer physically takes a tape
    // reading: choose whichever wall end is convenient, then enter the
    // corresponding obstruction-edge distance. Geometry stays canonical.
    const edgeWrap = document.createElement('label');
    edgeWrap.className = 'obstruction__field';
    const edgeLabel = document.createElement('span');
    edgeLabel.textContent = 'Measure from';
    const edgeSelect = document.createElement('select');
    edgeSelect.className = 'obstruction__input';
    for (const [value, label] of [['left', 'Left wall edge'], ['right', 'Right wall edge']]) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      option.selected = (item.referenceEdge ?? 'left') === value;
      edgeSelect.append(option);
    }
    edgeSelect.addEventListener('change', () => {
      if (model.setObstructionReferenceEdge(item.id, edgeSelect.value)) {
        renderObstructions();
        scheduleSave();
      }
    });
    edgeWrap.append(edgeLabel, edgeSelect);
    fields.append(edgeWrap);
    const referenceEdge = item.referenceEdge === 'right' ? 'right' : 'left';
    addField(`From ${referenceEdge} edge`, 'referenceOffset', model.obstructionReferenceOffset(item.id),
      'Measure from the wall edge you selected to the corresponding obstruction edge.');
    addField('From floor', 'offsetFromFloor', item.offsetFromFloor);
    addField(item.type === 'wall_opening' ? 'Overall width (including casing)' : 'Width', 'widthUnits', item.widthUnits);
    addField('Height', 'heightUnits', item.heightUnits);
    // DEPTH is the clear space required, not the box (owner, 2026-08-05). A
    // breaker panel is 4" deep but its door swings 20": what matters to the
    // cabinetry is how much space in front of the wall has to stay open, so
    // that is the number to enter.
    addField('Depth', 'depthUnits', item.depthUnits,
      'How much space in FRONT of the wall must stay clear — not the item’s own thickness. For anything with a door that swings (a breaker panel, an access hatch), enter the swing clearance.');
    // In/Out from the wall face on the plan: + into the room, − recessed.
    addField('In/Out', 'faceOffsetUnits', item.faceOffsetUnits);

    card.append(head, fields);
    return card;
  }

  /** Build the show/hide checkboxes for the elevation helper lines. */
  // Guarded: the custom-height inputs below re-render this container from
  // their change handlers, which Chrome can fire mid-replaceChildren — see
  // non-reentrant-render.js for the NotFoundError this prevents.
  const renderHelperToggles = nonReentrantRender(() => {
    const box = root.getElementById('helper-lines');
    if (!box) return;
    box.replaceChildren();
    for (const line of rules.elevationReferenceLines ?? []) {
      const id = `helper-${line.label.replace(/[^a-z0-9]+/gi, '-')}`;
      const label = document.createElement('label');
      label.className = 'helper-line';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.id = id;
      cb.checked = model.isHelperLineVisible(line.label);
      cb.addEventListener('change', () => {
        model.setHelperLineVisible(line.label, cb.checked);
        renderElevation();
        scheduleSave();
      });
      const text = document.createElement('span');
      text.textContent = line.label;
      label.append(cb, text);
      box.append(label);
    }

    // The custom lines (up to 3): each a checkbox plus a height the designer
    // types. A plain div, not a <label> — a label wrapping both controls can
    // forward a click on the text field to the checkbox.
    const customLines = model.customHelperLines();
    customLines.forEach((line, i) => {
      const row = document.createElement('div');
      row.className = 'helper-line helper-line--custom';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = line.visible !== false;
      cb.disabled = !Number.isFinite(line.units);
      cb.setAttribute('aria-label', `Show custom helper line ${i + 1}`);
      cb.addEventListener('change', () => {
        model.setCustomHelperVisible(i, cb.checked);
        renderElevation();
        scheduleSave();
      });

      const text = document.createElement('span');
      text.textContent = `Custom ${i + 1}`;

      const height = document.createElement('input');
      height.type = 'text';
      height.className = 'helper-line__height';
      height.placeholder = 'height';
      height.setAttribute('aria-label', `Custom helper line ${i + 1} height`);
      height.value = Number.isFinite(line.units) ? fmt(line.units) : '';
      // Commit on change (blur/Enter) so the line doesn't jump on every keystroke.
      height.addEventListener('change', () => {
        const ok = model.setCustomHelperLine(i, height.value);
        height.classList.toggle('helper-line__height--invalid', ok === false);
        if (ok) {
          renderHelperToggles();
          renderElevation();
          scheduleSave();
        }
      });
      height.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') height.blur();
      });

      row.append(cb, text, height);
      box.append(row);
    });
  });

  function renderHeights() {
    const set = (id, value) => {
      const inputEl = root.getElementById(id);
      if (inputEl && inputEl.ownerDocument.activeElement !== inputEl) inputEl.value = fmt(value);
    };
    set('ceiling-height', model.state.ceilingHeightUnits);
    set('door-height', model.state.doorHeightUnits);
    const flat = root.getElementById('ceiling-flat');
    if (flat) flat.checked = model.state.ceilingFlat !== false;
  }

  /** Renders ceiling openings and fixtures with two explicit wall references. */
  function renderCeilingOpenings() {
    const list = root.getElementById('ceiling-opening-list');
    const refA = root.getElementById('ceiling-opening-ref-a');
    const refB = root.getElementById('ceiling-opening-ref-b');
    if (!list || !refA || !refB) return;
    const walls = model.state.walls.filter((wall) => (wall.segmentKind ?? 'wall') === 'wall');
    const previousA = refA.value;
    const previousB = refB.value;
    const options = () => walls.map((wall) => {
      const option = document.createElement('option');
      option.value = wall.id;
      option.textContent = `W${wall.wallNumber}`;
      return option;
    });
    refA.replaceChildren(...options());
    refB.replaceChildren(...options());
    refA.value = walls.some((wall) => wall.id === previousA) ? previousA : (walls[0]?.id ?? '');
    refB.value = walls.some((wall) => wall.id === previousB) && previousB !== refA.value
      ? previousB
      : (walls.find((wall) => wall.id !== refA.value)?.id ?? '');
    const addButton = root.querySelector('#ceiling-opening-form button[type="submit"]');
    if (addButton) addButton.disabled = walls.length < 2;

    const wallLabel = (id) => `W${walls.find((wall) => wall.id === id)?.wallNumber ?? '?'}`;
    list.replaceChildren(...model.state.ceilingOpenings.map((opening) => {
      const card = document.createElement('details');
      card.className = 'ceiling-opening-card';
      const head = document.createElement('summary');
      head.className = 'ceiling-opening-card__head';
      const title = document.createElement('strong');
      title.textContent = ({
        attic_stairs: 'Drop-down attic stairs', attic_hatch: 'Attic hatch',
        ceiling_fan: 'Ceiling fan', ceiling_light: 'Ceiling light', ceiling_sprinkler: 'Sprinkler',
      })[opening.kind] ?? 'Room obstruction';
      const remove = document.createElement('button');
      remove.type = 'button'; remove.className = 'obstruction__delete'; remove.textContent = '×'; remove.setAttribute('aria-label', `Remove ${title.textContent}`);
      remove.addEventListener('click', (event) => { event.stopPropagation(); if (model.removeCeilingOpening(opening.id)) { renderCeilingOpenings(); repaintView(); scheduleSave(); } });
      head.append(title, remove);
      const fields = document.createElement('div');
      fields.className = 'ceiling-opening-card__fields';
      const addSelect = (labelText, index) => {
        const label = document.createElement('label'); label.textContent = labelText;
        const select = document.createElement('select');
        for (const wall of walls) { const option = document.createElement('option'); option.value = wall.id; option.textContent = `W${wall.wallNumber}`; select.append(option); }
        select.value = opening.referenceWallIds[index];
        select.addEventListener('change', () => {
          const refs = [...opening.referenceWallIds]; refs[index] = select.value;
          if (model.updateCeilingOpening(opening.id, { referenceWallIds: refs })) { renderCeilingOpenings(); repaintView(); scheduleSave(); }
          else select.value = opening.referenceWallIds[index];
        });
        label.append(select); fields.append(label);
      };
      const addLength = (labelText, property, value, allowBlank = false) => {
        const label = document.createElement('label'); label.textContent = labelText;
        const input = document.createElement('input'); input.type = 'text'; input.inputMode = 'text'; input.dataset.pad = 'length'; input.value = value == null ? '' : fmt(value);
        input.addEventListener('change', () => {
          const parsed = input.value.trim() === '' && allowBlank ? null : parseLength(input.value);
          const ok = (parsed !== null || allowBlank) && model.updateCeilingOpening(opening.id, { [property]: parsed });
          input.classList.toggle('obstruction__input--invalid', !ok);
          if (ok) { repaintView(); scheduleSave(); }
        });
        makeCaptureTarget(input, { kind: 'ceiling_opening_field', entityId: opening.id, property, label: `${title.textContent} · ${labelText} (${wallLabel(opening.referenceWallIds[0])}/${wallLabel(opening.referenceWallIds[1])})` });
        label.append(input); fields.append(label);
      };
      const fan = opening.kind === 'ceiling_fan';
      addSelect(fan ? 'Reference 1 (fan center)' : 'Reference 1 (width)', 0);
      addSelect(fan ? 'Reference 2 (fan center)' : 'Reference 2 (length)', 1);
      addLength(fan ? 'Center from ref 1' : 'Inward from ref 1', 'xUnits', opening.xUnits);
      addLength(fan ? 'Center from ref 2' : 'Inward from ref 2', 'yUnits', opening.yUnits);
      if (fan) {
        addLength('Diameter', 'diameterUnits', opening.diameterUnits ?? opening.widthUnits);
        addLength('Distance from finished floor (FF)', 'fromFloorUnits', opening.fromFloorUnits);
      } else {
        addLength('Width', 'widthUnits', opening.widthUnits);
        addLength('Length', 'lengthUnits', opening.lengthUnits);
        addLength('Drop below ceiling', 'heightUnits', opening.heightUnits ?? 1);
      }
      if (opening.kind === 'attic_stairs') {
        addLength('Floor base from ref 2', 'ladderFloorOffsetUnits', opening.ladderFloorOffsetUnits, true);
        const toggle = document.createElement('label');
        toggle.className = 'ceiling-opening-card__toggle';
        const checkbox = document.createElement('input'); checkbox.type = 'checkbox'; checkbox.checked = opening.showLadderExtended !== false;
        checkbox.setAttribute('aria-label', 'Show stair down / extended');
        checkbox.addEventListener('change', () => {
          if (model.updateCeilingOpening(opening.id, { showLadderExtended: checkbox.checked })) {
            repaintView(); scheduleSave();
          } else checkbox.checked = opening.showLadderExtended !== false;
        });
        toggle.append(checkbox, 'Show stair down / extended');
        fields.append(toggle);
      }
      card.append(head, fields);
      return card;
    }));
  }

  /**
   * Full repaint including a table REBUILD. Use only when the set of walls
   * changes (cleanup, clear, undo) — never in response to focus or a keystroke,
   * or it will yank the input out from under the user.
   */
  function renderAll() {
    canvas.renderRaw(model.state.strokes, model.state.current);
    canvas.renderWalls(planDisplayWalls(), model.state.selectedWallId, (w) => model.displayLength(w), wallRenderOpts());
    canvas.renderAnnotations(model.annotationsFor('plan'));
    table.render(
      model.state.walls,
      model.state.selectedWallId,
      (w) => model.displayLength(w),
      (w) => model.displayAngle(w),
      (w) => model.displayThickness(w),
      thickPlaceholder,
      (w) => model.autoLength(w),
    );
    renderStatus();
    renderDoorButton();
    renderPartitionButton();
    renderWallOneButton();
    renderHeights();
    renderCeilingOpenings();
    renderObstructions();
    renderDepthLines();
    renderWallHeights();
    renderPartitionPanel();
    renderSectionsPanel();
    renderStorageNeeds();
    renderStorageSummary();
    renderPricingPanel();
    renderHandleSummary();
    renderGridButton();
    renderSnapClosedButton();
    renderDimButtons();
    renderSummaryButton();
    renderFieldCaptureMode();
    renderHelperToggles(); // elevation height lines — control now lives in the side panel
    if (currentView === 'elevation') renderElevation();
    scheduleSave();
  }

  /**
   * Repaint canvas + status and update the table IN PLACE (values, placeholders,
   * highlight) without rebuilding its rows. Safe to call while an input is
   * focused — it skips the focused input. Use for selection and length edits.
   */
  function renderGeometry() {
    canvas.renderWalls(planDisplayWalls(), model.state.selectedWallId, (w) => model.displayLength(w), wallRenderOpts());
    canvas.renderAnnotations(model.annotationsFor('plan'));
    table.sync(
      model.state.walls,
      model.state.selectedWallId,
      (w) => model.displayLength(w),
      (w) => model.displayAngle(w),
      (w) => model.displayThickness(w),
      thickPlaceholder,
      (w) => model.autoLength(w),
    );
    renderStatus();
    renderDoorButton();
    renderPartitionButton();
    renderWallOneButton();
    renderHeights();
    renderCeilingOpenings();
    renderObstructions();
    renderDepthLines();
    renderWallHeights();
    renderPartitionPanel();
    renderSectionsPanel();
    renderStorageNeeds();
    renderStorageSummary();
    renderPricingPanel();
    renderHandleSummary();
    renderFieldCaptureMode();
    if (currentView === 'elevation') renderElevation();
    scheduleSave();
  }

  function setView(view) {
    currentView = view;
    if (workspace) workspace.dataset.view = view;
    root.getElementById('view-plan')?.classList.toggle('is-active', view === 'plan');
    root.getElementById('view-elevation')?.classList.toggle('is-active', view === 'elevation');
    if (view === 'elevation') {
      renderHeights();
      renderElevation();
    }
  }

  // ---- sketches: local persistence + a named library -----------------------
  //
  // Each sketch is identified by Client / Opportunity / Space and auto-saves to
  // the local store, so a refresh (or the tablet sleeping) never loses work AND the
  // designer can keep several sketches and reopen a chosen one from a list —
  // all at one app URL. Saves are debounced: a burst of edits coalesces into
  // one write. Persistence is best-effort — a failed write is a no-op, never an
  // error surfaced mid-sketch (see local-db.js).

  /**
   * Identity of the sketch currently open. `version`/`isPreferred` remain in
   * the sync wire shape for compatibility with existing data; N4 exposes one
   * editable state per space and no version/preferred controls.
   */
  const current = { id: newId(), client: '', opportunity: '', space: '', version: 'Version 1', isPreferred: false, isTemplate: false, templateName: '' };

  let saveTimer = null;
  let saveGeneration = 0;
  let spaceSwitcherGeneration = 0;
  // Start true so the initial paint's auto-save is suppressed until the restore
  // settles. Otherwise startup would save a fresh EMPTY sketch and overwrite
  // lastOpenedId before restoreLastOpened() reads it — losing the real sketch.
  let restoring = true;

  function newId() {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function scheduleSave() {
    if (restoring) return; // don't echo a load straight back to disk
    if (saveTimer !== null) clearTimeout(saveTimer);
    onLocalSaveStatus?.('saving');
    saveTimer = setTimeout(saveNow, 400);
  }

  function saveNow() {
    saveTimer = null;
    const generation = ++saveGeneration;
    const stored = putSketch(serializeSketch(model.state, current));
    setMeta('lastOpenedId', current.id);
    Promise.resolve(stored).then((saved) => {
      if (generation !== saveGeneration) return;
      onLocalSaveStatus?.(saved === false ? 'error' : 'saved');
    }).catch(() => {
      if (generation !== saveGeneration) return;
      onLocalSaveStatus?.('error');
    });
    // Tell the sync engine this room changed, and nudge a (debounced) sync.
    // Local save is the source of truth; sync is best-effort on top.
    if (sync) {
      sync.markDirty(current.id);
      scheduleSync();
    }
    return stored;
  }

  // Coalesce a burst of saves into one sync a few seconds later, so typing a
  // length doesn't fire a network round trip per keystroke.
  let syncTimer = null;
  function scheduleSync() {
    if (!sync) return;
    if (syncTimer !== null) clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      syncTimer = null;
      sync.syncNow().catch(() => {}); // failures surface via the status callback
    }, 2500);
  }

  /**
   * Called after a sync pulled remote changes: refresh the library if it's
   * open. The open sketch is not force-reloaded mid-edit (a locally-dirty room
   * is never overwritten anyway); reopening it picks up the remote state.
   */
  async function onRemoteChange(status) {
    const panel = root.getElementById('sketches-panel');
    if (panel && !panel.hidden) refreshSketchesList();
    refreshOpportunitySpaceSwitcher();
    // The OPEN sketch was deleted on another device (A5, ADR 0104). Keeping
    // it on screen would resurrect it: the next auto-save re-puts the doc,
    // and this device haunts itself with a room no other device can see.
    // Cancel any pending save, clear a doc a racing save already re-put,
    // then fall back exactly like a local delete of the open sketch does.
    if ((status?.tombstonedRoomIds ?? []).includes(current.id)) {
      if (saveTimer !== null) { clearTimeout(saveTimer); saveTimer = null; }
      saveGeneration += 1; // an in-flight save's status callback goes quiet
      await deleteSketch(current.id); // no markDeleted: the tombstone already exists remotely
      const remaining = (await listSketches()).filter((d) => d.id !== current.id);
      remaining.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
      if (remaining.length) await openSketch(remaining[0].id);
      else newSketch();
    }
  }

  /**
   * Sentinel value for the space switcher's "+ New space…" option. Never a
   * sketch id: the change handler must intercept it before openSketch, or a
   * designer picking it would "open" a sketch whose id is this string.
   */
  const NEW_SPACE_OPTION = '__new-space__';

  /**
   * One complete, always-visible space name plus every other saved space under
   * this exact Client/Opportunity. N4 makes each space one state, so selecting
   * a space opens that document directly. The list always ends with the
   * "+ New space…" creation option (which replaced the "+ Space" button).
   */
  async function refreshOpportunitySpaceSwitcher() {
    const select = root.getElementById('space-switcher');
    if (!select) return;
    const generation = ++spaceSwitcherGeneration;
    const client = String(current.client ?? '').trim();
    const opportunity = String(current.opportunity ?? '').trim();
    const docs = await listSketches();
    if (generation !== spaceSwitcherGeneration) return;

    const same = (value, expected) => String(value ?? '').trim().toLocaleLowerCase() === expected.toLocaleLowerCase();
    const groups = new Map();
    for (const doc of docs) {
      const meta = readSketchMeta(doc);
      const space = String(meta.space ?? '').trim();
      if (meta.isTemplate || !space || !same(meta.client, client) || !same(meta.opportunity, opportunity)) continue;
      if (!groups.has(space)) groups.set(space, []);
      groups.get(space).push(doc);
    }

    const currentSpace = String(current.space ?? '').trim();
    if (currentSpace && !groups.has(currentSpace)) groups.set(currentSpace, []);
    select.replaceChildren();
    const choices = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    if (!currentSpace) choices.unshift(['', []]);

    for (const [space, documents] of choices) {
      const option = root.createElement('option');
      const isCurrent = space === currentSpace;
      // Multiple documents can only be legacy data created before N4. Keep the
      // newest reachable without inventing a silent destructive migration.
      const newest = [...documents].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0];
      option.value = isCurrent
        ? current.id
        : (readSketchMeta(newest ?? {}).id ?? '');
      option.textContent = space || 'No space open';
      option.selected = isCurrent;
      option.title = [client, opportunity, space].filter(Boolean).join(' · ');
      select.append(option);
    }

    if (!select.options.length) {
      const option = root.createElement('option');
      option.value = current.id;
      option.textContent = currentSpace || 'No space open';
      select.append(option);
    }
    // The dropdown is also where a space is CREATED (the "+ Space" button it
    // replaces sat right beside it). A sentinel value, not a real id: the
    // change handler intercepts it before any openSketch call.
    const newOption = root.createElement('option');
    newOption.value = NEW_SPACE_OPTION;
    newOption.textContent = '+ New space…';
    select.append(newOption);
    select.value = current.id;
    select.title = [client, opportunity, currentSpace].filter(Boolean).join(' · ') || 'Current space';
  }

  /** Writes the current identity into the header fields. */
  function renderSketchIdentity() {
    const set = (id, value) => {
      const el = root.getElementById(id);
      if (el && el.value !== value) el.value = value;
    };
    set('meta-client', current.client);
    set('meta-opportunity', current.opportunity);
    set('meta-space', current.space);
    updateInventoryInboxFlag();
    refreshOpportunitySpaceSwitcher();
  }

  /** Loads a stored sketch document into the model and the header. */
  function applyDoc(doc, meta) {
    const hydrated = hydrateSketch(doc);
    if (!hydrated) return false;
    restoring = true;
    current.id = meta.id;
    current.client = meta.client;
    current.opportunity = meta.opportunity;
    current.space = meta.space;
    current.version = meta.version ?? 'Version 1';
    current.isPreferred = meta.isPreferred === true;
    current.isTemplate = meta.isTemplate === true;
    current.templateName = meta.templateName ?? '';
    model.state.strokes = [];
    model.state.current = null;
    model.state.walls = hydrated.walls;
    model.state.selectedWallId = hydrated.selectedWallId;
    model.state.scalePxPerUnit = hydrated.scalePxPerUnit;
    model.state.snapClosed = hydrated.snapClosed !== false;
    model.state.closedPerimeter = hydrated.closedPerimeter === true;
    model.state.wallOneOverrideId = hydrated.wallOneOverrideId ?? null;
    if (hydrated.ceilingHeightUnits) model.state.ceilingHeightUnits = hydrated.ceilingHeightUnits;
    if (hydrated.doorHeightUnits) model.state.doorHeightUnits = hydrated.doorHeightUnits;
    model.state.helperLineOverrides =
      hydrated.helperLineOverrides && typeof hydrated.helperLineOverrides === 'object' ? hydrated.helperLineOverrides : {};
    model.state.customHelperLines = Array.isArray(hydrated.customHelperLines) ? hydrated.customHelperLines : [];
    model.state.obstructions = Array.isArray(hydrated.obstructions) ? hydrated.obstructions : [];
    model.state.ceilingOpenings = Array.isArray(hydrated.ceilingOpenings) ? hydrated.ceilingOpenings : [];
    model.state.annotations = Array.isArray(hydrated.annotations) ? hydrated.annotations : [];
    model.state.planHelperLines = Array.isArray(hydrated.planHelperLines) ? hydrated.planHelperLines : [];
    model.state.planHelperLinesVisible = hydrated.planHelperLinesVisible !== false;
    model.state.gridVisible = hydrated.gridVisible !== false;
    model.state.crownEnabled = hydrated.crownEnabled === true;
    model.state.baseTrimEnabled = hydrated.baseTrimEnabled === true;
    // Elevation dimension toggles (ADR 0061 item E) — absent on every doc
    // saved before them, which reads as off, the default.
    model.state.shelfSpacingDimsVisible = hydrated.shelfSpacingDimsVisible === true;
    model.state.drawerFaceDimsVisible = hydrated.drawerFaceDimsVisible === true;
    model.state.storageSummaryVisible = hydrated.storageSummaryVisible !== false;
    // Client storage requirements (ADR 0043); absent on every pre-2026-07-29 doc.
    model.state.cornerHeights = hydrated.cornerHeights && typeof hydrated.cornerHeights === 'object' ? hydrated.cornerHeights : {};
    model.state.chainStartHeightUnits = Number.isFinite(hydrated.chainStartHeightUnits) && hydrated.chainStartHeightUnits > 0
      ? hydrated.chainStartHeightUnits : null;
    model.state.wallHeightBreaks = hydrated.wallHeightBreaks && typeof hydrated.wallHeightBreaks === 'object' ? hydrated.wallHeightBreaks : {};
    model.state.ceilingFlat = hydrated.ceilingFlat !== false;
    // ADR 0051 step 2's load shim (ADR 0057): hang requirements saved under
    // the legacy per-type keys (`hang:DH`) re-key to categories (`hang:short`)
    // here, merging collisions; unknown keys ride through untouched. The doc
    // is only rewritten on the next save, like every other load migration.
    model.state.storageNeeds = migrateStorageNeedKeys(
      hydrated.storageNeeds && typeof hydrated.storageNeeds === 'object' ? hydrated.storageNeeds : {},
      rules,
    );
    model.state.storageNeedsSource = hydrated.storageNeedsSource && typeof hydrated.storageNeedsSource === 'object'
      ? { ...hydrated.storageNeedsSource } : null;
    model.state.storageCompareTo = ({ current: 'current', has: 'current', need: 'need', wants: 'need' })[hydrated.storageCompareTo] ?? 'need';
    model.state.partitions = Array.isArray(hydrated.partitions) ? hydrated.partitions : [];
    model.state.wallLabels = Array.isArray(hydrated.wallLabels) ? hydrated.wallLabels : [];
    // Sections saved before heights inherited (ADR 0039 addendum) baked the
    // type-default height into every section. A pin EXACTLY at the default is
    // indistinguishable from inheriting, so convert it to null on load —
    // that's what lets the space-level height reach pre-existing sections.
    const bakedDefaultHeight = (s) => {
      const preset = (rules.sectionTypePresets ?? []).find((p) => String(p.code).toUpperCase() === String(s.typeCode).toUpperCase());
      return preset?.defaultHeightUnits > 0 ? preset.defaultHeightUnits : rules.sectionDefaultHeightUnits;
    };
    model.state.sections = (Array.isArray(hydrated.sections) ? hydrated.sections : []).map((s) =>
      s.heightUnits != null && Math.abs(s.heightUnits - bakedDefaultHeight(s)) < 1e-9 ? { ...s, heightUnits: null } : s);
    model.state.sharedSpace = hydrated.sharedSpace?.persons
      ? { enabled: hydrated.sharedSpace.enabled === true, persons: [...hydrated.sharedSpace.persons] }
      : { enabled: false, persons: ['A', 'B'] };
    model.state.spaceHeightUnits = Number.isFinite(hydrated.spaceHeightUnits) && hydrated.spaceHeightUnits > 0 ? hydrated.spaceHeightUnits : null;
    model.state.platformHeightUnits = Number.isFinite(hydrated.platformHeightUnits) && hydrated.platformHeightUnits > 0 ? hydrated.platformHeightUnits : 0;
    model.state.countertopThicknessUnits = Number.isFinite(hydrated.countertopThicknessUnits) && hydrated.countertopThicknessUnits > 0
      ? hydrated.countertopThicknessUnits : 0;
    model.state.toTargetHeight = hydrated.toTargetHeight === true;
    model.state.stackTargetHeightUnits = Number.isFinite(hydrated.stackTargetHeightUnits) && hydrated.stackTargetHeightUnits > 0
      ? hydrated.stackTargetHeightUnits : null;
    model.state.wallStackTargets = hydrated.wallStackTargets ?? {};
    model.state.crownAllowanceUnits = Number.isFinite(hydrated.crownAllowanceUnits) && hydrated.crownAllowanceUnits >= 0
      ? hydrated.crownAllowanceUnits : null;
    model.state.crownMouldingHeightUnits = Number.isFinite(hydrated.crownMouldingHeightUnits) && hydrated.crownMouldingHeightUnits > 0
      ? hydrated.crownMouldingHeightUnits : null;
    model.state.baseTrimHeightUnits = Number.isFinite(hydrated.baseTrimHeightUnits) && hydrated.baseTrimHeightUnits > 0
      ? hydrated.baseTrimHeightUnits : null;
    // Space pricing (ADR 0058): hydrateSketch already validated the shape.
    model.state.pricing = hydrated.pricing ?? { level: 1, countertopMaterial: null, low: null, high: null, needsPricing: 0 };
    // Basic mode (ADR 0059): hydrateSketch already validated the shape.
    model.state.basic = hydrated.basic ?? { mode: 'detailed', spaceType: null, bounds: null, cards: [] };
    model.resolve(); // recompute closure + pxPerUnit from the restored geometry
    renderSketchIdentity();
    setMode(model.state.walls.length ? 'select' : 'draw');
    renderAll();
    // Fit AFTER the render, never before (owner report, 2026-08-29: spaces
    // opened small and off-centre). renderWalls resets the wall-chip anchors
    // to THIS sketch's; a fit run first consumed the previous space's chips
    // — two sketches live at unrelated model coordinates, so the union box
    // shoved the new room into a corner. Boot restores hid the bug (no
    // stale anchors yet); space-SWITCHING is where it bit.
    canvas.fit(model.allPoints());
    restoring = false;
    return true;
  }

  /**
   * Reopen whichever sketch was last open; otherwise stay on the fresh blank.
   * `restoring` stays true throughout so no auto-save fires mid-restore, and is
   * released in `finally` so a first-ever run (nothing to restore) still enables
   * saving afterward.
   */
  async function restoreLastOpened() {
    try {
      const lastId = await getMeta('lastOpenedId');
      if (lastId) {
        const doc = await getSketch(lastId);
        if (doc) applyDoc(doc, readSketchMeta(doc));
      }
    } finally {
      restoring = false;
    }
  }

  /**
   * Start a new, empty sketch (the current one stays saved under its own id).
   * `identity` seeds Client/Opportunity/Space after the naming dialog has
   * collected all three values. A workspace never intentionally starts as an
   * unnamed space.
   * The legacy wire label stays Version 1; the product exposes one state for
   * this space and no preferred-version selection.
   */
  function newSketch(identity = {}) {
    restoring = true;
    current.id = newId();
    current.client = identity.client ?? '';
    current.opportunity = identity.opportunity ?? '';
    current.space = identity.space ?? '';
    current.version = 'Version 1';
    current.isPreferred = false;
    current.isTemplate = false;
    current.templateName = '';
    model.clear();
    canvas.resetView();
    renderSketchIdentity();
    setMode('draw');
    renderAll();
    restoring = false;
    saveNow();
    closeSketchesPanel();
  }

  async function openSketch(id) {
    const doc = await getSketch(id);
    if (doc && applyDoc(doc, readSketchMeta(doc))) {
      setMeta('lastOpenedId', id);
      // Repaint a returned inventory response against whichever opportunity
      // space the designer opens. Version 1 links still match a source sketch;
      // version 2 stays reviewable for explicit opportunity-space mapping.
      handleInventoryHash();
    }
    closeSketchesPanel();
  }

  /**
   * Delete a sketch. When it was the one open, fall back to the most recently
   * updated survivor rather than a blank sketch — a blank one would be
   * immediately auto-saved, so deleting your open sketch used to leave a new
   * "Untitled sketch" behind every time. Only a genuinely empty library
   * starts a fresh blank.
   */
  async function deleteSketchById(id) {
    await deleteSketch(id);
    // Report the delete for propagation (A5, ADR 0104) — the doc is gone, so
    // this call is the only trace the intent leaves. Without it the engine's
    // push loop shrugs "gone locally; nothing to push" and every other
    // device keeps the room forever.
    if (sync) { sync.markDeleted(id); scheduleSync(); }
    if (id !== current.id) {
      refreshSketchesList();
      return;
    }
    const remaining = (await listSketches()).filter((d) => d.id !== id);
    remaining.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
    if (remaining.length) {
      await openSketch(remaining[0].id);
      return;
    }
    newSketch();
  }

  /**
   * Ask for the new space's name BEFORE opening an empty workspace. Creating a
   * nameless room and asking the designer to find a hidden identity field made
   * the tablet workflow both ambiguous and, below 1200px, impossible.
   */
  function newSpaceIn(clientName, opportunityName) {
    if (!String(clientName ?? '').trim() || !String(opportunityName ?? '').trim()) {
      openIdentityPanel('opportunity');
      return;
    }
    openIdentityPanel('space', { client: clientName, opportunity: opportunityName });
  }

  /**
   * Copy is the alternative-design workflow (N4), not a second version hidden
   * under the same space. The existing naming dialog prompts for a distinct
   * editable space name before any document is minted.
   */
  async function copySpace(id) {
    if (saveTimer !== null) await saveNow();
    const source = await getSketch(id);
    if (!source) return;
    const meta = readSketchMeta(source);
    openIdentityPanel('copy', meta, { copySourceId: id });
  }

  // ---- templates (ADR 0029) -------------------------------------------------

  /**
   * Save a sketch as a reusable TEMPLATE: an independent copy with cleared
   * identity and `isTemplate: true`. Flushes any pending auto-save first so the
   * template captures the latest edits. The source sketch is untouched. Jumps
   * the library to the Templates filter so the new template is visible.
   */
  async function saveAsTemplate(id) {
    if (saveTimer !== null) saveNow();
    // A fresh workspace exists in memory before its first edit queues an
    // IndexedDB write. Persist it here so Save template works immediately.
    let doc = await getSketch(id);
    if (!doc && id === current.id) {
      doc = serializeSketch(model.state, current);
      await putSketch(doc);
    }
    if (!doc) return;
    const copy = duplicateSketchDoc(doc, {
      id: newId(),
      makeId: newId,
      nowIso: new Date().toISOString(),
      asTemplate: true,
      templateName: sketchLabel(readSketchMeta(doc)),
    });
    if (!copy) return;
    await putSketch(copy);
    if (sync) { sync.markDirty(copy.id); scheduleSync(); }
    libraryFilter = 'templates';
    openSketchesPanel();
  }

  /**
   * The library's "use template" control (owner, 2026-08-29): with a space
   * OPEN, the template populates THAT space — the designer clicked it from
   * inside the space, so making them name a new one dropped the very field
   * they were standing in. With content already drawn, it asks first, naming
   * the loss (owner ruling: "ask for confirmation before replacing"); an
   * empty space applies silently, so the prompt stays meaningful. With no
   * space open (or a template itself open), the original create flow runs
   * unchanged — and creating a NEW space from a template stays available via
   * + Space, whose dialog has its own template picker.
   */
  async function useTemplate(id) {
    const spaceOpen = Boolean(current.id)
      && String(current.space ?? '').trim() !== ''
      && current.isTemplate !== true;
    const decision = templateUseDecision({
      spaceOpen,
      spaceName: current.space,
      summary: sketchContentSummary(model.state),
    });
    if (decision.mode === 'create') {
      const hasOpportunity = String(current.client ?? '').trim() && String(current.opportunity ?? '').trim();
      openIdentityPanel(
        hasOpportunity ? 'space' : 'opportunity',
        hasOpportunity ? { client: current.client, opportunity: current.opportunity } : {},
        { templateId: id },
      );
      return;
    }
    // Cancel means CANCEL: the ask happens before anything — instantiation
    // included — so the space is bit-for-bit what it was.
    //
    // The ask is the HOUSE dialog, never the browser-native confirm (its
    // literal name is avoided here — the wiring test greps this function,
    // prose included). confirm-dialog.js
    // (2026-07-29) records why: native confirm "is blocked outright in some
    // embedded contexts — a silently-skipped confirmation on a destructive
    // action is the worst possible failure." #114 used the native call
    // anyway, and the owner hit exactly that on staging (2026-08-29): the
    // suppressed dialog answered "no" invisibly, so applying a template to a
    // space with content did nothing at all. The picker closes first so the
    // dialog stands over the very room it is naming.
    if (decision.mode === 'confirm') {
      closeSketchesPanel();
      const ok = await confirmAction({
        title: decision.title,
        body: decision.body,
        confirmLabel: 'Replace contents',
        danger: true,
      });
      if (ok !== true) return;
    }
    // A save timer armed on the old content firing after the apply would
    // write the replaced walls straight back. The old content is being
    // discarded on purpose, so the timer is dropped, not flushed.
    if (saveTimer !== null) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    const nowIso = new Date().toISOString();
    const copy = isBaseRoomTemplateId(id)
      ? instantiateBaseRoomTemplate(id, { sketchId: current.id, makeId: newId, nowIso })
      : duplicateSketchDoc(await getSketch(id), { id: current.id, makeId: newId, nowIso, fromTemplate: true });
    const retargeted = retargetTemplateCopy(copy, current);
    if (!retargeted) {
      status.textContent = 'That template is no longer available.';
      return;
    }
    await putSketch(retargeted);
    await openSketch(retargeted.id); // same id — re-hydrates this space from the applied doc
    if (sync) { sync.markDirty(retargeted.id); scheduleSync(); }
    status.textContent = `Template applied to ${current.client} · ${current.opportunity} · ${current.space}.`;
  }

  /** Rename a template in place. Keeps `current` in sync if it is the open one. */
  async function renameTemplate(id, name) {
    const doc = await getSketch(id);
    if (!doc) return;
    const trimmed = String(name ?? '').trim();
    doc.templateName = trimmed || 'Untitled template';
    doc.updatedAt = new Date().toISOString();
    await putSketch(doc);
    if (id === current.id) current.templateName = doc.templateName;
    if (sync) { sync.markDirty(id); scheduleSync(); }
  }

  async function moveTemplate(id, direction) {
    const templates = (await listSketches())
      .filter((doc) => readSketchMeta(doc).isTemplate)
      .sort(templateLibraryOrder);
    const at = templates.findIndex((doc) => doc.id === id);
    const to = at + direction;
    if (at < 0 || to < 0 || to >= templates.length) return;
    [templates[at], templates[to]] = [templates[to], templates[at]];
    for (const [rank, doc] of templates.entries()) {
      if (doc.templateRank === rank) continue;
      doc.templateRank = rank;
      doc.updatedAt = new Date().toISOString();
      await putSketch(doc);
      if (sync) { sync.markDirty(doc.id); }
    }
    if (sync) scheduleSync();
    refreshSketchesList();
  }

  function templateLibraryOrder(a, b) {
    const ar = readSketchMeta(a).templateRank;
    const br = readSketchMeta(b).templateRank;
    if (ar !== null || br !== null) {
      if (ar === null) return 1;
      if (br === null) return -1;
      if (ar !== br) return ar - br;
    }
    return String(b.updatedAt).localeCompare(String(a.updatedAt));
  }

  // ---- the Sketches panel (a list to open/duplicate/delete saved sketches) --

  // Which set the library shows ('sketches' | 'templates') and the live search
  // query. Both are view state, not persisted — they reset with each session.
  let libraryFilter = 'sketches';
  let librarySearch = '';
  let libraryNote = '';
  // Opportunity rows are deliberately collapsed when the library opens. Keep
  // expansion as session-only view state so a redraw (for example, starring a
  // version) does not unexpectedly close the opportunity the designer is using.
  const expandedLibraryOpportunities = new Set();

  /**
   * Two-step delete state. A first tap arms the row's button ("Delete?"); a
   * second tap on the SAME row commits. Arming a different row, closing the
   * panel, or a few seconds of hesitation disarms — so an accidental tap
   * costs nothing and there is no dialog to be suppressed.
   */
  let pendingDeleteId = null;
  let pendingDeleteBtn = null;
  let pendingDeleteTimer = null;

  const DELETE_ARM_MS = 5000;

  function disarmDelete() {
    if (pendingDeleteTimer !== null) {
      clearTimeout(pendingDeleteTimer);
      pendingDeleteTimer = null;
    }
    if (pendingDeleteBtn?.isConnected) {
      pendingDeleteBtn.textContent = '🗑';
      pendingDeleteBtn.classList.remove('sketches-list__delete--armed');
      pendingDeleteBtn.setAttribute('aria-label', pendingDeleteBtn.dataset.labelIdle ?? 'Delete');
    }
    pendingDeleteId = null;
    pendingDeleteBtn = null;
  }

  function armDelete(id, btn, label) {
    disarmDelete(); // only one row armed at a time
    pendingDeleteId = id;
    pendingDeleteBtn = btn;
    btn.dataset.labelIdle = `Delete ${label}`;
    btn.textContent = 'Delete?';
    btn.classList.add('sketches-list__delete--armed');
    btn.setAttribute('aria-label', `Confirm deleting ${label} — this cannot be undone`);
    pendingDeleteTimer = setTimeout(disarmDelete, DELETE_ARM_MS);
  }

  function openSketchesPanel() {
    const panel = root.getElementById('sketches-panel');
    if (!panel) return;
    panel.hidden = false;
    refreshSketchesList();
  }

  function openTemplateLibrary() {
    libraryFilter = 'templates';
    librarySearch = '';
    libraryNote = '';
    openSketchesPanel();
  }

  function closeSketchesPanel() {
    const panel = root.getElementById('sketches-panel');
    if (panel) panel.hidden = true;
    disarmDelete();
  }

  let identityMode = 'opportunity';
  let pendingTemplateId = null;
  let pendingCopySourceId = null;
  let identityTemplateLoadToken = 0;
  let identityTemplateCatalog = [];

  function closeIdentityPanel() {
    const panel = root.getElementById('identity-panel');
    if (panel) panel.hidden = true;
    root.getElementById('identity-template-picker')?.removeAttribute('open');
    identityTemplateLoadToken += 1;
    pendingTemplateId = null;
    pendingCopySourceId = null;
    const error = root.getElementById('identity-error');
    if (error) error.textContent = '';
  }

  function renderIdentityDialogCopy() {
    const addingSpace = identityMode === 'space';
    const copyingSpace = identityMode === 'copy';
    const title = root.getElementById('identity-title');
    const submit = root.getElementById('create-identity');
    const templateField = root.getElementById('identity-template-field');
    if (templateField) templateField.hidden = copyingSpace;
    if (title && copyingSpace) title.textContent = 'Copy space';
    if (submit && copyingSpace) submit.textContent = 'Copy space';
    if (copyingSpace) return;
    if (title) title.textContent = pendingTemplateId
      ? (addingSpace ? 'Use template for a new space' : 'Use template for a new opportunity')
      : (addingSpace ? 'Add a space' : 'New opportunity');
    if (submit) submit.textContent = pendingTemplateId
      ? 'Create from template'
      : (addingSpace ? 'Create space' : 'Create opportunity');
  }

  /** Render touch-sized choices; unlike native options, these can carry inline SVG plans. */
  function renderIdentityTemplatePicker(items = identityTemplateCatalog) {
    const list = root.getElementById('identity-template-options');
    const summary = root.getElementById('identity-template-summary');
    const picker = root.getElementById('identity-template-picker');
    if (!list || !summary || !picker || items.length === 0) return;

    list.replaceChildren();
    let group = null;
    for (const item of items) {
      if (item.group && item.group !== group) {
        group = item.group;
        list.append(el('div', 'identity-template-picker__group', group));
      }

      const option = el('button', 'identity-template-picker__option');
      option.type = 'button';
      option.dataset.templateId = item.id;
      option.setAttribute('aria-pressed', String((pendingTemplateId ?? '') === item.id));
      option.setAttribute('aria-label', `Start from ${item.name}`);
      const copy = el('span', 'identity-template-picker__copy');
      copy.append(
        el('span', 'identity-template-picker__name', item.name),
        el('span', 'identity-template-picker__detail', item.detail),
      );
      option.append(planThumbSvg(item.previewDocument), copy);
      option.addEventListener('click', () => {
        pendingTemplateId = item.id || null;
        renderIdentityDialogCopy();
        renderIdentityTemplatePicker();
        picker.removeAttribute('open');
        summary.focus({ preventScroll: true });
      });
      list.append(option);
    }

    const selected = items.find((item) => item.id === (pendingTemplateId ?? '')) ?? items[0];
    const selectedCopy = el('span', 'identity-template-picker__copy');
    selectedCopy.append(
      el('span', 'identity-template-picker__name', selected.name),
      el('span', 'identity-template-picker__detail', selected.detail),
    );
    summary.replaceChildren(planThumbSvg(selected.previewDocument), selectedCopy);
    summary.setAttribute('aria-label', `Start from: ${selected.name}`);
  }

  /** Populate the naming dialog from the same built-in + personal template catalog as the library. */
  async function populateIdentityTemplateOptions() {
    const list = root.getElementById('identity-template-options');
    if (!list || identityMode === 'copy') return;
    const token = ++identityTemplateLoadToken;
    identityTemplateCatalog = identityTemplatePickerItems([]);
    renderIdentityTemplatePicker();
    list.setAttribute('aria-busy', 'true');

    try {
      const personal = (await listSketches())
        .filter((doc) => readSketchMeta(doc).isTemplate)
        .sort(templateLibraryOrder);
      if (token !== identityTemplateLoadToken || root.getElementById('identity-panel')?.hidden) return;
      identityTemplateCatalog = identityTemplatePickerItems(personal);
      renderIdentityTemplatePicker();
    } finally {
      if (token === identityTemplateLoadToken) list.removeAttribute('aria-busy');
    }
  }

  /**
   * One naming surface serves both standalone entry paths. SSH embedding can
   * bypass it by supplying Client/Opportunity and opening an existing space;
   * the room editor does not depend on the dialog.
   */
  function openIdentityPanel(mode, identity = {}, options = {}) {
    identityMode = mode === 'copy' ? 'copy' : (mode === 'space' ? 'space' : 'opportunity');
    pendingTemplateId = typeof options.templateId === 'string' ? options.templateId : null;
    pendingCopySourceId = identityMode === 'copy' && typeof options.copySourceId === 'string'
      ? options.copySourceId : null;
    const panel = root.getElementById('identity-panel');
    const client = root.getElementById('identity-client');
    const opportunity = root.getElementById('identity-opportunity');
    const space = root.getElementById('identity-space');
    const context = root.getElementById('identity-context');
    if (!panel || !client || !opportunity || !space) return;

    const addingSpace = identityMode === 'space' || identityMode === 'copy';
    client.value = String(identity.client ?? (addingSpace ? current.client : current.client || '')).trim();
    opportunity.value = String(identity.opportunity ?? (addingSpace ? current.opportunity : '')).trim();
    space.value = '';
    client.readOnly = addingSpace;
    opportunity.readOnly = addingSpace;
    renderIdentityDialogCopy();
    if (context) context.textContent = identityMode === 'copy'
      ? `${client.value} · ${opportunity.value} — give the copy a new space name. Dimensions copy too; confirm any Leica or tape readings describe both spaces.`
      : addingSpace
        ? `${client.value} · ${opportunity.value} — name the space before opening it.`
      : 'Name the opportunity and its first space before opening the workspace.';
    void populateIdentityTemplateOptions();
    closeSketchesPanel();
    panel.hidden = false;
    (addingSpace ? space : client).focus();
  }

  const sameIdentityPart = (left, right) => String(left ?? '').trim().toLocaleLowerCase() === String(right ?? '').trim().toLocaleLowerCase();

  async function createNamedWorkspace(event) {
    event.preventDefault();
    const client = String(root.getElementById('identity-client')?.value ?? '').trim();
    const opportunity = String(root.getElementById('identity-opportunity')?.value ?? '').trim();
    const space = String(root.getElementById('identity-space')?.value ?? '').trim();
    const error = root.getElementById('identity-error');
    if (!client || !opportunity || !space) {
      if (error) error.textContent = 'Client, Opportunity, and Space are required.';
      root.getElementById(!client ? 'identity-client' : (!opportunity ? 'identity-opportunity' : 'identity-space'))?.focus();
      return;
    }

    const docs = (await listSketches()).filter((doc) => !readSketchMeta(doc).isTemplate);
    const targetOpportunityKey = opportunityKey({ client, opportunity });
    const sameOpportunity = docs.filter((doc) => opportunityKey(readSketchMeta(doc)) === targetOpportunityKey);
    if (identityMode === 'opportunity' && sameOpportunity.length) {
      if (error) error.textContent = 'That opportunity already exists. Open it, then use + Space.';
      return;
    }
    if (sameOpportunity.some((doc) => sameIdentityPart(readSketchMeta(doc).space, space))) {
      if (error) error.textContent = 'That space already exists. Open it, choose another name, or delete the duplicate you no longer need.';
      return;
    }

    // Flush the room being left before switching `current`; cancel the pending
    // timer so it cannot wake later and save the old room under the new id.
    if (saveTimer !== null) {
      clearTimeout(saveTimer);
      saveTimer = null;
      await saveNow();
    }
    if (identityMode === 'copy') {
      const source = await getSketch(pendingCopySourceId);
      const copy = copySpaceDoc(source, docs, {
        id: newId(),
        makeId: newId,
        nowIso: new Date().toISOString(),
        space,
      });
      if (!copy) {
        if (error) error.textContent = 'That space could not be copied. Choose a new name and try again.';
        return;
      }
      await putSketch(copy);
      if (sync) { sync.markDirty(copy.id); scheduleSync(); }
      await openSketch(copy.id);
    } else if (pendingTemplateId) {
      const templateId = pendingTemplateId;
      const nowIso = new Date().toISOString();
      const copy = isBaseRoomTemplateId(templateId)
        ? instantiateBaseRoomTemplate(templateId, { sketchId: newId(), makeId: newId, nowIso })
        : duplicateSketchDoc(await getSketch(templateId), { id: newId(), makeId: newId, nowIso, fromTemplate: true });
      if (!copy) {
        if (error) error.textContent = 'That template is no longer available.';
        return;
      }
      Object.assign(copy, {
        client,
        opportunity,
        space,
        version: 'Version 1',
        isPreferred: false,
        isTemplate: false,
        templateName: '',
      });
      await putSketch(copy);
      await openSketch(copy.id);
    } else {
      newSketch({ client, opportunity, space });
    }
    closeIdentityPanel();
    status.textContent = identityMode === 'copy'
      ? `${space} copied independently. Copied measurements are marked Inherited until each value is re-measured.`
      : `${client} · ${opportunity} · ${space} created. Changes save automatically.`;
  }

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  /** True if a sketch's identity matches the (lower-cased) search query. */
  function metaMatches(meta, q) {
    if (!q) return true;
    const haystack = [meta.client, meta.opportunity, meta.space, meta.templateName]
      .map((v) => String(v ?? '').toLowerCase()).join(' ');
    return q.split(/\s+/).filter(Boolean).every((token) => haystack.includes(token));
  }

  /** Sync the filter tabs + search box to the current library view state. */
  function syncLibraryControls() {
    const sketchesTab = root.getElementById('filter-sketches');
    const templatesTab = root.getElementById('filter-templates');
    const onTemplates = libraryFilter === 'templates';
    sketchesTab?.classList.toggle('sketches-filter__tab--active', !onTemplates);
    templatesTab?.classList.toggle('sketches-filter__tab--active', onTemplates);
    sketchesTab?.setAttribute('aria-selected', String(!onTemplates));
    templatesTab?.setAttribute('aria-selected', String(onTemplates));
    const search = root.getElementById('sketches-search');
    if (search && search.value !== librarySearch) search.value = librarySearch;
    const note = root.getElementById('sketches-context');
    if (note) note.textContent = libraryNote || (onTemplates
      ? 'Choose a reusable room shape to start a new space.'
      : 'Each opportunity contains its saved spaces. Copy a space to explore an alternative.');
  }

  function emptyRow(text) {
    return el('li', 'sketches-list__empty', text);
  }

  async function refreshSketchesList() {
    const listEl = root.getElementById('sketches-list');
    if (!listEl) return;
    disarmDelete(); // the armed button is about to be replaced by the rebuild
    syncLibraryControls();
    const docs = await listSketches();
    docs.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt))); // newest first
    listEl.replaceChildren();

    const q = librarySearch.trim().toLowerCase();
    const templates = docs.filter((d) => readSketchMeta(d).isTemplate);
    const sketches = docs.filter((d) => !readSketchMeta(d).isTemplate);

    if (libraryFilter === 'templates') renderTemplates(listEl, templates, q);
    else renderSketchTree(listEl, sketches, q);
  }

  /** Flat list of reusable templates: thumbnail, editable name, Use, delete. */
  function renderTemplates(listEl, templates, q) {
    const builtIns = BASE_ROOM_TEMPLATES.map((template) => baseRoomTemplateDocument(template.id));
    const shownBuiltIns = builtIns.filter((doc) => metaMatches(readSketchMeta(doc), q));
    const shownPersonal = templates.filter((d) => metaMatches(readSketchMeta(d), q)).sort(templateLibraryOrder);
    const shown = [...shownBuiltIns, ...shownPersonal];
    if (shown.length === 0) {
      listEl.append(
        emptyRow(
          `No templates match "${librarySearch.trim()}".`,
        ),
      );
      return;
    }
    for (const doc of shown) {
      const meta = readSketchMeta(doc);
      const builtIn = isBaseRoomTemplateId(meta.id);
      const li = el('li', `sk-template${meta.id === current.id ? ' sk-template--current' : ''}`);

      const use = el('button', 'sk-template__use');
      use.type = 'button';
      use.title = 'Use template (opens a new copy)';
      use.setAttribute('aria-label', `Use template ${meta.templateName || 'Untitled'}`);
      use.append(planThumbSvg(doc));
      use.addEventListener('click', () => useTemplate(meta.id));

      const body = el('div', 'sk-template__body');
      const name = builtIn
        ? el('strong', 'sk-template__name sk-template__name--builtin', meta.templateName)
        : el('input', 'sk-template__name');
      if (!builtIn) {
        name.type = 'text';
        name.value = meta.templateName || 'Untitled template';
        name.setAttribute('aria-label', 'Template name');
        name.addEventListener('change', () => renameTemplate(meta.id, name.value));
        name.addEventListener('keydown', (e) => { if (e.key === 'Enter') name.blur(); });
      }
      const when = el('span', 'sketches-list__when', builtIn ? 'Built in · available offline' : (meta.updatedAt ? new Date(meta.updatedAt).toLocaleString() : ''));
      body.append(name, when);

      const useText = el('button', 'sk-add sk-template__use-text', 'Use');
      useText.type = 'button';
      useText.addEventListener('click', () => useTemplate(meta.id));

      if (builtIn) {
        li.append(use, body, useText);
        listEl.append(li);
        continue;
      }

      const up = el('button', 'sk-add sk-template__rank', '↑');
      up.type = 'button'; up.title = 'Move template up';
      up.setAttribute('aria-label', `Move ${meta.templateName || 'template'} up`);
      up.disabled = shownPersonal.indexOf(doc) === 0;
      up.addEventListener('click', () => moveTemplate(meta.id, -1));
      const down = el('button', 'sk-add sk-template__rank', '↓');
      down.type = 'button'; down.title = 'Move template down';
      down.setAttribute('aria-label', `Move ${meta.templateName || 'template'} down`);
      down.disabled = shownPersonal.indexOf(doc) === shownPersonal.length - 1;
      down.addEventListener('click', () => moveTemplate(meta.id, 1));

      const del = deleteButton(meta.id, meta.templateName || 'template');

      li.append(use, body, useText, up, down, del);
      listEl.append(li);
    }
  }

  /** The Client › Opportunity › Space tree of real sketches. */
  function renderSketchTree(listEl, sketches, q) {
    const shown = sketches.filter((d) => metaMatches(readSketchMeta(d), q));
    if (shown.length === 0) {
      listEl.append(emptyRow(sketches.length === 0 ? 'No saved opportunities yet.' : `No opportunities match "${librarySearch.trim()}".`));
      return;
    }

    // Build the Client › Opportunity › Space tree, keeping display names.
    const tree = new Map();
    const dn = (v, fallback) => (String(v ?? '').trim() || fallback);
    for (const doc of shown) {
      const meta = readSketchMeta(doc);
      const cName = dn(meta.client, '(no client)');
      const oName = dn(meta.opportunity, '(no opportunity)');
      const sName = dn(meta.space, '(no space)');
      if (!tree.has(cName)) tree.set(cName, new Map());
      const opps = tree.get(cName);
      if (!opps.has(oName)) opps.set(oName, new Map());
      const spaces = opps.get(oName);
      if (!spaces.has(sName)) spaces.set(sName, []);
      spaces.get(sName).push(doc);
    }

    let opportunityIndex = 0;
    for (const [cName, opps] of tree) {
      const clientLi = el('li', 'sk-client');

      for (const [oName, spaces] of opps) {
        const opportunityKey = JSON.stringify([cName, oName]);
        const opportunityLabel = `${cName} · ${oName}`;
        const expanded = Boolean(q) || expandedLibraryOpportunities.has(opportunityKey);
        const oppEl = el('div', 'sk-opp');
        const oppHead = el('div', 'sk-opp__head');
        const oppBody = el('div', 'sk-opp__body');
        const bodyId = `sk-opportunity-${opportunityIndex++}`;
        oppBody.id = bodyId;
        oppBody.hidden = !expanded;

        const toggle = el('button', 'sk-opp__toggle');
        toggle.type = 'button';
        toggle.setAttribute('aria-controls', bodyId);
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.setAttribute('aria-label', `${expanded ? 'Collapse' : 'Expand'} ${opportunityLabel}`);
        const caret = el('span', 'sk-opp__caret', expanded ? '▾' : '▸');
        caret.setAttribute('aria-hidden', 'true');
        toggle.append(caret, el('span', 'sk-opp__name', opportunityLabel));
        toggle.addEventListener('click', () => {
          const willExpand = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(willExpand));
          toggle.setAttribute('aria-label', `${willExpand ? 'Collapse' : 'Expand'} ${opportunityLabel}`);
          caret.textContent = willExpand ? '▾' : '▸';
          oppBody.hidden = !willExpand;
          if (willExpand) expandedLibraryOpportunities.add(opportunityKey);
          else expandedLibraryOpportunities.delete(opportunityKey);
        });
        oppHead.append(toggle);
        const addSpace = el('button', 'sk-add', '＋ Space');
        addSpace.type = 'button';
        addSpace.title = `New space in ${cName} · ${oName}`;
        const sample = readSketchMeta(spaces.values().next().value[0]);
        addSpace.addEventListener('click', () => newSpaceIn(sample.client, sample.opportunity));
        oppHead.append(addSpace);
        oppEl.append(oppHead, oppBody);

        for (const [sName, documents] of spaces) {
          documents.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
          const spaceEl = el('div', 'sk-space');
          const spaceHead = el('div', 'sk-space__head');
          spaceHead.append(el('span', 'sk-space__name', sName));
          if (documents.length > 1) {
            const legacy = el('span', 'sketches-list__when', 'Legacy duplicates — rename or delete extras');
            legacy.title = 'These records predate one-state-per-space. Nothing was silently deleted.';
            spaceHead.append(legacy);
          }
          spaceEl.append(spaceHead);

          for (const doc of documents) {
            const meta = readSketchMeta(doc);
            const row = el('div', `sk-version${meta.id === current.id ? ' sk-version--current' : ''}`);

            const open = el('button', 'sk-version__open');
            open.type = 'button';
            open.append(planThumbSvg(doc));
            const text = el('span', 'sk-version__text');
            text.append(el('span', 'sk-version__name', documents.length > 1 ? 'Legacy space state' : 'Open space'));
            text.append(el('span', 'sketches-list__when', meta.updatedAt ? new Date(meta.updatedAt).toLocaleString() : ''));
            open.append(text);
            open.addEventListener('click', () => openSketch(meta.id));

            const copy = el('button', 'sk-add sk-version__copy', 'Copy');
            copy.type = 'button';
            copy.title = `Copy ${sName} under a new space name`;
            copy.setAttribute('aria-label', `Copy ${sName} under a new space name`);
            copy.addEventListener('click', () => copySpace(meta.id));

            const tmpl = el('button', 'sk-version__template', '⧉');
            tmpl.type = 'button';
            tmpl.title = 'Save as template';
            tmpl.setAttribute('aria-label', `Save ${sName} as a template`);
            tmpl.addEventListener('click', () => saveAsTemplate(meta.id));

            const del = deleteButton(meta.id, sName);

            row.append(open, copy, tmpl, del);
            spaceEl.append(row);
          }
          oppBody.append(spaceEl);
        }
        clientLi.append(oppEl);
      }
      listEl.append(clientLi);
    }
  }

  /** A two-step delete button wired to the shared arm/commit state. */
  function deleteButton(id, label) {
    const del = el('button', 'sketches-list__delete', '🗑');
    del.type = 'button';
    del.setAttribute('aria-label', `Delete ${label}`);
    del.addEventListener('click', () => {
      if (pendingDeleteId === id) {
        disarmDelete();
        deleteSketchById(id);
        return;
      }
      armDelete(id, del, label);
    });
    return del;
  }

  /**
   * The architectural-opening button targets a continuous physical wall. Opening
   * removal lives on the opening card; this control never revives legacy
   * chain-segment removal semantics.
   */
  function renderDoorButton() {
    const btn = root.getElementById('action-door');
    if (!btn) return;
    const selected = model.selectedSegment();
    const isPhysicalWall = selected && (selected.segmentKind ?? 'wall') === 'wall' && !model.isPartition(selected.id);
    setIconTip(btn, 'Add opening', false);
    btn.disabled = !isPhysicalWall;
  }

  /** Sets an icon button's tooltip + aria-label, and its active (accent) state. */
  function setIconTip(btn, tip, active) {
    btn.dataset.tip = tip;
    btn.setAttribute('aria-label', tip);
    btn.classList.toggle('is-active', Boolean(active));
  }

  /**
   * "Add partition" adds a two-sided interior wall to the selected wall
   * (ADR 0032). Enabled only when a real wall is selected — a partition needs a
   * host wall to stick out from, and a door/opening can't host one.
   */
  function renderPartitionButton() {
    const btn = root.getElementById('action-partition');
    if (!btn) return;
    const selected = model.selectedSegment();
    btn.disabled = !(selected && (selected.segmentKind ?? 'wall') === 'wall');
  }

  /**
   * Partition list (ADR 0032). Its length (depth), thickness, and elevation
   * toggle are edited on the "Part" row in the Walls table; this panel just
   * lists each partition with a delete, and selects it on the plan.
   */
  function renderPartitionPanel() {
    const list = root.getElementById('partition-list');
    const title = root.getElementById('partitions-title');
    if (!list) return;
    const parts = model.partitionSegments(); // [{ segmentId, depthUnits, segment }]
    if (title) title.textContent = parts.length ? `Partitions · ${parts.length}` : 'Partitions';
    // An empty section is just noise in a panel this tall (owner, 2026-08-04).
    // Safe to hide entirely: partitions are CREATED from the toolbar, so nothing
    // becomes unreachable — unlike Obstructions or Depth lines, which carry
    // their own add controls and must stay put.
    const section = list.closest('.panel-section');
    if (section) section.hidden = parts.length === 0;

    list.replaceChildren();
    if (parts.length === 0) return;
    parts.forEach((p, i) => list.append(buildPartitionCard(p, i + 1)));
  }

  /** One partition row in the panel: select on the plan, or delete. */
  function buildPartitionCard(part, n) {
    const card = document.createElement('div');
    card.className = 'partition-card partition-card--row';
    card.dataset.partitionId = part.segmentId;

    const name = document.createElement('button');
    name.type = 'button';
    name.className = 'partition-card__name';
    name.textContent = `Partition ${n} — ${fmt(part.depthUnits)} deep`;
    name.title = 'Select this partition on the plan (edit length/thickness/elev on its “Part” row)';
    name.addEventListener('click', () => { model.selectWall(part.segmentId); renderGeometry(); });

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'partition-card__delete';
    del.textContent = '🗑';
    del.setAttribute('aria-label', `Delete partition ${n}`);
    del.addEventListener('click', () => {
      if (model.removePartition(part.segmentId)) renderAll();
    });

    card.append(name, del);
    return card;
  }

  // ---- Admin: section-type definitions (ADR 0039) ---------------------------
  // Visible only to sketch.app_admins members. Edits the section_type_presets
  // business rule through Supabase (RLS enforces admin-only; the DB trigger
  // audits every change), refreshes the local rules cache, and reloads so the
  // whole app re-resolves against the new definitions. Online-only by nature —
  // defining company-wide types is not a field activity.

  const SECTION_FLAGS = ['hanging', 'shelving', 'drawers', 'shoes'];
  // Structural CLASS → platform anchor (ADR 0040). One per type (mutually
  // exclusive): Tall = full-height floor unit (rides up on a platform), Base =
  // countertop unit (shrinks to keep the work surface), Upper = wall-hung
  // (aligns to the tall tops). Stored as `anchor`; 'floor' (Tall) is the default.
  const SECTION_CLASSES = [
    { value: 'floor', label: 'Tall' },
    { value: 'countertop', label: 'Base' },
    { value: 'top', label: 'Upper' },
  ];
  const SECTION_ANCHORS = SECTION_CLASSES.map((c) => c.value);
  let adminTypes = null; // working copy while the panel is open
  let adminObstructionTypes = null; // wall-item defaults, saved as one business-rule row
  let adminDoorStyles = null; // category-grouped style rates + fabrication maxima

  async function initAdminPanel() {
    const button = root.getElementById('open-admin');
    const overlay = root.getElementById('admin-panel');
    if (!button || !overlay || !supabase || !auth) return;
    const show = async (user) => {
      if (!user) { button.hidden = true; return; }
      try {
        const res = await supabase.schema('sketch').from('app_admins').select('user_id').eq('user_id', user.id).maybeSingle();
        button.hidden = !res.data;
      } catch {
        button.hidden = true; // offline / query failed → just not shown
      }
    };
    auth.onChange(show);
    show(await auth.currentUser());
    button.addEventListener('click', () => {
      overlay.hidden = false;
      renderAdminPanel();
    });
    root.getElementById('close-admin')?.addEventListener('click', () => { overlay.hidden = true; });
    overlay.addEventListener('click', (evt) => { if (evt.target === overlay) overlay.hidden = true; });
    root.getElementById('admin-type-add')?.addEventListener('click', () => {
      ensureAdminTypes();
      adminTypes.push({ code: '', name: '', flags: [], anchor: 'floor', cornerLShelf: false, capshelfUnits: null, hangRods: null, hangCategory: null, defaultShelves: null, defaultDrawers: null, minWidthUnits: null, standardWidths: [], defaultDepthUnits: null, defaultHeightUnits: null, defaultWidthUnits: null });
      renderAdminPanel({ keepScroll: true, focusLast: true });
    });
    root.getElementById('admin-type-save')?.addEventListener('click', saveAdminTypes);
    root.getElementById('admin-cvmap-save')?.addEventListener('click', saveAdminCvMapping);
    root.getElementById('admin-obstruction-save')?.addEventListener('click', saveAdminObstructionTypes);
    root.getElementById('admin-door-style-save')?.addEventListener('click', saveAdminDoorStyles);
    root.getElementById('admin-door-style-add')?.addEventListener('click', () => {
      ensureAdminDoorStyles();
      adminDoorStyles.push({ key: '', category: '', style: '', ratePerSqFt: null, maxLeafWidthUnits: null, maxLeafHeightUnits: null });
      renderAdminDoorStyles();
    });
  }

  /**
   * Working copy = the RESOLVED presets, normalized to the full shape.
   * `...p` FIRST is load-bearing: fields this bundle doesn't know about ride
   * through untouched, so a not-yet-updated client saving types can never
   * strip fields a newer bundle added (the drift class that briefly hid
   * per-section features on the owner's tablet, 2026-07-27).
   */
  function ensureAdminTypes() {
    if (adminTypes) return;
    adminTypes = (rules.sectionTypePresets ?? []).map((p) => ({
      ...p,
      code: String(p.code ?? ''),
      name: String(p.name ?? ''),
      flags: Array.isArray(p.flags) ? p.flags.filter((f) => SECTION_FLAGS.includes(f)) : [],
      anchor: SECTION_ANCHORS.includes(p.anchor) ? p.anchor : 'floor',
      cornerLShelf: p.cornerLShelf === true,
      capshelfUnits: p.capshelfUnits > 0 ? p.capshelfUnits : null,
      hangRods: Number.isInteger(p.hangRods) && p.hangRods > 0 ? p.hangRods : null,
      hangCategory: typeof p.hangCategory === 'string' && p.hangCategory ? p.hangCategory : null,
      defaultShelves: Number.isInteger(p.defaultShelves) ? p.defaultShelves : null,
      defaultDrawers: Number.isInteger(p.defaultDrawers) ? p.defaultDrawers : null,
      minWidthUnits: p.minWidthUnits > 0 ? p.minWidthUnits : null,
      standardWidths: Array.isArray(p.standardWidths) ? p.standardWidths.filter((w) => w > 0) : [],
      defaultDepthUnits: p.defaultDepthUnits > 0 ? p.defaultDepthUnits : null,
      defaultHeightUnits: p.defaultHeightUnits > 0 ? p.defaultHeightUnits : null,
      defaultWidthUnits: p.defaultWidthUnits > 0 ? p.defaultWidthUnits : null,
    }));
  }

  /**
   * One GRID row per type, all fields always present — a flag's dependent
   * fields grey out in place instead of appearing/vanishing, so toggling a
   * flag re-renders NOTHING and the list never loses its scroll position
   * (owner: "every change takes me back to the top").
   */
  function renderAdminPanel(opts = {}) {
    const list = root.getElementById('admin-type-list');
    if (!list) return;
    ensureAdminTypes();
    const scroll = opts.keepScroll ? list.scrollTop : 0;
    list.replaceChildren(adminGridHead());
    adminTypes.forEach((t, i) => list.append(...adminTypeRow(t, i)));
    list.scrollTop = opts.focusLast ? list.scrollHeight : scroll;
    if (opts.focusLast) list.querySelector('.admin-grid__row:last-of-type input')?.focus();
    renderAdminObstructionTypes();
    renderAdminCvMapping();
    renderAdminDoorStyles();
    renderAdminNumbers();
  }

  // ---- CV pricing mapping (D116 / RS-7) ------------------------------------
  // The grid half lives in config/cv-mapping-editor.js (pure, tested); this is
  // only DOM. Working copy, like adminTypes: nothing persists until Save.
  let adminCvMapping = null;

  function ensureAdminCvMapping() {
    if (!adminCvMapping) adminCvMapping = structuredClone(rules.cvTypeMapping);
  }

  /** The advanced switches the grid shows but does not edit (drift rule). */
  function cvMappingBadges(row) {
    const badges = [];
    if (row?.configByShoeStyle === true) badges.push('shoe styles');
    if (row?.configByDrawerCount) badges.push('per-drawer-count');
    if (row?.hamperAccessory === true) badges.push('hamper accessory');
    if (row?.interim === true) badges.push('interim');
    return badges;
  }

  function renderAdminCvMapping(opts = {}) {
    const list = root.getElementById('admin-cvmap-list');
    if (!list) return;
    ensureAdminCvMapping();
    const catalog = rules.cvSectionCatalog;
    const scroll = opts.keepScroll ? list.scrollTop : 0;
    const classes = catalogClasses(catalog);
    const cell = (cls, ...children) => {
      const div = document.createElement('div');
      div.className = cls;
      div.append(...children);
      return div;
    };
    const select = (value, options, onChange, label) => {
      const el = document.createElement('select');
      el.setAttribute('aria-label', label);
      el.append(new Option('—', ''));
      for (const opt of options) el.append(new Option(opt, opt, false, opt === value));
      if (value && !options.includes(value)) el.append(new Option(`${value} (not in catalog)`, value, false, true));
      el.addEventListener('change', onChange);
      return el;
    };
    const head = ['Code', 'Class', 'Door-less config', 'CV height', '$0', 'Status']
      .map((t) => cell('admin-cvmap__head', t));
    const rowsOut = [head];
    for (const entry of mappingGrid(adminCvMapping, rules.sectionTypePresets)) {
      const { code, row } = entry;
      const edit = (patch) => {
        adminCvMapping = applyMappingEdit(adminCvMapping, code, patch);
        renderAdminCvMapping({ keepScroll: true });
      };
      const codeCell = cell('admin-cvmap__code');
      const strong = document.createElement('strong');
      strong.textContent = code;
      codeCell.append(strong);
      if (entry.name) codeCell.append(document.createTextNode(` ${entry.name}`));
      for (const badge of cvMappingBadges(row)) {
        const b = document.createElement('span');
        b.className = 'admin-cvmap__badge';
        b.textContent = badge;
        codeCell.append(b);
      }
      const zero = row?.zeroPrice === true;
      const classId = String(row?.class ?? '');
      const classSel = select(classId, classes, (evt) => {
        // A config is only meaningful inside its class, so changing class
        // clears it for an explicit re-pick — never a guessed carry-over.
        edit({ class: evt.target.value || null, config: null, doorConfig: null });
      }, `${code} class`);
      const configOptions = classId ? catalogConfigsFor(catalog, classId) : [];
      const configSel = select(String(row?.config ?? ''), configOptions, (evt) => edit({ config: evt.target.value || null }), `${code} config`);
      const height = document.createElement('input');
      height.type = 'text';
      height.inputMode = 'decimal';
      height.setAttribute('aria-label', `${code} CV height`);
      height.value = row?.cvHeightIn ?? '';
      height.addEventListener('change', () => {
        const n = Number.parseFloat(height.value);
        edit({ cvHeightIn: Number.isFinite(n) && n > 0 ? n : null });
      });
      const zeroBox = document.createElement('input');
      zeroBox.type = 'checkbox';
      zeroBox.checked = zero;
      zeroBox.setAttribute('aria-label', `${code} zero price`);
      zeroBox.addEventListener('change', () => edit({ zeroPrice: zeroBox.checked ? true : null }));
      for (const el of [classSel, configSel, height]) el.disabled = zero;
      const status = document.createElement('span');
      status.className = `admin-cvmap__status admin-cvmap__status--${entry.status}`;
      status.textContent = entry.status === 'mapped-no-preset' ? 'no preset' : entry.status;
      rowsOut.push([
        codeCell,
        cell('', classSel),
        cell('', configSel),
        cell('', height),
        cell('', zeroBox),
        cell('', status),
      ]);
    }
    list.replaceChildren(...rowsOut.flat());
    list.scrollTop = scroll;
    // The RS-7 readout: catalog families no mapping can reach. Live against
    // the WORKING copy, so an admin sees a family disappear as they map it.
    const readout = root.getElementById('admin-cvmap-unreachable');
    if (readout) {
      const un = unreachableCatalogFamilies(adminCvMapping, catalog);
      readout.textContent = un.length
        ? `Catalog families no type can reach (priced rows exist, no route): ${un.map((u) => `${u.family} (${u.rows})`).join(', ')}`
        : 'Every priced catalog family is reachable from at least one mapping.';
    }
  }

  async function saveAdminCvMapping() {
    const status = root.getElementById('admin-types-status');
    const say = (msg, warn) => {
      if (!status) return;
      status.textContent = msg;
      status.className = `admin-card__status${warn ? ' admin-card__status--warn' : ''}`;
    };
    ensureAdminCvMapping();
    const errors = (adminCvMapping.rows ?? [])
      .flatMap((row) => validateMappingRow(row, rules.cvSectionCatalog));
    if (errors.length) return say(errors[0], true);
    say('Saving…');
    try {
      // v2 envelope (SD-118): rows win by code, removals are explicit, and a
      // default row added after this save still arrives — no resave needed.
      const res = await supabase.schema('sketch').from('business_rules').upsert({
        scope: 'global',
        scope_id: null,
        rule_key: 'cv_type_mapping',
        rule_value: (() => {
          const v2 = versionedListSaveValue(adminCvMapping.rows ?? [], DEFAULT_BUSINESS_RULES.cvTypeMapping.rows, (r) => r.code,
            { claimRemovals: rules.listRuleVersions?.cv_type_mapping !== 1 });
          return { ...adminCvMapping, version: 2, rows: v2.items, removed: v2.removed };
        })(),
        is_active: true,
      }, { onConflict: 'scope,scope_id,rule_key' }).select('rule_key');
      if (res.error || !res.data?.length) throw new Error(res.error?.message ?? 'Admin rights required.');
      const { pullBusinessRules } = await import('../sync/rules.js');
      await pullBusinessRules(supabase, setMeta).catch(() => {});
      say('Saved. Reloading…');
      setTimeout(() => { if (typeof location !== 'undefined') location.reload(); }, 400);
    } catch (err) {
      say(`Save failed: ${err?.message ?? err}`, true);
    }
  }

  function ensureAdminDoorStyles() {
    if (!adminDoorStyles) adminDoorStyles = structuredClone(rules.doorStyles ?? []);
  }

  function renderAdminDoorStyles() {
    const box = root.getElementById('admin-door-style-list');
    if (!box) return;
    ensureAdminDoorStyles();
    box.replaceChildren();
    const row = (head = false) => {
      const el = document.createElement('div');
      el.className = `admin-door-style-grid__row${head ? ' admin-door-style-grid__row--head' : ''}`;
      return el;
    };
    const head = row(true);
    for (const label of ['Key', 'Category', 'Style', '$ / sq ft', 'Max W', 'Max H', '']) {
      const cell = document.createElement('span');
      cell.textContent = label;
      head.append(cell);
    }
    box.append(head);
    adminDoorStyles.forEach((style, index) => {
      const line = row();
      const field = (prop, numeric = false) => {
        const input = document.createElement('input');
        input.type = numeric ? 'number' : 'text';
        if (numeric) { input.min = '0.01'; input.step = '0.01'; input.inputMode = 'decimal'; }
        input.value = style[prop] ?? '';
        input.setAttribute('aria-label', `${style.key} ${prop}`);
        input.addEventListener('change', () => {
          adminDoorStyles[index][prop] = numeric ? Number(input.value) : input.value.trim();
        });
        return input;
      };
      line.append(field('key'), field('category'), field('style'), field('ratePerSqFt', true),
        field('maxLeafWidthUnits', true), field('maxLeafHeightUnits', true));
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'btn btn--icon';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove door style ${style.key || index + 1}`);
      remove.addEventListener('click', () => { adminDoorStyles.splice(index, 1); renderAdminDoorStyles(); });
      line.append(remove);
      box.append(line);
    });
  }

  async function saveAdminDoorStyles() {
    const status = root.getElementById('admin-types-status');
    const say = (message, warn = false) => {
      if (!status) return;
      status.textContent = message;
      status.className = `admin-card__status${warn ? ' admin-card__status--warn' : ''}`;
    };
    ensureAdminDoorStyles();
    const invalid = adminDoorStyles.find((style) => !style.key || !style.category || !style.style
      || !(style.ratePerSqFt >= 0) || !(style.maxLeafWidthUnits > 0) || !(style.maxLeafHeightUnits > 0));
    if (invalid) return say(`Door style ${invalid.key || '(blank)'} needs category, style, rate, max width and max height.`, true);
    const keys = adminDoorStyles.map((style) => style.key);
    if (new Set(keys).size !== keys.length) return say('Door style keys must be unique.', true);
    say('Saving door styles…');
    try {
      const value = versionedListSaveValue(adminDoorStyles, DEFAULT_BUSINESS_RULES.doorStyles, (style) => style.key,
        { claimRemovals: rules.listRuleVersions?.door_styles !== 1 });
      const res = await supabase.schema('sketch').from('business_rules').upsert({
        scope: 'global', scope_id: null, rule_key: 'door_styles', rule_value: value, is_active: true,
      }, { onConflict: 'scope,scope_id,rule_key' }).select('rule_key');
      if (res.error || !res.data?.length) throw new Error(res.error?.message ?? 'Admin rights required.');
      const { pullBusinessRules } = await import('../sync/rules.js');
      await pullBusinessRules(supabase, setMeta).catch(() => {});
      say('Door styles saved. Reloading…');
      setTimeout(() => { if (typeof location !== 'undefined') location.reload(); }, 400);
    } catch (err) {
      say(`Save failed: ${err?.message ?? err}`, true);
    }
  }


  /**
   * Obstruction defaults live in the same owner-maintained catalog the drawing
   * palette consumes. Preserve unrecognised properties so a save from this
   * client cannot strip newer type behavior (anchor, capsSectionHeight, etc.).
   * `inset` is positive, field-friendly language even though each placed
   * obstruction stores the equivalent negative faceOffsetUnits coordinate.
   */
  function ensureAdminObstructionTypes() {
    if (adminObstructionTypes) return;
    adminObstructionTypes = (rules.obstructionTypes ?? []).map((t) => ({
      ...t,
      key: String(t.key ?? ''),
      code: String(t.code ?? ''),
      label: String(t.label ?? ''),
      width: Number.isFinite(t.width) && t.width > 0 ? t.width : null,
      height: Number.isFinite(t.height) && t.height > 0 ? t.height : null,
      offsetFromFloor: Number.isFinite(t.offsetFromFloor) && t.offsetFromFloor >= 0 ? t.offsetFromFloor : 0,
      depth: Number.isFinite(t.depth) && t.depth > 0 ? t.depth : null,
      inset: Number.isFinite(t.inset) && t.inset >= 0
        ? t.inset
        : (Number.isFinite(t.faceOffset) && t.faceOffset < 0 ? -t.faceOffset : 0),
    }));
  }

  function renderAdminObstructionTypes() {
    const box = root.getElementById('admin-obstruction-list');
    if (!box) return;
    ensureAdminObstructionTypes();
    box.replaceChildren();

    const head = document.createElement('div');
    head.className = 'admin-obstruction-grid__row admin-obstruction-grid__row--head';
    for (const label of ['Key', 'Code', 'Name', 'Width', 'Height', 'From floor', 'Depth', 'Inset']) {
      const cell = document.createElement('span');
      cell.textContent = label;
      head.append(cell);
    }
    box.append(head);

    const lengthInput = (type, prop, { blank = false, zero = false } = {}) => {
      const input = document.createElement('input');
      input.inputMode = 'text';
      input.dataset.pad = 'length';
      input.value = Number.isFinite(type[prop]) ? fmt(type[prop]) : '';
      input.setAttribute('aria-label', `${type.label || type.key} ${prop}`);
      input.addEventListener('change', () => {
        const raw = input.value.trim();
        if (blank && raw === '') {
          type[prop] = null;
          input.classList.remove('obstruction__input--invalid');
          return;
        }
        const parsed = parseLength(raw);
        const valid = Number.isFinite(parsed) && (zero ? parsed >= 0 : parsed > 0);
        if (!valid) {
          input.classList.add('obstruction__input--invalid');
          input.value = Number.isFinite(type[prop]) ? fmt(type[prop]) : '';
          return;
        }
        input.classList.remove('obstruction__input--invalid');
        type[prop] = parsed;
        input.value = fmt(parsed);
      });
      return input;
    };

    for (const type of adminObstructionTypes) {
      const row = document.createElement('div');
      row.className = 'admin-obstruction-grid__row';
      const key = document.createElement('span');
      key.className = 'admin-obstruction-grid__key';
      key.textContent = type.key;
      const code = document.createElement('input');
      code.value = type.code;
      code.maxLength = 8;
      code.setAttribute('aria-label', `${type.label || type.key} drawing code`);
      code.addEventListener('change', () => {
        type.code = code.value.trim().toUpperCase().slice(0, 8);
        code.value = type.code;
      });
      const name = document.createElement('input');
      name.value = type.label;
      name.setAttribute('aria-label', `${type.key} name`);
      name.addEventListener('change', () => {
        type.label = name.value.trim();
        name.value = type.label;
      });
      row.append(
        key,
        code,
        name,
        lengthInput(type, 'width'),
        lengthInput(type, 'height'),
        lengthInput(type, 'offsetFromFloor', { zero: true }),
        lengthInput(type, 'depth', { blank: true }),
        lengthInput(type, 'inset', { zero: true }),
      );
      box.append(row);
    }
  }

  async function saveAdminObstructionTypes() {
    const status = root.getElementById('admin-types-status');
    const say = (msg, warn = false) => {
      if (!status) return;
      status.textContent = msg;
      status.className = `admin-card__status${warn ? ' admin-card__status--warn' : ''}`;
    };
    ensureAdminObstructionTypes();
    if (!adminObstructionTypes.length) return say('At least one obstruction type is required.', true);
    if (adminObstructionTypes.some((t) => !t.key || !t.label || !(t.width > 0) || !(t.height > 0))) {
      return say('Every obstruction needs a key, name, width, and height.', true);
    }
    const payload = adminObstructionTypes.map((t) => Object.fromEntries(
      Object.entries({
        ...t,
        code: t.code.trim().toUpperCase(),
        label: t.label.trim(),
        inset: Number.isFinite(t.inset) && t.inset >= 0 ? t.inset : 0,
      }).filter(([, value]) => value !== null && value !== ''),
    ));
    say('Saving obstruction defaults…');
    try {
      const res = await supabase
        .schema('sketch')
        .from('business_rules')
        .upsert(
          {
            scope: 'global',
            scope_id: null,
            rule_key: 'obstruction_types',
            // v2 envelope (SD-118): a default type added after this save
            // still arrives; a type the admin deleted stays deleted.
            rule_value: versionedListSaveValue(payload, DEFAULT_BUSINESS_RULES.obstructionTypes, (t) => t.key,
              { claimRemovals: rules.listRuleVersions?.obstruction_types !== 1 }),
            is_active: true,
          },
          { onConflict: 'scope,scope_id,rule_key' },
        )
        .select('rule_key');
      if (res.error) return say(`Save failed: ${res.error.message}`, true);
      if (!res.data?.length) return say('Save blocked — admin rights required.', true);
      const { pullBusinessRules } = await import('../sync/rules.js');
      await pullBusinessRules(supabase, setMeta).catch(() => {});
      say('Obstruction defaults saved. Reloading…');
      setTimeout(() => { if (typeof location !== 'undefined') location.reload(); }, 400);
    } catch (err) {
      say(`Save failed: ${err?.message ?? err}`, true);
    }
  }

  /**
   * The numeric rules (owner, 2026-08-05: "these need to be added to the admin
   * maintenance tables"). One row per EDITABLE_NUMBER_RULES entry — the list
   * lives beside the rule defaults, so a new tunable number is one entry there
   * and appears here with no UI work.
   *
   * Each row saves ITSELF on change, as an UPSERT: unlike the type presets,
   * many of these rules exist only as compiled-in defaults with no DB row yet,
   * and an update would match nothing — the exact silent no-op that bit the
   * obstruction types (see HANDOFF, "migrations can report success and change
   * nothing"). The unique (scope, scope_id, rule_key) constraint makes the
   * upsert safe. Rules apply on next load (ADR 0039); saving from here reloads.
   */
  function renderAdminNumbers() {
    const box = root.getElementById('admin-number-list');
    if (!box) return;
    box.replaceChildren();
    const status = root.getElementById('admin-types-status');
    const say = (text, bad = false) => {
      if (!status) return;
      status.textContent = text;
      status.classList.toggle('admin-card__status--error', bad);
    };
    for (const rule of EDITABLE_NUMBER_RULES) {
      const row = document.createElement('label');
      row.className = 'admin-number';
      row.title = rule.hint;
      const name = document.createElement('span');
      name.className = 'admin-number__label';
      name.textContent = rule.label;
      const input = document.createElement('input');
      input.className = 'admin-number__input';
      input.inputMode = 'text';
      if (rule.kind === 'length') input.dataset.pad = 'length';
      const current = rules[rule.prop];
      input.value = rule.kind === 'length' ? fmt(current) : String(current);
      input.setAttribute('aria-label', rule.label);
      const unit = document.createElement('span');
      unit.className = 'admin-number__unit';
      unit.textContent = rule.kind === 'degrees' ? '°' : rule.kind === 'length' ? '' : '';
      input.addEventListener('change', async () => {
        const raw = input.value.trim();
        const parsed = rule.kind === 'length' ? parseLength(raw) : Number(raw);
        // 0 is legal where it means "off" (storage rounding); negatives never are.
        if (parsed === null || !Number.isFinite(parsed) || parsed < 0
          || (rule.kind !== 'length' && raw === '')) {
          input.classList.add('obstruction__input--invalid');
          return;
        }
        input.classList.remove('obstruction__input--invalid');
        say(`Saving ${rule.label}…`);
        try {
          const res = await supabase
            .schema('sketch')
            .from('business_rules')
            .upsert(
              { scope: 'global', scope_id: null, rule_key: rule.key, rule_value: parsed, is_active: true },
              { onConflict: 'scope,scope_id,rule_key' },
            )
            .select('rule_key');
          // platform-boundary-exception: displays raw error text in a save
          // toast, no retry-policy branch — see platform/boundary-exceptions.json.
          if (res.error) return say(`Save failed: ${res.error.message}`, true);
          if (!res.data?.length) return say('Save blocked — admin rights required.', true);
          const { pullBusinessRules } = await import('../sync/rules.js');
          await pullBusinessRules(supabase, setMeta).catch(() => {});
          say(`${rule.label} saved. Reloading…`);
          setTimeout(() => { if (typeof location !== 'undefined') location.reload(); }, 400);
        } catch (err) {
          say(`Save failed: ${err?.message ?? err}`, true);
        }
      });
      row.append(name, input, unit);
      box.append(row);
    }
  }

  function adminGridHead() {
    const head = document.createElement('div');
    head.className = 'admin-grid__row admin-grid__row--head';
    for (const label of ['Code', 'Name', 'Class', 'Hang', 'Shelv', 'Drwr', 'Shoes', 'L-Shf', 'Cat', 'Rods', 'Shlvs', 'Drws', 'Min', 'Sizes', 'W', 'D', 'H', 'Cap', '']) {
      const cell = document.createElement('span');
      cell.textContent = label;
      head.append(cell);
    }
    return head;
  }

  function adminTypeRow(t, i) {
    const row = document.createElement('div');
    row.className = 'admin-grid__row';

    const text = (value, placeholder, tip, apply) => {
      const input = document.createElement('input');
      input.value = value ?? '';
      input.placeholder = placeholder;
      input.title = tip;
      input.addEventListener('change', () => apply(input.value.trim()));
      return input;
    };
    const num = (value, tip, apply, integer = false) => {
      const input = document.createElement('input');
      input.inputMode = integer ? 'numeric' : 'decimal';
      input.value = value ?? '';
      input.title = tip;
      input.addEventListener('change', () => {
        const raw = input.value.trim();
        if (!raw) { apply(null); return; }
        const parsed = integer ? Number.parseInt(raw, 10) : parseLength(raw);
        if ((integer && Number.isInteger(parsed) && parsed >= 0) || (!integer && parsed > 0)) apply(parsed);
        else input.value = value ?? '';
      });
      return input;
    };

    const code = text(t.code, 'code', 'Short code drawn on the sketch (DH, HAMP…)', (v) => { t.code = v.toUpperCase().slice(0, 8); });
    code.classList.add('admin-grid__code');
    const name = text(t.name, 'name', 'Full name shown in tooltips', (v) => { t.name = v; });

    // Structural CLASS → platform anchor (ADR 0040). Tall rides up, Base shrinks
    // to keep the countertop, Upper aligns to the tall tops. Uppers are standard
    // at 24"/36"/42" and cannot exceed 48" (set that in the H field).
    const cls = document.createElement('select');
    cls.className = 'admin-grid__class';
    cls.title = 'Structural class → platform behaviour (ADR 0040). Tall = full-height floor unit (rides up); Base = countertop unit (shrinks to keep the work surface); Upper = wall-hung (standard 24/36/42, max 48).';
    for (const c of SECTION_CLASSES) {
      const opt = document.createElement('option');
      opt.value = c.value;
      opt.textContent = c.label;
      cls.append(opt);
    }
    cls.value = SECTION_ANCHORS.includes(t.anchor) ? t.anchor : 'floor';
    cls.addEventListener('change', () => { t.anchor = cls.value; });

    // Which hanging CATEGORY this type's capacity reports under (owner,
    // 2026-08-04). Several types feed one category and their inches ADD: a
    // double hang contributes 2× its width to short hang, a single-rod short
    // hang 1×. The categories themselves are a business rule, so this list
    // follows whatever Admin says they are.
    const hangCat = document.createElement('select');
    hangCat.className = 'admin-grid__class';
    hangCat.title = 'Hanging category this type reports under in the storage summary and the client’s requirements. A double hang is two rods of SHORT hang.';
    const blankCat = document.createElement('option');
    blankCat.value = '';
    blankCat.textContent = '—';
    hangCat.append(blankCat);
    for (const c of rules.hangCategories ?? []) {
      const opt = document.createElement('option');
      opt.value = c.key;
      opt.textContent = c.label ?? c.key;
      hangCat.append(opt);
    }
    hangCat.value = (rules.hangCategories ?? []).some((c) => c.key === t.hangCategory) ? t.hangCategory : '';
    hangCat.addEventListener('change', () => { t.hangCategory = hangCat.value || null; });

    // Numeric fields dependent on a flag, greyed until it is on.
    const rods = num(t.hangRods, 'Hang rods per section (double hang = 2) — drives linear hang inches', (v) => { t.hangRods = v; }, true);
    const shelves = num(t.defaultShelves, 'Default shelves on a new section', (v) => { t.defaultShelves = v; }, true);
    const drawers = num(t.defaultDrawers, 'Default drawers on a new section', (v) => { t.defaultDrawers = v; }, true);
    const dependents = { hanging: [rods, hangCat], shelving: [shelves], drawers: [drawers], shoes: [] };
    const syncDisabled = () => {
      for (const [flag, inputs] of Object.entries(dependents)) {
        for (const input of inputs) input.disabled = !t.flags.includes(flag);
      }
    };

    const flagBoxes = SECTION_FLAGS.map((flag) => {
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = t.flags.includes(flag);
      box.title = flag;
      box.addEventListener('change', () => {
        t.flags = box.checked ? [...t.flags, flag] : t.flags.filter((f) => f !== flag);
        syncDisabled(); // in place — no re-render, no scroll jump
      });
      return box;
    });

    // L-Shelf flag (owner, 2026-07-28): an L-shaped corner shelf — required to
    // sit in a corner and unique. The flag is stored now; corner-placement
    // behaviour is a follow-up (HANDOFF #19).
    const lshelf = document.createElement('input');
    lshelf.type = 'checkbox';
    lshelf.checked = t.cornerLShelf === true;
    lshelf.title = 'L-shelf: an L-shaped corner shelf (goes into corners; unique).';
    lshelf.addEventListener('change', () => { t.cornerLShelf = lshelf.checked; });

    const min = num(t.minWidthUnits, 'Minimum opening for this type — replaces the global minimum in the narrow warning', (v) => { t.minWidthUnits = v; });
    const sizes = document.createElement('input');
    sizes.className = 'admin-grid__sizes';
    sizes.value = t.standardWidths.map((w) => fmt(w)).join(', ');
    sizes.placeholder = 'free';
    sizes.title = 'Set sizes, comma-separated (e.g. 18, 24, 30) — designers pick from these instead of typing a width. Blank = free width.';
    sizes.addEventListener('change', () => {
      const parsed = sizes.value.split(',').map((part) => parseLength(part.trim())).filter((v) => v !== null && v > 0);
      t.standardWidths = [...new Set(parsed)].sort((a, b) => a - b);
      sizes.value = t.standardWidths.map((w) => fmt(w)).join(', ');
    });
    const width = num(t.defaultWidthUnits, 'Standard width — new sections arrive LOCKED at it; blank = auto-fill', (v) => { t.defaultWidthUnits = v; });
    const depth = num(t.defaultDepthUnits, 'Default plan depth', (v) => { t.defaultDepthUnits = v; });
    const height = num(t.defaultHeightUnits, 'Default elevation height', (v) => { t.defaultHeightUnits = v; });
    // Capshelf — a ¾" shelf built onto an UPPER's bottom to hide construction
    // seams. It ADDS to the unit's height (a 36" upper is 36¾" overall) and is
    // in addition to the unit's own bottom shelf. Uppers only.
    const cap = num(t.capshelfUnits, 'Capshelf thickness (usually ¾") — a shelf on the BOTTOM of an upper, hiding construction seams. Adds to the unit\'s overall height. Uppers only.', (v) => { t.capshelfUnits = v; });
    const syncCap = () => { cap.disabled = t.anchor !== 'top'; };
    syncCap();
    cls.addEventListener('change', syncCap);

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'section-row__delete';
    del.textContent = '×';
    del.title = 'Remove this type (existing sections keep their code)';
    del.addEventListener('click', () => {
      adminTypes.splice(i, 1);
      renderAdminPanel({ keepScroll: true }); // structural change — rebuild, hold position
    });

    row.append(code, name, cls, ...flagBoxes, lshelf, hangCat, rods, shelves, drawers, min, sizes, width, depth, height, cap, del);
    syncDisabled();
    return [row];
  }

  async function saveAdminTypes() {
    const status = root.getElementById('admin-types-status');
    const say = (msg, warn = false) => {
      if (!status) return;
      status.textContent = msg;
      status.className = `admin-card__status${warn ? ' admin-card__status--warn' : ''}`;
    };
    ensureAdminTypes();
    const cleaned = adminTypes
      .map((t) => ({
        ...t,
        code: t.code.trim().toUpperCase(),
        name: t.name.trim(),
        // EVERY type carries an explicit class (owner, 2026-07-28) — Tall is
        // written out rather than left implicit, so a definition always says
        // what it is instead of relying on a reader's default.
        anchor: SECTION_ANCHORS.includes(t.anchor) ? t.anchor : 'floor',
        cornerLShelf: t.cornerLShelf === true ? true : null,
        // Only an UPPER carries a capshelf; strip a stray so a definition can
        // never disagree with itself.
        capshelfUnits: t.anchor === 'top' ? t.capshelfUnits : null,
        // Counts only make sense with their flag; strip strays so the stored
        // definition can't disagree with itself.
        hangRods: t.flags.includes('hanging') ? t.hangRods : null,
        hangCategory: t.flags.includes('hanging') ? t.hangCategory : null,
        defaultShelves: t.flags.includes('shelving') ? t.defaultShelves : null,
        defaultDrawers: t.flags.includes('drawers') ? t.defaultDrawers : null,
      }))
      .filter((t) => t.code);
    const codes = cleaned.map((t) => t.code);
    if (codes.length === 0) return say('At least one type is required.', true);
    if (new Set(codes).size !== codes.length) return say('Codes must be unique.', true);
    // Nulls and empty lists drop out of the stored JSON — absent = no rule.
    const payload = cleaned.map((t) =>
      Object.fromEntries(Object.entries(t).filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))));
    say('Saving…');
    try {
      // UPSERT, not update: the 20260829180000 migration deletes the seeded
      // row, and an UPDATE matching nothing reports success while saving
      // nothing (the 20260805 migration's own recorded lesson). v2 envelope
      // per SD-118 — a preset added to the defaults after this save still
      // arrives, with its behaviour (fixedHeight and friends) intact.
      const res = await supabase
        .schema('sketch')
        .from('business_rules')
        .upsert({
          scope: 'global',
          scope_id: null,
          rule_key: 'section_type_presets',
          rule_value: versionedListSaveValue(payload, DEFAULT_BUSINESS_RULES.sectionTypePresets, (p) => p.code,
            { claimRemovals: rules.listRuleVersions?.section_type_presets !== 1 }),
          is_active: true,
        }, { onConflict: 'scope,scope_id,rule_key' })
        .select('rule_key');
      if (res.error) return say(`Save failed: ${res.error.message}`, true);
      if (!res.data?.length) return say('Save blocked — admin rights required.', true);
      // Refresh the local cache, then reload so every consumer re-resolves.
      const { pullBusinessRules } = await import('../sync/rules.js');
      await pullBusinessRules(supabase, setMeta).catch(() => {});
      say('Saved. Reloading…');
      setTimeout(() => { if (typeof location !== 'undefined') location.reload(); }, 400);
    } catch (err) {
      say(`Save failed: ${err?.message ?? err}`, true);
    }
  }

  // ---- Interior editor (ADR 0056, plan §5/4c) --------------------------------
  // A per-section overlay (Admin-window pattern) that edits the composed
  // interior (ADR 0054) against a STAGED working copy, with a live
  // mini-elevation preview rendered by the REAL createElevation through
  // substituted feeds (interior-editor.js). Nothing touches the model until
  // Apply; Cancel/Esc/backdrop all discard.

  let interiorEditor = null; // { sectionId, draft } while the overlay is open
  let interiorPreview = null; // the lazy createElevation bound to the preview SVG
  let previewExpanded = false; // the preview is filling the viewport (RS-2b)

  /**
   * Expands the live preview to the whole viewport, or puts it back.
   *
   * A class toggle and nothing else: the SVG never moves in the DOM, so the
   * ONE `createElevation` instance stays bound to the same element with its
   * handlers and pan/zoom state intact. Re-parenting it, or standing up a
   * second renderer for a bigger box, would give two viewports on one SVG —
   * the failure `renderInteriorPreview` already guards against.
   *
   * Both directions refit. Changing the aperture invalidates whatever framing
   * the designer had: expanding while zoomed 3x would fill the screen with a
   * sliver, and collapsing while zoomed would hide the section in a small box.
   * Expanding to see the whole thing bigger is the entire point, so that is
   * what it does; Fit is one tap away if they disagree.
   */
  function setPreviewExpanded(on) {
    const overlay = root.getElementById('interior-editor');
    if (!overlay) return;
    previewExpanded = on === true;
    overlay.classList.toggle('is-preview-expanded', previewExpanded);
    const btn = root.getElementById('interior-preview-expand');
    if (btn) {
      btn.setAttribute('aria-pressed', String(previewExpanded));
      btn.title = previewExpanded ? 'Shrink the preview' : 'Expand the preview';
      btn.setAttribute('aria-label', btn.title);
    }
    interiorPreview?.fit();
  }

  function initInteriorEditor() {
    const overlay = root.getElementById('interior-editor');
    if (!overlay) return;
    const close = () => { overlay.hidden = true; interiorEditor = null; setPreviewExpanded(false); };
    root.getElementById('close-interior')?.addEventListener('click', close);
    root.getElementById('interior-cancel')?.addEventListener('click', close);
    overlay.addEventListener('click', (evt) => { if (evt.target === overlay) close(); });
    root.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape' || overlay.hidden) return;
      // Escape backs out ONE level: an expanded preview collapses, and only a
      // second press closes the editor. Otherwise expanding to read the drawing
      // would put the staged draft one keystroke from being discarded.
      if (previewExpanded) setPreviewExpanded(false);
      else close();
    });
    // RS-2b: the preview's aperture controls. zoomIn/zoomOut/fit have existed
    // on the elevation API all along and were never exposed; on a portrait
    // tablet or a phone they are the ONLY route to a legible drawing, because
    // no aperture those devices have can fit a 1200-wide viewBox at 11px.
    root.getElementById('interior-preview-zoom-in')?.addEventListener('click', () => interiorPreview?.zoomIn());
    root.getElementById('interior-preview-zoom-out')?.addEventListener('click', () => interiorPreview?.zoomOut());
    root.getElementById('interior-preview-fit')?.addEventListener('click', () => interiorPreview?.fit());
    root.getElementById('interior-preview-expand')?.addEventListener('click', () => setPreviewExpanded(!previewExpanded));
    root.getElementById('interior-apply')?.addEventListener('click', () => {
      if (!interiorEditor) return;
      const { blocked } = editorDoorDepth();
      const res = applyInteriorDraft(model, interiorEditor.sectionId, interiorEditor.draft, { doorsBlocked: blocked });
      if (!res.ok) return;
      close();
      repaintSections();
    });
  }

  function openInteriorEditor(sectionId) {
    const overlay = root.getElementById('interior-editor');
    const section = model.state.sections.find((s) => s.id === sectionId);
    if (!overlay || !section) return;
    interiorEditor = {
      sectionId,
      draft: createInteriorDraft(model.sectionInterior(sectionId), section.doors, section.requiredHolesOnly === true),
    };
    overlay.hidden = false;
    renderInteriorEditor();
  }

  /** The staged preview feeds for the open editor, or null when unplaceable. */
  function editorPreview() {
    if (!interiorEditor) return null;
    return previewElevation(model, rules, interiorEditor.sectionId, interiorEditor.draft, elevationRenderOpts());
  }

  /** Door depth rule state for the section being edited (resolved depth). The
   *  HANGING minimum is read from the STAGED draft (ADR 0060 item 1), so
   *  adding a rod raises the requirement before anything is committed. */
  function editorDoorDepth() {
    const preview = editorPreview();
    return doorDepthState(preview?.editedUnit?.depthUnits ?? 0, rules, {
      hanging: draftHasHang(interiorEditor?.draft),
    });
  }

  function renderInteriorEditor() {
    if (!interiorEditor) return;
    const { draft, sectionId } = interiorEditor;
    const section = model.state.sections.find((s) => s.id === sectionId);
    const list = root.getElementById('interior-list');
    const addBox = root.getElementById('interior-add');
    const doorsBox = root.getElementById('interior-doors');
    const warnBox = root.getElementById('interior-warnings');
    const title = root.getElementById('interior-title');
    if (!section || !list || !addBox || !doorsBox || !warnBox) return;
    if (title) title.textContent = `Interior · ${section.typeCode}`;

    let preview = editorPreview();
    // EVERY REGION OF THE BAND IS A SPACE (ADR 0067). A pinned board can leave
    // a region of the drawing with no element in it — real space the designer
    // can see and the space list could not name. It gains its `openSpace` here,
    // where the placed unit (and therefore the band) is known, staged like
    // every other heal. Idempotent, so this settles on the first render.
    if (preview?.editedUnit && draftEnsureBandSpaces(draft, preview.editedUnit, rules)) {
      preview = editorPreview();
    }
    const depth = doorDepthState(preview?.editedUnit?.depthUnits ?? 0, rules, { hanging: draftHasHang(draft) });
    const feed = preview ? draftFeedForUnit(draft, preview.editedUnit, rules) : null;
    const solvedEls = feed?.solved?.elements ?? [];

    // Rebuild the list, holding scroll (the Admin grid's keepScroll pattern).
    const scrollTop = list.scrollTop;
    list.replaceChildren();
    // The auto-inserted STRUCTURAL shelves are labelled in the list (ADR 0062
    // item 6) so a designer can see the structure rather than wondering where
    // a board came from — read from adjacency, never a stored flag.
    const roles = structuralShelfRoles(draft.elements);
    draft.elements.forEach((el, i) => {
      list.append(interiorElementRow(el, i, draft.elements.length, solvedEls[i], roles[i]));
    });
    list.scrollTop = scrollTop;

    renderInteriorHealed(root.getElementById('interior-healed'), draft);
    renderInteriorSpaces(root.getElementById('interior-spaces'), preview);
    renderInteriorAdd(addBox);
    renderInteriorHoles(root.getElementById('interior-holes'), feed);
    renderInteriorOpenings(root.getElementById('interior-openings'), preview);
    renderInteriorDoors(doorsBox, depth, feed, preview?.editedUnit);

    // Warnings: the solver's flags, drawn inline the way the elevation badges
    // them (`.elev-int--warn` on the preview shows WHERE; this says WHAT).
    const warnings = [];
    if (feed && feed.solved.overflowUnits > 0.01) {
      warnings.push(`Doesn't fit — over by ${fmt(feed.solved.overflowUnits)}. Overrunning elements draw clamped.`);
    }
    const ignoredPins = solvedEls.filter((s) => s?.pinIgnored).length;
    if (ignoredPins > 0) {
      warnings.push(`${ignoredPins === 1 ? 'A pinned shelf' : `${ignoredPins} pinned shelves`} can't hold that position — solving in flow instead.`);
    }
    if (depth.blocked) {
      const isDeep = preview?.editedUnit?.depthUnits > 0;
      const has = isDeep ? fmt(preview.editedUnit.depthUnits) : 'unplaced';
      // The message states the APPLICABLE minimum and why it applies, so the
      // designer can see that pulling the rod would change the answer.
      warnings.push(depth.hanging
        ? `Doors need ${fmt(depth.minUnits)} of depth for a hanging section — this unit is ${has}. Doors won't apply; interior edits still do.`
        : `Doors need at least ${fmt(depth.minUnits)} of depth — this unit is ${has}. Doors won't apply; interior edits still do.`);
    }
    // The SSH drawer tool's 48" stack warning, on the same math (ADR 0060),
    // measured where the owner measures it: at the TOP OF THE TOP DRAWER FACE,
    // off the floor (ADR 0062). The warning names what it measured, because
    // that figure is not the bank's own height.
    for (const solved of solvedEls) {
      if (solved?.kind === 'drawerBank' && solved.stackOverMax) {
        warnings.push(`Drawer stack reaches ${fmt(solved.faceTopUnits)} at the top drawer face — over the ${fmt(rules.drawerStackMaxHeightUnits ?? 48)} maximum (measured from the floor, including base trim).`);
      }
    }
    // A door height the band could not hold (ADR 0060 item 3): clamped, said.
    const doorSpan = doorBand(feed?.doors, feed?.band, feed);
    if (doorSpan?.clamped) {
      warnings.push(draft.doors?.coverage === 'split'
        ? `The upper/lower doors can't meet at that height — clamped to ${fmt(doorSpan.splitAtUnits ?? 0)} inside the doored extent.`
        : `Door height doesn't fit the opening — drawn at the full ${fmt(feed.band.topUnits - feed.band.bottomUnits)} instead.`);
    }
    warnBox.textContent = warnings.join('\n');

    renderInteriorPreview(preview);
  }

  /**
   * The HEAL-ON-OPEN note (ADR 0065 part 1): what structural boards this
   * section was missing and gained when it was opened.
   *
   * Stored compositions are never rewritten on load, so a section drawn before
   * an invariant shipped still holds the old geometry — which the owner read as
   * the fix having failed. It heals into the DRAFT when the editor opens, so it
   * is visible here and in the preview, Cancel discards it, and Apply persists
   * it. Silence when the composition already conformed: a note that appears
   * every time says nothing.
   */
  function renderInteriorHealed(box, draft) {
    if (!box) return;
    const notes = draftHealingNotes(draft);
    box.hidden = notes.length === 0;
    box.textContent = notes.length
      ? `${notes.join('\n')}\nThese are staged like any other edit — Cancel leaves the stored section as it was.`
      : '';
  }

  /**
   * SPACES — the editor's primary list (ADR 0065 part 2).
   *
   * The owner reads an interior as a stack of vertical spaces and edits it with
   * two verbs: SPLIT this space in two, and FILL this space with a treatment
   * whose COUNT divides the space's own height. Each row is one space, showing
   * the height the solver measured for it, so the count and the height a
   * designer is dividing are on the same line.
   */
  /**
   * Lights up the preview region ONE space row is talking about (ADR 0067).
   *
   * The rectangles are already in the drawing — `spaceRegion` marks the editor
   * preview alone asks for, each tagged with its element index — so this is a
   * class toggle, not a re-render: hovering a row cannot cost a solve.
   */
  function setSpaceHighlight(elementIndex, on) {
    const svg = root.getElementById('interior-preview-svg');
    if (!svg) return;
    for (const node of svg.querySelectorAll('.elev-int-space')) {
      const mine = node.getAttribute('data-space-index') === String(elementIndex);
      node.classList.toggle('elev-int-space--on', on === true && mine);
    }
  }

  function renderInteriorSpaces(box, preview) {
    if (!box) return;
    box.replaceChildren();
    const unit = preview?.editedUnit;
    const spaces = unit ? draftSpaces(interiorEditor.draft, unit, rules) : [];
    if (!spaces.length) {
      const note = document.createElement('p');
      note.className = 'interior-doors__note';
      note.textContent = unit
        ? 'This section has no spaces yet — add one from the advanced element list below.'
        : 'This section is not placed on a wall, so its spaces cannot be measured.';
      box.append(note);
      return;
    }
    for (const space of spaces) box.append(interiorSpaceRow(space, unit));
  }

  /** The count a freshly chosen treatment starts at — data-driven where the
   *  rules have an opinion (shoe shelves), otherwise the same seeds draftAdd
   *  uses, so the two entry points agree. */
  function defaultTreatmentCount(key) {
    if (key === 'shoes') {
      const preset = (rules.sectionTypePresets ?? []).find((p) => p.flags?.includes('shoes'));
      return Number.isInteger(preset?.defaultShelves) && preset.defaultShelves > 0 ? preset.defaultShelves : 7;
    }
    return key === 'drawers' ? 3 : 5;
  }

  /** One SPACE row: its measured height, the split control, the treatment fill,
   *  and — for a drawer bank — the per-drawer heights, because "drawer count
   *  and height" is one of the owner's most common edits. */
  function interiorSpaceRow(space, unit) {
    const draft = interiorEditor.draft;
    const solved = draftFeedForUnit(draft, unit, rules)?.solved?.elements?.[space.elementIndex];
    const row = document.createElement('div');
    row.className = `interior-space${solved?.overflow ? ' interior-space--warn' : ''}`;

    // The row states WHICH REGION it is (ADR 0067) so it can be matched to the
    // drawing by eye, and highlights that region on focus/hover.
    row.dataset.spaceIndex = String(space.elementIndex);
    const highlight = (on) => setSpaceHighlight(space.elementIndex, on);
    row.addEventListener('pointerenter', () => highlight(true));
    row.addEventListener('pointerleave', () => highlight(false));
    row.addEventListener('focusin', () => highlight(true));
    row.addEventListener('focusout', () => highlight(false));

    const head = document.createElement('div');
    head.className = 'interior-space__head';
    const label = document.createElement('span');
    label.className = 'interior-space__label';
    label.textContent = spaceLabel(space);
    const height = document.createElement('span');
    height.className = 'interior-space__height';
    // WHERE this space is, then how tall it is — both off the FLOOR, both read
    // straight off the solved element the preview draws (ADR 0067). A bare
    // height cannot be matched to a region; two spaces of equal height read as
    // the same row twice, which is exactly what the owner hit.
    height.textContent = spaceRangeLabel(space, fmtSystem);
    height.title = 'Off the floor — the same solve the drawing beside this list is showing';
    head.append(label, height);
    row.append(head);

    // ---- SPLIT -------------------------------------------------------------
    const splitRow = document.createElement('div');
    splitRow.className = 'interior-space__row';
    const splitTag = document.createElement('span');
    splitTag.className = 'interior-space__tag';
    // THE DATUM IS STATED IN THE LABEL (ADR 0067). "Split at 30"" is two
    // different boards depending on whether 30" is measured off the floor or
    // off this space's own bottom edge. A designer reads a drawing dimensioned
    // off the FLOOR, so the field is an absolute ELEVATION — which is what
    // draftSplitSpace already takes — and the label now says so.
    splitTag.textContent = 'Split at (off floor)';
    const splitAt = document.createElement('input');
    splitAt.inputMode = 'text';
    splitAt.dataset.pad = 'length'; // lengths go through the numeric pad (ADR 0047)
    splitAt.className = 'interior-el__height';
    // ⚠ THE MIDPOINT IS A PLACEHOLDER, NOT A VALUE (owner, 2026-08-11).
    // Pre-filled as a real value it reads as a STATEMENT ABOUT THE SPACE, and
    // the owner read it as exactly that: "these are 95" sections that say split
    // at 82" but the summary says it starts at 70"". It was never a fact about
    // the space — 82 1/4" is the middle of 70"–94 1/2", i.e. where a board
    // WOULD land. Grey placeholder text reads as a suggestion, which is what it
    // is, and matches how every other defaulted field in this app behaves
    // ("type default (95 1/4")"). draftSplitSpace already takes null → midpoint,
    // so an untouched field still splits down the middle.
    const midUnits = (space.fromUnits + space.toUnits) / 2;
    splitAt.placeholder = fmtSystem(midUnits);
    splitAt.setAttribute('aria-label', 'Elevation off the floor to split this space at');
    splitAt.title = `Elevation OFF THE FLOOR of the new fixed board’s top — not a height within the space. `
      + `This space runs ${spaceRangeLabel(space, fmtSystem)}; leave it blank to split down the middle.`;
    const splitBtn = document.createElement('button');
    splitBtn.type = 'button';
    splitBtn.className = 'interior-el__toggle';
    splitBtn.textContent = 'Split';
    splitBtn.title = 'Divide this space in two with a fixed board';
    // The legal range, ON SCREEN. A tablet never shows a `title`, so the
    // explanation above reached nobody — the same reason the greyed Place
    // button had to start stating its own reason.
    const splitHint = document.createElement('p');
    splitHint.className = 'interior-space__hint';
    const legal = () => `${fmtSystem(space.fromUnits)}–${fmtSystem(space.toUnits)}`;
    const restHint = () => `Adds a fixed board, measured off the FLOOR — anywhere in ${legal()}. `
      + `Blank splits down the middle (${fmtSystem(midUnits)}).`;
    splitHint.textContent = restHint();
    splitAt.addEventListener('input', () => {
      splitHint.textContent = restHint();
      splitHint.classList.remove('interior-space__hint--warn');
    });
    splitBtn.addEventListener('click', () => {
      const units = parseLength(splitAt.value);
      const at = Number.isFinite(units) ? units : null;
      if (draftSplitSpace(draft, space.elementIndex, at, unit, rules)) {
        renderInteriorEditor();
        return;
      }
      // REFUSED — SAY WHY, ON SCREEN. Silently doing nothing is the defect
      // class the owner has already reported twice on greyed controls; a Split
      // button that just sits there is the same thing wearing a different hat.
      splitHint.textContent = at === null
        ? `That is not a height. Type one in ${legal()}, or clear the box to split down the middle.`
        : (at <= space.fromUnits || at >= space.toUnits
          ? `${fmtSystem(at)} is outside this space (${legal()}), so there is nothing to divide there. `
            + 'Each space splits only within its own range.'
          : `${fmtSystem(at)} leaves no room for the board on one side. Move it further inside ${legal()}.`);
      splitHint.classList.add('interior-space__hint--warn');
    });
    splitRow.append(splitTag, splitAt, splitBtn);

    // ---- SPLIT INTO N EQUAL OPENINGS ---------------------------------------
    // Owner, 2026-08-11: "the designer should have a space and indicating the
    // number of spaces the opening will be evenly split into (e.g, 2, 3, 4,
    // etc.) Otherwise there is no easy way to take a vertical space and
    // automate the adding of shelves". "Fill with shelves" does NOT cover this:
    // a shelfGroup is one element and stays one row, so the openings it makes
    // can never be split again or filled. This inserts real boards.
    const evenRow = document.createElement('div');
    evenRow.className = 'interior-space__row';
    const evenTag = document.createElement('span');
    evenTag.className = 'interior-space__tag';
    evenTag.textContent = 'Split into';
    const evenInput = document.createElement('input');
    evenInput.inputMode = 'numeric';
    evenInput.className = 'interior-el__count';
    evenInput.value = '2';
    evenInput.setAttribute('aria-label', 'How many equal openings to split this space into');
    // Small whole numbers a designer REPLACES rather than edits by character
    // (the ADR 0066 caret defect: tapping "4" and typing 5 gave fifty-four).
    evenInput.addEventListener('focus', () => evenInput.select());
    const evenSuffix = document.createElement('span');
    evenSuffix.className = 'interior-space__tag';
    evenSuffix.textContent = 'equal openings';
    const evenBtn = document.createElement('button');
    evenBtn.type = 'button';
    evenBtn.className = 'interior-el__toggle';
    evenBtn.textContent = 'Split evenly';
    evenBtn.title = 'Divide this space into equal openings with fixed boards between them';
    // What you will GET, before you commit to it.
    const evenPreview = () => {
      const n = Number.parseInt(evenInput.value, 10);
      const each = evenSplitOpeningUnits(space, n, rules);
      splitHint.classList.remove('interior-space__hint--warn');
      splitHint.textContent = each === null
        ? restHint()
        : `${n} openings of ${fmtSystem(each)} each, divided by ${n - 1} fixed board${n > 2 ? 's' : ''}.`;
    };
    evenInput.addEventListener('input', evenPreview);
    evenBtn.addEventListener('click', () => {
      const n = Number.parseInt(evenInput.value, 10);
      if (draftSplitSpaceEvenly(draft, space.elementIndex, n, unit, rules)) {
        renderInteriorEditor();
        return;
      }
      splitHint.textContent = !Number.isInteger(n) || n < 2
        ? 'Type how many openings you want — 2 or more.'
        : `${legal()} is not deep enough for ${n} openings once ${n - 1} boards are in it.`;
      splitHint.classList.add('interior-space__hint--warn');
    });
    evenRow.append(evenTag, evenInput, evenSuffix, evenBtn);

    row.append(splitRow, evenRow, splitHint);

    // ---- MERGE (RS-8) ------------------------------------------------------
    // The inverse of Split, which the row offered no way back from: "need to
    // be able to remove spaces once they are created or have to build from
    // scratch each time" (owner, 2026-08-22). Deleting a space through the
    // advanced element list could already undo a split, but in two steps, in an
    // order nothing stated, inside a panel called "Advanced".
    //
    // THE SPACE YOU ARE ON SURVIVES; the neighbour is absorbed. The button
    // names what it is about to discard rather than trying to guess which side
    // the designer meant to keep — a rule they cannot predict is worse than one
    // they can read.
    const targets = mergeSpaceTargets(draft, space.elementIndex, unit, rules);
    if (targets.above || targets.below) {
      const mergeRow = document.createElement('div');
      mergeRow.className = 'interior-space__row';
      const mergeTag = document.createElement('span');
      mergeTag.className = 'interior-space__tag';
      mergeTag.textContent = 'Merge with';
      mergeRow.append(mergeTag);
      for (const [dir, target] of [['above', targets.above], ['below', targets.below]]) {
        // Label, pressability and wording all come from mergeControlFor, which
        // lives beside the rule that decides them — so the refusal's
        // presentation is testable rather than assumed (see that function).
        const spec = mergeControlFor(target, dir);
        if (!spec) continue;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn--compact';
        btn.textContent = spec.label;
        btn.title = spec.title;
        btn.disabled = spec.disabled;
        if (!spec.disabled) {
          btn.addEventListener('click', () => {
            if (draftMergeSpace(draft, space.elementIndex, dir, unit, rules)) renderInteriorEditor();
          });
        }
        mergeRow.append(btn);
      }
      row.append(mergeRow);
    }

    // ---- THE BOUNDING BOARDS ------------------------------------------------
    // Owner ruling, 2026-08-30: per-board controls belong on the SPACE ROWS,
    // where designers work — not in the "Advanced — element list" drawer, which
    // is where the owner could not find the fixed/adjustable toggle. The lock
    // moved here first; the toggle that started the whole thread follows.
    //
    // ONE LINE PER BOARD, not per property. Measured rather than guessed: the
    // row has 278px of usable width, and four controls on one line need 349px —
    // they would wrap into a strip where nothing says which two belong to
    // "above". Per-board lines need 229px, let the side be named ONCE instead
    // of on every button, group the way a designer thinks ("the board above
    // this space is locked and adjustable"), and leave somewhere for a future
    // per-board control to go.
    //
    // A board between two spaces appears on BOTH their rows, deliberately —
    // "the shelf under this space" and "the shelf above that space" are the
    // same board and both readings are right. Two VIEWS of one control: every
    // value is read from the element on each render and either row toggles that
    // same element, so nothing copies state and nothing can drift.
    const boards = spaceBoundaryShelves(draft, space.elementIndex);
    for (const [side, board] of [['above', boards.above], ['below', boards.below]]) {
      if (!board) continue; // that side abuts a bank, a rod, or the carcass
      const boardRow = document.createElement('div');
      boardRow.className = 'interior-space__row';
      const tag = document.createElement('span');
      tag.className = 'interior-space__tag';
      tag.textContent = `Board ${side}`;
      boardRow.append(tag);

      const lock = document.createElement('button');
      lock.type = 'button';
      lock.className = `btn btn--compact${board.locked ? ' is-active' : ''}`;
      lock.textContent = board.locked ? '🔒' : '🔓';
      lock.title = board.locked
        ? `The board ${side} this space is LOCKED — it cannot be dragged. Tap to unlock. Its height can still be typed.`
        : `Lock the board ${side} this space so it cannot be dragged by accident.`;
      lock.setAttribute('aria-pressed', String(board.locked));
      lock.setAttribute('aria-label', `${board.locked ? 'Unlock' : 'Lock'} the board ${side} this space`);
      lock.addEventListener('click', () => {
        if (draftSetShelfLocked(draft, board.index, !board.locked)) renderInteriorEditor();
      });

      // FIXED vs ADJUSTABLE — a different axis from the lock, and the labels
      // have to carry that or the two read as one setting: this says how the
      // board is ATTACHED (bolted through the panel, or resting on pins), while
      // the padlock beside it says whether it may be moved at all.
      const fixed = document.createElement('button');
      fixed.type = 'button';
      fixed.className = `btn btn--compact${board.fixed ? ' is-active' : ''}`;
      fixed.textContent = board.fixed ? 'Fixed' : 'Adjustable';
      fixed.title = board.fixed
        ? `Fixed — bolted through the panel, and drawn solid. Tap to make the board ${side} this space adjustable.`
        : `Adjustable — resting on pins, and drawn dashed. Tap to make the board ${side} this space fixed.`;
      fixed.setAttribute('aria-pressed', String(board.fixed));
      fixed.setAttribute('aria-label', `Make the board ${side} this space ${board.fixed ? 'adjustable' : 'fixed'}`);
      fixed.addEventListener('click', () => {
        if (draftSetShelfFixed(draft, board.index, !board.fixed)) renderInteriorEditor();
      });

      boardRow.append(lock, fixed);
      row.append(boardRow);
    }

    // ---- FILL --------------------------------------------------------------
    const fillRow = document.createElement('div');
    fillRow.className = 'interior-space__row';
    const fillTag = document.createElement('span');
    fillTag.className = 'interior-space__tag';
    fillTag.textContent = 'Fill with';
    const pick = document.createElement('select');
    pick.className = 'interior-add__select';
    pick.setAttribute('aria-label', 'What goes in this space');
    for (const t of SPACE_TREATMENTS) {
      const opt = document.createElement('option');
      opt.value = t.key;
      opt.textContent = t.label;
      if (t.key === space.treatment?.key) opt.selected = true;
      pick.append(opt);
    }
    const countWrap = document.createElement('label');
    countWrap.className = 'interior-el__field';
    const countTag = document.createElement('span');
    const countInput = document.createElement('input');
    countInput.inputMode = 'numeric';
    countInput.setAttribute('aria-label', 'How many, within this space');
    countWrap.append(countTag, countInput);

    const spec = () => SPACE_TREATMENTS.find((t) => t.key === pick.value);
    const syncCount = () => {
      const s = spec();
      // `hidden` loses to the field's own `display: inline-flex`, so the row is
      // hidden the way CSS will actually honour.
      countWrap.style.display = s?.counted ? '' : 'none';
      countTag.textContent = s?.countLabel ?? '';
      const current = space.treatment?.key === s?.key ? space.treatment.count : defaultTreatmentCount(s?.key);
      countInput.value = String(current > 0 ? current : defaultTreatmentCount(s?.key));
    };
    syncCount();
    const fill = () => {
      const s = spec();
      if (!s) return;
      const count = Number.parseInt(countInput.value, 10);
      const ok = draftFillSpace(draft, space.elementIndex, {
        key: s.key,
        count: s.counted ? count : 0,
        style: space.treatment?.style,
        fixed: space.treatment?.fixed,
      }, unit, rules);
      if (ok) renderInteriorEditor();
    };
    // Choosing a treatment applies it at once — the owner starts from a template
    // and modifies it, so an extra "confirm" tap on every change is friction on
    // the commonest gesture. Nothing is committed either way until Apply.
    pick.addEventListener('change', () => { syncCount(); fill(); });
    countInput.addEventListener('change', fill);
    fillRow.append(fillTag, pick, countWrap);
    row.append(fillRow);

    // ---- the bank's own face heights (owner: count AND height) -------------
    if (space.kind === 'drawerBank') {
      const bank = draft.elements[space.elementIndex];
      const facesRow = document.createElement('div');
      facesRow.className = 'interior-space__row';
      const n = bank.drawers.length;
      // DRW 1 IS THE BOTTOM DRAWER (ADR 0060 item 6) — the same numbering the
      // element list uses, because it is the same bank.
      bank.drawers.forEach((d, k) => {
        facesRow.append(editorDrawerHeightField(`Drw ${n - k}`, d.heightUnits, (units) => (
          draftSetDrawerHeight(draft, space.elementIndex, k, units)
        )));
      });
      row.append(facesRow);
    }
    return row;
  }

  /** One element row: reorder (▲▼, the section rows' precedent), label,
   *  per-kind controls, remove. Every edit mutates the DRAFT and re-renders. */
  function interiorElementRow(elData, index, count, solved, structuralRole = null) {
    const row = document.createElement('div');
    row.className = `interior-el${solved && (solved.overflow || solved.pinIgnored) ? ' interior-el--warn' : ''}`;
    const head = document.createElement('div');
    head.className = 'interior-el__head';

    const mover = (dir, glyph, tip) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'interior-el__move';
      btn.textContent = glyph;
      btn.title = tip;
      btn.disabled = dir < 0 ? index === 0 : index === count - 1;
      btn.addEventListener('click', () => {
        if (draftMove(interiorEditor.draft, index, dir)) renderInteriorEditor();
      });
      return btn;
    };
    const label = document.createElement('span');
    label.className = 'interior-el__label';
    label.textContent = `${elementLabel(elData)}${STRUCTURAL_ROLE_LABELS[structuralRole] ?? ''}`;
    // System-32 figures read in ¼" imperial (ADR 0061) — the exact value is
    // what the solver placed, this is only what the designer is shown.
    if (solved) label.title = `Solves ${fmtSystem(solved.fromUnits)} → ${fmtSystem(solved.toUnits)} off the floor`;

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'interior-el__delete';
    del.textContent = '×';
    // A structural shelf can be deleted like anything else — and comes straight
    // back, because draftRemove re-establishes the invariant (ADR 0062 item 6).
    // Say so on the control rather than letting the board silently reappear.
    del.title = structuralRole
      ? 'This shelf is structural — removing it re-inserts it'
      : 'Remove this element';
    del.addEventListener('click', () => {
      if (draftRemove(interiorEditor.draft, index)) renderInteriorEditor();
    });

    head.append(mover(-1, '▲', 'Move up (toward the top of the unit)'), mover(1, '▼', 'Move down (toward the floor)'), label, del);
    row.append(head);

    const controls = document.createElement('div');
    controls.className = 'interior-el__controls';

    if (elData.kind === 'shelf') {
      const fixed = document.createElement('button');
      fixed.type = 'button';
      fixed.className = `interior-el__toggle${elData.fixed ? ' is-active' : ''}`;
      fixed.textContent = elData.fixed ? 'Fixed' : 'Adjustable';
      fixed.title = 'Fixed shelves draw solid; adjustable draw dashed';
      fixed.addEventListener('click', () => {
        if (draftSetShelfFixed(interiorEditor.draft, index, !elData.fixed)) renderInteriorEditor();
      });
      // LOCK (owner, 2026-08-30). Same control as the obstruction lock — the
      // padlock IS the state — because it means the same thing: the board
      // cannot be dragged, while its Pin at field below still takes a typed
      // height. Kept beside Fixed/Adjustable but distinct from it: that button
      // says how the board is attached, this one says whether it may move.
      const lock = document.createElement('button');
      lock.type = 'button';
      lock.className = `interior-el__toggle${elData.locked ? ' is-active' : ''}`;
      lock.textContent = elData.locked ? '🔒 Locked' : '🔓 Unlocked';
      lock.title = elData.locked
        ? 'Locked — cannot be dragged. Tap to unlock. The pin below still accepts a typed height.'
        : 'Unlocked — tap to lock this shelf so it cannot be dragged by accident.';
      lock.setAttribute('aria-pressed', String(elData.locked === true));
      lock.addEventListener('click', () => {
        if (draftSetShelfLocked(interiorEditor.draft, index, !elData.locked)) renderInteriorEditor();
      });
      controls.append(fixed, doubleThickToggle(elData, index), lock);
      // A pin is a System-32 figure: shown in 1/4" imperial, and re-typing what
      // is shown re-snaps to the SAME hole (ADR 0061 — a proven round trip).
      const pin = editorLengthField('Pin at', elData.atUnits, 'Pin the shelf TOP at this height off the floor (blank = solved in flow)', (units, raw) => {
        if (raw.trim() === '') return draftSetShelfPin(interiorEditor.draft, index, null);
        if (units === null) return false;
        return draftSetShelfPin(interiorEditor.draft, index, units);
      }, fmtSystem);
      controls.append(pin);
    } else if (elData.kind === 'shelfGroup') {
      // COUNT-BASED shelving (ADR 0060 item 9): the designer states a number
      // and the solver spaces them — no repeated "add shelf" taps.
      const fixed = document.createElement('button');
      fixed.type = 'button';
      fixed.className = `interior-el__toggle${elData.fixed ? ' is-active' : ''}`;
      fixed.textContent = elData.fixed ? 'Fixed' : 'Adjustable';
      fixed.title = 'Fixed shelves draw solid; adjustable draw dashed';
      fixed.addEventListener('click', () => {
        if (draftSetShelfGroupFixed(interiorEditor.draft, index, !elData.fixed)) renderInteriorEditor();
      });
      controls.append(fixed, doubleThickToggle(elData, index));
      const cnt = document.createElement('label');
      cnt.className = 'interior-el__field';
      cnt.title = 'How many shelves to space evenly through this element';
      const tag = document.createElement('span');
      tag.textContent = 'Shelves';
      const input = document.createElement('input');
      input.inputMode = 'numeric';
      input.dataset.pad = 'count';
      input.value = String(elData.count);
      input.addEventListener('change', () => {
        const v = Number.parseInt(input.value, 10);
        if (Number.isInteger(v) && draftSetShelfGroupCount(interiorEditor.draft, index, v)) renderInteriorEditor();
        else input.value = String(elData.count);
      });
      cnt.append(tag, input);
      controls.append(cnt);
    } else if (elData.kind === 'drawerBank') {
      // DRW 1 IS THE BOTTOM DRAWER (owner, 2026-08-09). The list reads top →
      // bottom, so row k gets number (n − k): the last row is Drw 1, at the
      // floor. Heights are picked from the shop's standard faces, never typed.
      const n = elData.drawers.length;
      elData.drawers.forEach((d, k) => {
        const wrap = document.createElement('span');
        wrap.className = 'interior-el__drawer';
        wrap.append(editorDrawerHeightField(`Drw ${n - k}`, d.heightUnits, (units) => (
          draftSetDrawerHeight(interiorEditor.draft, index, k, units)
        )));
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'interior-el__delete';
        rm.textContent = '×';
        rm.title = 'Remove this drawer';
        rm.addEventListener('click', () => {
          if (draftRemoveDrawer(interiorEditor.draft, index, k)) renderInteriorEditor();
        });
        wrap.append(rm);
        controls.append(wrap);
      });
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'interior-el__toggle';
      add.textContent = '+ drawer';
      add.title = 'Add a drawer to this bank (repeats the last face height)';
      add.addEventListener('click', () => {
        if (draftAddDrawer(interiorEditor.draft, index, rules)) renderInteriorEditor();
      });
      controls.append(add);
    } else if (elData.kind === 'shoeShelves') {
      for (const style of ['FSS', 'SSS']) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `interior-el__toggle${elData.style === style ? ' is-active' : ''}`;
        btn.textContent = style;
        btn.title = style === 'FSS' ? 'Flat shoe shelves' : 'Slanted shoe shelves';
        btn.addEventListener('click', () => {
          if (draftSetShoeStyle(interiorEditor.draft, index, style)) renderInteriorEditor();
        });
        controls.append(btn);
      }
      const cnt = document.createElement('label');
      cnt.className = 'interior-el__field';
      cnt.title = 'Number of shoe shelves';
      const tag = document.createElement('span');
      tag.textContent = 'Count';
      const input = document.createElement('input');
      input.inputMode = 'numeric';
      input.value = String(elData.count);
      input.addEventListener('change', () => {
        const v = Number.parseInt(input.value, 10);
        if (Number.isInteger(v) && draftSetShoeCount(interiorEditor.draft, index, v)) renderInteriorEditor();
        else input.value = String(elData.count);
      });
      cnt.append(tag, input);
      controls.append(cnt);
    }
    // rod / openSpace need no controls: a rod's span IS the leftover hang zone.

    if (controls.children.length) row.append(controls);
    return row;
  }

  /**
   * The DOUBLE-THICK tag (ADR 0061 item C, owner 2026-08-09): "it would be
   * nicer if users could just select a shelf and tag it as double thick and it
   * becomes a single shelf that is 1 1/2 inches thick". One button, on a shelf
   * or on a whole group — never a second element beside the first.
   */
  function doubleThickToggle(elData, index) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `interior-el__toggle${elData.doubleThick ? ' is-active' : ''}`;
    const thick = (rules.sectionShelfThicknessUnits > 0 ? rules.sectionShelfThicknessUnits : 0.75)
      * (rules.doubleThickShelfMultiplier > 0 ? rules.doubleThickShelfMultiplier : 2);
    btn.textContent = elData.doubleThick ? `Double (${fmtSystem(thick)})` : 'Double thick';
    btn.title = `One shelf ${fmtSystem(thick)} thick, not two boards side by side`;
    btn.addEventListener('click', () => {
      if (draftSetShelfDoubleThick(interiorEditor.draft, index, !elData.doubleThick)) renderInteriorEditor();
    });
    return btn;
  }

  /**
   * The 32 mm drilling mode (ADR 0061 item B). "Required holes only" means the
   * shop drills only the shelf and drawer-bank positions the composition needs,
   * but NOT the full 32 mm rows — so shelves stop snapping and take exactly the
   * heights typed. The
   * line under it names the holes that are still bored, read from the SOLVED
   * result, so the label cannot drift from what the model believes.
   */
  function renderInteriorHoles(box, feed) {
    if (!box) return;
    box.replaceChildren();
    const tag = document.createElement('span');
    tag.className = 'interior-doors__tag';
    tag.textContent = 'Holes';
    const on = interiorEditor.draft.requiredHolesOnly === true;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `interior-el__toggle${on ? ' is-active' : ''}`;
    btn.textContent = on ? 'Required holes only' : 'Full 32mm rows';
    btn.title = on
      ? 'Only the required holes are drilled — shelves take the exact heights you type'
      : 'The full 32mm system rows are drilled — shelves snap to the nearest hole';
    btn.addEventListener('click', () => {
      if (draftSetRequiredHolesOnly(interiorEditor.draft, !on)) renderInteriorEditor();
    });
    const note = document.createElement('span');
    note.className = 'interior-doors__note';
    const holes = feed?.solved?.holes;
    if (holes?.mode === 'required') {
      const reasons = [...new Set((holes.required ?? []).map((r) => r.reason))]
        .map((r) => ({
          topShelf: 'top shelf',
          bottomShelf: 'bottom shelf',
          drawerBank: 'drawer banks',
          drawerShelf: 'fixed shelves at drawers',
          fixedShelf: 'fixed shelves',
          adjustableShelf: 'adjustable shelves',
        })[r] ?? r);
      note.textContent = reasons.length ? `Still drilled: ${reasons.join(', ')}.` : 'No required holes in this section.';
    } else if (holes) {
      // The pitch is a metric rule but a designer sees inches (ADR 0061).
      note.textContent = `${holes.elevations.length} holes at ${fmtSystem(holes.pitchMm / 25.4)}.`;
    }
    box.append(tag, btn, note);
  }

  /**
   * OPENING-BASED shelf insertion (ADR 0061 item D — how CV works): pick one of
   * the clear openings in the composition and drop N evenly spaced shelves into
   * it. The inserted shelves arrive PINNED at their solved heights, so each can
   * then be repositioned on its own.
   */
  function renderInteriorOpenings(box, preview) {
    if (!box) return;
    box.replaceChildren();
    const unit = preview?.editedUnit;
    const openings = unit ? draftOpenings(interiorEditor.draft, unit, rules) : [];
    const tag = document.createElement('span');
    tag.className = 'interior-doors__tag';
    tag.textContent = 'Opening';
    box.append(tag);
    if (!openings.length) {
      const note = document.createElement('span');
      note.className = 'interior-doors__note';
      note.textContent = 'No open space to split — add an Open space element first.';
      box.append(note);
      return;
    }
    const select = document.createElement('select');
    select.className = 'interior-add__select';
    select.id = 'interior-opening-pick';
    select.setAttribute('aria-label', 'Opening to add shelves into');
    openings.forEach((o, i) => {
      const opt = document.createElement('option');
      opt.value = String(o.elementIndex);
      opt.textContent = `${i + 1}· ${fmtSystem(o.spanUnits)} (${fmtSystem(o.fromUnits)}–${fmtSystem(o.toUnits)})`;
      select.append(opt);
    });
    const count = document.createElement('input');
    count.inputMode = 'numeric';
    count.dataset.pad = 'count';
    count.value = '3';
    count.id = 'interior-opening-count';
    count.className = 'interior-el__height';
    count.setAttribute('aria-label', 'How many shelves to add');
    const add = document.createElement('button');
    add.type = 'button';
    add.id = 'interior-opening-add';
    add.className = 'interior-el__toggle';
    add.textContent = '+ shelves';
    add.title = 'Add this many evenly spaced shelves into the selected opening';
    add.addEventListener('click', () => {
      const n = Number.parseInt(count.value, 10);
      const at = Number.parseInt(select.value, 10);
      if (!Number.isInteger(n) || !Number.isInteger(at)) return;
      if (draftInsertShelvesIntoOpening(interiorEditor.draft, at, n, unit, rules)) renderInteriorEditor();
    });
    box.append(select, count, add);
  }

  /** A labelled length input for the editor — numeric-pad wired (ADR 0047).
   *  `apply(units, raw)` gets the parsed units (null on junk) + the raw text. */
  function editorLengthField(label, valueUnits, tip, apply, format = fmt) {
    const wrap = document.createElement('label');
    wrap.className = 'interior-el__field';
    wrap.title = tip;
    const tag = document.createElement('span');
    tag.textContent = label;
    const input = document.createElement('input');
    input.inputMode = 'text';
    input.dataset.pad = 'length';
    input.value = valueUnits > 0 ? format(valueUnits) : '';
    input.addEventListener('change', () => {
      const units = parseLength(input.value);
      if (apply(Number.isFinite(units) && units > 0 ? units : null, input.value)) renderInteriorEditor();
      else input.value = valueUnits > 0 ? format(valueUnits) : '';
    });
    input.addEventListener('keydown', (evt) => {
      if (evt.key === 'Enter') input.blur();
    });
    wrap.append(tag, input);
    return wrap;
  }

  /**
   * A drawer's face height as a SELECT of the shop's standard heights (owner,
   * 2026-08-09 — the same list the SSH drawer calculator offers), never a free
   * text box. A bank carrying a non-standard CV height (7.42, 8.66, 8.68,
   * 12.44) gets that value as an extra option, labelled as-is, so opening the
   * editor cannot silently restandardise a real order.
   *
   * `rerender` is the caller's repaint (ADR 0066): the modal re-renders itself,
   * the section row's inline copy has already repainted the whole panel through
   * editSectionInline(). One builder, two hosts — the select, its options and
   * the "(as built)" labelling cannot drift between them.
   */
  function editorDrawerHeightField(label, valueUnits, apply, rerender = renderInteriorEditor) {
    const wrap = document.createElement('label');
    wrap.className = 'interior-el__field';
    wrap.title = 'Face height of this drawer — standard shop heights';
    const tag = document.createElement('span');
    tag.textContent = label;
    const select = document.createElement('select');
    select.className = 'interior-el__height';
    for (const opt of drawerHeightOptions(valueUnits, rules)) {
      const o = document.createElement('option');
      o.value = String(opt.units);
      o.textContent = opt.standard ? fmt(opt.units) : `${fmt(opt.units)} (as built)`;
      if (Math.abs(opt.units - valueUnits) < 1e-9) o.selected = true;
      select.append(o);
    }
    select.addEventListener('change', () => {
      const units = Number.parseFloat(select.value);
      if (Number.isFinite(units) && apply(units)) rerender();
    });
    wrap.append(tag, select);
    return wrap;
  }

  /** The add-element control: kinds, plus the drawer-bank presets as one-tap
   *  inserts (plan §5/4c — the three real CV banks from drawer_bank_presets). */
  function renderInteriorAdd(box) {
    box.replaceChildren();
    const select = document.createElement('select');
    select.className = 'interior-add__select';
    select.setAttribute('aria-label', 'Add an interior element');
    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = '+ add element…';
    select.append(blank);
    for (const [kind, name] of [
      ['rod', 'Rod (+ support shelf)'],
      ['shelf', 'Shelf (one)'],
      ['shelfGroup', 'Shelves (choose a count)'],
      // Both structural invariants are announced on the control that triggers
      // them, the way the rod's has been since ADR 0060 (ADR 0062).
      ['drawerBank', 'Drawer bank (1 drawer, + fixed shelves)'],
      ['shoeShelves', 'Shoe shelves'],
      ['openSpace', 'Open space'],
    ]) {
      const opt = document.createElement('option');
      opt.value = `kind:${kind}`;
      opt.textContent = name;
      select.append(opt);
    }
    for (const bank of rules.drawerBankPresets ?? []) {
      const opt = document.createElement('option');
      opt.value = `bank:${bank.key}`;
      opt.textContent = `Bank — ${bank.label} (+ fixed shelves)`;
      select.append(opt);
    }
    select.addEventListener('change', () => {
      const [what, key] = select.value.split(':');
      select.value = '';
      if (what === 'kind') {
        if (draftAdd(interiorEditor.draft, key, rules) >= 0) renderInteriorEditor();
      } else if (what === 'bank') {
        const bank = (rules.drawerBankPresets ?? []).find((b) => b.key === key);
        if (bank && draftAddBank(interiorEditor.draft, bank.heights) >= 0) renderInteriorEditor();
      }
    });
    box.append(select);
  }

  /** Doors cover a designer-selected run of persistent SPACE ids. */
  function renderInteriorDoors(box, depth, feed, unit) {
    box.replaceChildren();
    const tag = document.createElement('span');
    tag.className = 'interior-doors__tag';
    tag.textContent = 'Doors';
    box.append(tag);
    const draft = interiorEditor.draft;
    const spaces = unit ? draftSpaces(draft, unit, rules).filter((space) => space.elementId) : [];
    const current = draft.doors?.fromElementId && draft.doors?.toElementId ? draft.doors : null;

    if (!current) {
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'interior-el__toggle';
      add.textContent = draft.doors ? 'Replace legacy doors with covered spaces' : 'Add doors';
      add.disabled = depth.blocked || spaces.length === 0;
      add.title = depth.blocked ? doorDepthTitle(depth, fmt) : 'Cover all current spaces; adjust the endpoints below.';
      add.addEventListener('click', () => {
        if (spaces.length && draftSetDoorExtent(draft, spaces[0].elementId, spaces.at(-1).elementId)) renderInteriorEditor();
      });
      box.append(add);
    } else {
      const none = document.createElement('button');
      none.type = 'button';
      none.className = 'interior-el__toggle';
      none.textContent = 'Remove doors';
      none.addEventListener('click', () => {
        if (draftSetDoorExtent(draft, null, null)) renderInteriorEditor();
      });
      box.append(none);

      const endpointField = (label, field) => {
        const wrap = document.createElement('label');
        wrap.className = 'interior-doors__handle';
        const text = document.createElement('span');
        text.textContent = label;
        const select = document.createElement('select');
        for (const space of spaces) {
          const opt = document.createElement('option');
          opt.value = space.elementId;
          opt.textContent = `${space.treatment.label} · ${fmt(space.heightUnits)}`;
          opt.selected = current[field] === space.elementId;
          select.append(opt);
        }
        select.addEventListener('change', () => {
          const from = field === 'fromElementId' ? select.value : current.fromElementId;
          const to = field === 'toElementId' ? select.value : current.toElementId;
          if (draftSetDoorExtent(draft, from, to)) renderInteriorEditor();
        });
        wrap.append(text, select);
        return wrap;
      };
      box.append(endpointField('First covered space', 'fromElementId'));
      box.append(endpointField('Last covered space', 'toElementId'));

      const styleField = document.createElement('label');
      styleField.className = 'interior-doors__handle';
      const styleText = document.createElement('span');
      styleText.textContent = 'Door style';
      const styleSelect = document.createElement('select');
      const prompt = document.createElement('option');
      prompt.value = '';
      prompt.textContent = 'Select a priced style';
      styleSelect.append(prompt);
      const groups = new Map();
      for (const style of rules.doorStyles ?? []) {
        let group = groups.get(style.category);
        if (!group) {
          group = document.createElement('optgroup');
          group.label = style.category;
          groups.set(style.category, group);
          styleSelect.append(group);
        }
        const opt = document.createElement('option');
        opt.value = style.key;
        opt.textContent = `${style.style} · $${style.ratePerSqFt}/sq ft · max ${fmt(style.maxLeafWidthUnits)} × ${fmt(style.maxLeafHeightUnits)}`;
        opt.selected = current.styleKey === style.key;
        group.append(opt);
      }
      styleSelect.addEventListener('change', () => {
        if (draftSetDoorStyle(draft, styleSelect.value)) renderInteriorEditor();
      });
      styleField.append(styleText, styleSelect);
      box.append(styleField);

      const handle = document.createElement('label');
      handle.className = 'interior-doors__handle';
      const text = document.createElement('span');
      text.textContent = 'Door hardware';
      const select = document.createElement('select');
      select.setAttribute('aria-label', 'Door handle type');
      for (const type of rules.handleTypes ?? []) {
        const opt = document.createElement('option');
        opt.value = type.key;
        opt.textContent = type.label ?? type.key;
        opt.selected = type.key === (interiorEditor.draft.doors?.handleType ?? rules.defaultHandleType);
        select.append(opt);
      }
      select.addEventListener('change', () => {
        if (draftSetDoorHandleType(interiorEditor.draft, select.value)) renderInteriorEditor();
      });
      handle.append(text, select);
      box.append(handle);

      const style = doorStyleByKey(rules.doorStyles, current.styleKey);
      const geometry = deriveDoorGeometry(current, feed?.solved?.elements, unit?.widthUnits ?? 0, style, rules);
      const note = document.createElement('span');
      note.className = 'interior-doors__note';
      note.textContent = geometry.ok
        ? `${geometry.areaSqFt.toFixed(2)} sq ft · ${geometry.leaves.length} leaf column${geometry.leaves.length === 1 ? '' : 's'} · ${geometry.bands.length} height band${geometry.bands.length === 1 ? '' : 's'}; splits follow space boundaries.`
        : `Needs pricing: ${geometry.reason}.`;
      box.append(note);
    }
  }

  /** The live preview: the REAL renderer against the overlay's SVG, fed the
   *  staged draft (interior-editor.js builds the substituted opts). */
  function renderInteriorPreview(preview) {
    const svg = root.getElementById('interior-preview-svg');
    if (!svg) return;
    if (!preview) {
      // Unplaceable (no layout on the wall): show nothing rather than a guess.
      if (interiorPreview) interiorPreview.render([], { pxPerUnit: 3, ceilingHeightUnits: model.state.ceilingHeightUnits });
      return;
    }
    if (!interiorPreview) {
      // One instance for the app's lifetime — render() clears its viewport, so
      // reopening must NOT create a second instance (it would stack viewports
      // and re-wire pointer handlers on the same SVG).
      const thumb = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      interiorPreview = createElevation(svg, thumb, {
        // DRAG-TO-PLACE (owner rulings, 2026-08-29). The drag is an ACCELERATOR
        // over the "Pin at" field beside this preview, which does the same job
        // by typing — so the feature satisfies WCAG 2.2 SC 2.5.7 by reusing the
        // path that was already there rather than by adding a second one.
        //
        // The renderer has already clamped `atUnits` to this shelf's travel and
        // the solver snaps it onto a real system hole, so this handler stays
        // what it looks like: one setter call and a re-render. Writing on every
        // move is deliberate — the re-solve is what makes the board step hole
        // to hole under the finger, using the app's own snapper rather than a
        // preview ghost that could disagree with it.
        // A drag must move ONE board (ruling 3). Unpinned shelves solve in
        // flow, so pinning the dragged one would re-cut the segments and shift
        // its neighbours — caught by dragging in a real browser, where all five
        // boards moved. Materializing the solved positions as pins first
        // anchors them at exactly where they already are.
        onShelfDragStart: () => {
          if (!interiorEditor) return;
          const unit = editorPreview()?.editedUnit;
          if (!unit) return;
          if (draftPinShelvesAtSolved(interiorEditor.draft, unit, rules)) renderInteriorEditor();
        },
        onDragShelf: (elementIndex, atUnits) => {
          if (!interiorEditor) return;
          if (draftSetShelfPin(interiorEditor.draft, elementIndex, atUnits)) renderInteriorEditor();
        },
      });
    }
    interiorPreview.render(preview.walls, preview.opts);
  }

  /** Widths a designer typed that exceed the max-opening rule, awaiting the
   *  explicit "allow wide" confirmation (owner: >36" only by explicit
   *  override). sectionId -> the raw string they typed. */
  const pendingWideWidths = new Map();

  /** Whether the collapsed type palette is open (ADR 0066 item 5). Held here,
   *  not on the element, so it survives the repaint an "add" triggers. */
  let sectionAddOpen = false;

  /**
   * The designer's reusable-section library (D97 / RS-3, ADR 0100).
   *
   * Held behind `createReusableSectionLibrary` rather than as a raw array,
   * because the raw array lost data: a failed read left it `[]`, and the next
   * save wrote that `[]` plus one component back over the whole key. The
   * library refuses to save until it has actually loaded, and reports which
   * state it is in so the panel can tell a designer the truth.
   *
   * `load()` never throws — it records the failure — so there is deliberately
   * no `.catch()` here. One would only re-hide the state it just set.
   */
  // The instance is shared with the sync engine (main.js constructs and
  // passes it), so a component saved here is the same object the engine
  // pushes; the local fallback keeps tests and library-less boots working.
  const reusableSectionLibrary = sectionLibrary
    ?? createReusableSectionLibrary({ getMetaResult, setMeta, makeId: newId });
  reusableSectionLibrary.load().then((state) => {
    renderSectionsPanel();
    // A loaded library may hold components saved before sync existed; the
    // engine's init sweep backs them up on its next run.
    if (state?.state === 'ready') scheduleSync();
  });

  async function saveReusableSection(section) {
    const defaultName = `${section.typeCode || 'Section'} component`;
    const name = window.prompt?.('Name this reusable section', defaultName)?.trim();
    if (!name) return;
    const saved = await reusableSectionLibrary.add(section, name);
    if (!saved.ok) {
      // Said out loud. The refusal exists to protect a library we could not
      // read; a designer who is not told simply tries again and assumes it
      // worked.
      window.alert?.(saved.reason);
      renderSectionsPanel();
      return;
    }
    // Cloud backup (D97): mark it for the engine and nudge a sync, same as a
    // sketch save.
    if (sync?.markReusableSectionDirty) {
      await sync.markReusableSectionDirty(saved.component.id);
      scheduleSync();
    }
    renderSectionsPanel();
  }

  /** Section ids whose inline drawer FACE HEIGHTS disclosure is expanded — same
   *  reason: changing a face repaints the panel, and a control that collapsed
   *  itself after every change would be unusable for a bank of four. */
  const openFaceRows = new Set();

  /** Structural boards a row edit HEALED into a section (ADR 0065 part 1 —
   *  "the owner must be TOLD a board appeared"). The modal shows this note
   *  before Apply; a row edit commits immediately, so the note lands on the row
   *  afterwards and stays until the designer dismisses it. */
  const inlineHealNotes = new Map();

  /**
   * Runs ONE inline interior edit on a section (ADR 0066) — the row's
   * equivalent of the modal's open → edit → Apply, in one gesture.
   *
   * The three steps are the modal's own, in the modal's order:
   *   1. `createInteriorDraft` — which HEALS the stored composition into the
   *      draft (ADR 0065 part 1), so a legacy section gains its structural
   *      boards here exactly as it does when the modal opens it;
   *   2. `edit(draft, unit, targets)` — the caller's one change, made through
   *      an inline-interior.js function, i.e. through a modal draft helper;
   *   3. `applyInteriorDraft` — the modal's single commit, including the door
   *      depth gate (blocked doors are refused; the interior still lands).
   *
   * The row edit is IMMEDIATE by design: there is no staged Apply on a row,
   * because a staged edit needs somewhere to show "staged", which is what the
   * modal is for. The modal keeps its staging semantics untouched.
   *
   * @param {string} sectionId
   * @param {(draft:object, unit:object|null, targets:object)=>boolean} edit
   * @returns {boolean} whether anything was committed
   */
  function editSectionInline(sectionId, edit) {
    const section = model.state.sections.find((s) => s.id === sectionId);
    if (!section) return false;
    const draft = createInteriorDraft(model.sectionInterior(sectionId), section.doors, section.requiredHolesOnly === true);
    const unit = inlineUnitFor(sectionId);
    // Targets are resolved FRESH against this draft, never carried over from
    // the render — an element index read at paint time is a stale index.
    const targets = inlineInteriorTargets(draft, unit, rules);
    const outcome = edit(draft, unit, targets);
    // A REFUSAL WITH A REASON lands beside the control the designer just used,
    // through the same per-row note channel the healing notes use. A control
    // that snaps back silently teaches nothing; one that says which boards are
    // locked or hand-placed tells them what to do about it.
    if (outcome && outcome.ok === false && outcome.reason) {
      inlineHealNotes.set(sectionId, [outcome.reason]);
      repaintSections();
      return false;
    }
    if (outcome !== true) return false;
    const blocked = doorDepthState(unit?.depthUnits ?? 0, rules, { hanging: draftHasHang(draft) }).blocked;
    if (!applyInteriorDraft(model, sectionId, draft, { doorsBlocked: blocked }).ok) return false;
    const notes = draftHealingNotes(draft);
    if (notes.length) inlineHealNotes.set(sectionId, notes);
    repaintSections();
    return true;
  }

  /** The placed unit a section solves in — the same lookup previewElevation()
   *  does, available to the row without building a whole preview. */
  function inlineUnitFor(sectionId) {
    const section = model.state.sections.find((s) => s.id === sectionId);
    if (!section) return null;
    let run = section;
    const seen = new Set();
    while (run.stackOnId && !seen.has(run.id)) {
      seen.add(run.id);
      const below = model.state.sections.find((s) => s.id === run.stackOnId);
      if (!below) break;
      run = below;
    }
    const runSections = model.sectionsForWall(run.wallId);
    const i = runSections.findIndex((s) => s.id === run.id);
    if (i < 0) return null;
    return (model.stacksForWall(run.wallId)[i]?.units ?? []).find((u) => u.section?.id === sectionId) ?? null;
  }

  /** Repaint everything a section change touches: both drawings + this panel. */
  function repaintSections() {
    renderGeometry();
    if (currentView === 'elevation') renderElevation();
    renderSectionsPanel();
    scheduleSave();
  }

  /**
   * Wall height for the selected wall (ADR 0044) — the two ends and any
   * mid-wall breakpoints.
   *
   * The end fields write the CORNER, which the neighbouring wall shares, so
   * raising one end here raises the wall next door's matching end at the same
   * time. That is the behaviour the owner asked for ("wouldn't it have to auto
   * adjust?"), and the note under the fields says so, because a control that
   * quietly changes something off-screen has to explain itself.
   */
  // Guarded: the corner-height and stack-target inputs re-render this
  // container from their change handlers, which Chrome can fire
  // mid-replaceChildren — see non-reentrant-render.js.
  const renderWallHeights = nonReentrantRender(() => {
    const box = root.getElementById('wall-heights');
    const title = root.getElementById('wall-heights-title');
    if (!box) return;
    box.replaceChildren();
    const section = box.closest('.wall-heights');
    const isFlat = model.state.ceilingFlat !== false;
    if (section) section.hidden = isFlat;
    if (isFlat) return;

    const selected = model.selectedSegment();
    const isWall = selected && (selected.segmentKind ?? 'wall') === 'wall' && !model.isPartition(selected.id);
    if (title) title.textContent = isWall ? `Wall height · W${selected.wallNumber}` : 'Wall height';
    if (!isWall) {
      const hint = document.createElement('p');
      hint.className = 'wall-heights__hint';
      hint.textContent = 'Select a wall to set its ceiling height.';
      box.append(hint);
      return;
    }

    const endField = (which, label) => {
      const wrap = document.createElement('label');
      wrap.className = 'space-height__field';
      wrap.title = `Ceiling height at the ${label.toLowerCase()} end of this wall. Shared with the wall it meets there.`;
      const tag = document.createElement('span');
      tag.textContent = label;
      const input = document.createElement('input');
      input.inputMode = 'text';
      input.dataset.pad = 'length';
      input.placeholder = fmt(model.state.ceilingHeightUnits);
      const stated = which === 'end'
        ? model.state.cornerHeights[selected.id]
        : null;
      // Show the RESOLVED height, so a corner set from the neighbouring wall
      // reads here too — the value is shared, and the field must not imply
      // otherwise by looking empty.
      input.value = fmt(model.cornerHeightUnits(selected.id, which));
      void stated;
      makeCaptureTarget(input, {
        kind: 'wall_height_field', entityId: selected.id,
        property: which === 'start' ? 'startHeightUnits' : 'endHeightUnits',
        label: `W${selected.wallNumber} ${label.toLowerCase()} corner height`,
      });
      const commit = () => {
        if (model.setCornerHeight(selected.id, which, input.value)) {
          renderWallHeights();
          repaintView();
          if (currentView === 'elevation') renderElevation();
          scheduleSave();
        } else {
          input.value = fmt(model.cornerHeightUnits(selected.id, which));
        }
      };
      input.addEventListener('change', commit);
      input.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') { commit(); input.blur(); }
      });
      wrap.append(tag, input);
      return wrap;
    };

    const ends = document.createElement('div');
    ends.className = 'wall-heights__ends';
    ends.append(endField('start', 'Left'), endField('end', 'Right'));
    box.append(ends);

    const note = document.createElement('p');
    note.className = 'wall-heights__hint';
    note.textContent = 'Each end is shared with the wall it meets, so the neighbour follows. Blank = the room’s ceiling height.';
    box.append(note);

    // Per-wall stack target (owner, 2026-08-04). Only shown while designing to
    // a target, because without that mode there is nothing for it to override.
    // Blank = follows the space, which is why the placeholder states the figure
    // the wall inherits rather than leaving the field to look unset.
    if (model.state.toTargetHeight === true) {
      const target = document.createElement('label');
      target.className = 'space-height__field';
      target.title = 'Height stacks on THIS wall run to. Blank follows the space target — the usual case; set it only where one wall differs.';
      const tag = document.createElement('span');
      tag.textContent = 'Stack to';
      const input = document.createElement('input');
      input.inputMode = 'text';
      input.dataset.pad = 'length';
      input.placeholder = `space (${fmt(model.stackTargetHeightUnits())})`;
      const own = model.wallStackTargetUnits(selected.id);
      input.value = own > 0 ? fmt(own) : '';
      input.setAttribute('aria-label', `Stack target height for wall ${selected.wallNumber}`);
      makeCaptureTarget(input, {
        kind: 'wall_height_field', entityId: selected.id, property: 'stackTargetUnits',
        label: `W${selected.wallNumber} stack target height`,
      });
      const commit = () => {
        if (model.setWallStackTarget(selected.id, input.value)) {
          renderWallHeights();
          repaintSections();
        } else {
          const now = model.wallStackTargetUnits(selected.id);
          input.value = now > 0 ? fmt(now) : '';
        }
      };
      input.addEventListener('change', commit);
      input.addEventListener('keydown', (evt) => { if (evt.key === 'Enter') { commit(); input.blur(); } });
      target.append(tag, input);
      box.append(target);
    }

    // Mid-wall breakpoints: "level to here, then slope on".
    const add = document.createElement('div');
    add.className = 'wall-heights__add';
    const atInput = document.createElement('input');
    atInput.className = 'depth-lines__input';
    atInput.inputMode = 'text';
    atInput.dataset.pad = 'length';
    atInput.placeholder = 'at e.g. 36';
    atInput.setAttribute('aria-label', 'Breakpoint distance along the wall');
    makeCaptureTarget(atInput, {
      kind: 'wall_height_draft', entityId: selected.id, property: 'breakAtUnits',
      label: `W${selected.wallNumber} breakpoint distance`,
    });
    const hInput = document.createElement('input');
    hInput.className = 'depth-lines__input';
    hInput.inputMode = 'text';
    hInput.dataset.pad = 'length';
    hInput.placeholder = 'height';
    hInput.setAttribute('aria-label', 'Breakpoint height');
    makeCaptureTarget(hInput, {
      kind: 'wall_height_draft', entityId: selected.id, property: 'breakHeightUnits',
      label: `W${selected.wallNumber} breakpoint height`,
    });
    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'btn';
    addBtn.textContent = '+ Add';
    const commitBreak = () => {
      if (!model.addWallHeightBreak(selected.id, atInput.value, hInput.value)) {
        atInput.classList.add('wall-table__input--invalid');
        return;
      }
      renderWallHeights();
      repaintView();
      if (currentView === 'elevation') renderElevation();
      scheduleSave();
    };
    addBtn.addEventListener('click', commitBreak);
    for (const input of [atInput, hInput]) {
      input.addEventListener('keydown', (evt) => { if (evt.key === 'Enter') commitBreak(); });
    }
    add.append(atInput, hInput, addBtn);
    box.append(add);

    const list = document.createElement('div');
    list.className = 'depth-lines__list';
    for (const point of model.wallHeightBreaksFor(selected.id)) {
      const row = document.createElement('div');
      row.className = 'depth-lines__row';
      const label = document.createElement('span');
      label.textContent = `${fmt(point.atUnits)} → ${fmt(point.heightUnits)}`;
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'depth-lines__remove';
      del.textContent = '×';
      del.setAttribute('aria-label', `Remove the height point at ${fmt(point.atUnits)}`);
      del.addEventListener('click', () => {
        if (!model.removeWallHeightBreak(selected.id, point.atUnits)) return;
        renderWallHeights();
        repaintView();
        if (currentView === 'elevation') renderElevation();
        scheduleSave();
      });
      row.append(label, del);
      list.append(row);
    }
    box.append(list);
  });

  /**
   * The CLIENT's storage requirements (ADR 0043) — one row per storage type,
   * so the summary box can show the design against what the client asked for.
   *
   * Two figures per row: what they have TODAY and what they WANT. `needed` is
   * left blank in the common case, where it falls back to `existing` — the
   * placeholder shows the value it will inherit, so the default is visible
   * rather than implied. In a shared space each person gets their own pair,
   * because "120 inches of hanging" means nothing shared between two people.
   */
  // Guarded: every input in this panel commits via a change handler that
  // calls renderStorageNeeds() again. Chrome fires that change synchronously
  // while replaceChildren removes a focused, user-dirty input (typing a figure
  // and pressing Enter, or typing and tapping the canvas), and the re-entrant
  // rebuild crashed the app with a NotFoundError — see non-reentrant-render.js.
  const renderStorageNeeds = nonReentrantRender(() => {
    const box = root.getElementById('storage-needs');
    if (!box) return;
    box.replaceChildren();
    const shared = model.state.sharedSpace.enabled === true;
    const persons = model.state.sharedSpace.persons;
    const types = model.storageNeedTypes();
    if (!types.length) return;

    // Hang is a LENGTH (fraction-friendly); pairs and drawers are counts.
    const show = (type, v) => (v === null || v === undefined ? '' : type.kind === 'hang' ? fmt(v) : String(v));

    if (model.state.storageNeedsSource?.kind === 'client') {
      const source = model.state.storageNeedsSource;
      const badge = document.createElement('div');
      badge.className = 'storage-needs__source';
      const submitted = Number.isNaN(Date.parse(source.submittedAt))
        ? '' : ` · submitted ${new Date(source.submittedAt).toLocaleString()}`;
      badge.textContent = `Client-provided import${submitted}`;
      if (source.designerEditedAt) badge.textContent += ' · designer edited after import';
      box.append(badge);
      for (const imported of source.importedSpaces ?? []) {
        if (!imported.notes) continue;
        const note = document.createElement('div');
        note.className = 'storage-needs__source-note';
        note.textContent = `${imported.name}: ${imported.notes}`;
        box.append(note);
      }
    }

    const field = (type, entry, fieldName, slot, placeholder) => {
      const input = document.createElement('input');
      input.className = 'storage-needs__input';
      input.inputMode = 'text';
      input.dataset.pad = 'length';
      input.placeholder = placeholder;
      input.value = show(type, entry[fieldName][slot]);
      const fieldLabel = fieldName === 'existing' ? 'current' : 'need';
      input.setAttribute('aria-label', `${type.name} ${fieldLabel} ${shared ? slot.toUpperCase() : ''}`.trim());
      if (type.kind === 'hang') {
        makeCaptureTarget(input, {
          kind: 'storage_need_field', entityId: captureEntityId(type.key, slot),
          property: fieldName === 'existing' ? 'existingUnits' : 'neededUnits',
          label: `${type.name} ${fieldLabel}${shared ? ` · ${slot.toUpperCase()}` : ''}`,
        });
      }
      const commit = () => {
        if (model.setStorageNeed(type.key, fieldName, slot, input.value)) {
          renderStorageNeeds(); // the "needed" placeholder tracks "existing"
          renderStorageSummary();
          scheduleSave();
        } else {
          input.value = show(type, model.storageNeedFor(type.key)[fieldName][slot]);
        }
      };
      input.addEventListener('change', commit);
      // Enter commits too, the way the wall table does — on a tablet the
      // keyboard's Done key is the natural way to finish a figure, and without
      // this the number sat in the box uncommitted until something else stole
      // focus (caught in live testing, 2026-07-29).
      input.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') { commit(); input.blur(); }
      });
      return input;
    };

    // Which figure the summary box measures the design against (owner,
    // 2026-07-29). It sits here, over the Current/Need columns it chooses between,
    // because the floating box cannot carry a control (see renderStorageSummary).
    const modes = document.createElement('div');
    modes.className = 'storage-needs__modes';
    const modesTag = document.createElement('span');
    modesTag.textContent = 'Compare design with';
    modes.append(modesTag);
    for (const [mode, label] of [['current', 'Current'], ['need', 'Need']]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `storage-needs__mode${model.state.storageCompareTo === mode ? ' is-active' : ''}`;
      btn.textContent = label;
      btn.title = mode === 'current'
        ? 'Show the design against the client’s current storage'
        : 'Show the design against what the client needs';
      btn.setAttribute('aria-pressed', String(model.state.storageCompareTo === mode));
      btn.addEventListener('click', () => {
        if (model.setStorageCompareTo(mode)) { renderStorageNeeds(); renderStorageSummary(); scheduleSave(); }
      });
      modes.append(btn);
    }
    box.append(modes);

    const head = document.createElement('div');
    head.className = 'storage-needs__row storage-needs__row--head';
    head.append(
      Object.assign(document.createElement('span'), { textContent: '' }),
      Object.assign(document.createElement('span'), { textContent: 'Current' }),
      Object.assign(document.createElement('span'), { textContent: 'Need' }),
    );
    box.append(head);

    for (const type of types) {
      const entry = model.storageNeedFor(type.key);
      const unit = type.kind === 'hang' ? '″' : type.unit;
      // Hang rows are CATEGORIES now (ADR 0051 step 2 / ADR 0057): the label
      // is the category's own name — repeating the key in brackets would just
      // echo it in snake_case.
      const label = type.name;

      if (type.kind === 'shoes') {
        const section = document.createElement('div');
        section.className = 'storage-shoes';
        const title = document.createElement('div');
        title.className = 'storage-needs__label storage-needs__label--group';
        // Named from the ROW, not a literal, so a second footwear row could
        // never again render as a second card with the same words on it.
        // The explanatory line under it is gone (owner, 2026-08-30: "extra
        // text not needed") — the field labels below already say pairs, pair
        // width and toe-to-heel.
        title.textContent = `${type.name} needs`;
        section.append(title);
        for (const group of entry.shoeGroups) {
          const row = document.createElement('div');
          row.className = 'storage-shoes__row';
          if (shared) {
            const slotLabel = document.createElement('label');
            slotLabel.textContent = 'Section';
            const slot = document.createElement('select');
            for (const key of ['a', 'b']) {
              const option = document.createElement('option');
              option.value = key; option.textContent = key.toUpperCase();
              option.selected = (group.slot ?? 'a') === key;
              slot.append(option);
            }
            slot.addEventListener('change', () => {
              if (model.updateShoeInventoryGroup(type.key, group.id, 'slot', slot.value)) {
                renderStorageNeeds(); renderStorageSummary(); scheduleSave();
              }
            });
            slotLabel.append(slot); row.append(slotLabel);
          }
          const specs = [
            ['label', 'Description', group.label, 'Shoes, boots…'],
            ['existingPairs', 'Current pairs', group.existingPairs, '0'],
            ['neededPairs', 'Needed pairs', group.neededPairs, 'same'],
            ['widthUnits', 'Pair width', group.widthUnits == null ? '' : fmt(group.widthUnits), 'in'],
            ['lengthUnits', 'Toe to heel', group.lengthUnits == null ? '' : fmt(group.lengthUnits), 'in'],
          ];
          for (const [fieldName, labelText, value, placeholder] of specs) {
            const fieldLabel = document.createElement('label');
            fieldLabel.textContent = labelText;
            const input = document.createElement('input');
            input.value = value ?? '';
            input.placeholder = placeholder;
            input.inputMode = fieldName === 'label' ? 'text' : fieldName.endsWith('Pairs') ? 'numeric' : 'decimal';
            if (fieldName === 'widthUnits' || fieldName === 'lengthUnits') {
              makeCaptureTarget(input, {
                kind: 'footwear_field', entityId: captureEntityId(type.key, group.id), property: fieldName,
                label: `${group.label || 'Footwear entry'} · ${labelText}`,
              });
            }
            input.addEventListener('change', () => {
              if (model.updateShoeInventoryGroup(type.key, group.id, fieldName, input.value)) {
                renderStorageNeeds(); renderStorageSummary(); scheduleSave();
              } else input.value = value ?? '';
            });
            fieldLabel.append(input);
            row.append(fieldLabel);
          }
          const remove = document.createElement('button');
          remove.type = 'button'; remove.className = 'btn btn--icon'; remove.textContent = '×';
          remove.setAttribute('aria-label', `Remove ${group.label || 'footwear entry'}`);
          remove.addEventListener('click', () => {
            if (model.removeShoeInventoryGroup(type.key, group.id)) {
              renderStorageNeeds(); renderStorageSummary(); scheduleSave();
            }
          });
          row.append(remove);
          section.append(row);
        }
        const add = document.createElement('button');
        add.type = 'button'; add.className = 'btn storage-shoes__add'; add.textContent = '+ Add footwear entry';
        add.addEventListener('click', () => {
          if (model.addShoeInventoryGroup(type.key, shared ? 'a' : 'space')) { renderStorageNeeds(); scheduleSave(); }
        });
        section.append(add);
        box.append(section);
        continue;
      }

      if (!shared) {
        const row = document.createElement('div');
        row.className = 'storage-needs__row';
        const tag = document.createElement('span');
        tag.className = 'storage-needs__label';
        tag.textContent = label;
        tag.title = `Client requirement in ${type.kind === 'hang' ? 'linear inches' : type.unit}`;
        row.append(tag, field(type, entry, 'existing', 'space', unit), field(type, entry, 'needed', 'space', show(type, entry.existing.space) || unit));
        box.append(row);
        continue;
      }
      const group = document.createElement('div');
      group.className = 'storage-needs__group';
      const tag = document.createElement('div');
      tag.className = 'storage-needs__label storage-needs__label--group';
      tag.textContent = label;
      group.append(tag);
      ['a', 'b'].forEach((slot, i) => {
        const row = document.createElement('div');
        row.className = 'storage-needs__row';
        const who = document.createElement('span');
        who.className = 'storage-needs__person';
        who.textContent = persons[i];
        row.append(who, field(type, entry, 'existing', slot, unit), field(type, entry, 'needed', slot, show(type, entry.existing[slot]) || unit));
        group.append(row);
      });
      box.append(group);
    }
  });

  /**
   * The plan's storage-summary table (owner, 2026-07-27): linear inches of
   * hang per hanging type (rods counted — double hang is 2× its width), pairs
   * of shoes, total drawers. Hidden when the sketch has no capacity to report.
   */
  function renderStorageSummary() {
    const box = root.getElementById('storage-summary');
    if (!box) return;
    const { shared, persons, rows, compareTo, hasNeeds } = model.storageSummary();
    // Hidden when there's nothing to report OR the designer toggled it off to
    // free the screen space it occupies over the drawing.
    box.hidden = rows.length === 0 || model.state.storageSummaryVisible === false;
    box.replaceChildren();
    if (box.hidden) return;
    const caption = document.createElement('caption');
    // The box is deliberately pointer-events:none so it never blocks sketching
    // underneath, which means it can hold no controls — a switch drawn here
    // simply cannot be tapped (found in live testing, 2026-07-29). It states
    // which requirement the deltas are against; the switch lives in the
    // Storage needs panel, beside the figures it selects between.
    caption.textContent = hasNeeds ? `Storage vs ${compareTo}` : 'Storage';
    box.append(caption);
    const cell = (tag, text, cls) => {
      const el = document.createElement(tag);
      el.textContent = text;
      if (cls) el.className = cls;
      return el;
    };
    // Linear inches round to `storage_rounding_units` (¼" by default) rather
    // than to the whole inch (owner, 2026-08-05). Counts — pairs, drawers —
    // are integers and are never rounded.
    const fmtVal = (row, v) => (row.kind === 'hang'
      ? fmt(roundToNearest(v, rules.storageRoundingUnits))
      : `${v}`);
    /**
     * A capacity cell, plus the shortfall/surplus against the client's stated
     * requirement when there is one (ADR 0043). The delta rides INSIDE the cell
     * rather than in its own column, so the shared layout keeps its A|B|U|Total
     * shape on a tablet. No requirement stated → the cell looks as it always did.
     */
    const valueCell = (row, have, want, diff) => {
      const td = cell('td', fmtVal(row, have), 'storage-summary__value');
      if (want === null || want === undefined || diff === null || diff === undefined) return td;
      const short = diff < -0.005;
      const badge = document.createElement('span');
      badge.className = `storage-summary__delta${short ? ' storage-summary__delta--short' : ''}`;
      const size = Math.abs(diff);
      badge.textContent = `${short ? '−' : '+'}${fmtVal(row, size)}`;
      badge.title = `${compareTo === 'current' ? 'currently has' : 'needs'} ${fmtVal(row, want)}, design provides ${fmtVal(row, have)}`;
      td.append(badge);
      return td;
    };
    if (shared) {
      // Columns per person, UNASSIGNED, and total (owner: totals by person and
      // in total; U is what the total holds beyond either person's assigned).
      const head = document.createElement('tr');
      head.append(
        cell('th', ''),
        cell('th', persons[0], 'storage-summary__value'),
        cell('th', persons[1], 'storage-summary__value'),
        cell('th', 'U', 'storage-summary__value'),
        cell('th', 'Total', 'storage-summary__value'),
      );
      box.append(head);
      for (const row of rows) {
        const unassigned = row.total - row.byPerson[0] - row.byPerson[1];
        const tr = document.createElement('tr');
        tr.append(
          // Hang rows are categories (ADR 0057) — the label IS the name.
          cell('td', row.kind === 'hang' ? row.name : `${row.name} (${row.unit})`),
          valueCell(row, row.byPerson[0], row.need.a, row.diff.a),
          valueCell(row, row.byPerson[1], row.need.b, row.diff.b),
          // Unassigned capacity answers to no one's requirement, so no delta.
          cell('td', fmtVal(row, unassigned), 'storage-summary__value'),
          valueCell(row, row.total, row.need.total, row.diff.total),
        );
        box.append(tr);
      }
    } else {
      for (const row of rows) {
        const tr = document.createElement('tr');
        tr.append(
          // Hang rows are categories (ADR 0057) — the label IS the name.
          cell('td', row.kind === 'hang' ? row.name : `${row.name} (${row.unit})`),
          valueCell(row, row.total, row.need.total, row.diff.total),
        );
        box.append(tr);
      }
    }
  }

  // ---- space pricing (ADR 0058, plan §5/4e) --------------------------------

  /** UI-only: whether the Pricing section is expanded. Per session, never
   *  persisted — a collapsed panel is the restrained default. */
  let pricingOpen = false;

  const fmtMoney = (v) => (Number.isFinite(v) ? `$${Math.round(v).toLocaleString('en-US')}` : '—');
  const fmtRange = (low, high) => (Number.isFinite(low) && Number.isFinite(high)
    ? `${fmtMoney(low)}–${fmtMoney(high)}` : '—');

  /**
   * The Pricing panel (ADR 0058): computes the space's priced breakdown fresh
   * from the model (src/pricing/space-pricing.js — catalog anchors via the
   * vendored priceSection(), composition deltas, panel-run math, platform,
   * countertops), STAMPS the space totals onto its single document and renders
   * the collapsed summary line plus —
   * when expanded — the level/countertop pickers and per-wall breakdowns with
   * their basis badges. Runs on every repaint so the stamped totals are
   * current whenever a save fires, even with the section collapsed.
   */
  function renderPricingPanel() {
    const panel = root.getElementById('pricing-popup');
    const totalBox = root.getElementById('pricing-total');
    const body = root.getElementById('pricing-body');
    const toggle = root.getElementById('pricing-toggle');
    if (!panel || !totalBox || !body || !toggle) return;
    // Both lenses, combined (ADR 0059): PLACED sections through the ADR 0058
    // pipeline + still-STAGED bucket cards as spans. The stamped total is the
    // combined range — what this one space state carries.
    const combined = priceBasicSpace(model, rules);
    const summary = combined.placed;
    const staged = combined.staged;
    model.setPricingTotals({
      low: combined.space.low,
      high: combined.space.high,
      needsPricing: combined.space.needsPricing,
    });

    const anyPlaced = summary.walls.some((w) => w.lines.length > 0);
    const anyStaged = staged.lines.length > 0;
    // The staged/placed breakdown on the summary line only when BOTH exist —
    // with one lens empty the combined figure IS that lens's figure.
    const breakdown = anyPlaced && anyStaged
      ? ` (placed ${fmtRange(summary.space.low, summary.space.high)} + staged ${fmtRange(staged.low, staged.high)})`
      : '';
    totalBox.textContent = anyPlaced || anyStaged
      ? `Space ${fmtRange(combined.space.low, combined.space.high)} at level ${summary.level}${breakdown}`
        + (combined.space.needsPricing > 0 ? ` — ${combined.space.needsPricing} need${combined.space.needsPricing === 1 ? 's' : ''} pricing` : '')
      : 'No sections to price yet.';
    totalBox.classList.toggle('pricing__total--warn', combined.space.needsPricing > 0);

    toggle.setAttribute('aria-expanded', String(pricingOpen));
    toggle.classList.toggle('is-active', pricingOpen);
    toggle.setAttribute('aria-label', pricingOpen ? 'Close pricing' : 'Open pricing');
    panel.hidden = !pricingOpen;
    body.hidden = !pricingOpen;
    body.replaceChildren();
    if (!pricingOpen) return;

    // Material level — one pick per space, from the multiplier rule's levels.
    const levelField = document.createElement('label');
    levelField.className = 'pricing__field';
    levelField.title = 'Material level for this space. The multiplier scales material prices only; non-material charges never scale.';
    const levelTag = document.createElement('span');
    levelTag.textContent = 'Material level';
    const levelSel = document.createElement('select');
    const levels = Array.isArray(rules.cvMaterialMultipliers?.levels) ? rules.cvMaterialMultipliers.levels : [];
    for (const l of levels) {
      const opt = document.createElement('option');
      opt.value = String(l.level);
      opt.textContent = `Level ${l.level} (×${l.factor})`;
      if (l.level === summary.level) opt.selected = true;
      levelSel.append(opt);
    }
    levelSel.addEventListener('change', () => {
      if (model.setPricingLevel(Number(levelSel.value))) { renderPricingPanel(); scheduleSave(); }
    });
    levelField.append(levelTag, levelSel);
    body.append(levelField);

    // Countertop material — separate from material levels (plan §1.3).
    const ctField = document.createElement('label');
    ctField.className = 'pricing__field';
    ctField.title = 'Countertop material for this space (per sq ft, plus the fixed charge per contiguous top). Separate from material levels.';
    const ctTag = document.createElement('span');
    ctTag.textContent = 'Countertop';
    const ctSel = document.createElement('select');
    const noneOpt = document.createElement('option');
    noneOpt.value = '';
    noneOpt.textContent = 'none';
    ctSel.append(noneOpt);
    for (const m of rules.cvCountertops?.materials ?? []) {
      const opt = document.createElement('option');
      opt.value = m.materialCode;
      opt.textContent = `${m.name} ($${m.perSqFt}/sq ft)`;
      if (m.materialCode === summary.countertopMaterial) opt.selected = true;
      ctSel.append(opt);
    }
    ctSel.addEventListener('change', () => {
      if (model.setPricingCountertopMaterial(ctSel.value || null)) { renderPricingPanel(); scheduleSave(); }
    });
    ctField.append(ctTag, ctSel);
    body.append(ctField);

    // Staged bucket cards (ADR 0059): the tray's span lines, before the
    // per-wall placed breakdowns — the space range is the sum of both.
    if (anyStaged) {
      const det = document.createElement('details');
      det.className = 'pricing__wall';
      const sum = document.createElement('summary');
      sum.className = 'pricing__wall-sum';
      sum.textContent = `Staged ${fmtRange(staged.low, staged.high)}`;
      if (staged.needsPricing > 0) {
        const warn = document.createElement('span');
        warn.className = 'pricing-badge pricing-badge--needs-pricing';
        warn.textContent = `${staged.needsPricing} needs pricing`;
        sum.append(warn);
      }
      det.append(sum);
      const list = document.createElement('ul');
      list.className = 'pricing__lines';
      for (const line of staged.lines) {
        const li = document.createElement('li');
        li.className = 'pricing__line';
        const label = document.createElement('span');
        label.className = 'pricing__line-label';
        label.textContent = line.label;
        const badge = document.createElement('span');
        badge.className = `pricing-badge pricing-badge--${line.basis}`;
        badge.textContent = line.basis === 'needs-pricing' ? 'needs pricing' : line.basis;
        const amount = document.createElement('span');
        amount.className = 'pricing__line-amount';
        amount.textContent = line.basis === 'needs-pricing' ? '—' : fmtRange(line.low, line.high);
        li.title = [...line.notes, line.anchors.length ? `anchors: ${line.anchors.join(', ')}` : '']
          .filter(Boolean).join('\n');
        li.append(label, badge, amount);
        list.append(li);
      }
      det.append(list);
      body.append(det);
    }

    const pricingLine = (line) => {
      const li = document.createElement('li');
      li.className = 'pricing__line';
      const label = document.createElement('span');
      label.className = 'pricing__line-label';
      label.textContent = line.label;
      const badge = document.createElement('span');
      badge.className = `pricing-badge pricing-badge--${line.basis}`;
      badge.textContent = line.basis === 'needs-pricing' ? 'needs pricing' : line.basis;
      const amount = document.createElement('span');
      amount.className = 'pricing__line-amount';
      amount.textContent = line.basis === 'needs-pricing' || line.basis === 'open'
        ? (line.basis === 'open' ? '$0' : '—')
        : fmtRange(line.low, line.high);
      li.title = [...line.notes, line.anchors.length ? `anchors: ${line.anchors.join(', ')}` : '']
        .filter(Boolean).join('\n');
      li.append(label, badge, amount);
      return li;
    };

    // Per-wall breakdowns. Section-priced lines are grouped in a second
    // disclosure level so a designer can open W2, then inspect DH / DRW / SS
    // independently without losing the wall-level total and shared charges.
    for (const wall of summary.walls) {
      if (!wall.lines.length) continue;
      const det = document.createElement('details');
      det.className = 'pricing__wall';
      const sum = document.createElement('summary');
      sum.className = 'pricing__wall-sum';
      sum.textContent = `${wall.label} ${fmtRange(wall.low, wall.high)}`;
      if (wall.needsPricing > 0) {
        const warn = document.createElement('span');
        warn.className = 'pricing-badge pricing-badge--needs-pricing';
        warn.textContent = `${wall.needsPricing} needs pricing`;
        sum.append(warn);
      }
      det.append(sum);
      const groups = new Map();
      const shared = [];
      for (const line of wall.lines) {
        if (!line.sectionId) { shared.push(line); continue; }
        if (!groups.has(line.sectionId)) groups.set(line.sectionId, { label: line.sectionLabel, lines: [] });
        groups.get(line.sectionId).lines.push(line);
      }
      for (const group of groups.values()) {
        const section = document.createElement('details');
        section.className = 'pricing__section';
        const sectionSummary = document.createElement('summary');
        const priced = group.lines.filter((line) => line.low != null && line.high != null);
        const low = priced.reduce((sum, line) => sum + line.low, 0);
        const high = priced.reduce((sum, line) => sum + line.high, 0);
        sectionSummary.textContent = `${group.label} ${priced.length ? fmtRange(low, high) : '—'}`;
        section.append(sectionSummary);
        const list = document.createElement('ul');
        list.className = 'pricing__lines';
        list.append(...group.lines.map(pricingLine));
        section.append(list);
        det.append(section);
      }
      if (shared.length) {
        const sharedDetails = document.createElement('details');
        sharedDetails.className = 'pricing__shared';
        const sharedSummary = document.createElement('summary');
        const priced = shared.filter((line) => line.low != null && line.high != null);
        sharedSummary.textContent = `Wall additions ${priced.length
          ? fmtRange(priced.reduce((sum, line) => sum + line.low, 0), priced.reduce((sum, line) => sum + line.high, 0))
          : '—'}`;
        sharedDetails.append(sharedSummary);
        const list = document.createElement('ul');
        list.className = 'pricing__lines';
        list.append(...shared.map(pricingLine));
        sharedDetails.append(list);
        det.append(sharedDetails);
      }
      body.append(det);
    }
  }

  // ---- handles required (ADR 0064) -----------------------------------------

  /** UI-only: whether the Handles section is expanded. Per session, never
   *  persisted — the Pricing panel's convention, for the same reason. */
  let handlesOpen = false;

  /**
   * The Handles-required panel (ADR 0064). The owner: *"the system should
   * capture and report 'handles required' as part of a summary report as this
   * will feed purchasing and/or the client provided handle form if the client
   * is electing to provide handles."*
   *
   * The figure is the take-off's `total` — the real quantity to BUY, with the
   * two-pull rule applied — NOT the drawer-face count. Where the two differ,
   * both are shown, because a purchaser reading "8 handles for 4 drawers"
   * needs to see why rather than assume a bug.
   *
   * Recomputed on every repaint from `handleTakeOff(model, rules)`, which is
   * cheap: it reads compositions, not solved interiors.
   */
  function renderHandleSummary() {
    const totalBox = root.getElementById('handles-total');
    const body = root.getElementById('handles-body');
    const toggle = root.getElementById('handles-toggle');
    if (!totalBox || !body || !toggle) return;
    const takeOff = handleTakeOff(model, rules);

    const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
    // Doors are named BESIDE drawer faces (ADR 0068) rather than folded into
    // one figure: "8 handles for 4 drawer faces" printed beside a drawing that
    // clearly has doors on it reads as a bug, when it is the door leaves.
    const parts = [];
    if (takeOff.faces > 0) parts.push(plural(takeOff.faces, 'drawer face'));
    if (takeOff.doorLeaves > 0) parts.push(plural(takeOff.doorLeaves, 'door'));
    totalBox.textContent = takeOff.total > 0
      ? `${plural(takeOff.total, 'handle')} for ${parts.join(' and ')}`
      : 'Nothing to handle yet.';

    toggle.disabled = takeOff.total === 0;
    toggle.textContent = handlesOpen ? 'Hide' : 'Show';
    toggle.setAttribute('aria-expanded', String(handlesOpen));
    body.hidden = !handlesOpen || takeOff.total === 0;
    body.replaceChildren();
    if (body.hidden) return;

    // BY TYPE first: it is the line a purchase order is written from, and the
    // one the client-provided-handle form multiplies by its per-handle charge.
    const types = document.createElement('ul');
    types.className = 'handles__lines';
    for (const row of takeOff.byType) {
      const li = document.createElement('li');
      li.className = 'handles__line';
      const label = document.createElement('span');
      label.className = 'handles__line-label';
      label.textContent = row.label;
      const amount = document.createElement('span');
      amount.className = 'handles__line-amount';
      amount.textContent = String(row.count);
      li.append(label, amount);
      types.append(li);
    }
    body.append(types);

    // Then per WALL, each wall's banks inside it — the same <details> shape the
    // pricing panel uses for its per-wall breakdown.
    for (const wall of takeOff.byWall) {
      const det = document.createElement('details');
      det.className = 'handles__wall';
      const sum = document.createElement('summary');
      sum.className = 'handles__wall-sum';
      sum.textContent = `${wall.label} — ${plural(wall.count, 'handle')}`;
      det.append(sum);
      const list = document.createElement('ul');
      list.className = 'handles__lines';
      for (const sec of wall.sections) {
        const li = document.createElement('li');
        li.className = 'handles__line';
        const label = document.createElement('span');
        label.className = 'handles__line-label';
        const noun = sec.kind === 'door' ? 'door' : 'face';
        label.textContent = `${sec.label} — ${plural(sec.faces, noun)}`;
        const amount = document.createElement('span');
        amount.className = 'handles__line-amount';
        amount.textContent = String(sec.count);
        // The WHY, on the row: a face wide enough to need two pulls says so.
        li.title = `${fmt(sec.faceWidthUnits)} ${noun}`
          + (sec.count > sec.faces ? ' — over the two-pull width, so each face takes two' : '');
        li.append(label, amount);
        list.append(li);
      }
      det.append(list);
      body.append(det);
    }
  }

  /**
   * The Sections panel (ADR 0037): a type palette (tap to add to the selected
   * wall/face), one row per section with its SOLVED opening width (editable —
   * typing locks it; unlocking returns it to auto-fill), the shared/double
   * panel toggle between neighbours, and a run summary with the overflow /
   * spare-space arithmetic. Supersedes the label palette (ADR 0034) — the
   * owner promoted labels into dimensioned sections.
   */
  function renderSectionsPanel() {
    const palette = root.getElementById('section-presets');
    const listBox = root.getElementById('section-list');
    const summary = root.getElementById('section-summary');
    const title = root.getElementById('sections-title');
    if (!palette || !listBox) return;
    renderSharedSpaceControls();
    renderSpaceHeightControl();
    renderBasicModeToggle();
    const host = activeWallHost();
    palette.replaceChildren();
    listBox.replaceChildren();
    if (summary) summary.textContent = '';
    // Basic mode (ADR 0059): the SECTIONS granularity changes — bucket cards
    // instead of the concrete palette/list. The sketch canvas, space
    // assumptions and everything else stay exactly as they are (the toggle is
    // a view change, never a data migration).
    const basicOn = model.state.basic?.mode === 'basic';
    const tray = root.getElementById('basic-tray');
    if (tray) {
      tray.hidden = !basicOn;
      tray.replaceChildren();
    }
    if (basicOn) {
      if (title) title.textContent = 'Sections · Basic';
      if (tray) renderBasicTray(tray, host);
      return;
    }
    if (!host) {
      if (title) title.textContent = 'Sections';
      return;
    }
    const sections = model.sectionsForWall(host.id);
    if (title) title.textContent = sections.length ? `Sections · ${host.label} · ${sections.length}` : `Sections · ${host.label}`;
    renderSideChooser(palette, host); // partition: pick which face first
    // The TYPE PALETTE is collapsed (ADR 0066 item 5). The owner starts from a
    // TEMPLATE and modifies it, so adding a raw type is the RARE action — and it
    // was occupying the panel above the section rows, ~644px of scroll ahead of
    // the common one. It is a disclosure, not a deletion: the owner does
    // sometimes build a section from an empty shell, and every type is one tap
    // away. The open/closed state SURVIVES the repaint an add triggers, so
    // adding three sections is three taps rather than three re-opens.
    const adder = document.createElement('details');
    adder.className = 'section-add';
    adder.open = sectionAddOpen;
    adder.addEventListener('toggle', () => { sectionAddOpen = adder.open; });
    const adderSummary = document.createElement('summary');
    adderSummary.className = 'section-add__summary';
    adderSummary.textContent = '+ Add section';
    adderSummary.title = 'Add a section of a given type to this wall';
    adder.append(adderSummary);
    const adderList = document.createElement('div');
    adderList.className = 'label-presets';
    for (const preset of rules.sectionTypePresets ?? []) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'label-preset';
      btn.textContent = preset.code;
      btn.title = `Add ${preset.name || preset.code}`;
      btn.addEventListener('click', () => {
        if (model.addSection(host.id, preset.code)) repaintSections();
      });
      adderList.append(btn);
    }
    adder.append(adderList);
    palette.append(adder);
    // The reusable-section library, INCLUDING when it is empty or broken.
    // Rendering it only when `length` was non-zero is what made a failed read
    // invisible: the whole disclosure disappeared exactly when it was needed.
    const libraryState = reusableSectionLibrary.status();
    if (libraryState.state !== 'loading' || libraryState.count) {
      const saved = document.createElement('details');
      saved.className = 'section-add';
      if (libraryState.state === 'error') saved.classList.add('section-add--error');
      const heading = document.createElement('summary');
      heading.className = 'section-add__summary';
      heading.textContent = libraryState.state === 'error'
        ? 'Reusable sections · unavailable'
        : `Reusable sections · ${libraryState.count}`;
      saved.append(heading);

      // Scope, per state. A designer learns the library is device-local BEFORE
      // switching devices, rather than by finding it missing on the tablet —
      // and a read failure says so instead of showing an empty list.
      const note = document.createElement('p');
      note.className = 'section-add__note';
      note.textContent = libraryState.note;
      saved.append(note);

      const choices = document.createElement('div');
      choices.className = 'label-presets';
      for (const component of reusableSectionLibrary.list()) {
        const btn = document.createElement('button');
        btn.type = 'button'; btn.className = 'label-preset'; btn.textContent = component.name;
        btn.addEventListener('click', () => {
          if (model.addSectionComponent(host.id, component.section)) repaintSections();
        });
        choices.append(btn);
      }
      saved.append(choices);
      palette.append(saved);
    }

    const layout = model.sectionLayoutForWall(host.id);
    if (!layout) return;

    const cornerInfo = model.cornerHangInfo(host.id);
    const stacks = model.stacksForWall(host.id);
    sections.forEach((s, i) => {
      const item = layout.items[i];
      // The PLACED unit for this run section — resolved depth and clear band,
      // which is what the row's inline interior controls solve against. Found
      // by section id exactly as previewElevation() finds it, never by index.
      const placedRun = (stacks[i]?.units ?? []).find((u) => u.section?.id === s.id) ?? null;
      listBox.append(sectionRow(s, item, i, sections.length, cornerInfo, placedRun));
      // Units stacked ON this section (ADR 0041), bottom → top, each showing
      // its solved elevation. They have no width of their own — they span the
      // run section below them.
      const placed = stacks[i]?.units ?? [];
      model.stackChainFor(s.id).forEach((u, k) => {
        listBox.append(stackedRow(u, placed[k + 1]));
      });
      listBox.append(stackAdder(s));
      // Between neighbours: the shared/double panel toggle for the join AFTER
      // this section (owner: shared by default, override per join).
      if (i < sections.length - 1) {
        const join = document.createElement('button');
        join.type = 'button';
        join.className = `section-join${s.fullPanelAfter ? ' section-join--double' : ''}`;
        join.textContent = s.fullPanelAfter ? '‖ double panel' : '| shared panel';
        join.title = 'Toggle whether these neighbours share one panel or each keep their own';
        join.addEventListener('click', () => {
          if (model.toggleSectionJoin(s.id)) repaintSections();
        });
        listBox.append(join);
      }
    });

    if (summary) {
      const clearTotal = (layout.startClearanceUnits ?? 0) + (layout.endClearanceUnits ?? 0);
      const runTxt = clearTotal > 0
        ? `${fmt(layout.runUnits)} wall − ${fmt(clearTotal)} hang clearance`
        : `${fmt(layout.runUnits)} wall`;
      if (layout.overflowUnits > 0.01) {
        summary.textContent = `${runTxt} — over by ${fmt(layout.overflowUnits)}. Locked widths don't fit.`;
        summary.className = 'section-summary section-summary--warn';
      } else if (layout.leftoverUnits > 0.01) {
        summary.textContent = `${runTxt} — ${fmt(layout.leftoverUnits)} spare (openings capped at ${fmt(rules.sectionMaxOpeningUnits)}).`;
        summary.className = 'section-summary';
      } else {
        summary.textContent = `${runTxt} — filled.`;
        summary.className = 'section-summary';
      }
    }
  }

  /**
   * The Basic ↔ Detailed mode toggle (ADR 0059) — a view change only, shown
   * in BOTH modes at the top of the Sections panel. Basic swaps the concrete
   * palette/list for the staged bucket-card tray; the sketch canvas and the
   * space assumptions above stay untouched either way.
   */
  function renderBasicModeToggle() {
    const box = root.getElementById('basic-mode');
    if (!box) return;
    box.replaceChildren();
    const current = model.state.basic?.mode === 'basic' ? 'basic' : 'detailed';
    const tag = document.createElement('span');
    tag.className = 'basic-mode__tag';
    tag.textContent = 'Mode';
    box.append(tag);
    for (const [mode, label] of [['basic', 'Basic'], ['detailed', 'Detailed']]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'basic-mode__btn';
      btn.textContent = label;
      const active = mode === current;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.title = mode === 'basic'
        ? 'Bucket cards with honest price ranges — a five-minute number'
        : 'Concrete sections with real dimensions and interiors';
      btn.addEventListener('click', () => {
        if (model.setBasicMode(mode)) repaintSections();
      });
      box.append(btn);
    }
  }

  /**
   * The Basic-mode TRAY (ADR 0059): space type (filters the bucket palette
   * via the availability matrix), the designer's span bounds (numeric-pad
   * length fields), the bucket palette, one CARD per staged bucket with its
   * quantity, wall assignment and Place action, and the sections already
   * placed from cards with their Unplace path.
   */
  function renderBasicTray(tray, host) {
    const basic = model.state.basic;
    const spaceTypes = Array.isArray(rules.basicSpaceTypes) ? rules.basicSpaceTypes : [];
    const spaceType = basic.spaceType ?? spaceTypes[0]?.key ?? null;

    // Space type — which buckets this space offers (admin-maintained matrix).
    const typeField = document.createElement('label');
    typeField.className = 'pricing__field';
    typeField.title = 'What this space is — filters which bucket cards are offered (admin-maintained availability).';
    const typeTag = document.createElement('span');
    typeTag.textContent = 'Space type';
    const typeSel = document.createElement('select');
    for (const t of spaceTypes) {
      const opt = document.createElement('option');
      opt.value = t.key;
      opt.textContent = t.label ?? t.key;
      if (t.key === spaceType) opt.selected = true;
      typeSel.append(opt);
    }
    typeSel.addEventListener('change', () => {
      if (model.setBasicSpaceType(typeSel.value)) repaintSections();
    });
    typeField.append(typeTag, typeSel);
    tray.append(typeField);

    // Span bounds: the designer's height×depth window the bucket spans are
    // priced inside (low = cheapest qualifying row, high = priciest). Length
    // fields, so the docked numeric pad serves them (ADR 0047).
    const boundsBox = document.createElement('div');
    boundsBox.className = 'basic-bounds';
    boundsBox.title = 'Bucket spans price between these bounds: from the cheapest section at the LOW height × depth to the priciest at the HIGH. Widen them when the client wants deeper or shorter sections.';
    const boundsTag = document.createElement('span');
    boundsTag.className = 'basic-bounds__tag';
    boundsTag.textContent = 'Bounds h×d';
    boundsBox.append(boundsTag);
    const bounds = model.basicBounds();
    const boundField = (field, value) => {
      const input = document.createElement('input');
      input.className = 'basic-bounds__input';
      input.inputMode = 'text';
      input.dataset.pad = 'length';
      input.value = fmt(value);
      input.setAttribute('aria-label', {
        lowHeightIn: 'Low bound height', lowDepthIn: 'Low bound depth',
        highHeightIn: 'High bound height', highDepthIn: 'High bound depth',
      }[field]);
      input.addEventListener('change', () => {
        if (model.setBasicBound(field, input.value)) repaintSections();
        else input.value = fmt(model.basicBounds()[field]);
      });
      return input;
    };
    const sep = (text) => {
      const s = document.createElement('span');
      s.className = 'basic-bounds__sep';
      s.textContent = text;
      return s;
    };
    boundsBox.append(
      boundField('lowHeightIn', bounds.lowHeightIn), sep('×'),
      boundField('lowDepthIn', bounds.lowDepthIn), sep('to'),
      boundField('highHeightIn', bounds.highHeightIn), sep('×'),
      boundField('highDepthIn', bounds.highDepthIn),
    );
    tray.append(boundsBox);

    // Bucket palette — only what the availability matrix offers this space
    // type. Tapping stages one unit (merging into the unassigned card).
    const palette = document.createElement('div');
    palette.className = 'label-presets';
    for (const bucket of availableBuckets(rules, spaceType)) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'label-preset';
      btn.textContent = `+ ${bucket.label ?? bucket.key}`;
      btn.title = `Stage one ${bucket.label ?? bucket.key} unit`;
      btn.addEventListener('click', () => {
        if (model.addBucketCard(bucket.key)) repaintSections();
      });
      palette.append(btn);
    }
    tray.append(palette);

    // Wall choices for assignment: real walls plus partition faces.
    const wallChoices = [
      ...model.state.walls
        .filter((w) => (w.segmentKind ?? 'wall') === 'wall' && w.wallNumber)
        .map((w) => ({ id: w.id, label: `W${w.wallNumber}` })),
      ...model.partitionFaceWalls().map((f) => ({ id: f.id, label: `W${f.wallNumber}` })),
    ];

    // One row per staged card.
    const list = document.createElement('div');
    list.className = 'bucket-cards';
    for (const card of basic.cards) {
      const bucket = bucketByKey(rules, card.bucketKey);
      const row = document.createElement('div');
      row.className = 'bucket-card';

      const name = document.createElement('span');
      name.className = 'bucket-card__name';
      name.textContent = bucket?.label ?? card.bucketKey;
      if (!bucket) name.title = 'No bucket definition — needs the basic_buckets rule';

      const qty = document.createElement('span');
      qty.className = 'bucket-card__qty';
      const stepBtn = (delta, label) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bucket-card__step';
        b.textContent = label;
        b.title = delta > 0 ? 'One more unit' : 'One unit fewer (0 removes the card)';
        b.addEventListener('click', () => {
          if (model.setBucketCardQty(card.id, card.qty + delta)) repaintSections();
        });
        return b;
      };
      const count = document.createElement('span');
      count.className = 'bucket-card__count';
      count.textContent = String(card.qty);
      qty.append(stepBtn(-1, '−'), count, stepBtn(+1, '+'));

      // DRAWER ADDER (owner, 2026-08-10) — drawers PER UNIT, charged at the
      // flat basic_drawer_adder_price_low/_high band so Basic never needs a
      // per-drawer-count catalog row. A stepper, not a typed field: the counts
      // are tiny and a stepper cannot reproduce the ADR 0066 caret defect
      // (tapping "4" and typing 5 giving fifty-four).
      const drawersOf = (c) => (Number.isInteger(c.drawers) && c.drawers > 0 ? c.drawers : 0);
      const drawers = document.createElement('span');
      drawers.className = 'bucket-card__drawers';
      drawers.title = `Drawers added to EACH unit. ${card.qty} unit${card.qty > 1 ? 's' : ''} × `
        + `${drawersOf(card)} = ${card.qty * drawersOf(card)} drawers at `
        + `$${rules.basicDrawerAdderPriceLow}–$${rules.basicDrawerAdderPriceHigh} each (assumes deco drawer `
        + 'faces). Placing a unit leaves the adder behind — a placed section is priced from what it actually holds.';
      const drawerTag = document.createElement('span');
      drawerTag.className = 'bucket-card__drawers-tag';
      drawerTag.textContent = 'Drw';
      const drawerStep = (delta, label) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bucket-card__step';
        b.textContent = label;
        b.title = delta > 0 ? 'One more drawer per unit' : 'One drawer fewer per unit';
        b.disabled = delta < 0 && drawersOf(card) === 0;
        b.addEventListener('click', () => {
          if (model.setBucketCardDrawers(card.id, drawersOf(card) + delta)) repaintSections();
        });
        return b;
      };
      const drawerCount = document.createElement('span');
      drawerCount.className = 'bucket-card__count';
      drawerCount.textContent = String(drawersOf(card));
      drawers.append(drawerTag, drawerStep(-1, '−'), drawerCount, drawerStep(+1, '+'));

      // Wall ASSIGNMENT — spatial context only; the span does not narrow.
      const assign = document.createElement('select');
      assign.className = 'bucket-card__assign';
      assign.title = 'Assign these units to a wall for context (per-wall counts + a glyph on the elevation). Assignment is not placement — the range stays wide.';
      const none = document.createElement('option');
      none.value = '';
      none.textContent = 'unassigned';
      assign.append(none);
      for (const w of wallChoices) {
        const opt = document.createElement('option');
        opt.value = w.id;
        opt.textContent = w.label;
        if (w.id === card.wallId) opt.selected = true;
        assign.append(opt);
      }
      assign.addEventListener('change', () => {
        if (model.assignBucketCard(card.id, assign.value || null)) repaintSections();
        else assign.value = card.wallId ?? '';
      });

      // PLACE — the conversion: one unit becomes a real section on a wall
      // (the card's assigned wall, else the selected one).
      const placeable = typeof bucket?.representativeType === 'string'
        && (rules.sectionTypePresets ?? []).some((p) => String(p.code).toUpperCase() === bucket.representativeType.toUpperCase());
      const target = card.wallId ?? host?.id ?? null;
      const place = document.createElement('button');
      place.type = 'button';
      place.className = 'btn bucket-card__place';
      place.textContent = 'Place';
      place.disabled = !placeable || !target;
      // The REASON, named. `representativeType` is matched against the LIVE
      // section_type_presets rule, which the Admin panel can overwrite — so a
      // bucket that placed yesterday stops placing if its type code is renamed
      // or deleted there, and the old tooltip made that indistinguishable from
      // a broken button (owner, twice: "the place button was grayed out").
      const why = !placeable
        ? (typeof bucket?.representativeType === 'string' && bucket.representativeType
          ? `Can’t place: no section type with code ${bucket.representativeType} — check Admin › section types.`
          : 'Prices only — this card has no sketch section type yet.')
        : (target ? '' : 'Pick a wall (or select one) to place onto.');
      place.title = why || `Convert one unit into a real ${bucket.representativeType} section on `
        + `${wallChoices.find((w) => w.id === target)?.label ?? 'the selected wall'}`;
      place.addEventListener('click', () => {
        if (model.placeBucketUnit(card.id, target)) repaintSections();
      });

      row.append(name, qty, drawers, assign, place);
      // Tablets have no hover, so the adder must state its arithmetic on
      // screen, not only in the title above.
      if (drawersOf(card) > 0) {
        const note = document.createElement('span');
        note.className = 'bucket-card__adder';
        const total = card.qty * drawersOf(card);
        note.textContent = `+ ${total} drawer${total > 1 ? 's' : ''} `
          + `($${rules.basicDrawerAdderPriceLow}–$${rules.basicDrawerAdderPriceHigh} each)`;
        row.append(note);
      }
      // Tablets have no hover, so a greyed button must say why on screen.
      if (why) {
        const note = document.createElement('span');
        note.className = 'bucket-card__why';
        note.textContent = why;
        row.append(note);
      }
      list.append(row);
    }
    if (!basic.cards.length) {
      const hint = document.createElement('p');
      hint.className = 'bucket-cards__hint';
      hint.textContent = 'Stage bucket cards for a five-minute range; place them onto walls to sharpen it.';
      list.append(hint);
    }
    tray.append(list);

    // Sections already PLACED from cards, with the unplace path back. Other
    // concrete sections are Detailed-mode business — counted, not listed.
    const converted = model.state.sections.filter((s) => typeof s.fromBucket === 'string' && s.fromBucket);
    const others = model.state.sections.length - converted.length;
    if (converted.length || others > 0) {
      const placedBox = document.createElement('div');
      placedBox.className = 'bucket-placed';
      const head = document.createElement('p');
      head.className = 'bucket-placed__head';
      head.textContent = converted.length
        ? `Placed from cards · ${converted.length}`
        : '';
      if (head.textContent) placedBox.append(head);
      const labelFor = (id) => wallChoices.find((w) => w.id === id)?.label ?? '?';
      for (const s of converted) {
        const row = document.createElement('div');
        row.className = 'bucket-placed__row';
        const txt = document.createElement('span');
        txt.textContent = `${s.typeCode} on ${labelFor(s.wallId)} — priced from sketch`;
        const un = document.createElement('button');
        un.type = 'button';
        un.className = 'section-row__delete';
        un.textContent = '×';
        un.title = 'Unplace — remove this section and return the unit to its card';
        un.addEventListener('click', () => {
          if (model.removeSection(s.id)) repaintSections();
        });
        row.append(txt, un);
        placedBox.append(row);
      }
      if (others > 0) {
        const note = document.createElement('p');
        note.className = 'bucket-placed__note';
        note.textContent = `${others} other section${others > 1 ? 's' : ''} — switch to Detailed to edit`;
        placedBox.append(note);
      }
      tray.append(placedBox);
    }
  }

  /**
   * The shared-space controls (owner, 2026-07-27): an A/B toggle at the
   * top of the Sections panel, with editable person names when on. Sketch-
   * level — it configures the storage summary's per-person split.
   */
  function renderSharedSpaceControls() {
    const box = root.getElementById('shared-space');
    if (!box) return;
    box.replaceChildren();
    const shared = model.state.sharedSpace;
    const toggle = document.createElement('label');
    toggle.className = 'shared-space__toggle';
    const check = document.createElement('input');
    check.type = 'checkbox';
    check.checked = shared.enabled === true;
    check.addEventListener('change', () => {
      model.setSharedSpace(check.checked);
      renderSectionsPanel();
      renderStorageNeeds();
      renderStorageSummary();
      scheduleSave();
    });
    toggle.append(check, document.createTextNode('Shared space (two people)'));
    box.append(toggle);
    if (!shared.enabled) return;
    const names = document.createElement('div');
    names.className = 'shared-space__names';
    shared.persons.forEach((name, i) => {
      const input = document.createElement('input');
      input.value = name;
      input.placeholder = i === 0 ? 'A' : 'B';
      input.title = 'Name shown in the storage summary and on section rows';
      input.addEventListener('change', () => {
        model.setPersonName(i, input.value);
        renderSectionsPanel();
        renderStorageNeeds();
        renderStorageSummary();
        scheduleSave();
      });
      names.append(input);
    });
    box.append(names);
  }

  /**
   * The space-level section height (owner, 2026-07-27): one input for "this
   * whole space is 84"" instead of editing every section. Per-section H pins
   * still win; blank returns to the type defaults.
   */
  function renderSpaceHeightControl() {
    const box = root.getElementById('space-height');
    if (!box) return;
    box.replaceChildren();
    const wrap = document.createElement('label');
    wrap.className = 'space-height__field';
    wrap.title = 'Section height for this whole space (e.g. 84). Sections without their own H follow it. Blank = each type’s default.';
    const tag = document.createElement('span');
    tag.textContent = 'Space height';
    const input = document.createElement('input');
    input.inputMode = 'text';
    input.dataset.pad = 'length';
    input.placeholder = `type default (${fmt(rules.sectionDefaultHeightUnits)})`;
    input.value = model.state.spaceHeightUnits > 0 ? fmtSystem(model.state.spaceHeightUnits) : '';
    makeCaptureTarget(input, {
      kind: 'design_space_field', entityId: 'active-space', property: 'spaceHeightUnits', label: 'Design space height',
    });
    input.addEventListener('change', () => {
      if (model.setSpaceHeight(input.value)) repaintSections();
      else input.value = model.state.spaceHeightUnits > 0 ? fmtSystem(model.state.spaceHeightUnits) : '';
    });
    wrap.append(tag, input);
    box.append(wrap);

    // Platform toggle (ADR 0040): Floor / 3" / 6" (raised heights from the
    // platform_heights rule). On a platform, sections shift up per their type
    // anchor and are drawn on legs — see the elevation renderer.
    const plat = document.createElement('div');
    plat.className = 'platform-toggle';
    plat.title = 'Stand the cabinetry on a platform (garage systems). Sections shift up and show legs on the elevation.';
    const platTag = document.createElement('span');
    platTag.className = 'platform-toggle__tag';
    platTag.textContent = 'Platform';
    plat.append(platTag);
    const current = model.state.platformHeightUnits > 0 ? model.state.platformHeightUnits : 0;
    const options = [0, ...(Array.isArray(rules.platformHeights) ? rules.platformHeights : [])];
    for (const units of options) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'platform-toggle__btn';
      btn.textContent = units > 0 ? fmt(units) : 'Floor';
      const active = units === current;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.addEventListener('click', () => {
        model.setPlatformHeight(units);
        repaintSections();
      });
      plat.append(btn);
    }
    box.append(plat);

    // Countertop thickness (ADR 0041): the gap left above every BASE in the
    // space before a unit stacks on it. A section can override its own.
    //
    // The designer picks the MATERIAL and the thickness follows (owner,
    // 2026-08-30: "rather than manual, select type w/ presets"). Typing a
    // figure by hand was both slower and a place to get a stock thickness
    // wrong. `Other` is the escape hatch, and is the ONLY choice that shows
    // the length box — so the number pad appears when it is wanted and never
    // when it is not.
    const ct = document.createElement('label');
    ct.className = 'space-height__field';
    ct.title = 'Countertop above base sections in this space. A stacked unit sits this far above its base. None = no countertop; a section can override it.';
    const ctTag = document.createElement('span');
    ctTag.textContent = 'Countertop';
    const ctSelect = document.createElement('select');
    ctSelect.className = 'space-height__select';
    ctSelect.setAttribute('aria-label', 'Countertop material');
    const ctInput = document.createElement('input');
    ctInput.inputMode = 'text';
    ctInput.dataset.pad = 'length';
    ctInput.placeholder = 'thickness';
    ctInput.setAttribute('aria-label', 'Countertop thickness');
    const presets = Array.isArray(rules.countertopMaterialPresets) ? rules.countertopMaterialPresets : [];
    const OTHER = COUNTERTOP_OTHER;
    ctSelect.append(new Option('None', ''));
    for (const preset of presets) {
      ctSelect.append(new Option(`${preset.label} ${fmt(preset.thicknessUnits)}`, preset.key));
    }
    ctSelect.append(new Option('Other…', OTHER));
    /**
     * Which choice a STORED thickness reads as. A figure that matches no
     * preset shows as Other with the figure still in the box, rather than
     * being rounded to the nearest material or silently cleared — a sketch
     * saved before the presets existed keeps exactly the number it had.
     */
    const stored = model.state.countertopThicknessUnits;
    ctSelect.value = countertopChoiceForThickness(stored, rules);
    ctInput.value = stored > 0 ? fmt(stored) : '';
    const showOther = () => { ctInput.hidden = ctSelect.value !== OTHER; };
    showOther();
    ctSelect.addEventListener('change', () => {
      showOther();
      if (ctSelect.value === OTHER) { ctInput.focus(); return; } // the figure is the input's to give
      const preset = presets.find((p) => p.key === ctSelect.value);
      const units = preset ? fmt(preset.thicknessUnits) : '';
      if (model.setCountertopThickness(units)) { ctInput.value = units; repaintSections(); }
    });
    ctInput.addEventListener('change', () => {
      if (model.setCountertopThickness(ctInput.value)) repaintSections();
      else ctInput.value = model.state.countertopThicknessUnits > 0 ? fmt(model.state.countertopThicknessUnits) : '';
    });
    ct.append(ctTag, ctSelect, ctInput);
    box.append(ct);

    // "To target height" (ADR 0041, revised 2026-07-29): stacks run to a
    // designer-set datum less the crown allowance, so the TOP unit of each stack
    // autosizes to fill. It was tied to the ceiling height; with walls of
    // differing heights there is no single ceiling to derive it from.
    const ceil = document.createElement('div');
    ceil.className = 'to-ceiling';
    const ceilLabel = document.createElement('label');
    ceilLabel.className = 'to-ceiling__toggle';
    ceilLabel.title = 'Design to a target height: each stack tops out at the target less the crown allowance, and the top unit autosizes to fill. Typically the shortest wall’s height.';
    const ceilBox = document.createElement('input');
    ceilBox.type = 'checkbox';
    ceilBox.checked = model.state.toTargetHeight === true;
    ceilBox.addEventListener('change', () => {
      model.setToTargetHeight(ceilBox.checked);
      renderSpaceHeightControl();
      repaintSections();
    });
    const ceilText = document.createElement('span');
    ceilText.textContent = 'To target height';
    ceilLabel.append(ceilBox, ceilText);
    ceil.append(ceilLabel);
    if (model.state.toTargetHeight === true) {
      const target = document.createElement('label');
      target.className = 'space-height__field';
      target.title = 'The height stacks run to — typically the shortest wall’s height. Blank follows the room’s ceiling height.';
      const targetTag = document.createElement('span');
      targetTag.textContent = 'Target';
      const targetInput = document.createElement('input');
      targetInput.inputMode = 'text';
    targetInput.dataset.pad = 'length';
      targetInput.placeholder = `ceiling (${fmt(model.state.ceilingHeightUnits)})`;
      targetInput.value = model.state.stackTargetHeightUnits > 0 ? fmt(model.state.stackTargetHeightUnits) : '';
      const commitTarget = () => {
        if (model.setStackTargetHeight(targetInput.value)) { renderSpaceHeightControl(); repaintSections(); }
        else targetInput.value = model.state.stackTargetHeightUnits > 0 ? fmt(model.state.stackTargetHeightUnits) : '';
      };
      targetInput.addEventListener('change', commitTarget);
      targetInput.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') { commitTarget(); targetInput.blur(); }
      });
      target.append(targetTag, targetInput);
      ceil.append(target);
      const crown = document.createElement('label');
      crown.className = 'space-height__field';
      crown.title = 'Crown-molding allowance below the target. Units top out at target − this.';
      const crownTag = document.createElement('span');
      crownTag.textContent = 'Crown';
      const crownInput = document.createElement('input');
      crownInput.inputMode = 'text';
      crownInput.dataset.pad = 'length';
      crownInput.placeholder = fmt(rules.crownAllowanceUnits ?? 3);
      crownInput.value = model.state.crownAllowanceUnits > 0 ? fmt(model.state.crownAllowanceUnits) : '';
      crownInput.addEventListener('change', () => {
        if (model.setCrownAllowance(crownInput.value)) { renderSpaceHeightControl(); repaintSections(); }
        else crownInput.value = model.state.crownAllowanceUnits > 0 ? fmt(model.state.crownAllowanceUnits) : '';
      });
      crown.append(crownTag, crownInput);
      ceil.append(crown);
      const note = document.createElement('p');
      note.className = 'to-ceiling__note';
      const top = model.stackTopUnits();
      const overrides = Object.keys(model.state.wallStackTargets ?? {}).length;
      note.textContent = top > 0
        ? `Stacks top out at ${fmt(top)} (target ${fmt(model.stackTargetHeightUnits())} − crown ${fmt(model.crownAllowanceUnits())})`
          + (overrides
            ? `, except ${overrides === 1 ? '1 wall with its own target' : `${overrides} walls with their own targets`}.`
            : ' on every wall.')
        : 'Set a target height to use this.';
      ceil.append(note);
    }
    box.append(ceil);

    // TRIM (owner, 2026-08-10): "We need to have the ability to add crown and
    // base." Two independent on/off toggles, priced per linear foot off the
    // real section widths — so unlike the old Ballpark there is no section
    // count to type. Named "Crown moulding" so it cannot be confused with the
    // Crown ALLOWANCE above, which is a height, not a cost.
    const trim = document.createElement('div');
    trim.className = 'to-ceiling';
    // Each toggle carries a HEIGHT field (owner, 2026-08-28: defaults "may
    // vary/need to be overridden on occasion") — blank follows the admin
    // rule, shown as the placeholder; a value overrides it for this space.
    const trimToggle = (label, on, title, apply, height) => {
      const lab = document.createElement('label');
      lab.className = 'to-ceiling__toggle';
      lab.title = title;
      const check = document.createElement('input');
      check.type = 'checkbox';
      check.checked = on;
      check.addEventListener('change', () => { apply(check.checked); repaintSections(); });
      const txt = document.createElement('span');
      txt.textContent = label;
      lab.append(check, txt);
      if (!height) return lab;
      const field = document.createElement('label');
      field.className = 'space-height__field';
      field.title = `${label} height. Blank = the shop default (${fmt(height.defaultUnits)}).`;
      const tag = document.createElement('span');
      tag.textContent = 'H';
      const input = document.createElement('input');
      input.inputMode = 'text';
      input.dataset.pad = 'length';
      input.placeholder = fmt(height.defaultUnits);
      input.value = height.overrideUnits > 0 ? fmt(height.overrideUnits) : '';
      input.addEventListener('change', () => {
        if (height.apply(input.value)) repaintSections();
        else input.value = height.overrideUnits > 0 ? fmt(height.overrideUnits) : '';
      });
      field.append(tag, input);
      const row = document.createElement('div');
      row.className = 'to-ceiling__row';
      row.append(lab, field);
      return row;
    };
    trim.append(trimToggle(
      'Crown moulding',
      model.state.crownEnabled === true,
      `Adds crown at $${rules.crownPricePerLinearFoot}/linear foot. Base cabinets are excluded.`,
      (on) => model.setCrownEnabled(on),
      {
        defaultUnits: rules.crownMouldingHeightUnits,
        overrideUnits: model.state.crownMouldingHeightUnits,
        apply: (v) => model.setCrownMouldingHeight(v),
      },
    ));
    trim.append(trimToggle(
      'Base trim',
      model.state.baseTrimEnabled === true,
      `Adds base trim at $${rules.baseTrimPricePerLinearFoot}/linear foot. Uppers and sections with their bottom shelf switched off are excluded.`,
      (on) => model.setBaseTrimEnabled(on),
      {
        defaultUnits: rules.baseTrimHeightUnits,
        overrideUnits: model.state.baseTrimHeightUnits,
        apply: (v) => model.setBaseTrimHeight(v),
      },
    ));
    box.append(trim);
  }

  /**
   * A unit STACKED on another (ADR 0041): it rests on the unit below plus the
   * join gap, spans that unit's width, and shows its SOLVED elevation (bottom)
   * and height — both derived, so they're read-only here. Height is editable
   * only when it isn't autosized by "to the ceiling".
   */
  function stackedRow(unit, placed) {
    const row = document.createElement('div');
    row.className = 'section-row section-row--stacked';
    const head = document.createElement('div');
    head.className = 'section-row__head';

    const rail = document.createElement('span');
    rail.className = 'section-row__stack-rail';
    rail.textContent = '⌃';
    rail.title = 'Stacked on the unit below';

    const code = document.createElement('span');
    code.className = 'section-row__code';
    // The elevation's own caption for this unit (ADR 0066 item 6).
    code.textContent = sectionCodeLabel(unit.typeCode, placed?.depthUnits ?? unit.depthUnits, fmt);

    const where = document.createElement('span');
    where.className = 'section-row__stack-at';
    where.textContent = placed
      ? `${fmt(placed.bottomUnits)} → ${fmt(placed.topUnits)}`
      : '—';
    where.title = 'Solved elevation: bottom → top, above the unit it rests on';

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'section-row__delete';
    del.textContent = '×';
    del.title = 'Remove this stacked unit';
    del.addEventListener('click', () => {
      if (model.unstackUnit(unit.id)) repaintSections();
    });

    head.append(rail, code, where, del);
    row.append(head);

    // Height: pinned per unit, or autosized when "to the ceiling" makes this
    // the top of its stack.
    const dims = document.createElement('div');
    dims.className = 'section-row__dims';
    // `autosized` comes from the SOLVER, not from the mode: only the unit whose
    // top is chasing a target has its height computed. In a three-tier stack the
    // middle unit keeps its own editable height unless it carries its own "to
    // height" — greying it because the mode was on was simply wrong.
    const autosized = placed?.autosized === true;
    const h = dimInput('H', unit.heightUnits, 'Height of this stacked unit; blank inherits the type default', (v) => {
      if (model.setSectionDim(unit.id, 'heightUnits', v)) repaintSections();
    }, fmtSystem);
    if (autosized) {
      const input = h.querySelector('input');
      if (input) {
        input.value = placed ? fmt(placed.heightUnits) : '';
        input.classList.add('is-auto');
        input.title = unit.targetTopUnits > 0
          ? 'Solved from this unit’s "to height" — clear that to pin the height instead'
          : 'Autosized to the stack target — turn off "To target height" to pin it';
      }
    }
    dims.append(h);
    // "To height": the elevation this unit's TOP runs to (owner, 2026-08-04).
    // What a designer measures off the floor with a tape, and the only way to
    // fix a MIDDLE unit's top — the stack target can only reach the topmost one.
    const toHeight = dimInput('To ht', unit.targetTopUnits,
      'Top of this unit, measured from the floor. Its height is solved to reach it; blank uses the height instead.',
      (v) => { if (model.setStackTargetTop(unit.id, v)) repaintSections(); });
    dims.append(toHeight);
    // Over a BASE a unit carries its own width/depth and the designer positions
    // it along that base; above a non-base it inherits both, so those fields are
    // shown read-only-ish (inherited values, greyed) — ADR 0041.
    const free = model.stackedDimsAreFree(unit);
    const d = dimInput('D', placed?.depthUnits ?? unit.depthUnits, free
      ? 'Depth of this unit — independent because it sits on a base'
      : 'Depth is inherited from the unit below (only a unit on a BASE may differ)', (v) => {
      if (model.setSectionDim(unit.id, 'depthUnits', v)) repaintSections();
    });
    if (!free) {
      const di = d.querySelector('input');
      if (di) { di.disabled = true; di.classList.add('is-auto'); }
    }
    dims.append(d);
    if (free) {
      const w = dimInput('W', placed?.widthUnits ?? unit.widthUnits, 'Width of this unit — may be narrower than the base below it', (v) => {
        if (model.setSectionWidth(unit.id, v)) repaintSections();
      });
      if (unit.widthUnits == null) w.querySelector('input')?.classList.add('is-auto');
      const off = dimInput('Pos', unit.stackOffsetUnits, 'Position along the unit below — distance from its LEFT edge. 0 = flush left.', (v) => {
        if (model.setStackOffset(unit.id, v)) repaintSections();
      });
      dims.append(w, off);
    }
    // A stacked unit's interior is composable too (ADR 0056) — same editor.
    dims.append(interiorButton(unit.id));
    if (placed?.overflow) {
      dims.append(sectionFlag(unit.targetTopUnits > 0
        ? 'Its "to height" is below where this unit already rests.'
        : 'Does not fit below the ceiling line.'));
    }
    row.append(dims);
    // A stacked unit gets the same inline edits as a run section (ADR 0066) —
    // it is a section with a placed unit, which is all the controls need. A 24"
    // vanity drawer bank stacked on a base is exactly the case the owner
    // described, so putting the friction back here would miss it.
    const inlineBox = interiorInlineControls(interiorInlineState(unit.id, placed ?? null));
    if (inlineBox) row.append(inlineBox);
    const healed = inlineHealNotes.get(unit.id);
    if (healed) {
      row.append(sectionFlag(healed.join(' '), 'OK', () => {
        inlineHealNotes.delete(unit.id);
        renderSectionsPanel();
      }));
    }
    return row;
  }

  /**
   * What the row's inline interior controls need to draw themselves: the same
   * healed draft the modal would open, the targets each control addresses, and
   * the door depth verdict (ADR 0066).
   *
   * The draft built here is for DISPLAY only and is thrown away — every edit
   * builds its own through editSectionInline(), so no control can write through
   * a draft that went stale between paint and tap.
   */
  function interiorInlineState(sectionId, unit) {
    const section = model.state.sections.find((s) => s.id === sectionId);
    if (!section) return null;
    const draft = createInteriorDraft(model.sectionInterior(sectionId), section.doors, section.requiredHolesOnly === true);
    return {
      sectionId,
      draft,
      targets: inlineInteriorTargets(draft, unit, rules),
      depth: doorDepthState(unit?.depthUnits ?? 0, rules, { hanging: draftHasHang(draft) }),
      coverage: draft.doors?.fromElementId ? 'all' : (draft.doors?.coverage ?? null),
    };
  }

  /**
   * The ROUTINE interior edits, on the row (ADR 0066 items 1–4): doors, shelf
   * count, drawer count and per-drawer face heights — the four the owner named
   * as what they actually change. "Interior…" stays beside them for everything
   * else, and the modal is untouched.
   *
   * Every control commits through editSectionInline(), i.e. through the modal's
   * own draft helpers and the modal's own commit. Nothing here knows how a
   * shelf is spaced or where a board goes.
   *
   * @returns {HTMLElement|null} null when the section offers no inline edit at
   *   all (no doors judgeable and no unambiguous count target).
   */
  function interiorInlineControls(state) {
    if (!state) return null;
    const { sectionId, targets, depth, coverage } = state;
    const box = document.createElement('div');
    box.className = 'section-row__inline';

    // ---- DOORS (ADR 0060 item 1 as amended: 24" with a rod anywhere, else 4")
    const doors = document.createElement('label');
    doors.className = 'section-row__inline-field';
    const doorsTag = document.createElement('span');
    doorsTag.textContent = 'Doors';
    const doorsPick = document.createElement('select');
    doorsPick.className = 'section-row__inline-select';
    doorsPick.setAttribute('aria-label', 'Door coverage for this section');
    for (const choice of DOOR_COVERAGE_CHOICES) {
      const opt = document.createElement('option');
      opt.value = choice.value ?? '';
      opt.textContent = choice.label;
      opt.selected = (choice.value ?? null) === coverage;
      // BLOCKED coverages are DISABLED, not hidden, and carry the modal's own
      // sentence (doorDepthTitle) — the designer must be able to see that doors
      // are possible here and why this section cannot have them. "None" always
      // stays live, so doors already on a too-shallow section can be cleared.
      if (choice.value !== null && depth.blocked) {
        opt.disabled = true;
        opt.textContent = `${choice.label} — ${doorDepthTitle(depth, fmt)}`;
      }
      doorsPick.append(opt);
    }
    if (depth.blocked) doorsPick.title = doorDepthTitle(depth, fmt);
    doorsPick.addEventListener('change', () => {
      const value = doorsPick.value === '' ? null : doorsPick.value;
      if (!editSectionInline(sectionId, (draft) => applyInlineDoors(draft, value))) {
        doorsPick.value = coverage ?? '';
      }
    });
    doors.append(doorsTag, doorsPick);
    box.append(doors);
    if (depth.blocked) {
      // Said in plain text as well as on the disabled options, because on a
      // tablet an option's state is invisible until the select is opened.
      const why = document.createElement('span');
      why.className = 'section-row__inline-note';
      why.textContent = depth.hanging
        ? `${doorDepthTitle(depth, fmt)} — this section hangs.`
        : doorDepthTitle(depth, fmt);
      box.append(why);
    }

    // ---- BOTTOM SHELF (owner, 2026-08-10) ----------------------------------
    // "Need abilty to 'turn off' the bottom shelf of a section (which would
    // also exclude that section from autocalcs for base trim…no bottom shelf,
    // no base trim)." Its only effect today is that exclusion, so the control
    // says so rather than leaving the designer to infer it.
    {
      const section = model.state.sections.find((s) => s.id === sectionId);
      const lab = document.createElement('label');
      lab.className = 'section-row__inline-check';
      lab.title = 'Uncheck for a section with no bottom shelf. It is then excluded from the base-trim linear feet.';
      const check = document.createElement('input');
      check.type = 'checkbox';
      check.checked = section?.bottomShelf !== false;
      check.addEventListener('change', () => {
        if (model.setSectionBottomShelf(sectionId, check.checked)) repaintSections();
        else check.checked = section?.bottomShelf !== false;
      });
      const txt = document.createElement('span');
      txt.textContent = 'Bottom shelf';
      lab.append(check, txt);
      box.append(lab);
    }

    // ---- SHELF COUNT (a group, a RUN of real boards, or the one empty space) --
    // The run is now the commonest case: "Fill with → Shelves" makes discrete
    // pinned boards, not a group, so without it this field would find neither a
    // group nor a SOLE empty space and disappear after one use.
    if (targets.shelves) {
      const shelfTitle = {
        group: 'How many shelves, spaced evenly through this space',
        run: 'How many shelves in this section. Re-spaces them evenly — refused if any '
          + 'is locked or has been moved by hand, so nothing you placed is lost.',
        fill: 'Fill this section’s open space with this many evenly spaced shelves',
      }[targets.shelves.kind];
      box.append(inlineCountField(
        'Shv',
        targets.shelves.kind === 'fill' ? null : targets.shelves.count,
        shelfTitle,
        (n) => editSectionInline(sectionId, (draft, unit, t) => applyInlineShelfCount(draft, t.shelves, n, unit, rules)),
      ));
    }

    // ---- DRAWER COUNT (an existing bank, or the one empty space) -----------
    if (targets.drawers) {
      box.append(inlineCountField(
        'Drw',
        targets.drawers.kind === 'bank' ? targets.drawers.count : null,
        targets.drawers.kind === 'bank'
          ? 'How many drawers in this bank — the face heights you set are kept'
          : 'Fill this section’s open space with a bank of this many drawers',
        (n) => editSectionInline(sectionId, (draft, unit, t) => applyInlineDrawerCount(draft, t.drawers, n, unit, rules)),
      ));
    }

    // ---- DRAWER FACE HEIGHTS ----------------------------------------------
    // Collapsed behind a disclosure so a bank of four does not blow the row
    // open, but reachable WITHOUT the modal, which was the owner's condition.
    if (targets.drawers?.kind === 'bank' && targets.drawers.faces.length) {
      const faces = document.createElement('details');
      faces.className = 'section-row__faces';
      faces.open = openFaceRows.has(sectionId);
      faces.addEventListener('toggle', () => {
        if (faces.open) openFaceRows.add(sectionId);
        else openFaceRows.delete(sectionId);
      });
      const sum = document.createElement('summary');
      sum.className = 'section-row__faces-summary';
      const bank = drawerStackState(targets.drawers.faces.map((heightUnits) => ({ heightUnits })), rules);
      sum.textContent = `Faces · ${targets.drawers.faces.length} · bank ${fmt(bank.heightUnits)}`;
      sum.title = 'Face height of each drawer — the shop’s standard heights';
      faces.append(sum);
      const list = document.createElement('div');
      list.className = 'section-row__faces-list';
      // DRW 1 IS THE BOTTOM DRAWER (ADR 0060 item 6) — the same numbering the
      // modal uses, because it is the same bank.
      const n = targets.drawers.faces.length;
      targets.drawers.faces.forEach((heightUnits, k) => {
        list.append(editorDrawerHeightField(`Drw ${n - k}`, heightUnits, (units) => (
          editSectionInline(sectionId, (draft, _unit, t) => applyInlineDrawerFace(draft, t.drawers, k, units))
        ), () => {}));
      });
      faces.append(list);
      box.append(faces);
    }

    return box;
  }

  /** A compact whole-number field for an inline count. A null value shows blank
   *  — the control is offering to CREATE something, and "0" would read as a
   *  count that is already zero. */
  function inlineCountField(label, value, tip, apply) {
    const wrap = document.createElement('label');
    wrap.className = 'section-row__inline-field';
    wrap.title = tip;
    const tag = document.createElement('span');
    tag.textContent = label;
    const input = document.createElement('input');
    input.className = 'section-row__inline-count';
    input.inputMode = 'numeric';
    input.dataset.pad = 'count';
    input.value = Number.isInteger(value) ? String(value) : '';
    // FOCUS SELECTS (found in live testing, 2026-08-09): tapping "Drw 4" and
    // typing 5 produced FIFTY-FOUR drawers, because the caret landed before the
    // existing digit. These are small whole numbers a designer REPLACES, never
    // edits character by character, so the whole value is selected on focus and
    // the next digit typed is the new count.
    input.addEventListener('focus', () => input.select());
    input.addEventListener('change', () => {
      const n = Number.parseInt(input.value, 10);
      if (!Number.isInteger(n) || !apply(n)) input.value = Number.isInteger(value) ? String(value) : '';
    });
    // Enter commits, as it does in the modal's own fields (editorLengthField).
    // Found in live testing: without it the count only landed on tapping away,
    // which is fine on a tablet and baffling with a keyboard.
    input.addEventListener('keydown', (evt) => {
      if (evt.key === 'Enter') input.blur();
    });
    wrap.append(tag, input);
    return wrap;
  }

  /** The compact per-row affordance that opens the interior editor (ADR 0056). */
  function interiorButton(sectionId) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'section-row__interior';
    btn.textContent = 'Interior…';
    btn.title = 'Edit what is inside this section — rods, shelves, drawers, shoe shelves, doors';
    btn.addEventListener('click', () => openInteriorEditor(sectionId));
    return btn;
  }

  /** The "stack a unit on this one" control under a section (ADR 0041). */
  function stackAdder(section) {
    const wrap = document.createElement('div');
    wrap.className = 'stack-add';
    const chain = model.stackChainFor(section.id);
    // A new unit always goes on TOP of whatever is already stacked here.
    const onId = chain.length ? chain[chain.length - 1].id : section.id;
    const select = document.createElement('select');
    select.className = 'stack-add__select';
    select.setAttribute('aria-label', 'Type to stack');
    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = '+ stack a unit…';
    select.append(blank);
    // The stack list is the owner's sentence (C1, 2026-08-29): shelving
    // sections only — L-shelves in, shoe shelves and bases out. The model's
    // predicate is the one truth; stackUnit refuses the same set, so the
    // filter here is presentation, not the guard.
    for (const preset of (rules.sectionTypePresets ?? []).filter((p) => model.isStackableType(p.code))) {
      const opt = document.createElement('option');
      opt.value = preset.code;
      opt.textContent = `${preset.code} — ${preset.name ?? ''}`.trim();
      select.append(opt);
    }
    select.addEventListener('change', () => {
      if (!select.value) return;
      if (model.stackUnit(onId, select.value)) repaintSections();
      select.value = '';
    });
    wrap.append(select);
    return wrap;
  }

  /** One section's row: reorder, identity, solved/locked width, lock, dims, the
   *  inline interior edits (ADR 0066), flags. */
  function sectionRow(s, item, index, count, cornerInfo = { start: false, end: false }, placedUnit = null) {
    const row = document.createElement('div');
    row.className = `section-row${item && (item.narrow || item.wide) ? ' section-row--warn' : ''}`;

    const head = document.createElement('div');
    head.className = 'section-row__head';

    // Reorder along the wall (owner, 2026-07-26): the list runs wall-start →
    // wall-end top to bottom, so ↑ moves the section toward the wall start.
    const mover = (dir, glyph, tip) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'section-row__move';
      btn.textContent = glyph;
      btn.title = tip;
      btn.disabled = dir < 0 ? index === 0 : index === count - 1;
      btn.addEventListener('click', () => {
        if (model.moveSection(s.id, dir)) repaintSections();
      });
      return btn;
    };
    const moves = document.createElement('span');
    moves.className = 'section-row__moves';
    moves.append(
      mover(-1, '▲', 'Move toward the wall start'),
      mover(1, '▼', 'Move toward the wall end'),
    );

    // IDENTITY (ADR 0066 item 6): the row captions itself with exactly what the
    // elevation draws — the code with its resolved DEPTH ("SV-16"") and the
    // solved opening WIDTH — so a row is recognisable against the drawing
    // instead of being matched by its position in a stack of near-identical
    // rows. One definition, in section-identity.js, read by both surfaces.
    const identity = sectionRowIdentity({
      typeCode: s.typeCode,
      depthUnits: placedUnit?.depthUnits ?? s.depthUnits ?? rules.sectionDefaultDepthUnits,
      widthUnits: item?.openingUnits,
    }, fmt);
    const code = document.createElement('span');
    code.className = 'section-row__code';
    code.textContent = identity.code;
    code.title = `${identity.label} — the same caption this section carries on the elevation`;

    const preset = (rules.sectionTypePresets ?? []).find((p) => String(p.code).toUpperCase() === String(s.typeCode).toUpperCase());
    const setSizes = Array.isArray(preset?.standardWidths) ? preset.standardWidths.filter((w) => w > 0) : [];

    let width;
    if (setSizes.length) {
      // Set-size types (hampers): pick from the type's standard widths
      // instead of typing freely (ADR 0039).
      width = document.createElement('select');
      width.className = 'section-row__width';
      width.title = `${s.typeCode} comes in set sizes`;
      const current = s.locked && Number.isFinite(s.widthUnits) ? s.widthUnits : null;
      const sizes = current !== null && !setSizes.some((v) => Math.abs(v - current) < 1e-9) ? [...setSizes, current] : setSizes;
      for (const v of sizes.slice().sort((a, b) => a - b)) {
        const opt = document.createElement('option');
        opt.value = String(v);
        opt.textContent = fmt(v);
        opt.selected = current !== null && Math.abs(v - current) < 1e-9;
        width.append(opt);
      }
      width.addEventListener('change', () => {
        if (model.setSectionWidth(s.id, width.value) === true) repaintSections();
      });
    } else {
      width = document.createElement('input');
      width.className = `section-row__width${s.locked ? '' : ' is-auto'}`;
      width.inputMode = 'text';
      width.dataset.pad = 'length';
      width.value = item ? fmt(item.openingUnits) : '';
      width.title = s.locked ? 'Locked width — clear the lock to auto-fill' : 'Auto-filled — type a width to lock it';
      width.addEventListener('change', () => {
        const result = model.setSectionWidth(s.id, width.value);
        if (result === 'needs-override') {
          pendingWideWidths.set(s.id, width.value);
          renderSectionsPanel(); // show the Allow button; nothing changed yet
        } else if (result) {
          pendingWideWidths.delete(s.id);
          repaintSections();
        } else {
          width.value = item ? fmt(item.openingUnits) : '';
        }
      });
    }

    const lock = document.createElement('button');
    lock.type = 'button';
    lock.className = `section-row__lock${s.locked ? ' is-active' : ''}`;
    lock.textContent = s.locked ? '🔒' : '🔓';
    lock.title = s.locked ? 'Unlock — return to auto-fill' : 'Lock at the current width';
    lock.addEventListener('click', () => {
      if (s.locked) {
        if (model.unlockSection(s.id)) repaintSections();
      } else if (item && model.setSectionWidth(s.id, String(item.openingUnits)) === true) {
        repaintSections();
      }
    });

    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'section-row__delete';
    del.textContent = '×';
    del.title = 'Remove this section';
    del.addEventListener('click', () => {
      pendingWideWidths.delete(s.id);
      if (model.removeSection(s.id)) repaintSections();
    });

    const saveComponent = document.createElement('button');
    saveComponent.type = 'button';
    saveComponent.className = 'section-row__save';
    saveComponent.textContent = '⧉';
    saveComponent.title = 'Save a reusable copy of this configured section';
    saveComponent.setAttribute('aria-label', 'Save reusable section');
    saveComponent.addEventListener('click', () => saveReusableSection(s));

    head.append(moves, code, width, lock, saveComponent, del);
    row.append(head);

    const dims = document.createElement('div');
    dims.className = 'section-row__dims';
    // A section height is a PANEL height: it snaps to a legal System-32 panel
    // on edit and is SHOWN rounded to 1/4" (ADR 0061), so a designer types 84
    // and reads 84 back even though the panel is really 83.898".
    // HEIGHT (RS-2a). The owner: "there is no way to adjust/change the height of
    // an existing section". There always was — this field is editable — but the
    // only thing saying so was a hover `title`, and the tablet this is used on
    // has no hover. Grey italic said "inherited" to whoever already knew that
    // is what grey italic means, and said nothing about inherited FROM WHAT.
    //
    // So the row now states its own state in words: where the figure comes from
    // when it is inherited, and a Reset when it is pinned. Nothing about the
    // inheritance chain or the 32 mm snapping changes — `setSectionDim` and
    // `fmtSystem` are untouched; this is the same capability, said out loud.
    const height = model.sectionHeightSource(s);
    const heightFrom = {
      space: 'inherits the space height',
      type: `inherits the ${s.typeCode} type default`,
      default: 'inherits the shop default',
      // Not an inheritance at all — a product dimension. The owner ruled a
      // wall bed's height "should not be overridden", so `setSectionDim`
      // refuses the write. Saying "inherits the type default" here would
      // invite an edit that is then silently rejected, which is worse than
      // the unlabelled field RS-2a replaced.
      'type-fixed': `fixed by the ${s.typeCode} type — this size is the product`,
    }[height.source];
    const hField = dimInput(
      'H', height.units,
      'Height on the elevation — blank returns to the space/type default',
      (v) => model.setSectionDim(s.id, 'heightUnits', v),
      fmtSystem,
      heightFrom
        ? { note: heightFrom }
        : {
          note: 'set for this section',
          action: {
            label: 'Reset',
            // Blank IS the reset — the model reads null as "inherit". The
            // button exists because nothing on screen said that.
            onClick: () => { if (model.setSectionDim(s.id, 'heightUnits', '')) repaintSections(); },
          },
        },
    );
    if (s.heightUnits == null) hField.querySelector('input')?.classList.add('is-auto'); // inheriting, not pinned
    if (height.source === 'type-fixed') {
      // Disabled, not merely captioned: the model refuses the write, so an
      // editable box would let a designer type a number, watch it revert, and
      // learn nothing about why.
      const input = hField.querySelector('input');
      if (input) { input.disabled = true; input.title = 'This type’s height is fixed and cannot be changed.'; }
    }
    dims.append(
      dimInput('D', s.depthUnits ?? rules.sectionDefaultDepthUnits, 'Depth on the plan', (v) => model.setSectionDim(s.id, 'depthUnits', v)),
      hField,
    );
    // Shelf/drawer counts appear when the type seeded them (ADR 0039) — but
    // ONLY where the inline control below does not already own that count (ADR
    // 0066). Two "Shv" fields on one row that write through different helpers
    // is precisely the confusion this pass exists to remove, and the legacy
    // field's applyCountsToInterior is blind to a shelfGroup, so on a
    // group-bearing section it was the wrong one of the two.
    const inline = interiorInlineState(s.id, placedUnit);
    if (Number.isInteger(s.shelfCount) && !inline?.targets.shelves) {
      // SECTIONS are the designer's unit for PLAIN SHELVING (owner,
      // 2026-08-29): "convention is 1 shelf would refer to the shelf
      // somewhere vertically in an otherwise empty section, creating two
      // shelving sections." The control speaks sections; the stored mirror
      // stays PHYSICAL shelves (sections − 1) — the storage-summary math
      // reads shelves, and converting only at this edge means no stored
      // count changes meaning under anyone's feet. SHOE shelves are exempt:
      // their count IS the physical shelf count (the pairs-per-shelf math),
      // caught live when the SS row displayed 8 for 7 shoe shelves.
      const shoeSection = (model.presetFlagsFor?.(s.typeCode) ?? []).includes('shoes');
      if (shoeSection) {
        dims.append(countInput('Shv', s.shelfCount, 'Number of shoe shelves — pairs-per-shelf capacity reads this directly', (v) => model.setSectionCount(s.id, 'shelfCount', v)));
      } else {
        dims.append(countInput('Sec', s.shelfCount + 1, 'Number of shelving sections — the shelves between them fall out of the count', (v) => Number.isInteger(v) && v >= 1 && model.setSectionCount(s.id, 'shelfCount', v - 1)));
      }
    }
    if (Number.isInteger(s.drawerCount) && !inline?.targets.drawers) {
      dims.append(countInput('Drw', s.drawerCount, 'Number of drawers', (v) => model.setSectionCount(s.id, 'drawerCount', v)));
    }
    // The interior editor (ADR 0056): compose what's INSIDE this section.
    dims.append(interiorButton(s.id));
    // Shoes sections: the shelf width one pair occupies here (her 8" pairs vs
    // his 9½", boots vs heels) — drives this section's pairs in the summary.
    // Shown when the COMPOSITION holds shoe shelves (ADR 0057) — a designer
    // who adds shoe shelves to any section gets the pair width with them;
    // sections without a stored width fall back to the rule.
    if (model.sectionHasShoeShelves(s)) {
      const pw = s.pairWidthUnits > 0 ? s.pairWidthUnits : rules.shoePairWidthUnits;
      const pairWidth = dimInput('Pr', pw, 'Width one pair of shoes occupies on these shelves', (v) => model.setSectionDim(s.id, 'pairWidthUnits', v));
      const pairWidthInput = pairWidth.querySelector('input');
      if (pairWidthInput) makeCaptureTarget(pairWidthInput, {
        kind: 'section_field', entityId: s.id, property: 'pairWidthUnits', label: `${sectionCodeLabel(s.typeCode, s.depthUnits, fmt)} shoe pair width`,
      });
      dims.append(pairWidth);
    }
    // An L-SHELF spans a corner (ADR 0042): the designer picks the SECOND wall
    // it attaches to, then sizes each leg independently (length ≤ 36" and its
    // own depth). The leg in this run is the section's own width/depth.
    if (model.isLShelf(s)) {
      const pick = document.createElement('select');
      pick.className = 'section-row__lwall';
      pick.title = 'The second wall this L-shelf attaches to. Only walls sharing a corner with this one are offered.';
      const none = document.createElement('option');
      none.value = '';
      none.textContent = 'attach wall…';
      pick.append(none);
      for (const w of model.lShelfCandidateWalls(s)) {
        const opt = document.createElement('option');
        opt.value = w.id;
        opt.textContent = w.label;
        pick.append(opt);
      }
      pick.value = s.secondWallId ?? '';
      pick.addEventListener('change', () => {
        if (model.setLShelfSecondWall(s.id, pick.value || null)) repaintSections();
        else { pick.value = s.secondWallId ?? ''; pick.classList.add('is-invalid'); }
      });
      dims.append(pick);
      if (s.secondWallId) {
        const legLen = dimInput('Leg', s.legTwoUnits, `Length of the leg along the attached wall (max ${fmt(model.L_SHELF_MAX_LEG_UNITS)})`, (v) => {
          if (model.setLShelfLeg(s.id, 'second', 'length', v)) repaintSections();
        });
        const legDepth = dimInput('LegD', s.legTwoDepthUnits ?? rules.sectionDefaultDepthUnits, 'Depth of that leg — independent of this one', (v) => {
          if (model.setLShelfLeg(s.id, 'second', 'depth', v)) repaintSections();
        });
        if (s.legTwoDepthUnits == null) legDepth.querySelector('input')?.classList.add('is-auto');
        dims.append(legLen, legDepth);
      }
    }

    // A BASE (countertop-anchored) section carries the gap a stacked unit sits
    // above it — its own override, else the space setting (ADR 0041).
    if (model.sectionAnchor(s) === 'countertop') {
      const ctField = dimInput(
        'Ctop',
        s.countertopThicknessUnits > 0 ? s.countertopThicknessUnits : model.state.countertopThicknessUnits,
        'Countertop thickness above this base — blank follows the space setting',
        (v) => { if (model.setSectionCountertop(s.id, v)) repaintSections(); },
      );
      if (s.countertopThicknessUnits == null) ctField.querySelector('input')?.classList.add('is-auto');
      dims.append(ctField);
    }
    // Shared space: assign this section to a person (or neither). Only shown
    // when the split is on — assignment means nothing in a single space.
    if (model.state.sharedSpace.enabled) {
      const assign = document.createElement('span');
      assign.className = 'section-row__assign';
      const options = [...model.state.sharedSpace.persons.map((p, i) => ({ label: p, value: i })), { label: 'U', value: null }];
      for (const opt of options) {
        const btn = document.createElement('button');
        btn.type = 'button';
        const active = (s.assignedTo ?? null) === opt.value;
        btn.className = `section-row__assign-btn${active ? ' is-active' : ''}`;
        // Up to 4 characters of an optional name; A/B remain the neutral
        // defaults and an initial alone may be ambiguous.
        btn.textContent = opt.value === null ? 'U' : opt.label.trim().slice(0, 4) || '?';
        btn.title = opt.value === null ? 'Unassigned (counts toward the total only)' : `Assign to ${opt.label}`;
        btn.addEventListener('click', () => {
          if (model.assignSection(s.id, opt.value)) {
            renderSectionsPanel();
            renderStorageSummary();
            scheduleSave();
          }
        });
        assign.append(btn);
      }
      dims.append(assign);
    }
    row.append(dims);

    // The ROUTINE interior edits, on the row (ADR 0066): doors, shelf count,
    // drawer count, drawer face heights. "Interior…" above stays for the rest.
    const inlineBox = interiorInlineControls(inline);
    if (inlineBox) row.append(inlineBox);
    // A row edit heals a legacy composition on the way through (ADR 0065 part
    // 1). The modal shows that note before Apply; here the edit has already
    // landed, so it is reported after the fact and dismissed by hand.
    const healed = inlineHealNotes.get(s.id);
    if (healed) {
      row.append(sectionFlag(healed.join(' '), 'OK', () => {
        inlineHealNotes.delete(s.id);
        renderSectionsPanel();
      }));
    }

    // Flags: the pending >max confirmation, or the narrow/wide warnings.
    const pending = pendingWideWidths.get(s.id);
    if (pending !== undefined) {
      row.append(sectionFlag(`${pending}″ is over the ${fmt(rules.sectionMaxOpeningUnits)} max —`, 'Allow', () => {
        model.setSectionAllowWide(s.id, true);
        const result = model.setSectionWidth(s.id, pending);
        pendingWideWidths.delete(s.id);
        if (result !== true) model.setSectionAllowWide(s.id, false); // parse failed; undo
        repaintSections();
      }));
    } else if (item?.narrow) {
      const typeMin = preset?.minWidthUnits > 0 ? preset.minWidthUnits : rules.sectionMinOpeningUnits;
      row.append(sectionFlag(`Under the ${fmt(typeMin)} minimum for ${s.typeCode} — consider removing a section.`));
    } else if (item?.wide) {
      row.append(sectionFlag(`Over the ${fmt(rules.sectionMaxOpeningUnits)} max —`, 'Allow', () => {
        if (model.setSectionAllowWide(s.id, true)) repaintSections();
      }));
    } else if (s.allowWide) {
      row.append(sectionFlag('Wide override on —', 'Revoke', () => {
        if (model.setSectionAllowWide(s.id, false)) repaintSections();
      }));
    }

    // Hanging-into-hanging (owner, 2026-07-27): a hanging section at a corner
    // with an active clearance may EXTEND into it, accepting the collision
    // with the perpendicular hang. Allowed, not advised — warned while on.
    // A section can touch two corners. Treat each independently: the former
    // section-wide switch made extending one end silently change the other.
    for (const end of ['start', 'end']) {
      const atEnd = end === 'start' ? index === 0 : index === count - 1;
      if (!atEnd) continue;
      if (model.sectionIntoHangingAt(s, end)) {
        row.append(sectionFlag(`Hanging into hanging at the ${end} corner — not advised.`, 'Revoke', () => {
          if (model.setSectionIntoHanging(s.id, end, false)) repaintSections();
        }));
      } else if (model.sectionHasHang(s) && cornerInfo[end]) {
        row.append(sectionFlag(`${fmt(rules.sectionHangClearanceUnits)} hang clearance at the ${end} corner —`, 'Extend into it', () => {
          if (model.setSectionIntoHanging(s.id, end, true)) repaintSections();
        }));
      }
    }

    // A connecting-shelf gap is its own configurable opening. Removing either
    // board never collapses the reserved width; it remains OPEN in plan.
    const connectingShelves = model.connectingShelvesForWall(s.wallId)
      .filter((shelf) => shelf.sectionId === s.id
        && ((index === 0 && shelf.end === 'start') || (index === count - 1 && shelf.end === 'end')));
    for (const connecting of connectingShelves) {
      const details = document.createElement('details');
      details.className = 'section-row__connecting';
      const summary = document.createElement('summary');
      summary.textContent = `Connecting shelf · ${connecting.end} corner · ${fmt(connecting.widthUnits)}`;
      details.append(summary);
      const checks = document.createElement('div');
      checks.className = 'section-row__connecting-controls';
      for (const [key, label] of [['top', 'Top shelf'], ['bottom', 'Bottom shelf']]) {
        const lab = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = key === 'top' ? connecting.topShelf !== false : connecting.bottomShelf !== false;
        input.addEventListener('change', () => {
          if (model.setConnectingShelf(s.id, connecting.end, { [key]: input.checked })) repaintSections();
        });
        lab.append(input, document.createTextNode(label));
        checks.append(lab);
      }
      const depth = dimInput(
        'Depth', connecting.depthOverrideUnits,
        'Connecting shelf depth. Blank restores the adjoining section depth.',
        (raw) => {
          const value = String(raw).trim() === '' ? null : parseLength(raw);
          if (value === null && String(raw).trim() !== '') return false;
          return model.setConnectingShelf(s.id, connecting.end, { depthUnits: value });
        },
      );
      const input = depth.querySelector('input');
      if (input) {
        input.value = connecting.depthOverrideUnits > 0 ? fmt(connecting.depthOverrideUnits) : '';
        input.placeholder = `inherit ${fmt(connecting.depthUnits)}`;
      }
      checks.append(depth);
      details.append(checks);
      row.append(details);
    }
    return row;
  }

  /** A labelled mini length input for a section dimension. */
  /**
   * A labelled length input for a section dimension.
   *
   * `tip` is a hover title and therefore INVISIBLE ON A TABLET, which is the
   * device this is used on (RS-2a). It is kept for pointer users, but anything
   * a designer must know to operate the control belongs in `opts.note` — real
   * text, on screen — not in it.
   *
   * @param {object} [opts]
   * @param {string} [opts.note] a line rendered UNDER the field, always visible.
   * @param {{label:string, onClick:Function}} [opts.action] a control beside the
   *   note — used for "Reset", so returning to an inherited value is a button
   *   rather than the folk knowledge that clearing the box does it.
   */
  function dimInput(label, value, tip, apply, format = fmt, opts = {}) {
    const wrap = document.createElement('label');
    wrap.className = 'section-row__dim';
    wrap.title = tip;
    const tag = document.createElement('span');
    tag.textContent = label;
    const input = document.createElement('input');
    input.inputMode = 'text';
    input.dataset.pad = 'length';
    input.value = format(value);
    input.addEventListener('change', () => {
      if (apply(input.value)) repaintSections();
      else input.value = format(value);
    });
    wrap.append(tag, input);
    if (opts.note || opts.action) {
      const foot = document.createElement('span');
      foot.className = 'section-row__dim-note';
      if (opts.note) {
        const text = document.createElement('span');
        text.textContent = opts.note;
        foot.append(text);
      }
      if (opts.action) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'section-row__dim-reset';
        btn.textContent = opts.action.label;
        // Inside a <label>, so stop the click reaching the input it labels.
        btn.addEventListener('click', (evt) => { evt.preventDefault(); opts.action.onClick(); });
        foot.append(btn);
      }
      wrap.append(foot);
    }
    return wrap;
  }

  /** A labelled integer input for a section's shelf/drawer count. */
  function countInput(label, value, tip, apply) {
    const wrap = document.createElement('label');
    wrap.className = 'section-row__dim';
    wrap.title = tip;
    const tag = document.createElement('span');
    tag.textContent = label;
    const input = document.createElement('input');
    input.inputMode = 'numeric';
    input.value = String(value);
    input.addEventListener('change', () => {
      if (apply(Number.parseInt(input.value, 10))) repaintSections();
      else input.value = String(value);
    });
    wrap.append(tag, input);
    return wrap;
  }

  /** A warning/confirm line under a section row, optionally with a button. */
  function sectionFlag(text, buttonLabel, onClick) {
    const flag = document.createElement('div');
    flag.className = 'section-row__flag';
    const span = document.createElement('span');
    span.textContent = text;
    flag.append(span);
    if (buttonLabel) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = buttonLabel;
      btn.addEventListener('click', onClick);
      flag.append(btn);
    }
    return flag;
  }

  /**
   * "Make Wall 1" pins the selected wall as Wall 1; once the selection IS the
   * pinned Wall 1 the same button reads "Unpin Wall 1" and reverts to the
   * automatic rule. Openings can never be Wall 1, so a selected door disables
   * it. Mirrors the door button's context-aware pattern.
   */
  function renderWallOneButton() {
    const btn = root.getElementById('action-wall-one');
    if (!btn) return;
    const selected = model.selectedSegment();
    const isWall = selected && (selected.segmentKind ?? 'wall') === 'wall';
    const pinned = isWall && model.isWallOnePinned() && selected.id === model.state.wallOneOverrideId;
    setIconTip(btn, pinned ? 'Unpin Wall 1' : 'Make this Wall 1', pinned);
    btn.disabled = !isWall;
  }

  function renderStatus() {
    const { walls, closure } = model.state;
    const wallCount = walls.filter((w) => (w.segmentKind ?? 'wall') === 'wall').length;
    const openingCount = walls.length - wallCount;

    if (walls.length === 0) {
      status.textContent = 'Draw a room to start.';
      status.className = 'status';
      return;
    }

    const parts = [`${wallCount} wall${wallCount === 1 ? '' : 's'}`];
    if (openingCount) parts.push(`${openingCount} opening${openingCount === 1 ? '' : 's'}`);

    if (closure.solved && closure.openChain) {
      // A partial room (the owner's 3-wall runs): the ends were never drawn
      // together, so there is no gap to warn about — say what it is instead.
      parts.push('open run');
      status.className = 'status';
    } else if (closure.solved && !closure.closed) {
      const gap = closure.residualGapUnits.toFixed(2);
      parts.push(`⚠ gap ${gap} ${rules.defaultUnitOfMeasure}${closure.degenerate ? ' (walls can’t fully close it)' : ''}`);
      status.className = 'status status--warn';
    } else if (closure.solved) {
      parts.push('closed ✓');
      status.className = 'status status--ok';
    } else {
      status.className = 'status';
    }
    status.textContent = parts.join(' · ');
  }

  // ---- toolbar wiring ------------------------------------------------------

  // Secondary tools live in small Office-style disclosure groups so tablet
  // users keep drawing height. Only one group may cover the canvas at a time.
  // An open group stays open while its tools are used — including a pen picker
  // that floats outside the <details> element — and closes only through its own
  // summary or when another group opens (owner, 2026-08-18).
  const toolbarMenus = [...root.querySelectorAll('.toolbar-menu')];
  for (const menu of toolbarMenus) {
    menu.querySelector(':scope > summary')?.addEventListener('click', () => {
      for (const other of toolbarMenus) {
        if (other !== menu) other.removeAttribute('open');
      }
    });
    menu.addEventListener('toggle', () => {
      if (!menu.open) return;
      for (const other of toolbarMenus) {
        if (other !== menu) other.removeAttribute('open');
      }
    });
  }

  // ---- in-sketch Field Capture + legacy session review (ADR 0073) ----------
  // The active mode writes only to an explicitly armed visible sketch/input
  // target. Legacy sessions remain supporting audit/review data, not the
  // primary capture interaction.
  let fieldCaptureMode = createFieldCaptureMode();

  /**
   * Durability-on-capture (ruling 2026-08-29): the candidate list is the one
   * measurement state that existed nowhere but page memory — three readings
   * taken in a client's closet, gone with a killed tab. Persist it on every
   * DATA transition (capture/select/delete/apply); arm/disarm stay transient
   * because restore always comes back disarmed. Zero network cost: this is a
   * local meta write, unrelated to sync.
   */
  function persistCaptureCandidates() {
    Promise.resolve(setMeta('fieldCaptureCandidates', {
      sketchId: current.id,
      state: serializeFieldCaptureMode(fieldCaptureMode),
    })).catch((err) => console.error('field capture candidates could not be persisted:', err));
  }
  let fieldSession = null;
  let fieldBluetoothDevice = null;
  let fieldLaserListening = false;
  let fieldBluetoothReconnectPromise = null;
  let fieldCorrectionMode = false;
  let shapeCapturePoints = [];
  let shapeCaptureCleanedWalls = [];
  let shapeCaptureDrawing = false;

  const fieldEl = (id) => root.getElementById(id);
  const fieldId = () => newId();
  const fieldWallCount = () => model.state.walls.filter((wall) => (wall.segmentKind ?? 'wall') === 'wall').length || 4;

  function armCanvasWallTarget(wallId) {
    const wall = model.state.walls.find((item) => item.id === wallId && (item.segmentKind ?? 'wall') === 'wall');
    if (!wall || !fieldCaptureMode.active) return false;
    return armVisibleCaptureTarget({
      kind: 'wall_length', entityId: wall.id, property: 'lengthUnits', label: `W${wall.wallNumber} length`,
    });
  }

  function armVisibleCaptureTarget(target) {
    if (!fieldCaptureMode.active) return false;
    try {
      fieldCaptureMode = armFieldCaptureTarget(fieldCaptureMode, target);
      // On a phone the designer can collapse the sheet to reach a target. Once
      // that explicit tap arms it, bring the controls back and silently focus
      // the T1M HID buffer for this destination.
      setPhoneCaptureCollapsed(false);
      renderFieldCaptureMode();
      repaintView();
      // repaintView can replace the tapped input while its pointer/click
      // sequence is still finishing. Blur whichever capture input exists
      // after that replacement so a browser or embedded webview cannot move
      // focus (and its OS keyboard) onto the freshly rendered field.
      setTimeout(() => {
        if (!fieldCaptureMode.active) return;
        const active = root.activeElement;
        if (active?.matches?.('[data-capture-kind]')) active.blur();
      }, 0);
      return true;
    } catch (error) {
      const statusEl = fieldEl('capture-laser-status');
      if (statusEl) statusEl.textContent = error.message;
      return false;
    }
  }

  /** Marks one visible length input as an explicit capture destination. */
  function makeCaptureTarget(control, target) {
    // Validate BEFORE decoration. A control that looks like a target but can
    // never arm is worse than an undecorated control: that was the fan defect.
    const valid = validateFieldCaptureTarget(target);
    control.dataset.captureKind = valid.kind;
    control.dataset.captureEntityId = valid.entityId;
    control.dataset.captureProperty = valid.property;
    control.dataset.captureLabel = valid.label;
    // Field Capture is laser-first. On touch, arm the destination without
    // focusing the editable field: focus would open either the OS keyboard or
    // the app's large fraction pad and cover the remaining targets. Manual
    // Entry has its own explicit button and input in the capture dock.
    control.addEventListener('pointerdown', (event) => {
      if (!fieldCaptureMode.active) return;
      event.preventDefault();
      control.setAttribute('inputmode', 'none');
      control.blur();
      armVisibleCaptureTarget(valid);
    });
    // Keyboard/programmatic focus remains an accessible target-selection path;
    // the pad's shouldOpen guard below still keeps it laser-first in FC mode.
    control.addEventListener('focus', () => {
      if (!fieldCaptureMode.active) return;
      armVisibleCaptureTarget(valid);
      control.blur();
    });
    // Some mobile/webview event sequences apply focus after pointerdown even
    // when its default was cancelled. The click-phase release catches that
    // final path; the target was already armed above.
    control.addEventListener('click', (event) => {
      if (!fieldCaptureMode.active) return;
      event.preventDefault();
      control.setAttribute('inputmode', 'none');
      control.blur();
      // Run once after the click's browser-default phase as well. Some mobile
      // webviews re-assert focus after listeners return.
      setTimeout(() => {
        if (fieldCaptureMode.active) control.blur();
      }, 0);
    });
  }

  function renderFieldCaptureMode() {
    const dock = fieldEl('phone-capture-dock');
    if (dock) dock.hidden = !fieldCaptureMode.active;
    root.body?.classList.toggle('phone-capture-mode', fieldCaptureMode.active);
    const target = fieldEl('phone-capture-target');
    const confirmed = isArmedFieldCaptureTargetConfirmed(fieldCaptureMode);
    const locked = isArmedFieldCaptureTargetLocked(fieldCaptureMode);
    if (target) {
      const currentValue = captureTargetCurrentValue(fieldCaptureMode.armedTarget);
      target.textContent = fieldCaptureMode.armedTarget
        ? `${fieldCaptureMode.armedTarget.label} · ${currentValue > 0 ? fmt(currentValue) : 'NA'}${confirmed ? ' · confirmed' : ''}`
        : 'Tap a wall badge or measurement field';
    }
    const toggle = fieldEl('toggle-phone-capture');
    if (toggle) toggle.hidden = !fieldCaptureMode.armedTarget;
    const disarm = fieldEl('capture-disarm');
    if (disarm) disarm.disabled = !fieldCaptureMode.armedTarget;
    for (const input of root.querySelectorAll('[data-capture-kind]')) {
      // Capture targets are selectors, not manual-entry controls, while Field
      // Capture is active. Apply this to every freshly rendered field as well
      // as the original tapped node.
      input.setAttribute('inputmode', fieldCaptureMode.active ? 'none' : 'text');
      const armed = fieldCaptureMode.active
        && input.dataset.captureKind === fieldCaptureMode.armedTarget?.kind
        && input.dataset.captureEntityId === fieldCaptureMode.armedTarget?.entityId
        && input.dataset.captureProperty === fieldCaptureMode.armedTarget?.property;
      input.classList.toggle('capture-target--armed', armed);
      input.closest('label, td')?.classList.toggle('capture-target-wrap--armed', armed);
    }
    renderCaptureReadingTray();
    renderCaptureLaserActions();
    setManualCaptureVisible(Boolean(fieldCaptureMode.armedTarget));
    renderCaptureInputState({ confirmed, locked });
  }

  function captureTargetCurrentValue(target) {
    return target ? roomSketchCaptureTargetValue(target, { model, root }) : null;
  }

  function renderCaptureLaserActions() {
    const connect = fieldEl('capture-connect-laser');
    if (!connect) return;
    const connected = Boolean(fieldBluetoothDevice);
    connect.hidden = connected;
    // Disabled tracks only CONNECTED state. It used to also grey out when
    // the browser lacked Web Bluetooth — a wordless dead button: the click
    // never ran, so the handler's own clear refusal ("This browser does not
    // expose Web Bluetooth…") was never produced (owner, 2026-08-29: "The
    // Connect Leica button does not respond"). The owner's switch ruling
    // applies verbatim — a control that silently refuses is worse than one
    // that explains itself — so the click now always reaches the handler,
    // whose report() is visible everywhere including RM (#121).
    connect.disabled = connected;
    const setup = fieldEl('capture-laser-setup');
    if (setup) setup.hidden = connected;
    // The Space-setup menu's duplicate "Leica setup" button was removed (owner,
    // 2026-08-30), so there is no third control to keep in step here. The two
    // that remain are this dock and the Remeasure overlay's Laser button, both
    // wired to connectCaptureLaser().
  }

  async function connectCaptureLaser() {
    const statusEl = fieldEl('capture-laser-status');
    const report = (message) => {
      if (statusEl) statusEl.textContent = message;
      if (status) status.textContent = message;
      // The SAME connect handler serves the remeasure overlay's Laser button
      // — and that overlay covers both other status surfaces, so a designer
      // tapping it saw NOTHING when the message mattered most (owner,
      // 2026-08-29, in-app browser: "I cannot pair either device" — the
      // no-Web-Bluetooth refusal was being written under the overlay).
      const rm = root.getElementById('remeasure-status');
      if (rm && root.getElementById('remeasure-overlay')?.hidden === false) rm.textContent = message;
    };
    try {
      report('Choose the awake Leica DISTO from Chrome’s device list. The T1M pairs in the device Bluetooth settings as a keyboard.');
      const device = await requestBluetoothDevice(window.navigator);
      const inspection = await inspectGattServices(device);
      keepCaptureLaserDevice(device);
      report(`Connected to ${inspection.name}. Starting automatic listening…`);
      await startCaptureLaserListening();
    } catch (error) {
      report(error?.name === 'NotFoundError'
        ? 'No device was selected. Keep the DISTO awake and try again.'
        : `Leica connection did not complete: ${error.message}`);
    }
  }

  function setManualCaptureVisible(visible) {
    const form = fieldEl('capture-manual-form');
    if (form) form.hidden = !visible;
  }

  function sameCaptureTarget(a, b) {
    return Boolean(a && b
      && a.kind === b.kind
      && a.entityId === b.entityId
      && a.property === b.property);
  }

  /**
   * The REEKON T1M is a Bluetooth keyboard. Its saved reading is ordinary HID
   * keystrokes followed by Enter, so this invisible input must hold focus for
   * the explicitly armed destination. A confirmed target deliberately loses
   * focus until Correct unlocks it.
   */
  function renderCaptureInputState({ confirmed, locked } = {}) {
    const input = fieldEl('capture-manual-reading');
    const ready = fieldEl('capture-ready');
    const manual = fieldEl('capture-manual-entry');
    const correct = fieldEl('capture-correct');
    const target = fieldCaptureMode.armedTarget;
    if (!input) return;
    delete input.dataset.pad;
    delete input.dataset.manualEntry;
    input.setAttribute('inputmode', 'none');
    if (ready) ready.textContent = !target
      ? 'Select a measurement target.'
      : locked
        ? `${target.label} is confirmed. Choose Correct before replacing it.`
        : `${target.label} is armed — take a Leica or T1M reading now.`;
    if (manual) manual.disabled = !target || locked;
    if (correct) {
      correct.hidden = !confirmed;
      correct.disabled = confirmed && !locked;
      correct.textContent = locked ? 'Correct' : 'Correction active';
    }
    if (!target || locked) {
      input.value = '';
      input.blur();
      return;
    }
    const armed = { ...target };
    requestAnimationFrame(() => {
      if (!fieldCaptureMode.active
        || !sameCaptureTarget(fieldCaptureMode.armedTarget, armed)
        || isArmedFieldCaptureTargetLocked(fieldCaptureMode)) return;
      input.focus({ preventScroll: true });
    });
  }

  function openManualCaptureEntry({ clear = true } = {}) {
    const input = fieldEl('capture-manual-reading');
    if (!input || !fieldCaptureMode.armedTarget) {
      fieldEl('capture-laser-status').textContent = 'Select a measurement target before entering a manual value.';
      return;
    }
    if (isArmedFieldCaptureTargetLocked(fieldCaptureMode)) {
      fieldEl('capture-laser-status').textContent = 'This measurement is confirmed. Choose Correct before replacing it.';
      return;
    }
    if (clear) input.value = '';
    input.dataset.pad = 'length';
    input.dataset.manualEntry = 'true';
    input.setAttribute('inputmode', 'none');
    input.focus({ preventScroll: true });
    // On a fine pointer the numeric pad does not exist (mountNumericPad
    // returns a stub), so this button's only VISIBLE effect never happens —
    // a desktop click silently focused an unmarked buffer (owner,
    // 2026-08-29: "the enter manual measurement button does not do anything
    // as well"). Detected by behaviour — did a pad actually appear? — not by
    // pointer sniffing, so a coarse device whose pad failed also explains.
    if (!numericPad.isActive()) {
      fieldEl('capture-laser-status').textContent =
        `Type the ${fieldCaptureMode.armedTarget.label} value on your keyboard and press Enter — fractions like 120 1/2 work.`;
    }
  }

  function keepCaptureLaserDevice(device) {
    fieldBluetoothDevice = device;
    fieldLaserListening = false;
    try {
      if (device?.id) window.localStorage.setItem('room-sketch.last-laser-device-id', device.id);
    } catch {
      // Browser storage may be disabled; DISTO-name matching still permits a
      // best-effort reconnect without making Field Capture fail.
    }
    device?.addEventListener?.('gattserverdisconnected', () => {
      if (fieldBluetoothDevice !== device) return;
      fieldBluetoothDevice = null;
      fieldLaserListening = false;
      renderCaptureLaserActions();
      const statusEl = fieldEl('capture-laser-status');
      if (statusEl) statusEl.textContent = 'Laser disconnected. Connect it again when ready.';
    });
    renderCaptureLaserActions();
  }

  function preferredLaserDeviceId() {
    try { return window.localStorage.getItem('room-sketch.last-laser-device-id') ?? ''; } catch { return ''; }
  }

  async function startCaptureLaserListening() {
    if (!fieldBluetoothDevice || fieldLaserListening) return;
    const statusEl = fieldEl('capture-laser-status');
    fieldLaserListening = true;
    if (statusEl) statusEl.textContent = 'Laser connected and starting automatic listening…';
    try {
      await subscribeLeicaDistanceEvidence(fieldBluetoothDevice, (evidence) => {
        // The transport can remain subscribed for the selected device. An
        // armed REMEASURE row gets first claim (owner, 2026-08-28); otherwise
        // only active Field Capture with an explicit destination may consume
        // a measurement.
        if (evidence.error) {
          statusEl.textContent = `Laser reading was rejected: ${evidence.error}.`;
          return;
        }
        const inches = evidence.meters * 39.3700787402;
        if (deliverRemeasureReading(inches)) return;
        if (!fieldCaptureMode.active || !fieldCaptureMode.armedTarget) return;
        try {
          receiveFieldCaptureReading(inches, 'leica_d1');
        } catch (error) {
          statusEl.textContent = `${error.message} The ${fmt(inches)} laser reading was not captured.`;
        }
      });
      if (statusEl) statusEl.textContent = fieldCaptureMode.armedTarget
        ? `Listening automatically for ${fieldCaptureMode.armedTarget.label}.`
        : 'Laser is listening. Tap a measurement field.';
    } catch (error) {
      fieldLaserListening = false;
      fieldBluetoothDevice = null;
      renderCaptureLaserActions();
      if (statusEl) statusEl.textContent = `Laser listener did not start: ${error.message}`;
    }
  }

  async function autoReconnectCaptureLaser() {
    if (fieldBluetoothDevice) {
      await startCaptureLaserListening();
      return fieldLaserListening;
    }
    if (fieldBluetoothReconnectPromise) return fieldBluetoothReconnectPromise;
    const statusEl = fieldEl('capture-laser-status');
    fieldBluetoothReconnectPromise = (async () => {
      try {
        if (statusEl) statusEl.textContent = 'Looking for the previously paired laser…';
        const device = await getPermittedBluetoothDevice(window.navigator, preferredLaserDeviceId());
        if (!device) {
          if (statusEl) statusEl.textContent = bluetoothReadiness(window.navigator).message;
          return false;
        }
        keepCaptureLaserDevice(device);
        if (statusEl) statusEl.textContent = `Reconnecting to ${device.name || 'the paired laser'}…`;
        await startCaptureLaserListening();
        return fieldBluetoothDevice === device && fieldLaserListening;
      } catch (error) {
        fieldBluetoothDevice = null;
        fieldLaserListening = false;
        renderCaptureLaserActions();
        if (statusEl) statusEl.textContent = `Automatic laser reconnect did not complete: ${error.message}. Use Connect laser to try again.`;
        return false;
      } finally {
        fieldBluetoothReconnectPromise = null;
      }
    })();
    return fieldBluetoothReconnectPromise;
  }

  function renderCaptureReadingTray() {
    const tray = fieldEl('capture-reading-tray');
    const list = fieldEl('capture-reading-list');
    const apply = fieldEl('capture-apply-selected');
    if (!tray || !list || !apply) return;
    const readings = readingsForArmedTarget(fieldCaptureMode);
    const selected = selectedReadingForArmedTarget(fieldCaptureMode);
    tray.hidden = !fieldCaptureMode.armedTarget;
    list.replaceChildren();
    if (!fieldCaptureMode.armedTarget) {
      apply.disabled = true;
      return;
    }
    if (!readings.length) {
      const empty = document.createElement('p');
      empty.className = 'phone-capture-dock__hint';
      empty.textContent = 'No readings yet for this target.';
      list.append(empty);
      apply.disabled = true;
      return;
    }
    for (const reading of readings) {
      const row = document.createElement('div');
      row.className = 'capture-reading';
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'capture-reading-choice';
      radio.value = reading.id;
      radio.id = `capture-reading-${reading.id}`;
      radio.checked = selected?.id === reading.id;
      radio.setAttribute('aria-label', `Use ${fmt(reading.valueUnits)}`);
      radio.addEventListener('change', () => {
        fieldCaptureMode = selectFieldCaptureReading(fieldCaptureMode, reading.id);
        persistCaptureCandidates();
        renderCaptureReadingTray();
      });
      const body = document.createElement('label');
      body.htmlFor = radio.id;
      const value = document.createElement('span'); value.className = 'capture-reading__value'; value.textContent = fmt(reading.valueUnits);
      const meta = document.createElement('span'); meta.className = 'capture-reading__meta';
      meta.textContent = reading.source === 'leica_d1' ? 'Leica'
        : reading.source === 't1m_hid' ? 'T1M' : 'Manual';
      body.append(value, meta);
      const del = document.createElement('button');
      del.type = 'button'; del.className = 'capture-reading__delete'; del.textContent = '×';
      del.setAttribute('aria-label', `Delete ${fmt(reading.valueUnits)} reading`);
      del.disabled = isArmedFieldCaptureTargetLocked(fieldCaptureMode);
      del.addEventListener('click', (event) => {
        fieldCaptureMode = deleteFieldCaptureReading(fieldCaptureMode, reading.id);
        persistCaptureCandidates();
        renderCaptureReadingTray();
        fieldEl('capture-laser-status').textContent = `Deleted ${fmt(reading.valueUnits)} from ${fieldCaptureMode.armedTarget.label}.`;
      });
      row.append(radio, body, del);
      list.append(row);
    }
    apply.disabled = !selected || isArmedFieldCaptureTargetLocked(fieldCaptureMode);
  }

  function writeArmedCaptureTarget(target, valueUnits) {
    return writeRoomSketchCaptureTarget(target, valueUnits, { model, root });
  }

  function receiveFieldCaptureReading(valueUnits, source = 'manual') {
    fieldCaptureMode = captureFieldReading(fieldCaptureMode, valueUnits, { source, makeId: fieldId });
    persistCaptureCandidates();
    renderFieldCaptureMode();
    const reading = selectedReadingForArmedTarget(fieldCaptureMode);
    const statusEl = fieldEl('capture-laser-status');
    if (statusEl) statusEl.textContent = `Captured ${fmt(reading.valueUnits)} for ${reading.target.label}. Choose the desired reading, then apply it.`;
    return reading;
  }

  function applySelectedCaptureReading() {
    fieldCaptureMode = applySelectedFieldCaptureReading(fieldCaptureMode, { applyTarget: writeArmedCaptureTarget });
    persistCaptureCandidates();
    // Creation and breakpoint drafts do not exist in the model yet. Rebuilding
    // their renderer here would erase the just-applied reading before Submit.
    if (!['wall_height_draft', 'ceiling_opening_draft'].includes(fieldCaptureMode.armedTarget?.kind)) renderGeometry();
    const reading = fieldCaptureMode.lastReading;
    renderFieldCaptureMode();
    fieldEl('capture-laser-status').textContent = `Applied and confirmed ${fmt(reading.valueUnits)} for ${reading.target.label}. Tap another target, or choose Correct to replace it.`;
    return reading;
  }

  /**
   * The ONE writer for field sessions: every save also marks the session for
   * cloud backup (N1, ADR 0105) and nudges a sync. The owner's ruling makes
   * readings the most-important data in the app — a putMeasurementSession
   * call that skips the dirty mark is a reading that never leaves the phone.
   */
  function storeFieldSession(session) {
    const stored = putMeasurementSession(session);
    // Mark only — no sync nudge. The owner's N1 ruling: backup runs when
    // CLOSING field capture or the RM sheet, not regularly in the background
    // ("I'm concerned that it may slow down tablets/phones running regularly
    // in the background"). The dirty mark is a cheap local meta write that
    // makes ANY later sync carry the readings; closeFieldCapture() is the
    // trigger that starts one.
    sync?.markMeasurementSessionDirty?.(session.id);
    return stored;
  }

  async function ensureFieldSession() {
    const wallCount = fieldWallCount();
    if (fieldSession?.roomId === current.id) {
      // The sketch can gain a real wall after capture begins. Expand safely;
      // never reduce this reviewable record automatically.
      if (fieldSession.slots.length < wallCount) {
        fieldSession = resizeWallSlots(fieldSession, wallCount, { makeId: fieldId });
        await storeFieldSession(fieldSession);
      }
      return fieldSession;
    }
    const saved = (await listMeasurementSessions())
      .filter((session) => session.roomId === current.id && session.status === 'capturing')
      .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0];
    fieldSession = saved ?? createMeasurementSession({
      id: fieldId(), roomId: current.id, slots: createWallSlots(wallCount, { makeId: fieldId }),
    });
    if (fieldSession.slots.length < wallCount) {
      fieldSession = resizeWallSlots(fieldSession, wallCount, { makeId: fieldId });
    }
    await storeFieldSession(fieldSession);
    return fieldSession;
  }

  function persistFieldSession() { return fieldSession ? storeFieldSession(fieldSession) : Promise.resolve(false); }

  async function armFieldSlotForCanvasWall(wallId) {
    await ensureFieldSession();
    const wall = model.state.walls.find((item) => item.id === wallId);
    const slot = wall && Number.isInteger(wall.wallNumber)
      ? fieldSession.slots.find((item) => item.wallLabel === `W${wall.wallNumber}`)
      : null;
    if (!slot || slot.status === 'applied') return;
    fieldSession = armMeasurementSlot(fieldSession, slot.id);
    fieldCorrectionMode = false;
    await persistFieldSession();
    renderFieldSession();
  }

  function applyActiveConfirmedReading() {
    const active = fieldSession?.slots.find((slot) => slot.id === fieldSession.armedSlotId);
    if (!active || active.status !== 'confirmed') return { applied: [], skipped: [] };
    const result = applyReviewedMeasurements(fieldSession, model.state.walls, [active.id], (id, units) => model.setWallMeasuredLength(id, String(units)));
    if (result.applied.length) { renderAll(); scheduleSave(); }
    return result;
  }

  function renderBluetoothReadiness() {
    const status = fieldEl('field-bluetooth-status');
    const button = fieldEl('field-connect-laser');
    if (!status || !button) return;
    const readiness = bluetoothReadiness(window.navigator);
    status.textContent = readiness.message;
    button.disabled = !readiness.available;
    fieldEl('field-listen-leica').disabled = !fieldBluetoothDevice;
  }

  function renderFieldSession() {
    const slots = fieldEl('field-slots');
    const summary = fieldEl('field-session-summary');
    const armed = fieldEl('field-armed');
    const candidatesEl = fieldEl('field-candidates');
    const wallPicker = fieldEl('field-feature-wall');
    const referencePicker = fieldEl('field-feature-reference-wall');
    if (!slots || !summary || !armed || !wallPicker || !referencePicker || !candidatesEl) return;
    if (!fieldSession) {
      summary.textContent = 'Start a template-led field session. Manual capture works without a laser.';
      slots.replaceChildren(); wallPicker.replaceChildren(); return;
    }
    summary.textContent = `${fieldSession.slots.length} perimeter targets · Wall 1 is the left return; numbering is clockwise.`;
    const active = fieldSession.slots.find((slot) => slot.id === fieldSession.armedSlotId);
    renderPhoneCaptureTarget(active);
    renderPhoneCaptureGuide(active);
    const correctionText = fieldCorrectionMode ? ' · correction mode: the next reviewed reading replaces this confirmed value' : '';
    armed.textContent = active ? `Armed: ${active.wallLabel} — perimeter length${correctionText}` : 'Choose exactly one wall to arm.';
    slots.replaceChildren(...fieldSession.slots.map((slot) => {
      const row = document.createElement('div'); row.className = `field-slot field-slot--${slot.status}`;
      const arm = document.createElement('button'); arm.type = 'button'; arm.className = 'btn'; arm.textContent = slot.wallLabel;
      arm.classList.toggle('is-active', slot.id === fieldSession.armedSlotId);
      arm.addEventListener('click', async () => {
        fieldSession = armMeasurementSlot(fieldSession, slot.id);
        fieldCorrectionMode = false;
        await persistFieldSession();
        renderFieldSession();
      });
      const value = document.createElement('span'); value.textContent = Number.isFinite(slot.valueUnits) ? `${formatLength(slot.valueUnits, 'inches_fraction')} · ${slot.status}` : 'Not captured';
      row.append(arm, value); return row;
    }));
    const candidates = [...(active?.candidates?.filter((candidate) => candidate.status === 'candidate') ?? [])]
      .sort((a, b) => a.valueUnits - b.valueUnits || String(a.capturedAt).localeCompare(String(b.capturedAt)));
    if (!active || !candidates.length) {
      candidatesEl.replaceChildren();
    } else {
      const title = document.createElement('p'); title.className = 'field-candidates__title';
      title.textContent = `${candidates.length} readings for ${active.wallLabel} · shortest first. Choose one, or remove an outlier.`;
      candidatesEl.replaceChildren(title, ...candidates.map((candidate) => {
        const row = document.createElement('div'); row.className = 'field-candidate';
        row.classList.toggle('is-selected', candidate.id === active.selectedCandidateId);
        const value = document.createElement('span'); value.className = 'field-candidate__value'; value.textContent = formatLength(candidate.valueUnits, 'inches_fraction');
        const meta = document.createElement('span'); meta.className = 'field-candidate__meta'; meta.textContent = candidate.source?.kind === 'leica_d1' ? 'Leica' : candidate.source?.kind ?? 'manual';
        const choose = document.createElement('button'); choose.type = 'button'; choose.className = 'btn'; choose.textContent = candidate.id === active.selectedCandidateId ? 'Selected' : 'Use';
        choose.disabled = candidate.id === active.selectedCandidateId;
        choose.addEventListener('click', async () => {
          fieldSession = selectCandidateReading(fieldSession, candidate.id);
          await persistFieldSession(); renderFieldSession();
        });
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'btn'; remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove ${formatLength(candidate.valueUnits, 'inches_fraction')} outlier reading`);
        remove.addEventListener('click', async () => {
          fieldSession = discardCandidateReading(fieldSession, { candidateId: candidate.id });
          await persistFieldSession();
          renderFieldSession();
          fieldEl('field-session-summary').textContent = `${formatLength(candidate.valueUnits, 'inches_fraction')} removed as an outlier; the reading remains in the session audit.`;
        });
        row.append(value, meta, choose, remove); return row;
      }));
    }
    const wallOptions = fieldSession.slots.map((slot) => {
      const option = document.createElement('option'); option.value = slot.wallLabel; option.textContent = slot.wallLabel; return option;
    });
    wallPicker.replaceChildren(...wallOptions);
    referencePicker.replaceChildren(...fieldSession.slots.map((slot) => {
      const option = document.createElement('option'); option.value = slot.wallLabel; option.textContent = slot.wallLabel; return option;
    }));
    const features = fieldEl('field-features');
    features?.replaceChildren(...fieldSession.features.map((feature) => {
      const item = document.createElement('li');
      item.textContent = feature.hostSurface === 'ceiling'
        ? `Ceiling · ${feature.type} · ${feature.referenceWalls.join(' / ')}`
        : `${feature.hostWallLabel} · ${feature.type}`;
      return item;
    }));
    fieldEl('field-accept-reading').disabled = !active || active.status !== 'candidate';
    fieldEl('field-apply-reading').disabled = !active || active.status !== 'confirmed';
    fieldEl('field-discard-reading').disabled = !active || active.status !== 'candidate';
    fieldEl('field-edit-confirmed').disabled = !active || active.status !== 'confirmed';
  }

  function renderPhoneCaptureTarget(active) {
    const target = fieldEl('phone-capture-target');
    if (!target) return;
    target.textContent = active
      ? `${active.wallLabel} · perimeter length`
      : 'Select a wall measurement';
  }

  function renderPhoneCaptureGuide(active) {
    const progress = fieldEl('phone-capture-progress');
    const next = fieldEl('phone-next-field');
    if (!progress || !next) return;
    const steps = ['phone-step-select', 'phone-step-capture', 'phone-step-choose', 'phone-step-confirm'].map((id) => fieldEl(id));
    const total = fieldSession?.slots.length ?? 0;
    const confirmed = fieldSession?.slots.filter((slot) => slot.status === 'confirmed').length ?? 0;
    let currentStep = 0;
    let message = 'Step 1 of 4 — select a field on the room.';
    if (active?.status === 'confirmed') {
      currentStep = 3;
      message = `${active.wallLabel} confirmed · ${confirmed} of ${total} fields complete.`;
    } else if (active?.status === 'candidate') {
      currentStep = 2;
      const count = active.candidates?.filter((candidate) => candidate.status === 'candidate').length ?? 1;
      message = `Step 3 of 4 — choose the best of ${count} reading${count === 1 ? '' : 's'}, then Accept.`;
    } else if (active) {
      currentStep = 1;
      message = `Step 2 of 4 — capture one or more readings for ${active.wallLabel}.`;
    }
    progress.textContent = message;
    steps.forEach((step, index) => {
      step?.classList.toggle('is-current', index === currentStep);
      step?.classList.toggle('is-complete', index < currentStep);
    });
    const nextSlot = active?.status === 'confirmed'
      ? fieldSession?.slots.find((slot) => slot.status !== 'confirmed' && slot.status !== 'applied')
      : null;
    next.hidden = !nextSlot;
    if (nextSlot) next.textContent = `Next: ${nextSlot.wallLabel}`;
  }

  /** Shows legacy review only when this room actually has an older session. */
  async function refreshLegacyFieldReviewAction() {
    const review = fieldEl('open-field-review');
    if (!review) return;
    try {
      const sessions = await listMeasurementSessions();
      review.hidden = !sessions.some((session) => session.roomId === current.id);
    } catch {
      // Historical review is optional; a blocked old store must not prevent the
      // current in-sketch Field Capture workflow from opening.
      review.hidden = true;
    }
  }

  async function openFieldCapture() {
    // Recover candidates captured before a reboot/killed tab — same sketch
    // only, and only when nothing newer is already in memory. The restore is
    // validated whole and comes back disarmed (field-capture-mode.js).
    if (!(fieldCaptureMode.readings ?? []).length) {
      try {
        const saved = await getMeta('fieldCaptureCandidates');
        if (saved?.sketchId === current.id) {
          const restored = restoreFieldCaptureMode(saved.state);
          if (restored) fieldCaptureMode = restored;
        }
      } catch (err) {
        console.error('field capture candidates could not be restored:', err);
      }
    }
    fieldCaptureMode = activateFieldCapture(fieldCaptureMode);
    fieldEl('field-capture').hidden = true;
    setView('plan');
    canvas.setMode('select');
    const statusEl = fieldEl('capture-laser-status');
    if (statusEl) statusEl.textContent = fieldBluetoothDevice
      ? 'Laser is listening. Tap a measurement field.'
      : typeof window.navigator?.bluetooth?.getDevices === 'function'
        ? 'Looking for the previously paired laser…'
        : bluetoothReadiness(window.navigator).message;
    // Target selection owns the screen first. Controls open only after the
    // designer explicitly taps the destination field or wall badge.
    setPhoneCaptureCollapsed(true);
    setManualCaptureVisible(false);
    renderFieldCaptureMode();
    renderGeometry();
    void refreshLegacyFieldReviewAction();
    void autoReconnectCaptureLaser();
  }

  function setPhoneCaptureCollapsed(collapsed) {
    const dock = fieldEl('phone-capture-dock');
    const toggle = fieldEl('toggle-phone-capture');
    dock?.classList.toggle('phone-capture-dock--collapsed', collapsed);
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(!collapsed));
      toggle.textContent = collapsed ? 'Show' : 'Hide';
    }
  }
  function closeFieldCapture() {
    fieldCaptureMode = deactivateFieldCapture(fieldCaptureMode);
    renderFieldCaptureMode();
    fieldEl('field-capture').hidden = true;
    repaintView();
    // The owner-chosen backup trigger (N1): closing field capture starts the
    // sync that drains every session marked dirty during the visit. Immediate
    // rather than debounced — the designer is done capturing, and this is the
    // moment the readings should leave the device.
    sync?.syncNow?.().catch(() => {});
  }

  async function openFieldReview() {
    const sessions = await listMeasurementSessions();
    const session = fieldSession?.roomId === current.id ? fieldSession : sessions
      .filter((candidate) => candidate.roomId === current.id).sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0];
    const list = fieldEl('field-review-list'); const summary = fieldEl('field-review-summary');
    if (!session) { summary.textContent = 'No field capture session is available for this room.'; list.replaceChildren(); fieldEl('field-review').hidden = false; return; }
    const currentLabels = new Set(model.state.walls.filter((wall) => Number.isInteger(wall.wallNumber)).map((wall) => `W${wall.wallNumber}`));
    summary.textContent = 'Selected confirmed readings will update the current tablet sketch. Candidates remain review-only.';
    list.replaceChildren(...reviewableSlots(session).map((slot) => {
      const row = document.createElement('label'); row.className = 'field-slot';
      const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = slot.id; cb.disabled = slot.status !== 'confirmed' || !currentLabels.has(slot.wallLabel);
      const matchedWall = model.state.walls.find((wall) => `W${wall.wallNumber}` === slot.wallLabel);
      const overwrite = matchedWall?.isMeasured ? ' · replaces an existing tablet measurement when selected' : '';
      const text = document.createElement('span'); text.textContent = `${slot.wallLabel}: ${slot.valueUnits} in · ${slot.status}${currentLabels.has(slot.wallLabel) ? overwrite : ' · no matching sketch wall'}`;
      row.append(cb, text); return row;
    }));
    fieldEl('field-review').dataset.sessionId = session.id;
    fieldEl('field-review').hidden = false;
  }

  async function openShapeCapture() {
    fieldEl('field-capture').hidden = true;
    fieldEl('shape-capture').hidden = false;
    const stage = fieldEl('shape-capture-stage');
    if (!stage.dataset.ready) {
      stage.dataset.ready = 'true';
      stage.addEventListener('pointerdown', (evt) => {
        const box = stage.getBoundingClientRect();
        shapeCaptureDrawing = true;
        shapeCaptureCleanedWalls = [];
        stage.setPointerCapture(evt.pointerId);
        // Preserve the first touch: phone gestures can end before pointermove.
        shapeCapturePoints.push([{ x: evt.clientX - box.left, y: evt.clientY - box.top }]);
        drawShapeCapture();
      });
      stage.addEventListener('pointermove', (evt) => {
        if (!shapeCaptureDrawing) return;
        const box = stage.getBoundingClientRect();
        shapeCapturePoints.at(-1).push({ x: evt.clientX - box.left, y: evt.clientY - box.top });
        drawShapeCapture();
      });
      const stop = (evt) => { shapeCaptureDrawing = false; try { stage.releasePointerCapture(evt.pointerId); } catch {} };
      stage.addEventListener('pointerup', stop); stage.addEventListener('pointercancel', stop);
    }
    drawShapeCapture();
  }
  function drawShapeCapture() {
    const stage = fieldEl('shape-capture-stage'); if (!stage) return;
    const width = Math.max(1, stage.clientWidth); const height = Math.max(1, stage.clientHeight);
    const raw = shapeCapturePoints.map((stroke) => `<polyline points="${stroke.map((point) => `${point.x},${point.y}`).join(' ')}" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />`).join('');
    const cleaned = shapeCaptureCleanedWalls.map((wall) => `<line x1="${wall.start.x}" y1="${wall.start.y}" x2="${wall.end.x}" y2="${wall.end.y}" stroke="var(--accent)" stroke-width="5" stroke-linecap="round" />`).join('');
    stage.innerHTML = `<svg viewBox="0 0 ${width} ${height}" aria-label="Rough shape capture">${raw}${cleaned}</svg>`;
  }

  const on = (id, event, fn) => root.getElementById(id)?.addEventListener(event, fn);

  /**
   * Walls drawn since Draw mode was last entered. Cleanup is no longer a button
   * (owner, 2026-07-29): leaving Draw mode offers to snap what you drew, and
   * only then — with nothing drawn there is nothing to ask about, so switching
   * tools stays silent.
   */
  let drewSinceDrawMode = false;
  /** Guards against re-entering the prompt while it is already open. */
  let cleanupPromptOpen = false;

  const applyMode = (mode) => {
    canvas.setMode(mode);
    // annotate/erase/text apply to whichever surface is showing; the elevation
    // has its own mode. draw/select/pan are plan-only (the elevation ignores).
    elevation?.setMode(mode === 'annotate' || mode === 'erase' || mode === 'text' ? mode : 'view');
    for (const btn of root.querySelectorAll('[data-mode]')) {
      btn.classList.toggle('is-active', btn.dataset.mode === mode);
    }
  };

  /**
   * Runs the clean-up offer, then switches mode. Cleanup snaps the drawn
   * strokes into a numbered wall chain; declining leaves them exactly as drawn,
   * so the answer is never lost work — at worst the designer re-edits a snapped
   * drawing, which is the trade the owner accepted.
   */
  const setMode = (mode) => {
    const leavingDraw = canvas.getMode() === 'draw' && mode !== 'draw';
    if (!leavingDraw || !drewSinceDrawMode || cleanupPromptOpen) {
      applyMode(mode);
      return;
    }
    cleanupPromptOpen = true;
    // Switch first, so the tool button responds immediately and the dialog is
    // not sitting over a toolbar that still claims Draw is active.
    applyMode(mode);
    confirmAction({
      title: 'Clean up the walls?',
      body: 'Snaps what you drew to straight walls at 45° angles and numbers them. Choose Keep as drawn to leave the strokes alone.',
      confirmLabel: 'Clean up',
      cancelLabel: 'Keep as drawn',
    }).then((ok) => {
      cleanupPromptOpen = false;
      drewSinceDrawMode = false; // asked once per drawing session either way
      if (!ok) return;
      model.cleanup();
      canvas.fit(model.allPoints());
      applyMode('select');
      renderAll();
      scheduleSave();
    });
  };

  on('mode-draw', 'click', () => setMode('draw'));
  on('mode-select', 'click', () => setMode('select'));
  on('mode-pan', 'click', () => setMode('pan'));
  // Sketch/Text/Eraser TOGGLE: tapping the active one turns it off, back to the
  // normal move/select mode — otherwise, on the elevation (no Draw/Select/Pan
  // buttons), there was no way to leave the tool to drag a fixture again.
  on('mode-annotate', 'click', () => setMode(canvas.getMode() === 'annotate' ? 'select' : 'annotate'));
  on('mode-text', 'click', () => setMode(canvas.getMode() === 'text' ? 'select' : 'text'));
  on('mode-erase', 'click', () => setMode(canvas.getMode() === 'erase' ? 'select' : 'erase'));
  // Tape measure (owner, 2026-07-27): drag between two points, read the true
  // distance. Toggles like the other tools; the reading clears on leaving.
  on('mode-measure', 'click', () => setMode(canvas.getMode() === 'measure' ? 'select' : 'measure'));

  // Pen colour, thickness and text size live behind two buttons that open
  // pickers (owner, 2026-07-29). They used to be eight always-visible toolbar
  // buttons; on a tablet the row they forced cost drawing height, and the
  // trade — one extra tap to change colour — is worth it for a control you set
  // occasionally rather than constantly.
  const PEN_COLORS = ['#e5484d', '#2563eb', '#111827', '#16a34a', '#f59e0b'];
  const PEN_WIDTHS = [{ label: 'Thin', w: 1.5 }, { label: 'Medium', w: 3 }, { label: 'Thick', w: 6 }];
  /**
   * Text sizes in REAL INCHES, like the section captions (canvas.js `capPx`) —
   * NOT fixed drawing pixels. A fixed pixel size only looks right on a room
   * drawn at one scale and turns gigantic on a small-drawn one; the captions
   * learned that in 2026-07, and notes had the same bug.
   *
   * Calibrated against what is already on the plan (owner, 2026-07-29), measured
   * on screen: the wall measurement chip paints the same height as 1.8" of model
   * space, and the section code is 3.4". So Small matches the wall measurements
   * and X-large is 1.5× the section code, with the two middle steps evenly
   * spaced between.
   */
  const TEXT_SIZES = [
    { label: 'Small', size: 1.8 },
    { label: 'Medium', size: 2.9 },
    { label: 'Large', size: 4 },
    { label: 'X-large', size: 5.1 },
  ];

  let activePenColor = PEN_COLORS[0];
  let activePenWidth = 2;
  let activeTextSize = 22;

  const colorBtn = root.getElementById('pen-color-btn');
  const colorDot = root.getElementById('pen-color-dot');
  const widthBtn = root.getElementById('pen-width-btn');
  const widthDot = root.getElementById('pen-width-dot');

  /** Mirrors the current pen onto both drawing surfaces. */
  const pushStyle = () => {
    canvas.setAnnotationStyle({ color: activePenColor, width: activePenWidth });
    elevation?.setAnnotationStyle({ color: activePenColor, width: activePenWidth });
  };
  /** The two toolbar buttons SHOW the current choice, so the picker can hide. */
  const paintPenButtons = () => {
    if (colorDot) colorDot.style.background = activePenColor;
    if (widthDot) {
      const d = Math.max(4, Math.min(16, activePenWidth * 2.5));
      widthDot.style.width = `${d}px`;
      widthDot.style.height = `${d}px`;
    }
  };
  pushStyle();
  paintPenButtons();

  /** A labelled row of choices inside a picker. */
  const pickerRow = (title, options, isActive, onPick) => {
    const group = document.createElement('div');
    group.className = 'pen-picker__group';
    const heading = document.createElement('p');
    heading.className = 'pen-picker__title';
    heading.textContent = title;
    group.append(heading);
    const row = document.createElement('div');
    row.className = 'pen-picker__row';
    for (const option of options) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `pen-picker__option${isActive(option) ? ' is-active' : ''}`;
      btn.setAttribute('aria-label', option.aria ?? option.label);
      btn.append(option.render());
      btn.addEventListener('click', () => {
        closeContextMenu();
        onPick(option);
      });
      row.append(btn);
    }
    group.append(row);
    return group;
  };

  const swatchNode = (color) => {
    const dot = document.createElement('span');
    dot.className = 'pen-picker__swatch';
    dot.style.background = color;
    return dot;
  };
  const textNode = (label) => {
    const span = document.createElement('span');
    span.textContent = label;
    return span;
  };

  colorBtn?.addEventListener('click', () => {
    const box = colorBtn.getBoundingClientRect();
    const content = document.createElement('div');
    content.className = 'pen-picker';
    content.append(pickerRow(
      'Pen colour',
      PEN_COLORS.map((color) => ({ label: color, aria: `Pen colour ${color}`, color, render: () => swatchNode(color) })),
      (option) => option.color === activePenColor,
      (option) => {
        activePenColor = option.color;
        pushStyle();
        paintPenButtons();
        setMode('annotate'); // picking a colour means you want to draw
      },
    ));
    showPopover(box.left, box.bottom + 6, content, 'popover');
  });

  widthBtn?.addEventListener('click', () => {
    const box = widthBtn.getBoundingClientRect();
    const content = document.createElement('div');
    content.className = 'pen-picker';
    content.append(pickerRow(
      'Pen thickness',
      PEN_WIDTHS.map(({ label, w }) => ({
        label, aria: `${label} pen`, w,
        render: () => {
          const dot = document.createElement('span');
          dot.className = 'pen-picker__swatch pen-picker__swatch--ink';
          dot.style.width = `${w * 3}px`;
          dot.style.height = `${w * 3}px`;
          return dot;
        },
      })),
      (option) => option.w === activePenWidth,
      (option) => {
        activePenWidth = option.w;
        pushStyle();
        paintPenButtons();
        setMode('annotate');
      },
    ));
    // Text size shares this picker rather than claiming a third toolbar button —
    // it is the same question ("how big?") for the other drawing tool, and the
    // owner asked to be able to set it when inserting text.
    content.append(pickerRow(
      'Text size',
      TEXT_SIZES.map(({ label, size }) => ({ label, aria: `${label} text`, size, render: () => textNode(label) })),
      (option) => option.size === activeTextSize,
      (option) => {
        activeTextSize = option.size;
        setMode('text'); // choosing a text size means you want to type
      },
    ));
    showPopover(box.left, box.bottom + 6, content, 'popover');
  });

  // Straight-line tool: a toggle that makes Sketch lay straight lines. Turning
  // it on also enters Sketch mode. It coexists with the colour/size pickers.
  let straightOn = false;
  const setStraight = (on) => {
    straightOn = on;
    canvas.setAnnotationStraight(on);
    elevation?.setAnnotationStraight(on);
    const btn = root.getElementById('mode-line');
    btn?.classList.toggle('is-active', on);
    btn?.setAttribute('aria-pressed', String(on));
  };
  on('mode-line', 'click', () => {
    setStraight(!straightOn);
    if (straightOn) setMode('annotate');
  });

  on('depth-lines-show', 'change', (evt) => {
    model.setPlanHelperLinesVisible(evt.target.checked);
    repaintView();
    scheduleSave();
  });

  on('action-clear-annotations', 'click', () => {
    const surface = currentView === 'elevation' ? 'elevation' : 'plan';
    model.clearStrokeAnnotations(surface); // keep typed text notes (ADR 0033)
    if (surface === 'plan') canvas.renderAnnotations(model.annotationsFor('plan'));
    else if (currentView === 'elevation') renderElevation();
    scheduleSave();
  });

  // Grid toggle — a to-scale reference grid on plan and elevation.
  function renderGridButton() {
    const btn = root.getElementById('toggle-grid');
    if (!btn) return;
    btn.classList.toggle('is-active', model.state.gridVisible === true);
    btn.setAttribute('aria-pressed', String(model.state.gridVisible === true));
  }
  function renderSnapClosedButton() {
    const btn = root.getElementById('toggle-snap-closed');
    if (!btn) return;
    const on = model.state.snapClosed !== false;
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', String(on));
    btn.dataset.tip = on ? 'Snap closed room: on' : 'Snap closed room: off';
    btn.setAttribute('aria-label', btn.dataset.tip);
  }
  on('toggle-snap-closed', 'click', () => {
    model.setSnapClosed(!(model.state.snapClosed !== false));
    renderSnapClosedButton();
    repaintView();
    scheduleSave();
  });

  on('toggle-grid', 'click', () => {
    model.setGridVisible();
    renderGridButton();
    repaintView();
    scheduleSave();
  });

  // Elevation DIMENSION toggles (ADR 0061 item E, owner 2026-08-09: "it would
  // be nice to be able to toggle on/off the display of spacing between shelves
  // as well as drawer face heights v. requiring designers to open and click on
  // each item"). Two INDEPENDENT toggles, persisted per sketch like the grid.
  function renderDimButtons() {
    for (const [id, on] of [
      ['toggle-shelf-dims', model.state.shelfSpacingDimsVisible === true],
      ['toggle-drawer-dims', model.state.drawerFaceDimsVisible === true],
    ]) {
      const btn = root.getElementById(id);
      if (!btn) continue;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', String(on));
    }
  }
  on('toggle-shelf-dims', 'click', () => {
    model.setShelfSpacingDimsVisible();
    renderDimButtons();
    if (currentView === 'elevation') renderElevation();
    scheduleSave();
  });
  on('toggle-drawer-dims', 'click', () => {
    model.setDrawerFaceDimsVisible();
    renderDimButtons();
    if (currentView === 'elevation') renderElevation();
    scheduleSave();
  });

  // Storage-summary toggle — the totals table can be hidden to free screen space
  // (owner). Disabled when the sketch has nothing to summarize.
  function renderSummaryButton() {
    const btn = root.getElementById('toggle-summary');
    if (!btn) return;
    const has = model.storageSummary().rows.length > 0;
    btn.disabled = !has;
    const on = has && model.state.storageSummaryVisible !== false;
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', String(on));
  }
  on('toggle-summary', 'click', () => {
    model.setStorageSummaryVisible();
    renderSummaryButton();
    renderStorageSummary();
    scheduleSave();
  });

  // Hide/show the side panel to give sketching the full width. UI-only (not
  // saved) — a per-session preference, reset each load.
  on('toggle-panel', 'click', () => {
    const btn = root.getElementById('toggle-panel');
    const hidden = workspace?.classList.toggle('workspace--panel-hidden');
    if (btn) {
      btn.setAttribute('aria-pressed', String(!!hidden));
      btn.classList.toggle('is-active', !!hidden);
      setIconTip(btn, hidden ? 'Show side panel' : 'Hide side panel', !!hidden);
    }
    // The canvas/elevation viewBox width changed, so refit to the new space.
    if (currentView === 'elevation') elevation?.fit();
    else canvas.fit(model.allPoints());
  });

  // Expand/collapse the Pricing section (ADR 0058). UI-only, per session —
  // the collapsed state is the restrained default; totals are stamped and
  // saved either way (renderPricingPanel computes on every repaint).
  on('pricing-toggle', 'click', () => {
    pricingOpen = !pricingOpen;
    renderPricingPanel();
  });
  on('close-pricing', 'click', () => {
    pricingOpen = false;
    renderPricingPanel();
  });

  // Expand/collapse the Handles-required section (ADR 0064). UI-only, per
  // session — the Pricing panel's convention, and for the same reason: a
  // collapsed panel is the restrained default, and the take-off is recomputed
  // on every repaint either way.
  on('handles-toggle', 'click', () => {
    handlesOpen = !handlesOpen;
    renderHandleSummary();
  });

  // In-app numeric pad for length fields (owner, 2026-08-04). Touch only: on a
  // mouse the physical keyboard is left completely alone. Fields opt in with
  // data-pad="length" and the listener is delegated, because the side panel
  // rebuilds its inputs on every render.
  const numericPad = mountNumericPad(root, {
    shouldOpen: (input) => !fieldCaptureMode.active
      || (input?.id === 'capture-manual-reading' && input.dataset.manualEntry === 'true'),
  });
  if (numericPad.isActive || true) {
    // inputmode="none" is what stops the OS keyboard appearing while still
    // focusing the field and showing a caret. Applied on FOCUS rather than at
    // creation so it follows dynamically-rendered inputs, and only when the pad
    // is really in play — otherwise a desktop user would lose typing entirely.
    root.addEventListener('focusin', (evt) => {
      const input = evt.target?.closest?.('[data-pad="length"]');
      if (!input) return;
      const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
      if (coarse) input.setAttribute('inputmode', 'none');
    });
  }

  // Measurement sheet (ADR 0045): a printable record of the EMPTY space.
  const measurementSheet = createMeasurementSheet(root, {
    model,
    rules,
    fmt,
    wallRenderOpts,
    elevationOptsFor: elevationRenderOpts,
  });
  const sheetMetaFor = (meta) => ({
    identity: [meta.client, meta.opportunity, meta.space]
      .map((part) => String(part ?? '').trim())
      .filter(Boolean)
      .join(' · '),
    dateLabel: new Date().toLocaleDateString(),
  });
  const sheetPlanOptsFor = (m) => () => ({
    thicknessFor: (wall) => Number.isFinite(wall.thicknessUnits) ? wall.thicknessUnits : rules.defaultWallThicknessUnits,
    obstructionsFor: (wallId) => m.obstructionsForWall(wallId),
    ceilingOpenings: m.state.ceilingOpenings,
    ladderCeilingInsetUnits: rules.atticLadderCeilingInsetUnits,
    ladderSideClearanceUnits: rules.atticLadderSideClearanceUnits,
    doorSwingLeafAllowanceUnits: rules.doorSwingLeafAllowanceUnits,
    bifoldFoldedProjectionUnits: rules.bifoldFoldedProjectionUnits,
    partitionIds: new Set(m.state.partitions.map((part) => part.segmentId)),
    partitionDepthFor: (id) => m.partitionDepthFor(id),
    autoLengthFor: (wall) => m.autoLength(wall),
    partitionFaces: m.partitionFaceWalls().map((face) => ({ ...face, lengthLabel: fmt(face.lengthUnits) })),
    formatLength: fmt,
    pxPerUnit: m.state.pxPerUnit,
    gridVisible: true,
    gridSpacingUnits: rules.gridSpacingUnits,
  });
  const sheetElevationOptsFor = (m) => () => ({
    pxPerUnit: m.state.pxPerUnit ?? 3,
    ceilingHeightUnits: m.state.ceilingHeightUnits,
    wallHeightProfileFor: (wallId) => m.wallHeightProfileFor(wallId),
    obstructionsFor: (wallId) => m.obstructionsForWall(wallId),
    obstructionCodeFor: (id) => m.obstructionCode(id),
    atticStairsFor: (wallId) => m.state.ceilingOpenings.map((opening) => atticStairElevationForWall(m.state.walls, opening, wallId, {
      pxPerUnit: m.state.pxPerUnit, ceilingHeightUnits: m.state.ceilingHeightUnits,
      ladderCeilingInsetUnits: rules.atticLadderCeilingInsetUnits, ladderSideClearanceUnits: rules.atticLadderSideClearanceUnits,
    })).filter(Boolean),
    ceilingOpeningsFor: (wallId) => m.state.ceilingOpenings.map((opening) => ceilingOpeningElevationForWall(m.state.walls, opening, wallId, { pxPerUnit: m.state.pxPerUnit })).filter(Boolean),
    annotations: m.annotationsFor('elevation'),
    panelThicknessUnits: rules.sectionPanelThicknessUnits,
    sectionDefaultHeightUnits: rules.sectionDefaultHeightUnits,
    formatLength: fmt,
    gridVisible: true,
    gridSpacingUnits: rules.gridSpacingUnits,
  });
  on('open-sheet', 'click', async () => {
    if (saveTimer !== null) await saveNow();
    const docs = (await listSketches()).filter((doc) => {
      const meta = readSketchMeta(doc);
      return !meta.isTemplate && meta.client === current.client && meta.opportunity === current.opportunity;
    });
    const bySpace = new Map();
    for (const doc of docs) {
      const meta = readSketchMeta(doc);
      const list = bySpace.get(meta.space) ?? [];
      list.push(doc);
      bySpace.set(meta.space, list);
    }
    const chosen = [...bySpace.values()].map((documents) => {
      const currentDoc = documents.find((doc) => doc.id === current.id);
      // A group larger than one can only be legacy data. Keep the newest
      // reachable without silently deleting the others.
      return currentDoc
        ?? [...documents].sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0];
    });
    const rooms = chosen.map((doc) => {
      const meta = readSketchMeta(doc);
      if (doc.id === current.id) return { model, meta: sheetMetaFor(current), wallRenderOpts, elevationOptsFor: elevationRenderOpts };
      const roomModel = createSketchModel(rules);
      const hydrated = hydrateSketch(doc);
      Object.assign(roomModel.state, hydrated);
      roomModel.resolve();
      return {
        model: roomModel,
        meta: sheetMetaFor(meta),
        wallRenderOpts: sheetPlanOptsFor(roomModel),
        elevationOptsFor: sheetElevationOptsFor(roomModel),
      };
    });
    measurementSheet.open(sheetMetaFor(current), rooms);
  });
  on('close-sheet', 'click', () => measurementSheet.close());
  on('sheet-print', 'click', () => window.print());

  // Engineering remeasure: freeze the current serialized designer sketch once,
  // then edit only the separate Actual dataset. This is intentionally outside
  // Copy-space/applyDoc(); a production remeasure is never another design space.
  let activeRemeasure = null;
  // The remeasure row currently armed for a Bluetooth reading (owner,
  // 2026-08-28) — {key, label}, ephemeral like fieldCaptureMode's target.
  let remeasureArmed = null;
  // Take-several-then-choose for RM rows (owner, 2026-08-29) — the SAME pure
  // state machine Field Capture uses, in its own instance; the list lives in
  // the overlay, never under it.
  let remeasureCapture = null;

  /**
   * Routes a laser reading into the armed remeasure row. Returns true when
   * consumed, so the Leica callback can offer the reading HERE first — the
   * Field Capture gate alone starved the remeasure overlay by design. The
   * value goes through the session's own guarded setter (locked sessions and
   * bad keys reject there), formatted like a typed entry, then persists.
   */
  function deliverRemeasureReading(valueUnits) {
    if (remeasureEl('remeasure-overlay')?.hidden !== false) return false;
    if (!activeRemeasure || activeRemeasure.status === 'ready_for_engineering') return false;
    if (!remeasureArmed || !(valueUnits > 0)) return false;
    try {
      // The NUMBER, never the formatted string: the formatter rounds to the
      // display fraction, and this was the one site in the app where a
      // format result crossed into storage — a 31/32 reading stored as its
      // neighbouring 16th (found 2026-08-29, the owner's own fixture).
      activeRemeasure = setRemeasureValue(activeRemeasure, remeasureArmed.key, valueUnits);
    } catch (error) {
      remeasureEl('remeasure-status').textContent = error.message;
      return true; // consumed: it was aimed here, and the designer was told why it did not stick
    }
    if (remeasureCapture?.armedTarget) {
      try {
        remeasureCapture = captureFieldReading(remeasureCapture, valueUnits, { source: 'leica_d1', makeId: fieldId });
      } catch { /* the field write above already told the designer */ }
    }
    persistActiveRemeasure().catch(() => {});
    renderRemeasure();
    renderRemeasureReadings();
    remeasureEl('remeasure-status').textContent = `${remeasureArmed.label} ← ${fmt(valueUnits)}. Tap the next Actual field or read again to replace.`;
    return true;
  }
  const remeasureEl = (id) => root.getElementById(id);
  async function persistActiveRemeasure() {
    if (!activeRemeasure) return;
    await putRemeasureSession(activeRemeasure);
    // Mark only — the RM sheet's close is the sync trigger, per the same N1
    // ruling as field capture (see storeFieldSession). Correction to earlier
    // behavior: this used to nudge a debounced sync on every save.
    if (sync?.markRemeasureDirty) {
      await sync.markRemeasureDirty(activeRemeasure.id);
    }
  }
  const signedLength = (value) => {
    if (Math.abs(value) < 1e-9) return '—';
    return `${value > 0 ? '+' : '−'}${fmt(Math.abs(value))}`;
  };
  /**
   * The id whose drawings are currently in `#remeasure-drawings`.
   *
   * `renderRemeasure()` runs on every committed field edit, and re-running the
   * page build each time would redraw every SVG per keystroke. The pages show
   * the FROZEN original sketch, which cannot change for the life of a session,
   * so the session id is a complete cache key today.
   *
   * The adjusted design has now landed (RS-9 part 4), so the pages ARE a
   * function of the remeasured values and the key has grown with them, exactly
   * as this comment warned it must: a stale drawing here would show an engineer
   * numbers that are not the ones in the table beside it.
   */
  let drawnRemeasureKey = null;

  /** Session id plus every remeasured figure — what the drawings depend on. */
  function remeasureDrawingKey(session) {
    if (!session) return null;
    return `${session.id}|${session.measurements.map((m) => m.actualUnits).join(',')}`;
  }

  /**
   * Renders the empty-room plan and elevations into the remeasure report.
   *
   * Reuses the measurement sheet's own page builder (`renderPages`) rather than
   * drawing anything new: the owner's description of this report is *"it
   * literally is the measurement sheets, updated/corrected"*, and a second
   * drawing implementation would be a second set of bugs to keep in step.
   *
   * The pages go in this overlay's own container, NOT in `#sheet-overlay`.
   * Both elements carry `.sheet-overlay` (`position: fixed; inset: 0;
   * z-index: 90`) and this one comes later in the document, so opening the
   * sheet from here would leave it completely covered — the RS-5/RS-6 defect
   * again. See ADR 0098.
   */
  function renderRemeasureDrawings() {
    const box = remeasureEl('remeasure-drawings');
    if (!box) return;
    if (!activeRemeasure) {
      box.hidden = true;
      box.replaceChildren();
      drawnRemeasureKey = null;
      return;
    }
    const key = remeasureDrawingKey(activeRemeasure);
    if (drawnRemeasureKey === key) return;

    // The ADJUSTED design — the remeasured figures, not the frozen original.
    // The owner (RS-9): the report shows *"the adjusted design"*, and when the
    // field numbers do not form a loop, *"remove the snap closed and scale to
    // actual scale (v. adjusting to keep room closed)"*. `applyRemeasure`
    // marks every wall measured, so `solveClosure` has no free wall to stretch
    // and the room simply does not close — which is the correct output, not a
    // failure to reconcile. See ADR 0101.
    const adjusted = applyRemeasure(activeRemeasure);
    const hydrated = hydrateSketch({ ...adjusted, schemaVersion: SKETCH_SCHEMA_VERSION });
    if (!hydrated) {
      // A snapshot from an older schema cannot be drawn. Say so rather than
      // showing an empty frame that reads as "this room has nothing in it".
      box.replaceChildren(el('p', 'sheet-warning',
        'The frozen designer snapshot predates the current sketch format, so its drawings cannot be rebuilt. The measurements below are unaffected.'));
      box.hidden = false;
      drawnRemeasureKey = key;
      return;
    }
    const roomModel = createSketchModel(rules);
    Object.assign(roomModel.state, hydrated);
    roomModel.resolve();
    const meta = {
      ...sheetMetaFor(readSketchMeta(activeRemeasure.originalSketch)),
      // Every page says which dataset it is. These are the DESIGNER's numbers,
      // frozen at the start of the remeasure — not the remeasured ones — and a
      // page that called itself anything else would be lying to Engineering.
      documentLabel: 'Remeasure · adjusted (actual scale, room not closed)',
    };
    measurementSheet.renderPages(box, meta, [{
      model: roomModel,
      meta,
      wallRenderOpts: sheetPlanOptsFor(roomModel),
      elevationOptsFor: sheetElevationOptsFor(roomModel),
    }]);

    // How far the measured room is from closing. The owner ruled that nothing
    // is nudged to hide this, which makes the figure real information rather
    // than an error: an engineer reading a plan that does not meet needs to
    // know whether it is out by 1/4" or by a foot.
    const gap = roomModel.state.closure?.residualGapUnits ?? 0;
    if (gap > rules.closureToleranceUnits) {
      box.append(el('p', 'sheet-warning',
        `The remeasured walls do not close the room — the chain ends ${fmt(gap)} from where it began. `
        + 'Drawn at actual scale with snap-close off, so this gap is the measurements, not the drawing.'));
    }
    // A reading that could not be applied is SAID, not dropped: a drawing
    // silently missing one is worse than a drawing that admits it.
    for (const refusal of adjusted.remeasureRefusals ?? []) {
      box.append(el('p', 'sheet-warning', `Not applied — ${refusal.key}: ${refusal.reason}`));
    }
    box.hidden = false;
    drawnRemeasureKey = key;
  }

  function renderRemeasure() {
    renderRemeasureDrawings();
    const body = remeasureEl('remeasure-rows');
    const empty = remeasureEl('remeasure-empty');
    const start = remeasureEl('remeasure-start');
    const complete = remeasureEl('remeasure-complete');
    const print = remeasureEl('remeasure-print');
    const technician = remeasureEl('remeasure-technician');
    if (!body) return;
    body.replaceChildren();
    empty.hidden = !!activeRemeasure;
    start.hidden = !!activeRemeasure;
    complete.disabled = !activeRemeasure || activeRemeasure.status === 'ready_for_engineering';
    print.disabled = !activeRemeasure;
    technician.disabled = !activeRemeasure || activeRemeasure.status === 'ready_for_engineering';
    technician.value = activeRemeasure?.technician ?? '';
    remeasureEl('remeasure-identity').textContent = activeRemeasure
      ? [activeRemeasure.originalSketch.client, activeRemeasure.originalSketch.opportunity,
        activeRemeasure.originalSketch.space].filter(Boolean).join(' · ') || 'Room sketch'
      : 'Room sketch';
    remeasureEl('remeasure-status').textContent = activeRemeasure?.status === 'ready_for_engineering'
      ? `Ready for Engineering · completed ${new Date(activeRemeasure.completedAt).toLocaleString()}`
      : activeRemeasure
        ? 'Designer Original is locked. Enter or confirm the separate Actual / Remeasure values.'
        : 'Designer Original will be preserved as an immutable snapshot.';
    if (!activeRemeasure) return;
    let group = null;
    for (const row of remeasureReportRows(activeRemeasure)) {
      if (row.group !== group) {
        group = row.group;
        const groupRow = document.createElement('tr');
        groupRow.className = 'remeasure-group';
        const cell = document.createElement('td'); cell.colSpan = 4; cell.textContent = group;
        groupRow.append(cell); body.append(groupRow);
      }
      const tr = document.createElement('tr');
      tr.classList.toggle('is-changed', row.changed);
      tr.append(el('td', null, row.label), el('td', null, fmt(row.originalUnits)));
      const actualCell = document.createElement('td');
      const input = document.createElement('input');
      input.type = 'text'; input.inputMode = 'text'; input.dataset.pad = 'length';
      input.value = fmt(row.actualUnits); input.disabled = activeRemeasure.status === 'ready_for_engineering';
      input.setAttribute('aria-label', `${row.label} Actual / Remeasure`);
      // A Bluetooth target (owner, 2026-08-28): focusing the field arms it,
      // so the next Leica reading — or the T1M typing here as a HID keyboard
      // — fills THIS row. Manual entry is unchanged.
      input.addEventListener('focus', () => {
        remeasureArmed = { key: row.key, label: row.label };
        try {
          remeasureCapture = armFieldCaptureTarget(remeasureCapture ?? activateFieldCapture(createFieldCaptureMode()),
            { kind: 'remeasure_field', entityId: row.key, property: 'value', label: row.label });
        } catch { /* an unarmable state never blocks manual entry */ }
        renderRemeasureReadings();
        remeasureEl('remeasure-status').textContent = `Listening for ${row.label} — Leica reading or T1M keystrokes fill this field.`;
      });
      // The T1M ends a burst with Enter; change only fires on commit, so
      // Enter must blur or a T1M reading sits uncommitted in the field.
      input.addEventListener('keydown', (evt) => { if (evt.key === 'Enter') input.blur(); });
      input.addEventListener('change', async () => {
        try {
          activeRemeasure = setRemeasureValue(activeRemeasure, row.key, input.value);
          // A committed typed/T1M value joins the candidates. A correction
          // and a genuine second reading are indistinguishable to code, so
          // BOTH accumulate — the per-reading ✕ is the typo answer.
          const typedUnits = parseLength(String(input.value ?? ''));
          if (remeasureCapture?.armedTarget && Number.isFinite(typedUnits) && typedUnits > 0) {
            try {
              remeasureCapture = captureFieldReading(remeasureCapture, typedUnits, { source: 'typed', makeId: fieldId });
            } catch { /* locked states surface through the field write */ }
          }
          await persistActiveRemeasure();
          renderRemeasure();
          renderRemeasureReadings();
        } catch (error) {
          input.value = fmt(row.actualUnits);
          remeasureEl('remeasure-status').textContent = error.message;
        }
      });
      actualCell.append(input);
      tr.append(actualCell, el('td', null, signedLength(row.varianceUnits)));
      body.append(tr);
    }
  }
  async function openRemeasure() {
    const sessions = (await listRemeasureSessions())
      .filter((session) => session.sourceSketchId === current.id)
      .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
    activeRemeasure = sessions[0] ?? null;
    // A fresh candidates store per overlay session, like Field Capture's
    // own list — readings are working evidence, not part of the record.
    remeasureCapture = activateFieldCapture(createFieldCaptureMode());
    remeasureArmed = null;
    remeasureEl('remeasure-overlay').hidden = false;
    renderRemeasure();
    renderRemeasureReadings();
  }

  /**
   * The take-several-then-choose list for the ARMED remeasure row (owner,
   * 2026-08-29: "the popup that allows several measurements to be taken and
   * then the best selected (like … field capture)"). Selection IS
   * application — an RM field is a cheap form value, so tapping a candidate
   * fills it; no Apply ceremony (Coordinator ruling). Headed with the row's
   * label so an empty list on another row reads as SCOPED, never as lost.
   *
   * Deleting the applied reading falls back to the NEWEST remaining — the
   * pure delete helper picks the LOWEST VALUE, which is right for Field
   * Capture's low-to-high candidates but wrong here, where "next" means
   * next-in-time — and deleting the last one REVERTS the field to the
   * session's ORIGINAL: its own no-evidence state (actuals seed from
   * originals; a true blank is not representable in the session model).
   */
  function renderRemeasureReadings() {
    const box = remeasureEl('remeasure-readings');
    if (!box) return;
    const readings = remeasureCapture?.armedTarget && remeasureArmed
      ? readingsForArmedTarget(remeasureCapture) : [];
    box.hidden = !remeasureArmed;
    if (!remeasureArmed) { box.replaceChildren(); return; }
    const head = document.createElement('p');
    head.className = 'remeasure-readings__head';
    head.textContent = `Readings for ${remeasureArmed.label}`;
    const selected = selectedReadingForArmedTarget(remeasureCapture);
    const rows = readings.map((reading) => {
      const rowEl = document.createElement('div');
      rowEl.className = `remeasure-readings__row${selected?.id === reading.id ? ' is-applied' : ''}`;
      const pick = document.createElement('button');
      pick.type = 'button';
      pick.className = 'remeasure-readings__pick';
      pick.textContent = `${fmt(reading.valueUnits)} · ${reading.source === 'leica_d1' ? 'Laser' : 'Typed'}`;
      pick.addEventListener('click', async () => {
        try {
          remeasureCapture = selectFieldCaptureReading(remeasureCapture, reading.id);
          activeRemeasure = setRemeasureValue(activeRemeasure, remeasureArmed.key, reading.valueUnits);
          await persistActiveRemeasure();
          renderRemeasure();
          renderRemeasureReadings();
          remeasureEl('remeasure-status').textContent = `${remeasureArmed.label} ← ${fmt(reading.valueUnits)} (selected from ${readings.length} readings).`;
        } catch (error) {
          remeasureEl('remeasure-status').textContent = error.message;
        }
      });
      const drop = document.createElement('button');
      drop.type = 'button';
      drop.className = 'remeasure-readings__drop';
      drop.textContent = '✕';
      drop.setAttribute('aria-label', `Discard the ${fmt(reading.valueUnits)} reading`);
      drop.addEventListener('click', async () => {
        try {
          const wasApplied = selected?.id === reading.id;
          remeasureCapture = deleteFieldCaptureReading(remeasureCapture, reading.id);
          if (wasApplied) {
            const remaining = readingsForArmedTarget(remeasureCapture);
            if (remaining.length) {
              const newest = remaining.slice()
                .sort((a, b) => String(b.capturedAt).localeCompare(String(a.capturedAt)))[0];
              remeasureCapture = selectFieldCaptureReading(remeasureCapture, newest.id);
              activeRemeasure = setRemeasureValue(activeRemeasure, remeasureArmed.key, newest.valueUnits);
              remeasureEl('remeasure-status').textContent = `${remeasureArmed.label} ← ${fmt(newest.valueUnits)} (newest remaining reading).`;
            } else {
              const original = activeRemeasure.measurements.find((m) => m.key === remeasureArmed.key);
              activeRemeasure = setRemeasureValue(activeRemeasure, remeasureArmed.key, original.originalUnits);
              remeasureEl('remeasure-status').textContent = `${remeasureArmed.label} reverted to the original ${fmt(original.originalUnits)} — no readings remain.`;
            }
            await persistActiveRemeasure();
          }
          renderRemeasure();
          renderRemeasureReadings();
        } catch (error) {
          remeasureEl('remeasure-status').textContent = error.message;
        }
      });
      rowEl.append(pick, drop);
      return rowEl;
    });
    box.replaceChildren(head, ...rows);
  }
  on('open-remeasure', 'click', openRemeasure);
  on('remeasure-close', 'click', () => {
    remeasureEl('remeasure-overlay').hidden = true;
    remeasureArmed = null; // a reading after closing must have no destination here
    // Closing the RM sheet is the owner-chosen moment its records back up (N1).
    sync?.syncNow?.().catch(() => {});
  });
  on('remeasure-connect-laser', 'click', connectCaptureLaser);
  on('remeasure-start', 'click', async () => {
    activeRemeasure = createRemeasureSession({ id: newId(), sketch: serializeSketch(model.state, current) });
    await persistActiveRemeasure();
    renderRemeasure();
  });
  on('remeasure-technician', 'change', async (event) => {
    if (!activeRemeasure || activeRemeasure.status === 'ready_for_engineering') return;
    activeRemeasure = { ...activeRemeasure, technician: event.target.value.trim(), updatedAt: new Date().toISOString() };
    await persistActiveRemeasure();
  });
  on('remeasure-complete', 'click', async () => {
    if (!activeRemeasure) return;
    const ok = await confirmAction({
      title: 'Send remeasure to Engineering?',
      body: 'This locks the Actual / Remeasure dataset. The Designer Original remains unchanged.',
      confirmLabel: 'Ready for Engineering', cancelLabel: 'Keep editing',
    });
    if (!ok) return;
    const technician = remeasureEl('remeasure-technician').value.trim();
    if (!technician) {
      remeasureEl('remeasure-status').textContent = 'Enter the remeasure technician before sending to Engineering.';
      return;
    }
    activeRemeasure = completeRemeasureSession(activeRemeasure, { technician });
    await persistActiveRemeasure();
    renderRemeasure();
  });
  on('remeasure-print', 'click', () => window.print());

  // Client inventory link: opportunity-scoped request/response JSON lives in
  // the URL fragment, so it never reaches Cloudflare logs and works with the
  // installed offline app. The client names spaces; a designer explicitly maps
  // one or more of them into the currently open design space before import.
  let inventoryMode = 'designer';
  let inventoryPayload = null;
  const inventoryEl = (id) => root.getElementById(id);
  const INVENTORY_INBOX_KEY = 'room-sketch:client-inventory-inbox:v2';
  function inventoryInbox() {
    try {
      const values = JSON.parse(localStorage.getItem(INVENTORY_INBOX_KEY) ?? '[]');
      return Array.isArray(values) ? values.flatMap((value) => {
        try { return [validateResponse(value)]; } catch { return []; }
      }) : [];
    } catch { return []; }
  }
  function rememberInventoryResponse(response) {
    if (response.schemaVersion !== 2 || !response.opportunity) return;
    try {
      const kept = inventoryInbox().filter((item) => item.requestId !== response.requestId);
      localStorage.setItem(INVENTORY_INBOX_KEY, JSON.stringify([response, ...kept].slice(0, 20)));
    } catch {
      // The response remains fully reviewable from its URL even when this
      // browser refuses or has exhausted local storage.
    }
  }
  function inventoryResponseForCurrentOpportunity() {
    return inventoryInbox().find((response) => response.schemaVersion === 2
      && response.opportunity?.client === current.client.trim()
      && response.opportunity?.opportunity === current.opportunity.trim()) ?? null;
  }
  function updateInventoryInboxFlag() {
    const button = inventoryEl('open-inventory');
    if (!button) return;
    const received = Boolean(inventoryResponseForCurrentOpportunity());
    button.classList.toggle('has-client-response', received);
    button.textContent = received ? 'Inv •' : 'Inv';
    button.title = received ? 'Client inventory received for this opportunity' : 'Create or review a client inventory request';
  }
  function inventoryUrl(kind, payload) {
    const url = new URL(kind === 'inventory' ? '/client-inventory.html' : '/index.html', window.location.origin);
    url.hash = `${kind}=${encodeInventoryPayload(payload)}`;
    return url.toString();
  }
  function inventoryFieldRow(field, answer = null) {
    const row = document.createElement('div'); row.className = 'inventory-row'; row.dataset.key = field.key;
    const label = field.label ?? field.name ?? field.key;
    row.append(el('span', null, `${label} (${field.unit})`));
    for (const name of ['existing', 'needed']) {
      const value = answer?.[name];
      row.append(el('span', null, value == null ? '—' : (field.kind === 'hang' ? fmt(value) : String(value))));
    }
    return row;
  }

  function inventoryOpportunityMatches(response) {
    if (response.schemaVersion === 1) return response.sourceSketchId === current.id;
    if (response.legacySourceSketchId) return response.legacySourceSketchId === current.id;
    return response.opportunity?.client === current.client.trim()
      && response.opportunity?.opportunity === current.opportunity.trim();
  }

  function inventoryReviewSet(space, slot, label, fields) {
    const section = document.createElement('section');
    section.className = 'inventory-set-review';
    section.append(el('h4', null, label));
    const answers = new Map(space.inventories[slot].map((answer) => [answer.key, answer]));
    for (const field of fields) {
      const answer = answers.get(field.key);
      section.append(inventoryFieldRow(field, answer));
      if (field.kind === 'shoes' && answer?.shoeGroups?.length) {
        const detail = document.createElement('div'); detail.className = 'inventory-shoe-details';
        for (const group of answer.shoeGroups) {
          detail.append(el('div', null, `${group.label}: ${group.existingPairs ?? '—'} current / ${group.neededPairs ?? 'same'} needed · ${group.widthUnits == null ? '—' : fmt(group.widthUnits)} wide · ${group.lengthUnits == null ? '—' : fmt(group.lengthUnits)} toe to heel`));
        }
        section.append(detail);
      }
    }
    return section;
  }

  function updateInventoryImportButton() {
    const apply = inventoryEl('inventory-apply');
    if (inventoryMode !== 'response' || !inventoryPayload) { apply.disabled = true; return; }
    if (inventoryPayload.schemaVersion === 1) {
      apply.disabled = !inventoryOpportunityMatches(inventoryPayload);
      return;
    }
    const selected = [...inventoryEl('inventory-fields').querySelectorAll('[data-inventory-mapping]')]
      .some((select) => select.value !== 'skip');
    apply.disabled = !inventoryOpportunityMatches(inventoryPayload) || !selected;
  }

  function renderOpportunityResponse(response, fields) {
    const opportunity = document.createElement('p');
    opportunity.className = 'inventory-opportunity';
    opportunity.textContent = response.opportunity
      ? `${response.opportunity.client} · ${response.opportunity.opportunity}`
      : 'Legacy sketch-specific request';
    fields.append(opportunity);
    for (const space of response.spaces) {
      const card = document.createElement('section');
      card.className = 'inventory-space-review';
      card.dataset.responseSpace = space.id;
      const head = document.createElement('div');
      head.className = 'inventory-space-review__head';
      head.append(el('h3', null, space.name));
      const mapping = document.createElement('select');
      mapping.className = 'inventory-space-review__mapping';
      mapping.dataset.inventoryMapping = space.id;
      mapping.setAttribute('aria-label', `Import destination for ${space.name}`);
      const choices = space.mode === 'ab'
        ? [['skip', 'Do not import'], ['ab', 'Import A → A and B → B'], ['ba', 'Import A → B and B → A']]
        : [['skip', 'Do not import'], ['space', 'Import as whole space'], ['a', 'Import into A'], ['b', 'Import into B']];
      for (const [value, label] of choices) {
        const option = document.createElement('option'); option.value = value; option.textContent = label; mapping.append(option);
      }
      mapping.addEventListener('change', updateInventoryImportButton);
      head.append(mapping);
      card.append(head);
      if (space.mode === 'ab') {
        card.append(inventoryReviewSet(space, 'a', 'Inventory A', response.fields));
        card.append(inventoryReviewSet(space, 'b', 'Inventory B', response.fields));
      } else {
        card.append(inventoryReviewSet(space, 'space', 'Inventory', response.fields));
      }
      if (space.notes) {
        const notes = document.createElement('p');
        notes.className = 'inventory-notes';
        notes.textContent = `Notes: ${space.notes}`;
        card.append(notes);
      }
      fields.append(card);
    }
  }

  function exposeDesignerInventoryShare(url) {
    const subject = `Storage inventory request — ${current.opportunity.trim()}`;
    const body = `Please complete the storage inventory for ${current.client.trim()} · ${current.opportunity.trim()}. This standalone form supports multiple spaces and saves progress on the same device.\n\n${url}`;
    const email = inventoryEl('inventory-email');
    const text = inventoryEl('inventory-text');
    email.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    text.href = `sms:?body=${encodeURIComponent(body)}`;
    email.hidden = false;
    text.hidden = false;
  }

  function renderInventoryOverlay() {
    const fields = inventoryEl('inventory-fields');
    const link = inventoryEl('inventory-link');
    const generate = inventoryEl('inventory-generate');
    const apply = inventoryEl('inventory-apply');
    const copy = inventoryEl('inventory-copy');
    const email = inventoryEl('inventory-email');
    const text = inventoryEl('inventory-text');
    fields.replaceChildren(); link.value = ''; link.hidden = true; copy.hidden = true;
    email.hidden = true; text.hidden = true;
    generate.hidden = false;
    generate.textContent = inventoryMode === 'designer' ? 'Create opportunity request' : 'Create another request';
    inventoryEl('inventory-submit').hidden = true;
    apply.hidden = inventoryMode !== 'response';
    if (inventoryMode === 'designer') {
      inventoryEl('inventory-status').textContent = 'Create one standalone, multi-space request for this opportunity. The client can save, resume, and return a report for designer mapping.';
      const scope = document.createElement('p');
      scope.className = 'inventory-opportunity';
      scope.textContent = current.client.trim() && current.opportunity.trim()
        ? `${current.client.trim()} · ${current.opportunity.trim()}`
        : 'Enter Client and Opportunity in the top bar before creating the request.';
      fields.append(scope);
    } else if (inventoryPayload.schemaVersion === 1) {
      inventoryEl('inventory-status').textContent = inventoryOpportunityMatches(inventoryPayload)
        ? 'Legacy response: review, then import it into this sketch.'
        : 'This legacy response belongs to another sketch. Open that sketch before importing.';
      const head = document.createElement('div'); head.className = 'inventory-row inventory-row--head';
      head.append(el('span', null, 'Storage category'), el('span', null, 'Current'), el('span', null, 'Need'));
      fields.append(head);
      const answers = new Map(inventoryPayload.values.map((answer) => [answer.key, answer]));
      for (const field of inventoryPayload.fields) {
        const answer = answers.get(field.key);
        fields.append(inventoryFieldRow(field, answer));
        if (field.kind === 'shoes' && answer?.shoeGroups?.length) {
          const detail = document.createElement('div'); detail.className = 'inventory-shoe-details';
          for (const group of answer.shoeGroups) {
            detail.append(el('div', null, `${group.label}: ${group.existingPairs ?? '—'} current / ${group.neededPairs ?? 'same'} needed · ${group.widthUnits == null ? '—' : fmt(group.widthUnits)} wide · ${group.lengthUnits == null ? '—' : fmt(group.lengthUnits)} toe to heel`));
          }
          fields.append(detail);
        }
      }
    } else {
      inventoryEl('inventory-status').textContent = inventoryOpportunityMatches(inventoryPayload)
        ? `Review the client spaces. Choose which ones belong in ${current.space.trim() || 'this design space'}, then import.`
        : 'This response belongs to another opportunity. Open a space with the exact Client and Opportunity shown below.';
      renderOpportunityResponse(inventoryPayload, fields);
    }
    updateInventoryImportButton();
  }
  function openInventory(mode = 'designer', payload = null) {
    inventoryMode = mode; inventoryPayload = payload;
    inventoryEl('inventory-overlay').hidden = false;
    renderInventoryOverlay();
  }
  on('open-inventory', 'click', () => {
    const received = inventoryResponseForCurrentOpportunity();
    openInventory(received ? 'response' : 'designer', received);
  });
  on('inventory-close', 'click', () => { inventoryEl('inventory-overlay').hidden = true; });
  on('inventory-generate', 'click', async () => {
    const generate = inventoryEl('inventory-generate');
    generate.disabled = true;
    try {
      inventoryPayload = createOpportunityInventoryRequest({
        id: newId(),
        opportunity: { client: current.client, opportunity: current.opportunity },
        types: model.storageNeedTypes(),
      });
      const portableUrl = inventoryUrl('inventory', inventoryPayload);
      let shareUrl = portableUrl;
      let shortened = false;
      if (supabase) {
        try {
          const code = await publishInventoryShareLink(supabase, inventoryPayload);
          shareUrl = inventoryShareUrl(code, window.location.origin);
          shortened = true;
        } catch {
          // Offline, signed-out, or pre-migration clients retain the complete
          // request in the URL, so link creation never becomes a cloud gate.
        }
      }
      const link = inventoryEl('inventory-link'); link.value = shareUrl; link.hidden = false;
      inventoryEl('inventory-copy').hidden = false;
      exposeDesignerInventoryShare(link.value);
      inventoryEl('inventory-status').textContent = shortened
        ? `Short request link ready for ${current.client.trim()} · ${current.opportunity.trim()}. Use Email, Text, or Copy link; the client cannot access Room Sketch.`
        : 'The portable request link is ready. Sign in and connect to create a short link; this full link still works and the client cannot access Room Sketch.';
    } catch (error) { inventoryEl('inventory-status').textContent = error.message; }
    finally { generate.disabled = false; }
  });
  on('inventory-copy', 'click', async () => {
    const text = inventoryEl('inventory-link').value;
    try { await navigator.clipboard.writeText(text); inventoryEl('inventory-status').textContent = 'Link copied.'; }
    catch { inventoryEl('inventory-link').select(); inventoryEl('inventory-status').textContent = 'Copy the selected link.'; }
  });
  function applyLegacyInventoryResponse(payload, { automatic = false } = {}) {
    try {
      const response = validateResponse(payload);
      if (response.schemaVersion !== 1 || response.sourceSketchId !== current.id) throw new Error('Open the sketch this legacy response belongs to before importing.');
      for (const answer of response.values) {
        if (answer.existing != null) model.setStorageNeed(answer.key, 'existing', 'space', String(answer.existing));
        if (answer.needed != null) model.setStorageNeed(answer.key, 'needed', 'space', String(answer.needed));
        if (Array.isArray(answer.shoeGroups) && !model.setShoeInventoryGroups(answer.key, answer.shoeGroups)) {
          throw new Error('The footwear measurements could not be imported.');
        }
      }
      if (!model.markStorageNeedsClientProvided(response)) throw new Error('Client source information is incomplete.');
      renderAll(); scheduleSave();
      inventoryEl('inventory-status').textContent = automatic
        ? 'Client inventory received, imported, and flagged in Storage needs.'
        : 'Client inventory imported and flagged in Storage needs.';
      history.replaceState(null, '', `${location.pathname}${location.search}`);
      return true;
    } catch (error) {
      inventoryEl('inventory-status').textContent = error.message;
      return false;
    }
  }

  function addInventoryTotal(totals, key, field, slot, value) {
    if (value == null) return;
    const row = totals.get(key) ?? {
      existing: { space: null, a: null, b: null },
      needed: { space: null, a: null, b: null },
      shoeGroups: [],
    };
    row[field][slot] = (row[field][slot] ?? 0) + value;
    totals.set(key, row);
  }

  function applyOpportunityInventoryResponse(payload) {
    try {
      const response = validateResponse(payload);
      if (response.schemaVersion !== 2 || !inventoryOpportunityMatches(response)) throw new Error('Open a space in the response opportunity before importing.');
      const mappingBySpace = new Map([...inventoryEl('inventory-fields').querySelectorAll('[data-inventory-mapping]')]
        .map((select) => [select.dataset.inventoryMapping, select.value]));
      const selected = response.spaces.filter((space) => mappingBySpace.get(space.id) !== 'skip');
      if (!selected.length) throw new Error('Choose at least one client space to import.');
      const selectedMappings = selected.map((space) => mappingBySpace.get(space.id));
      if (selectedMappings.includes('space') && selectedMappings.some((mapping) => ['a', 'b', 'ab', 'ba'].includes(mapping))) {
        throw new Error('A whole-space import cannot be combined with A/B imports. Assign each selected single inventory to A or B, or import only whole-space inventories.');
      }
      if (model.state.sharedSpace.enabled && selectedMappings.includes('space')) {
        throw new Error('This design space already uses A/B. Assign each selected single inventory to A or B.');
      }
      const totals = new Map();
      const importedSpaces = [];
      let usesAB = false;
      const addSet = (space, sourceSlot, targetSlot) => {
        if (targetSlot === 'a' || targetSlot === 'b') usesAB = true;
        const slotLabel = sourceSlot === 'space' ? '' : ` ${sourceSlot.toUpperCase()}`;
        for (const answer of space.inventories[sourceSlot]) {
          addInventoryTotal(totals, answer.key, 'existing', targetSlot, answer.existing);
          addInventoryTotal(totals, answer.key, 'needed', targetSlot, answer.needed);
          if (answer.shoeGroups?.length) {
            const row = totals.get(answer.key);
            row.shoeGroups.push(...answer.shoeGroups.map((group) => ({
              ...group,
              slot: targetSlot,
              id: `${space.id}:${sourceSlot}:${group.id}`.slice(0, 80),
              label: `${space.name}${slotLabel} — ${group.label}`.slice(0, 80),
            })));
          }
        }
      };
      for (const space of selected) {
        const mapping = mappingBySpace.get(space.id);
        if (space.mode === 'single') addSet(space, 'space', mapping);
        else if (mapping === 'ab') { addSet(space, 'a', 'a'); addSet(space, 'b', 'b'); }
        else { addSet(space, 'a', 'b'); addSet(space, 'b', 'a'); }
        importedSpaces.push({ id: space.id, name: space.name, mapping, notes: space.notes });
      }
      if (usesAB) model.setSharedSpace(true);
      for (const [key, row] of totals) {
        if (row.shoeGroups.length && !model.setShoeInventoryGroups(key, row.shoeGroups)) throw new Error('The footwear measurements could not be imported.');
        for (const field of ['existing', 'needed']) {
          for (const slot of ['space', 'a', 'b']) {
            if (row[field][slot] != null) model.setStorageNeed(key, field, slot, String(row[field][slot]));
          }
        }
      }
      if (!model.markStorageNeedsClientProvided({
        requestId: response.requestId,
        sourceSketchId: current.id,
        submittedAt: response.submittedAt,
        opportunity: response.opportunity,
        importedSpaces,
      })) throw new Error('Client source information is incomplete.');
      renderAll(); scheduleSave();
      inventoryEl('inventory-status').textContent = `Imported ${selected.length} client ${selected.length === 1 ? 'space' : 'spaces'} into ${current.space.trim() || 'this design space'} and flagged Storage needs. The response remains available for other design spaces.`;
      updateInventoryInboxFlag();
      return true;
    } catch (error) {
      inventoryEl('inventory-status').textContent = error.message;
      return false;
    }
  }

  on('inventory-apply', 'click', () => {
    if (inventoryPayload?.schemaVersion === 1) applyLegacyInventoryResponse(inventoryPayload);
    else applyOpportunityInventoryResponse(inventoryPayload);
  });
  function handleInventoryHash() {
    const hash = window.location.hash.slice(1);
    if (hash.startsWith('inventory=')) {
      const url = new URL('/client-inventory.html', window.location.origin);
      url.hash = hash;
      window.location.replace(url.toString());
      return;
    } else if (hash.startsWith('inventory-response=')) {
      try {
        const response = validateResponse(decodeInventoryPayload(hash.slice('inventory-response='.length)));
        rememberInventoryResponse(response);
        updateInventoryInboxFlag();
        openInventory('response', response);
        // Version 1 links retain their historical matching-sketch auto-import.
        // Version 2 is opportunity-scoped and always waits for explicit mapping.
        if (response.schemaVersion === 1 && response.sourceSketchId === current.id) {
          applyLegacyInventoryResponse(response, { automatic: true });
        }
      }
      catch (error) { openInventory(); inventoryEl('inventory-status').textContent = error.message; }
    }
  }
  window.addEventListener('hashchange', handleInventoryHash);
  updateInventoryInboxFlag();

  // Side-panel tabs (owner, 2026-07-29): the same collapsible sections, split
  // between the empty room (Space) and what gets built into it (Design).
  //
  // Tab visibility composes with the existing plan/elevation rules rather than
  // replacing them: a section hidden because it is elevation-only stays hidden
  // in the Space tab on the plan. Two independent reasons to hide something, and
  // neither one has to know about the other.
  //
  // Remembered per device, like the display size — a designer working through
  // sections should not land back on Space after every reload.
  (() => {
    const TABS = [
      { id: 'space', btn: root.getElementById('tab-btn-space'), panel: root.getElementById('tab-space') },
      { id: 'design', btn: root.getElementById('tab-btn-design'), panel: root.getElementById('tab-design') },
    ].filter((t) => t.btn && t.panel);
    if (TABS.length !== 2) return;
    const KEY = 'room-sketch:panel-tab';
    const show = (id) => {
      const target = TABS.some((t) => t.id === id) ? id : 'space';
      for (const tab of TABS) {
        const on = tab.id === target;
        tab.panel.hidden = !on;
        tab.btn.classList.toggle('panel-tabs__tab--active', on);
        tab.btn.setAttribute('aria-selected', String(on));
      }
      try { window.localStorage?.setItem(KEY, target); } catch { /* private mode */ }
    };
    for (const tab of TABS) tab.btn.addEventListener('click', () => show(tab.id));
    let stored = 'space';
    try { stored = window.localStorage?.getItem(KEY) ?? 'space'; } catch { stored = 'space'; }
    show(stored);
  })();

  // Display size (owner, 2026-07-29). A 12.7" 3K tablet reports a ~1280px CSS
  // viewport across a foot of glass: the layout FITS, it is just physically
  // small. No viewport measurement can tell us that — CSS pixels carry no
  // physical size — so this is a control, not a heuristic.
  //
  // `zoom` (not transform) because it re-lays-out: at 130% the media queries see
  // ~985px and the panel stacks under the drawing exactly as it would on a
  // smaller screen, instead of a scaled-up picture that overflows.
  //
  // Stored per DEVICE in localStorage, never in the sketch — the same sketch
  // opened on a laptop must not inherit a tablet's zoom.
  (() => {
    const input = root.getElementById('ui-scale');
    const scaleDown = root.getElementById('ui-scale-down');
    const scaleUp = root.getElementById('ui-scale-up');
    if (!input || !scaleDown || !scaleUp) return;
    const KEY = 'room-sketch:ui-scale';
    const MIN_PERCENT = 50;
    const MAX_PERCENT = 400;
    const STEP_PERCENT = 5;
    /**
     * The layout needs a floor of usable width; below this it overflows and the
     * page starts scrolling — including, potentially, this very control off the
     * edge of the screen. So the ceiling is whatever the screen can actually
     * show, not a number picked in the abstract: a wide tablet allows more zoom
     * than a small one, and the field shows the figure that took effect.
     */
    const MIN_EFFECTIVE_UNITS = 640;
    const ceilingPercent = () => {
      const width = root.documentElement.clientWidth || 0;
      const fits = Math.floor((width / MIN_EFFECTIVE_UNITS) * 100);
      return Math.max(MIN_PERCENT, Math.min(MAX_PERCENT, fits || MAX_PERCENT));
    };
    /**
     * Reads a percentage the designer typed. Anything unreadable falls back to
     * 100 rather than to some previous value, so a slip is always recoverable by
     * eye. Out of range CLAMPS (rather than rejecting) because the intent of
     * "500" is unambiguous — as far up as it goes.
     *
     * Also accepts the fractional form (1.5) that the earlier fixed-list version
     * stored, so a device that already had a size set keeps it.
     */
    const cleanPercent = (raw) => {
      const v = Number(String(raw ?? '').trim());
      if (!Number.isFinite(v) || v <= 0) return 100;
      const percent = v <= 10 ? v * 100 : v; // 1.5 (old form) → 150
      return Math.min(ceilingPercent(), Math.max(MIN_PERCENT, Math.round(percent)));
    };
    /**
     * Side-by-side or stacked, decided on the width the layout ACTUALLY has.
     * A CSS media query cannot do this: it measures the UNZOOMED viewport, so at
     * 200% it still sees a wide screen while the layout has half that width, and
     * the page overflows (found in testing, 2026-07-29).
     */
    const STACK_BELOW_UNITS = 900;
    const isPhoneLayout = () => {
      // Chrome's "Desktop site" mode can report a tablet-sized layout viewport
      // on a phone. `screen.width` remains the physical device's CSS width, so
      // use touch + its short side to keep a phone out of the desktop side-panel
      // layout even when the page viewport lies.
      const shortSide = Math.min(window.screen?.width ?? Infinity, window.screen?.height ?? Infinity);
      return window.matchMedia?.('(pointer: coarse)').matches && shortSide <= 600;
    };
    const applyLayoutMode = (scale) => {
      const effective = root.documentElement.clientWidth / (scale || 1);
      const phone = isPhoneLayout();
      root.documentElement.classList.toggle('phone-layout', phone);
      workspace?.classList.toggle('workspace--stacked', phone || effective < STACK_BELOW_UNITS);
    };
    const apply = (rawPercent) => {
      const percent = cleanPercent(rawPercent);
      const scale = percent / 100;
      // 100% clears the property rather than setting zoom:1, so a browser
      // without `zoom` is left completely untouched.
      root.documentElement.style.zoom = percent === 100 ? '' : String(scale);
      // The stylesheet divides the body's 100dvh by this: viewport units ignore
      // zoom, so without it the page would be `scale`× the screen and scroll.
      root.documentElement.style.setProperty('--ui-scale', String(scale));
      // Write the CLAMPED figure back, so the field never shows a number that
      // is not the one in effect.
      input.value = String(percent);
      scaleDown.disabled = percent <= MIN_PERCENT;
      scaleUp.disabled = percent >= ceilingPercent();
      applyLayoutMode(scale);
      // The drawing surface changed size; refit so nothing sits off-screen.
      if (currentView === 'elevation') elevation?.fit();
      else canvas.fit(model.allPoints());
      return percent;
    };
    // Rotating the tablet changes the width the layout has, not the zoom — and
    // with it how far the display size can go, so re-apply (which re-clamps).
    window.addEventListener('resize', () => {
      input.max = String(ceilingPercent());
      apply(input.value);
    });
    input.max = String(ceilingPercent());
    let stored = 100;
    try { stored = cleanPercent(window.localStorage?.getItem(KEY)); } catch { stored = 100; }
    apply(stored);
    const commit = () => {
      const percent = apply(input.value);
      try { window.localStorage?.setItem(KEY, String(percent)); } catch { /* private mode */ }
    };
    const stepBy = (delta) => {
      input.value = String(cleanPercent(input.value) + delta);
      commit();
    };
    scaleDown.addEventListener('click', () => stepBy(-STEP_PERCENT));
    scaleUp.addEventListener('click', () => stepBy(STEP_PERCENT));
    input.addEventListener('change', commit);
    input.addEventListener('keydown', (evt) => {
      if (evt.key === 'Enter') { commit(); input.blur(); }
    });
  })();

  // Full screen: drops the browser's tabs and URL bar, which cost real drawing
  // space on a tablet (owner, 2026-07-29). Feature-detected — the button stays
  // hidden where the Fullscreen API is unavailable (notably iPhone/iPad Safari,
  // where the equivalent is Share → Add to Home Screen, which the manifest and
  // the apple-mobile-web-app meta tags make open chrome-free).
  (() => {
    const btn = root.getElementById('toggle-fullscreen');
    // The toolbar twin. It exists ONLY while full screen is on, because that is
    // when the app bar — and with it `btn` — is hidden (owner, 2026-08-12).
    // Keeping the everyday control in the bar is what avoids the 48px toolbar
    // wrap that made this button leave the toolbar in the first place.
    const exitBtn = root.getElementById('exit-fullscreen');
    if (!btn) return;
    // The API existing is not enough: iPhone Safari exposes pieces of it while
    // reporting fullscreenEnabled false, and an embedded/permission-restricted
    // browser rejects the request. Both would leave a button that does nothing.
    const hasApi = typeof root.documentElement?.requestFullscreen === 'function'
      || typeof root.documentElement?.webkitRequestFullscreen === 'function';
    const allowed = root.fullscreenEnabled !== false && root.webkitFullscreenEnabled !== false;
    const canFullscreen = hasApi && allowed;
    // Already running as an installed app? Then there is no chrome to hide.
    const standalone = typeof window !== 'undefined'
      && (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true);
    if (!canFullscreen || standalone) return;
    btn.hidden = false;
    const sync = () => {
      const on = !!(root.fullscreenElement || root.webkitFullscreenElement);
      btn.setAttribute('aria-pressed', String(on));
      btn.classList.toggle('is-active', on);
      setIconTip(btn, on ? 'Leave full screen' : 'Full screen', on);
      // The app bar goes with it, and the toolbar twin takes over. Driven off
      // the BROWSER's own state, never off which button was tapped, so pressing
      // Esc (or F11) leaves the header and the buttons in step.
      root.body?.classList.toggle('is-fullscreen', on);
      if (exitBtn) exitBtn.hidden = !on;
      // The drawing area just changed size; refit so nothing sits off-screen.
      if (currentView === 'elevation') elevation?.fit();
      else canvas.fit(model.allPoints());
    };
    const toggle = () => {
      const el = root.documentElement;
      const active = root.fullscreenElement || root.webkitFullscreenElement;
      const done = active
        ? (root.exitFullscreen?.() ?? root.webkitExitFullscreen?.())
        : (el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.());
      // A rejected request (some browsers require a trusted gesture chain) must
      // not leave the button claiming a state the browser is not in.
      Promise.resolve(done).catch(() => {}).finally(sync);
    };
    btn.addEventListener('click', toggle);
    exitBtn?.addEventListener('click', toggle);
    root.addEventListener('fullscreenchange', sync);
    root.addEventListener('webkitfullscreenchange', sync);
  })();

  // Depth line: a custom distance for the selected wall.
  const addCustomDepthLine = () => {
    const input = root.getElementById('depth-line-custom');
    const selected = model.selectedSegment();
    if (!selected || (selected.segmentKind ?? 'wall') !== 'wall') return;
    const parsed = parseLength(input.value);
    const ok = parsed !== null && model.addPlanHelperLine(selected.id, parsed);
    input.classList.toggle('depth-lines__input--invalid', !ok);
    if (ok) {
      input.value = '';
      renderDepthLines();
      repaintView();
      scheduleSave();
    }
  };
  on('depth-line-add', 'click', addCustomDepthLine);
  on('depth-line-custom', 'keydown', (evt) => {
    if (evt.key === 'Enter') addCustomDepthLine();
  });

  // Add a unified wall-attached opening. Unlike the retired chain-splitting
  // behavior, this keeps the physical host wall (and header) intact.
  on('action-door', 'click', () => {
    const selected = model.selectedSegment();
    if (!selected || (selected.segmentKind ?? 'wall') !== 'wall' || model.isPartition(selected.id)) {
      status.textContent = 'Select a physical wall first, then Add opening.';
      return;
    }
    const id = model.addObstruction(selected.id, 'wall_opening');
    if (!id) {
      status.textContent = 'That wall is too short to hold this opening.';
      return;
    }
    renderGeometry();
    status.textContent = 'Opening added to this wall. Choose its type in the Obstructions panel.';
  });

  // Add a two-sided interior/partition wall to the selected wall (ADR 0032).
  on('action-partition', 'click', () => {
    const selected = model.selectedSegment();
    if (!selected || (selected.segmentKind ?? 'wall') !== 'wall') {
      status.textContent = 'Select a wall first, then Add partition.';
      return;
    }
    const id = model.addPartition(selected.id);
    if (!id) {
      status.textContent = 'That wall can’t host a partition.';
      return;
    }
    setMode('select');
    renderAll(); // a new partition card appears in the panel
  });

  // Pin / unpin the selected wall as Wall 1. Renumbering rotates the chain but
  // keeps every segment id, so the selection — and everything keyed on wall
  // ids, like obstructions — carries through; the table needs a rebuild
  // because row order changes → renderAll.
  on('action-wall-one', 'click', () => {
    const selected = model.selectedSegment();
    if (!selected || (selected.segmentKind ?? 'wall') !== 'wall') return;
    const changed =
      model.isWallOnePinned() && selected.id === model.state.wallOneOverrideId
        ? model.clearWallOne()
        : model.setWallOne(selected.id);
    if (changed) renderAll();
  });

  // Clear everything throws away the whole sketch, so it asks first (owner,
  // 2026-07-29). Nothing else in the app is this destructive in one tap.
  on('action-clear', 'click', async () => {
    const ok = await confirmAction({
      title: 'Clear everything?',
      body: 'The walls, sections, obstructions and sketch on this drawing are all deleted. This cannot be undone.',
      confirmLabel: 'Clear everything',
      cancelLabel: 'Keep it',
      danger: true,
    });
    if (!ok) return;
    model.clear();
    canvas.resetView();
    renderAll();
    scheduleSave();
  });
  // Fit re-centers and sizes the current view; zoom itself is pinch / wheel
  // (the +/- buttons were redundant once both gestures existed — owner, 2026-07-25).
  on('action-fit', 'click', () => (currentView === 'elevation' ? elevation?.fit() : canvas.fit(model.allPoints())));

  // Plan / Elevation view toggle.
  on('view-plan', 'click', () => setView('plan'));
  on('view-elevation', 'click', () => setView('elevation'));


  // Obstructions: place the chosen type on the selected wall.
  populateObstructionTypes();
  on('add-obstruction', 'click', async () => {
    const host = activeWallHost();
    if (!host) return;
    const typeKey = root.getElementById('obstruction-type')?.value;
    const type = (rules.obstructionTypes ?? []).find((t) => t.key === typeKey);
    if (!(await confirmObstructionAdd(shortTypeName(type?.label ?? 'obstruction'), host.label))) return;
    if (!model.addObstruction(host.id, typeKey)) return;
    renderObstructions();
    repaintView();
    scheduleSave();
  });

  wireObstructionPalette();

  /**
   * Drag a new fixture out of the palette and drop it on the elevation.
   *
   * This uses raw pointer events with pointer capture rather than the HTML5
   * drag-and-drop API, because HTML5 DnD does not fire for touch — and a tablet
   * is the device this tool is actually used on. Capture also means the gesture
   * keeps working while the finger is over the SVG, which is a different
   * element in a different subtree.
   *
   * A drop that misses a wall panel is discarded rather than guessed at: there
   * is no sensible wall to attribute it to, and CLAUDE.md rules out guessing.
   */
  function wireObstructionPalette() {
    const grip = root.getElementById('obstruction-drag');
    if (!grip || !elevation) return;
    let ghost = null;

    const typeFor = () => {
      const key = root.getElementById('obstruction-type')?.value;
      return (rules.obstructionTypes ?? []).find((t) => t.key === key) ?? null;
    };

    grip.addEventListener('pointerdown', (evt) => {
      const type = typeFor();
      if (!type) return;
      evt.preventDefault();
      grip.setPointerCapture(evt.pointerId);
      ghost = document.createElement('div');
      ghost.className = 'obstruction-ghost';
      ghost.textContent = type.label;
      document.body.append(ghost);
      positionGhost(evt.clientX, evt.clientY);
    });

    function positionGhost(x, y) {
      if (!ghost) return;
      ghost.style.left = `${x}px`;
      ghost.style.top = `${y}px`;
      const over = currentView === 'elevation' && elevation.containsPoint(x, y);
      ghost.classList.toggle('obstruction-ghost--over', over);
    }

    grip.addEventListener('pointermove', (evt) => {
      if (ghost) positionGhost(evt.clientX, evt.clientY);
    });

    function finish(evt) {
      if (!ghost) return;
      ghost.remove();
      ghost = null;
      try { grip.releasePointerCapture(evt.pointerId); } catch { /* not captured */ }
      const type = typeFor();
      if (!type || currentView !== 'elevation') return;
      if (!elevation.containsPoint(evt.clientX, evt.clientY)) return;
      const placement = elevation.placementAt(evt.clientX, evt.clientY, type.width, type.height);
      if (!placement) {
        status.textContent = 'Drop it on a wall panel to place it.';
        return;
      }
      if (!model.addObstruction(placement.wallId, type.key, placement)) return;
      model.selectWall(placement.wallId);
      renderGeometry();
    }
    grip.addEventListener('pointerup', finish);
    grip.addEventListener('pointercancel', finish);
  }

  // Ceiling / door heights (parsed like a wall length). Re-render the elevation
  // and save; flag an invalid entry without discarding it.
  const onHeight = (id, apply) =>
    on(id, 'change', (evt) => {
      const ok = apply(evt.target.value);
      evt.target.classList.toggle('height-field__input--invalid', ok === false);
      if (ok) {
        if (currentView === 'elevation') renderElevation();
        scheduleSave();
      }
    });
  onHeight('ceiling-height', (v) => {
    const ok = model.setCeilingHeight(v);
    if (ok) {
      // Design's target placeholder and computed stack-top note both inherit
      // the room ceiling when no explicit target is set.
      renderWallHeights();
      renderSectionsPanel();
    }
    return ok;
  });
  onHeight('door-height', (v) => model.setDoorHeight(v));
  on('ceiling-flat', 'change', (event) => {
    model.setCeilingFlat(event.target.checked);
    renderWallHeights();
    if (currentView === 'elevation') renderElevation();
    scheduleSave();
  });
  makeCaptureTarget(fieldEl('ceiling-height'), {
    kind: 'space_field', entityId: 'active-space', property: 'ceilingHeightUnits', label: 'Ceiling height',
  });
  makeCaptureTarget(fieldEl('door-height'), {
    kind: 'space_field', entityId: 'active-space', property: 'doorHeightUnits', label: 'Door height',
  });
  const roomObstructionDraftTargets = [
    ['ceiling-opening-x', 'xUnits', 'Room obstruction · reference 1 distance'],
    ['ceiling-opening-y', 'yUnits', 'Room obstruction · reference 2 distance'],
    ['ceiling-opening-width', 'widthUnits', 'Room obstruction · width or fan diameter'],
    ['ceiling-opening-length', 'lengthUnits', 'Room obstruction · length'],
    ['ceiling-opening-height', 'heightUnits', 'Room obstruction · drop below ceiling'],
    ['ceiling-opening-floor', 'fromFloorUnits', 'Room obstruction · distance from finished floor'],
    ['ceiling-opening-projection', 'ladderFloorOffsetUnits', 'Attic stair · floor base from reference 2'],
  ];
  for (const [controlId, property, label] of roomObstructionDraftTargets) {
    makeCaptureTarget(fieldEl(controlId), {
      kind: 'ceiling_opening_draft', entityId: 'new-room-obstruction', property, label,
    });
  }
  on('obstruction-scope-room', 'click', () => setObstructionScope('room'));
  on('obstruction-scope-wall', 'click', () => setObstructionScope('wall'));
  setObstructionScope(obstructionScope);
  const configureCeilingFeatureForm = (kind) => {
    const fan = kind === 'ceiling_fan';
    fieldEl('ceiling-opening-ref-a-label').textContent = fan ? 'Reference 1 · center + size' : 'Reference 1 · width';
    fieldEl('ceiling-opening-ref-b-label').textContent = fan ? 'Reference 2 · center' : 'Reference 2 · length';
    fieldEl('ceiling-opening-x-label').textContent = fan ? 'Center from wall' : 'Inward from wall';
    fieldEl('ceiling-opening-y-label').textContent = fan ? 'Center from wall' : 'Inward from wall';
    fieldEl('ceiling-opening-width-label').textContent = fan ? 'Diameter' : 'Width';
    fieldEl('ceiling-opening-length-field').hidden = fan;
    fieldEl('ceiling-opening-height-field').hidden = fan;
    fieldEl('ceiling-opening-floor-field').hidden = !fan;
    fieldEl('ceiling-opening-projection-field').hidden = kind !== 'attic_stairs';
  };
  on('ceiling-opening-kind', 'change', (event) => {
    const kind = event.target.value;
    configureCeilingFeatureForm(kind);
    const type = (rules.obstructionTypes ?? []).find((item) => item.key === kind && item.host === 'ceiling');
    if (type) {
      fieldEl('ceiling-opening-width').value = fmt(type.width);
      fieldEl('ceiling-opening-length').value = fmt(type.depth ?? type.width);
      if (kind !== 'ceiling_fan') fieldEl('ceiling-opening-height').value = fmt(type.height);
    }
  });
  configureCeilingFeatureForm(fieldEl('ceiling-opening-kind').value);
  on('ceiling-opening-form', 'submit', (event) => {
    event.preventDefault();
    const kind = fieldEl('ceiling-opening-kind').value;
    const parse = (id, optional = false) => {
      const text = fieldEl(id).value.trim();
      return optional && text === '' ? null : parseLength(text);
    };
    const diameterUnits = kind === 'ceiling_fan' ? parse('ceiling-opening-width') : null;
    const id = model.addCeilingOpening({
      kind,
      referenceWallIds: [fieldEl('ceiling-opening-ref-a').value, fieldEl('ceiling-opening-ref-b').value],
      xUnits: parse('ceiling-opening-x'),
      yUnits: parse('ceiling-opening-y'),
      widthUnits: diameterUnits ?? parse('ceiling-opening-width'),
      lengthUnits: diameterUnits ?? parse('ceiling-opening-length'),
      heightUnits: kind === 'ceiling_fan' ? 1 : parse('ceiling-opening-height'),
      diameterUnits,
      fromFloorUnits: kind === 'ceiling_fan' ? parse('ceiling-opening-floor') : null,
      ladderFloorOffsetUnits: kind === 'attic_stairs' ? parse('ceiling-opening-projection', true) : null,
    });
    const error = fieldEl('ceiling-opening-error');
    if (!id) {
      error.textContent = kind === 'ceiling_fan'
        ? 'Choose two different, perpendicular walls and enter the fan center offsets, a positive diameter, and its non-negative distance from finished floor (FF).'
        : 'Choose two different, non-parallel walls and enter non-negative inward locations plus a positive room-obstruction size.';
      return;
    }
    error.textContent = '';
    for (const inputId of ['ceiling-opening-x', 'ceiling-opening-y', 'ceiling-opening-width', 'ceiling-opening-length', 'ceiling-opening-floor', 'ceiling-opening-projection']) fieldEl(inputId).value = '';
    fieldEl('ceiling-opening-height').value = '1';
    renderCeilingOpenings();
    repaintView();
    scheduleSave();
  });

  // Sketch identity fields (Client / Opportunity / Space) — commit on change
  // so the combined identity can be checked before it is saved. This is the
  // enforcement boundary for N4's one state per named space; typing cannot
  // rename a document on top of another space and recreate versioning by hand.
  const onIdentity = (id, key) =>
    on(id, 'change', async (evt) => {
      const candidate = { ...current, [key]: evt.target.value };
      const docs = await listSketches();
      if (spaceIdentityConflict(docs, candidate, { exceptId: current.id })) {
        evt.target.value = current[key];
        status.textContent = 'That Client · Opportunity · Space already exists. Choose another space name.';
        return;
      }
      current[key] = evt.target.value;
      if (key === 'client' || key === 'opportunity') updateInventoryInboxFlag();
      refreshOpportunitySpaceSwitcher();
      scheduleSave();
    });
  onIdentity('meta-client', 'client');
  onIdentity('meta-opportunity', 'opportunity');
  onIdentity('meta-space', 'space');
  on('space-switcher', 'change', async (evt) => {
    const id = evt.target.value;
    if (id === NEW_SPACE_OPTION) {
      // Creating, not switching: put the select back on the open sketch so
      // "+ New space…" never reads as the current space, then run the same
      // named-first flow the removed "+ Space" button used.
      evt.target.value = current.id;
      newSpaceIn(current.client, current.opportunity);
      return;
    }
    if (!id || id === current.id) return;
    if (saveTimer !== null) saveNow();
    await openSketch(id);
  });

  on('open-field-capture', 'click', openFieldCapture);
  on('close-field-capture', 'click', closeFieldCapture);
  on('close-phone-capture', 'click', closeFieldCapture);
  on('toggle-phone-capture', 'click', () => {
    const dock = fieldEl('phone-capture-dock');
    setPhoneCaptureCollapsed(!dock?.classList.contains('phone-capture-dock--collapsed'));
  });

  // Field Capture is a compact palette. Dragging its header repositions it for
  // this session: inside the drawing stage on tablet/desktop and inside the
  // viewport on a phone, with clamping so it cannot be lost off-screen.
  (() => {
    const dock = fieldEl('phone-capture-dock');
    const bar = dock?.querySelector('.phone-capture-dock__bar');
    const stage = dock?.closest('.stage');
    if (!dock || !bar || !stage) return;
    let drag = null;
    const clamp = (value, low, high) => Math.max(low, Math.min(value, Math.max(low, high)));
    const dragGeometry = () => {
      const phone = root.documentElement.classList.contains('phone-layout');
      if (phone) {
        const zoom = Number.parseFloat(root.documentElement.style.zoom) || 1;
        return {
          left: 0,
          top: 0,
          scaleX: zoom,
          scaleY: zoom,
          width: window.innerWidth / zoom,
          height: window.innerHeight / zoom,
        };
      }
      const stageRect = stage.getBoundingClientRect();
      return {
        left: stageRect.left,
        top: stageRect.top,
        scaleX: stage.clientWidth > 0 ? stageRect.width / stage.clientWidth : 1,
        scaleY: stage.clientHeight > 0 ? stageRect.height / stage.clientHeight : 1,
        width: stage.clientWidth,
        height: stage.clientHeight,
      };
    };
    const move = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const geometry = dragGeometry();
      const x = clamp((event.clientX - geometry.left) / geometry.scaleX - drag.offsetX, 0, geometry.width - dock.offsetWidth);
      const y = clamp((event.clientY - geometry.top) / geometry.scaleY - drag.offsetY, 0, geometry.height - dock.offsetHeight);
      dock.style.left = `${x}px`;
      dock.style.top = `${y}px`;
      dock.style.right = 'auto';
      dock.style.bottom = 'auto';
    };
    const stop = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      drag = null;
      bar.releasePointerCapture?.(event.pointerId);
    };
    bar.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button, input, select, a')) return;
      const dockRect = dock.getBoundingClientRect();
      const geometry = dragGeometry();
      drag = {
        pointerId: event.pointerId,
        offsetX: (event.clientX - dockRect.left) / geometry.scaleX,
        offsetY: (event.clientY - dockRect.top) / geometry.scaleY,
      };
      bar.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    });
    bar.addEventListener('pointermove', move);
    bar.addEventListener('pointerup', stop);
    bar.addEventListener('pointercancel', stop);
  })();
  on('capture-disarm', 'click', () => {
    fieldCaptureMode = disarmFieldCaptureTarget(fieldCaptureMode);
    setManualCaptureVisible(false);
    setPhoneCaptureCollapsed(true);
    renderFieldCaptureMode();
    repaintView();
    fieldEl('capture-laser-status').textContent = 'Target disarmed. Select a visible measurement target before the next reading.';
  });
  on('capture-manual-form', 'submit', (event) => {
    event.preventDefault();
    const input = fieldEl('capture-manual-reading');
    if (input?.dataset.skipNextSubmit === 'true') return;
    const valueUnits = parseLength(input?.value);
    const source = input?.dataset.manualEntry === 'true' ? 'manual' : 't1m_hid';
    delete input?.dataset.manualEntry;
    delete input?.dataset.pad;
    try {
      receiveFieldCaptureReading(valueUnits, source);
      input.value = '';
    } catch (error) {
      fieldEl('capture-laser-status').textContent = error.message;
    }
  });
  on('capture-manual-reading', 'change', () => {
    const input = fieldEl('capture-manual-reading');
    if (input?.dataset.manualEntry !== 'true') return;
    delete input.dataset.manualEntry;
    delete input.dataset.pad;
    input.dataset.skipNextSubmit = 'true';
    queueMicrotask(() => delete input.dataset.skipNextSubmit);
    try {
      receiveFieldCaptureReading(parseLength(input.value), 'manual');
      input.value = '';
    } catch (error) {
      fieldEl('capture-laser-status').textContent = error.message;
    }
  });
  on('capture-manual-entry', 'click', () => openManualCaptureEntry());
  on('capture-correct', 'click', () => {
    try {
      fieldCaptureMode = beginArmedFieldCaptureCorrection(fieldCaptureMode);
      renderFieldCaptureMode();
      fieldEl('capture-laser-status').textContent = `${fieldCaptureMode.armedTarget.label} is ready for a replacement Leica, T1M, or manual reading.`;
    } catch (error) {
      fieldEl('capture-laser-status').textContent = error.message;
    }
  });
  on('capture-apply-selected', 'click', () => {
    try {
      applySelectedCaptureReading();
    } catch (error) {
      fieldEl('capture-laser-status').textContent = error.message;
    }
  });
  on('capture-connect-laser', 'click', connectCaptureLaser);
  on('phone-next-field', 'click', async () => {
    const next = fieldSession?.slots.find((slot) => slot.status !== 'confirmed' && slot.status !== 'applied');
    if (!next) return;
    fieldSession = armMeasurementSlot(fieldSession, next.id);
    fieldCorrectionMode = false;
    await persistFieldSession();
    renderFieldSession();
  });
  on('field-start-session', 'click', async () => { await ensureFieldSession(); renderBluetoothReadiness(); renderFieldSession(); });
  on('field-connect-laser', 'click', async () => {
    const status = fieldEl('field-bluetooth-status');
    try {
      status.textContent = 'Choose the awake DISTO from Chrome’s device list. Room Sketch will list service IDs only; it will not read or apply a measurement.';
      const device = await requestBluetoothDevice(window.navigator);
      keepCaptureLaserDevice(device);
      fieldEl('field-listen-leica').disabled = false;
      status.textContent = `${describeBluetoothSelection(device)} Connecting to inspect service IDs…`;
      const inspection = await inspectGattServices(device);
      status.textContent = inspection.serviceUuids.length
        ? `Connected to ${inspection.name}. Primary service IDs: ${inspection.serviceUuids.join(', ')}. No measurement was read or applied.`
        : `Connected to ${inspection.name}, but Chrome reported no primary service IDs. No measurement was read or applied.`;
    } catch (error) {
      status.textContent = error?.name === 'NotFoundError'
        ? 'No device was selected. Keep the DISTO awake and try again when ready.'
        : `Bluetooth inspection did not complete: ${error.message}`;
    }
  });
  on('field-listen-leica', 'click', async () => {
    const status = fieldEl('field-bluetooth-status');
    if (!fieldBluetoothDevice) return;
    try {
      fieldEl('field-listen-leica').disabled = true;
      status.textContent = 'Listening for Leica readings. Arm one field, take several readings if useful, then choose the best candidate before accepting.';
      await subscribeLeicaDistanceEvidence(fieldBluetoothDevice, async (evidence) => {
        if (evidence.error) {
          status.textContent = `Leica evidence arrived but was not accepted: ${evidence.error}. Raw bytes: ${evidence.rawHex}. Nothing was saved.`;
          fieldEl('field-listen-leica').disabled = false;
          return;
        }
        const inches = evidence.meters * 39.3700787402;
        try {
          await ensureFieldSession();
          if (!fieldSession.armedSlotId) {
            status.textContent = `Leica reading received: ${evidence.meters.toFixed(4)} m (${inches.toFixed(2)} in); raw bytes ${evidence.rawHex}. Choose and arm one wall first; nothing was saved.`;
            fieldEl('field-listen-leica').disabled = false;
            return;
          }
          const armedSlot = fieldSession.slots.find((slot) => slot.id === fieldSession.armedSlotId);
          fieldSession = applyCandidateReading(fieldSession, {
            valueUnits: inches,
            source: 'leica_d1',
            device: { name: fieldBluetoothDevice.name ?? 'DISTO', rawHex: evidence.rawHex, meters: evidence.meters },
            allowOverwrite: fieldCorrectionMode,
            makeId: fieldId,
          });
          fieldCorrectionMode = false;
          await persistFieldSession();
          renderFieldSession();
          status.textContent = `Leica candidate captured for ${armedSlot.wallLabel}: ${inches.toFixed(2)} in. Take another reading or choose the best candidate, then Accept; nothing has been applied to the room.`;
        } catch (error) {
          status.textContent = `Leica reading was not saved: ${error.message}. Raw bytes: ${evidence.rawHex}.`;
        } finally {
          // Keep the one active notification subscription in place; repeated DISTO
          // shots add candidates without starting duplicate listeners.
        }
      });
      status.textContent = 'Listening for Leica readings. Arm the target field and take as many readings as needed; choose the best candidate before Accept.';
    } catch (error) {
      status.textContent = `Leica evidence listener did not start: ${error.message}. Nothing was saved.`;
      fieldEl('field-listen-leica').disabled = false;
    }
  });
  on('field-shape-entry', 'click', openShapeCapture);
  on('close-shape-capture', 'click', async () => { fieldEl('shape-capture').hidden = true; await openFieldCapture(); });
  on('shape-undo', 'click', () => { shapeCapturePoints.pop(); shapeCaptureCleanedWalls = []; fieldEl('shape-wall-count-correction').hidden = true; drawShapeCapture(); });
  on('shape-clear', 'click', () => { shapeCapturePoints = []; shapeCaptureCleanedWalls = []; fieldEl('shape-wall-count-correction').hidden = true; drawShapeCapture(); });
  on('shape-cleanup', 'click', async () => {
    const usable = shapeCapturePoints.filter((stroke) => stroke.length >= 2);
    const roughWalls = strokesToWalls(usable, { snapIncrementDegrees: rules.snapIncrementDegrees, makeId: fieldId });
    const hint = fieldEl('shape-capture').querySelector('.shape-capture__hint');
    if (!roughWalls.length) {
      hint.textContent = 'Draw one continuous room perimeter with clear corners before cleanup.';
      return;
    }
    // Store the raw trace for review, but replace it on-screen with the cleaned
    // perimeter so designers see one authoritative shape, not two overlays.
    shapeCaptureCleanedWalls = roughWalls;
    shapeCapturePoints = [];
    drawShapeCapture();
    await ensureFieldSession();
    try {
      fieldSession = resizeWallSlots(fieldSession, roughWalls.length, { makeId: fieldId });
    } catch (error) {
      hint.textContent = error.message;
      return;
    }
    // This shape remains separate field topology. It MUST NOT replace the open
    // tablet room: room sync and geometry retain their independent authority.
    fieldSession = { ...fieldSession, roughShape: { strokes: usable, wallCount: roughWalls.length }, updatedAt: new Date().toISOString() };
    await persistFieldSession();
    fieldEl('shape-wall-count').value = String(roughWalls.length);
    fieldEl('shape-wall-count-correction').hidden = false;
    hint.textContent = `Cleanup found ${roughWalls.length} snapped segments. Confirm or correct the measurement wall count below before continuing.`;
  });
  on('shape-apply-wall-count', 'click', async () => {
    const requestedCount = Number(fieldEl('shape-wall-count').value);
    const hint = fieldEl('shape-capture').querySelector('.shape-capture__hint');
    if (!Number.isInteger(requestedCount) || requestedCount < 1) {
      hint.textContent = 'Enter a whole-number wall count of at least 1.';
      return;
    }
    try {
      await ensureFieldSession();
      fieldSession = resizeWallSlots(fieldSession, requestedCount, { makeId: fieldId });
      fieldSession = { ...fieldSession, roughShape: { ...(fieldSession.roughShape ?? {}), wallCount: requestedCount }, updatedAt: new Date().toISOString() };
      await persistFieldSession();
      hint.textContent = `Measurement targets set to W1–W${requestedCount}. Continue to Field Capture when ready.`;
      fieldEl('shape-wall-count-correction').hidden = true;
    } catch (error) {
      hint.textContent = error.message;
    }
  });
  on('shape-continue', 'click', async () => { fieldEl('shape-capture').hidden = true; await openFieldCapture(); });
  async function reviewFieldReading(valueUnits, source) {
    if (!fieldSession) return;
    try {
      fieldSession = applyCandidateReading(fieldSession, {
        valueUnits,
        source,
        action: 'candidate',
        allowOverwrite: fieldCorrectionMode,
        makeId: fieldId,
      });
      fieldCorrectionMode = false;
      await persistFieldSession();
      fieldEl('field-reading').value = '';
      renderFieldSession();
    } catch (error) {
      fieldEl('field-session-summary').textContent = error.message;
    }
  }

  on('field-reading-form', 'submit', async (evt) => {
    evt.preventDefault();
    await reviewFieldReading(parseLength(fieldEl('field-reading')?.value), 'manual');
  });
  on('field-edit-confirmed', 'click', () => {
    const active = fieldSession?.slots.find((slot) => slot.id === fieldSession.armedSlotId);
    if (!active || active.status !== 'confirmed') return;
    fieldCorrectionMode = true;
    fieldEl('field-reading').value = String(active.valueUnits);
    fieldEl('field-session-summary').textContent = `${active.wallLabel} is in correction mode. Change the reading, then confirm to replace ${formatLength(active.valueUnits, 'inches_fraction')}.`;
    renderFieldSession();
    fieldEl('field-reading').focus();
  });
  on('field-accept-reading', 'click', async () => {
    if (!fieldSession) return;
    const active = fieldSession.slots.find((slot) => slot.id === fieldSession.armedSlotId);
    if (!active?.valueUnits) return;
    try { fieldSession = applyCandidateReading(fieldSession, { valueUnits: active.valueUnits, source: active.source?.kind ?? 'manual', device: active.source?.device, action: 'accept' }); await persistFieldSession(); renderFieldSession(); } catch (error) { fieldEl('field-session-summary').textContent = error.message; }
  });
  on('field-apply-reading', 'click', () => {
    const result = applyActiveConfirmedReading();
    fieldEl('field-session-summary').textContent = result.applied.length
      ? 'Applied this confirmed reading to the active sketch.'
      : 'This field cannot be applied until it is confirmed and matches a sketch wall.';
    renderFieldSession();
  });
  on('field-discard-reading', 'click', async () => {
    if (!fieldSession) return;
    try { fieldSession = discardCandidateReading(fieldSession); await persistFieldSession(); renderFieldSession(); } catch (error) { fieldEl('field-session-summary').textContent = error.message; }
  });
  on('field-feature-form', 'submit', async (evt) => {
    evt.preventDefault(); if (!fieldSession) return;
    const primaryOffset = Number(fieldEl('field-feature-offset')?.value);
    const secondaryOffset = Number(fieldEl('field-feature-secondary-offset')?.value);
    const type = fieldEl('field-feature-type').value;
    const isCeiling = ['attic_hatch', 'attic_stairs', 'ceiling_fan', 'ceiling_light', 'ceiling_sprinkler'].includes(type);
    try {
      if (isCeiling) {
        const firstWall = fieldEl('field-feature-wall').value;
        const secondWall = fieldEl('field-feature-reference-wall').value;
        fieldSession = addCeilingFieldFeature(fieldSession, { type, referenceWalls: [firstWall, secondWall],
          measurements: [
            ...(Number.isFinite(primaryOffset) && primaryOffset >= 0 ? [{ key: `from_${firstWall.toLowerCase()}`, valueUnits: primaryOffset, provenance: 'manual_edited' }] : []),
            ...(Number.isFinite(secondaryOffset) && secondaryOffset >= 0 ? [{ key: `from_${secondWall.toLowerCase()}`, valueUnits: secondaryOffset, provenance: 'manual_edited' }] : []),
          ], makeId: fieldId });
      } else {
        fieldSession = addFieldFeature(fieldSession, { type, hostWallLabel: fieldEl('field-feature-wall').value,
          measurements: Number.isFinite(primaryOffset) && primaryOffset >= 0 ? [{ key: 'offset_from_start', valueUnits: primaryOffset, provenance: 'manual_edited' }] : [], makeId: fieldId });
      }
      await persistFieldSession();
      fieldEl('field-feature-offset').value = '';
      fieldEl('field-feature-secondary-offset').value = '';
      renderFieldSession();
    } catch (error) { fieldEl('field-session-summary').textContent = error.message; }
  });
  on('open-field-review', 'click', async () => {
    closeFieldCapture();
    await openFieldReview();
  });
  on('close-field-review', 'click', () => { fieldEl('field-review').hidden = true; });
  on('field-apply-selected', 'click', async () => {
    const sessionId = fieldEl('field-review').dataset.sessionId;
    const session = fieldSession?.id === sessionId ? fieldSession : await getMeasurementSession(sessionId);
    if (!session) return;
    const selected = [...fieldEl('field-review-list').querySelectorAll('input:checked')].map((input) => input.value);
    const result = applyReviewedMeasurements(session, model.state.walls, selected, (id, units) => model.setWallMeasuredLength(id, String(units)));
    fieldEl('field-review-summary').textContent = result.applied.length ? `Applied ${result.applied.length} confirmed reading${result.applied.length === 1 ? '' : 's'}; review the closure before saving.` : 'No selected confirmed readings were applied.';
    if (result.applied.length) { renderAll(); scheduleSave(); }
  });

  on('open-library', 'click', () => {
    libraryFilter = 'sketches';
    librarySearch = '';
    libraryNote = '';
    expandedLibraryOpportunities.clear();
    openSketchesPanel();
  });
  on('save-current-template-library', 'click', () => saveAsTemplate(current.id));
  on('close-sketches', 'click', closeSketchesPanel);
  on('new-sketch', 'click', () => openIdentityPanel('opportunity'));
  on('identity-form', 'submit', createNamedWorkspace);
  on('close-identity', 'click', closeIdentityPanel);
  on('cancel-identity', 'click', closeIdentityPanel);
  on('identity-panel', 'click', (evt) => {
    if (evt.target.id === 'identity-panel') closeIdentityPanel();
  });
  // Library filter tabs (Sketches / Templates) and live search.
  on('filter-sketches', 'click', () => { libraryFilter = 'sketches'; libraryNote = ''; refreshSketchesList(); });
  on('filter-templates', 'click', () => { libraryFilter = 'templates'; libraryNote = ''; refreshSketchesList(); });
  on('sketches-search', 'input', (evt) => { librarySearch = evt.target.value; refreshSketchesList(); });
  // Tapping the panel backdrop (outside the card) closes it.
  on('sketches-panel', 'click', (evt) => {
    if (evt.target.id === 'sketches-panel') closeSketchesPanel();
  });

  // Collapsible side-panel sections: click a section title to fold it, so the
  // panel doesn't need endless scrolling as sections stack up.
  for (const section of root.querySelectorAll('.panel-section')) {
    const title = section.querySelector('.table-panel__title');
    title?.addEventListener('click', () => section.classList.toggle('panel-section--collapsed'));
  }

  setMode('draw');
  renderAll();

  // Ask for durable storage (so the sketch isn't evicted under disk pressure),
  // then reopen whichever sketch was last open. Both are async: the app paints
  // an empty canvas immediately and re-renders with the restored sketch a
  // moment later, so a slow store never delays first paint.
  requestPersistentStorage();
  // Response delivery must not wait for IndexedDB. A tablet can leave a local
  // database request pending indefinitely; the returned values still need to
  // appear immediately instead of looking blank. Run once against the fresh
  // in-memory sketch, then again if/when its saved opportunity space restores.
  handleInventoryHash();
  Promise.resolve(restoreLastOpened()).then(() => {
    if (!current.client || !current.opportunity || !current.space) {
      libraryFilter = 'sketches';
      librarySearch = '';
      libraryNote = 'Choose an opportunity, or create one with its first named space.';
      openSketchesPanel();
    }
  }).finally(handleInventoryHash);
  initAdminPanel(); // async; shows the admin section only for app_admins
  initInteriorEditor(); // per-section interior editor overlay (ADR 0056)

  return { model, canvas, renderAll, renderGeometry, restoreLastOpened, onRemoteChange };
}
